import { message } from 'antd';
import * as THREE from 'three';
import { CSG } from 'three-csg-ts';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { openMessageError } from '../../components/common/Message/openMessageError';
import { openNotification } from '../../components/common/Notification/openNotification';
import { Icon } from '../../components/icon/icon';
import {
  CeilingDoor,
  CutPosition,
  DoorDirection,
  DoorType,
  HorizontalType,
  InsideType,
  PointSnapPos,
  TypeCabinet,
  TypeConnerModule,
} from '../../constants/enum';
import i18n from '../../languages/i18n';
import callApi from '../../utils/api/callAPI';
import {
  greaterOrEqual,
  lessOrEqual,
  nearlyEqual,
  nearlyEqualVector,
  roundTo,
} from '../../utils/geometry/epsilon';
import { GeometryUtils } from '../../utils/geometry/GeometryUtils';
import ModuleTypeHelper from '../../utils/helpers/ModuleTypeHelper';
import {
  evaluateExpression,
  getInputType,
  isValidExpression,
} from '../../utils/string/inputExpression';
import InsideCabinetHandler from '../core/handlers/InsideCabinetHandler';
import InsideReferenceManager from '../core/handlers/InsideReferenceManager';
import { EdgeDetector } from '../core/layout/EdgeDetector';

// Constants
const slit = 0.0; // khe hở cửa
const topDoorGapBase = 0.03;
const countertopGap = 0.02;
const minDoorGap = 0.005;
const handlePadding = 0.03;
const handleSizeDefault = { x: 0.01, y: 0.15, z: 0.02 };
const lockPadding = 0.05;
const handleYRcm = 1.1;
const thicknessBase = 0.017;
const defaultNumDrawer = 3;
const defaultBottomGap = 0.03;
const defaultTopGap = 0.04;

const hinge45Clearance = 0.03;
const doorInsetOnPanel = 0.01;
const doorInsetOnPanel45 = 0.04;
const doorInsetOnPanelHandle = 0.065;
const sizeBarce = 0.05;

const aluminumFrameDepth = 0.02;
const aluminumHandleSize = 0.01;

const DRAWER_3_TOP_GAP = 0.03;
const DRAWER_3_BOTTOM_GAP = 0.02;

const DRAWER_DEFAULT_TOP_GAP = 0.04;
const DRAWER_DEFAULT_BOTTOM_GAP = 0.03;

const DRAWER_DEFAULT_TOP_GAP_TV = 0.03;
const DRAWER_DEFAULT_BOTTOM_GAP_TV = 0.02;

// Dung sai khi đối chiếu giao điểm ray với mặt của bounding box (1/1000 mm)
const RAY_DISTANCE_TOLERANCE = 1e-6;

export class InsideLoader {
  constructor(scene) {
    this.scene = scene;
    this.initializeLabelStyles();
    // Distance visualization objects
    this.distanceLines = new Map(); // Line objects cho distance
    this.distanceLabels = new Map(); // CSS2D labels cho distance
    this._tempVector3 = new THREE.Vector3();
    this.referenceManager = new InsideReferenceManager();

    this.cabinetType = TypeCabinet.KITCHEN;

    this.editting = false;

    this.beveled = false;
    this.topDoorGap = 0.03;

    this.braceHeight = 0.05;
    this.braceIndent = 0.01;
    this.braceThickness = 0.017;
    const accessoryCache = new Map();
    const doorCache = new Map();
  }

  setEditting(isEditting) {
    this.editting = isEditting;
  }

  setCabinetType(type) {
    this.cabinetType = type;
  }

  changeBeveledInside(beveled, cloneModule) {
    this.beveled = beveled;
    if (beveled) {
      this.topDoorGap = topDoorGapBase;
    } else {
      this.topDoorGap = 0;
    }

    const nextStep = true;
    cloneModule && cloneModule.changeBevelebModule(beveled);
    cloneModule && this.rescaleDoor(cloneModule, null, nextStep);
  }

  /**
   * Initialize CSS styles for distance labels
   * Thêm styles vào document head nếu chưa có
   */
  initializeLabelStyles() {
    const styleId = 'controller-styles';

    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
                .distance-label {
                    font-family: 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    font-weight: 500;
                    font-size: 10px;
                    color: #000000;
                    background-color: rgba(255, 255, 255, 0.95);
                    padding: 2px 4px;
                    border-radius: 2px;
                    border: 0px solid rgba(0, 0, 0, 0.2);
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                    white-space: nowrap;
                    user-select: none;
                    pointer-events: auto;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    z-index: 1000;
                    min-width: 20px;
                    text-align: center;
                    display: inline-block;
                }

                .distance-label:hover {
                    background-color: rgba(255, 255, 255, 1);
                    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
                    transform: scale(1.05);
                }

                /* Style cho label đang active */
                .distance-label.active {
                    background-color: #ffd93d;
                    color: #000000;
                    font-weight: 600;
                }

                /* Style cho editing mode */
                .distance-label.editing {
                    padding: 0;
                    background: transparent;
                    border: none;
                    box-shadow: none;
                }

                /* Input field cho editing */
                .distance-input {
                    font-family: 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    font-weight: 500;
                    font-size: 10px;
                    color: #000000;
                    background-color: #ffffff;
                    padding: 2px 4px;
                    border-radius: 4px;
                    border: 2px solid #FFC914;
                    outline: none;
                    width: 60px;
                    text-align: center;
                    transition: all 0.2s ease;
                }

                .distance-input:focus {
                    border-color: #FFC914;
                    transform: scale(1.05);
                }

                .distance-input.invalid {
                    border-color: #dc3545;
                    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
                    animation: shake 0.3s;
                }

                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-5px); }
                    75% { transform: translateX(5px); }
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: scale(0.8);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }

                .distance-label.new {
                    animation: fadeIn 0.3s ease-out;
                }
            `;
      document.head.appendChild(style);
    }
  }

  getSize(object3D) {
    return GeometryUtils.getSize(object3D);
  }

  disposeObject3D(object3D) {
    return GeometryUtils.disposeObject3D(object3D);
  }

  getSizeRotation(object3D, inside3D) {
    return GeometryUtils.getSizeRotation(object3D, inside3D);
  }

  getMeshByName(object3D, name) {
    return GeometryUtils.getMeshByName(object3D, name);
  }

  getMeshByNameOrIncludes(object3D, name) {
    return GeometryUtils.getMeshByNameOrIncludes(object3D, name);
  }

  findMeshByUuid(group, uuid) {
    return GeometryUtils.findMeshByUuid(group, uuid);
  }

  getModuleOrientation(group) {
    return GeometryUtils.getModuleOrientation(group);
  }

  findEdgeMeshes(insideGltf, clonedModule) {
    return EdgeDetector.findEdgeMeshes(insideGltf, clonedModule);
  }

  setInfoModelChild = gltfScene => {
    gltfScene?.traverse(child => {
      if (child.isMesh || child.isGroup) {
        child.userData.type = gltfScene.userData.type;

        if (gltfScene.userData.type === InsideType.DOOR) {
          child.userData.useAcrylic = gltfScene.userData?.useAcrylic || false;
        }

        child.userData.parentGltf = gltfScene.uuid;
        const size = this.getSize(child);
        child.userData.sizeDefault = size;
      }
    });
    const size = this.getSize(gltfScene);
    gltfScene.userData.sizeDefault = size;
  };

  setInfoModelChild2 = gltfScene => {
    gltfScene?.traverse(child => {
      if (child.isMesh || child.isGroup) {
        child.userData.type = gltfScene.userData.type;

        if (gltfScene.userData.type === InsideType.DOOR) {
          child.userData.useAcrylic = gltfScene.userData?.useAcrylic || false;
        }

        child.userData.parentGltf = gltfScene.uuid;
        const size = this.getSize(child);
      }
    });
    const size = this.getSize(gltfScene);
  };

  scaleCabinetShelfWidth = (gltfScene, sizeDefault, sizeScale) => {
    gltfScene?.traverse(child => {
      if (child.isMesh) {
        let scaleZ;
        const temp = sizeScale / sizeDefault.z;
        if (sizeDefault.z < sizeScale) {
          if (child.name === 'MAT_TRUOC' || child.name === 'MAT_SAU') {
            scaleZ = (sizeScale - 0.061) / child.userData.sizeDefault.z;
            child.scale.z = scaleZ;
          }
          if (child.name === 'MAT_PHAI') {
            child.position.z = child.position.z - (sizeScale - sizeDefault.z);
          }
          if (child.name === 'TAM_DAY') {
            scaleZ = (sizeScale - 0.027) / child.userData.sizeDefault.z;
            child.scale.z = scaleZ;
          }
          if (child.name === 'RAY_PHAI') {
            child.position.z = child.position.z - (sizeScale - sizeDefault.z);
          }
        } else {
          if (child.name === 'MAT_TRUOC' || child.name === 'MAT_SAU') {
            scaleZ = (sizeScale - 0.061) / child.userData.sizeDefault.z;
            child.scale.z = scaleZ;
          }
          if (child.name === 'MAT_PHAI') {
            child.position.z = child.position.z + (sizeDefault.z - sizeScale);
          }
          if (child.name === 'TAM_DAY') {
            scaleZ = (sizeScale - 0.027) / child.userData.sizeDefault.z;
            child.scale.z = scaleZ;
          }
          if (child.name === 'RAY_PHAI') {
            child.position.z = child.position.z + (sizeDefault.z - sizeScale);
          }
        }
      }
    });
  };

  scaleCabinetShelfDepth = (gltfScene, sizeDefault, sizeScale) => {
    gltfScene?.traverse(child => {
      if (child.isMesh) {
        let scaleX;
        if (sizeDefault.x < sizeScale) {
          if (child.name === 'MAT_TRUOC') {
            child.position.x = child.position.x + (sizeScale - sizeDefault.x);
          }
          if (child.name === 'MAT_PHAI' || child.name === 'MAT_TRAI') {
            scaleX = sizeScale / child.userData.sizeDefault.x;
            child.scale.x = scaleX;
          }
          if (child.name === 'TAM_DAY') {
            scaleX = sizeScale / child.userData.sizeDefault.x;
            child.scale.x = scaleX;
          }
          if (child.name === 'RAY_PHAI' || child.name === 'RAY_TRAI') {
            const deltaXRay =
              gltfScene.userData.sizeDefault.x - child.userData.sizeDefault.x;
            const sizeXRay = sizeScale - deltaXRay;
            child.scale.x = sizeXRay / child.userData.sizeDefault.x;
          }
        } else {
          if (child.name === 'MAT_TRUOC') {
            child.position.x = child.position.x - (sizeDefault.x - sizeScale);
          }
          if (child.name === 'MAT_PHAI' || child.name === 'MAT_TRAI') {
            scaleX = sizeScale / child.userData.sizeDefault.x;
            child.scale.x = scaleX;
          }
          if (child.name === 'TAM_DAY') {
            scaleX = sizeScale / child.userData.sizeDefault.x;
            child.scale.x = scaleX;
          }
          if (child.name === 'RAY_PHAI' || child.name === 'RAY_TRAI') {
            const deltaXRay =
              gltfScene.userData.sizeDefault.x - child.userData.sizeDefault.x;
            const sizeXRay = sizeScale - deltaXRay;
            child.scale.x = sizeXRay / child.userData.sizeDefault.x;
            // child.position.x = child.position.x - (sizeDefault.x - sizeScale);
          }
        }
      }
    });
  };

  resetGroupPositionToCurrentPosition(group) {
    const boundingBox = new THREE.Box3().setFromObject(group);

    const min = boundingBox.min;
    const max = boundingBox.max;

    const offset = new THREE.Vector3(
      min.x - group.position.x,
      min.y - group.position.y,
      max.z - group.position.z
    );

    group.position.add(offset);

    group.children.forEach(child => {
      child.position.sub(offset);
    });
  }

  // Lấy local transform của obj tương đối so với parent (cabinet group)
  getLocalTRS(obj) {
    return {
      p: [obj.position.x, obj.position.y, obj.position.z],
      r: [obj.rotation.x, obj.rotation.y, obj.rotation.z],
    };
  }

  applyLocalTRS(obj, trs) {
    if (!trs) return;
    const [px, py, pz] = trs.p || [0, 0, 0];
    const [rx, ry, rz] = trs.r || [0, 0, 0];
    const [sx, sy, sz] = trs.s || [1, 1, 1];
    obj.position.set(px, py, pz);
    obj.rotation.set(rx, ry, rz);
    obj.updateMatrixWorld(true);
  }

  async changeSize(
    clonedModule,
    model,
    originalSize,
    savedSize,
    type,
    localTRS
  ) {
    switch (type) {
      case InsideType.HPANEL: // Xà ngang
        const [, ryHpanel] = localTRS.r || [0, 0, 0];

        if (ryHpanel === 0) {
          model.scale.x = savedSize.x / originalSize.x;
          model.scale.z = savedSize.z / originalSize.z;
        } else if (ryHpanel === Math.PI / 2) {
          model.scale.z = savedSize.x / originalSize.z;
          model.scale.x = savedSize.z / originalSize.x;
        }
        model.userData.type = type;

        if (model.userData?.columnInfo) {
          const leftPos = model.userData.columnInfo.leftPos;
          const leftColPos = model.userData.columnInfo.leftColPos;
          // const leftEdge = model.userData.columnInfo.leftEdge;

          const md = this.createPanelColumn(
            clonedModule,
            model,
            null,
            leftPos,
            leftColPos
          );

          return md;
        }
        return model;

      case InsideType.ACCESSORY_FLIP: // Xà ngang
        model.scale.x = savedSize.x / originalSize.x;
        model.scale.z = savedSize.z / originalSize.z;
        model.userData.type = type;
        return model;

      case InsideType.HPANEL_SHOE: // Xà ngang
        model.userData.type = type;
        const clone1 = model.clone();
        const clone2 = model.clone();
        model.userData.sizeData.forEach(child => {
          if (child.name === 'HPANEL-SHOE-MAIN') {
            clone1.name = child.name;
            clone1.scale.x = child.size.x / originalSize.x;
            clone1.scale.y = child.size.y / originalSize.y;
            clone1.scale.z = child.size.z / originalSize.z;
            clone1.rotation.set(
              child.rotation.x,
              child.rotation.y,
              child.rotation.z
            );
          }
          if (child.name === 'HPANEL-SHOE-SUB') {
            clone2.name = child.name;
            clone2.scale.x = (child.size.x * model.scale.x) / originalSize.x;
            clone2.scale.y = (child.size.y * model.scale.y) / originalSize.y;
            clone2.scale.z = (child.size.z * model.scale.z) / originalSize.z;
            clone2.rotation.set(
              child.rotation.x,
              child.rotation.y,
              child.rotation.z
            );
            clone2.position.set(
              child.position.x,
              child.position.y,
              child.position.z
            );
          }
        });
        const cloneGroup = new THREE.Group();
        clone1.userData.parentGltf = cloneGroup.uuid;
        clone2.userData.parentGltf = cloneGroup.uuid;
        cloneGroup.add(clone1);
        cloneGroup.add(clone2);
        model = cloneGroup;
        return model;

      case InsideType.HPANEL_RIGHT: // Xà ngang tu goc phai
        model.scale.x = savedSize.x / originalSize.x;
        model.scale.z = savedSize.z / originalSize.z;
        model.userData.type = type;
        return model;

      case InsideType.HPANEL_LEFT: // Xà ngang tu goc trai
        model.scale.x = savedSize.x / originalSize.x;
        model.scale.z = savedSize.z / originalSize.z;
        model.userData.type = type;
        return model;

      case InsideType.VPANEL: // Xà dọc
        const [, ryVpanel] = localTRS.r || [0, 0, 0];

        if (ryVpanel === 0) {
          model.scale.x = savedSize.x / originalSize.x;
          model.scale.y = savedSize.y / originalSize.y;
        } else if (ryVpanel === Math.PI / 2) {
          model.scale.x = savedSize.z / originalSize.x;
          model.scale.y = savedSize.y / originalSize.y;
        }
        model.userData.type = type;
        return model;

      case InsideType.ACCESSORY_GENERAL: // Xà dọc
        model.scale.x = savedSize.x / originalSize.x;
        model.scale.z = savedSize.z / originalSize.z;
        model.scale.y = savedSize.y / originalSize.y;
        model.userData.type = type;
        return model;

      case InsideType.ACCESSORY_DISH_LOWER: // Xà dọc
        const [, ryAccessory] = localTRS.r || [0, 0, 0];

        if (ryAccessory === 0) {
          model.scale.x = savedSize.x / originalSize.x;
          model.scale.z = savedSize.z / originalSize.z;
          model.scale.y = savedSize.y / originalSize.y;
        } else if (ryAccessory === Math.PI / 2) {
          model.scale.x = savedSize.z / originalSize.x;
          model.scale.z = savedSize.x / originalSize.z;
          model.scale.y = savedSize.y / originalSize.y;
        }

        model.userData.type = type;
        return model;

      case InsideType.ACCESSORY_HOOD: // Xà dọc
        model.scale.x = savedSize.x / originalSize.x;
        model.scale.z = savedSize.z / originalSize.z;
        model.scale.y = savedSize.y / originalSize.y;
        model.userData.type = type;
        return model;

      case InsideType.DRAWER: // Đợt kệ
        const [, ryDrawer] = localTRS.r || [0, 0, 0];

        if (ryDrawer === 0) {
          this.scaleCabinetShelfWidth(model, originalSize, savedSize.z);
          InsideCabinetHandler.scaleDepth(model, savedSize.x);
        } else if (ryDrawer === Math.PI / 2) {
          this.scaleCabinetShelfWidth(model, originalSize, savedSize.x);
          InsideCabinetHandler.scaleDepth(model, savedSize.z);
        }

        InsideCabinetHandler.scaleHeight(model, savedSize.y - 0.008);
        model.userData.type = type;
        return model;

      case InsideType.U_DRAWER: // Đợt kệ
        InsideCabinetHandler.scaleDepth(model, savedSize.x);
        InsideCabinetHandler.scaleHeight(model, savedSize.y);
        InsideCabinetHandler.scaleWidth(model, savedSize.z, originalSize);
        model.userData.type = type;
        return model;

      case InsideType.DOOR: //Cửa
        const [rx, ry, rz] = localTRS.r || [0, 0, 0];
        if (ry === 0) {
          model.scale.x = savedSize.x / originalSize.x;
          model.scale.z = savedSize.z / originalSize.z;
        } else if (ry === Math.PI / 2) {
          model.scale.z = savedSize.x / originalSize.z;
          model.scale.x = savedSize.z / originalSize.x;
        }
        model.scale.y = savedSize.y / originalSize.y;
        model.userData.type = type;

        const materialType = model.userData?.materialType;
        const doorDirection = model.userData?.doorDirection;
        const horizontalType = model.userData?.horizontalType;
        // if (!materialType?.includes('tron')) {
        setTimeout(async () => {
          await this.cutSpecialDoor(
            clonedModule,
            model,
            materialType,
            doorDirection,
            horizontalType
          );
        }, 200);
      // }
      // return model;

      case InsideType.BRACE: //Bạ Cửa
        const [rxBrace, ryBrace, rzBrace] = localTRS.r || [0, 0, 0];
        if (ryBrace === 0) {
          model.scale.x = savedSize.x / originalSize.x;
          model.scale.z = savedSize.z / originalSize.z;
        } else if (ryBrace === Math.PI / 2) {
          model.scale.z = savedSize.x / originalSize.z;
          model.scale.x = savedSize.z / originalSize.x;
        }
        model.scale.y = savedSize.y / originalSize.y;
        model.userData.type = type;
        return model;

      case InsideType.MIRROR_DOOR: //Gương soi
        model.scale.x = savedSize.x / originalSize.x;
        model.scale.z = savedSize.z / originalSize.z;
        model.scale.y = savedSize.y / originalSize.y;
        model.userData.type = type;
        return model;

      case InsideType.ACCESSORY_DISH_UPPER: // Xà ngang
        model.scale.x = savedSize.x / originalSize.x;
        model.scale.z = savedSize.z / originalSize.z;
        model.userData.type = type;
        return model;

      case InsideType.VPANEL2: {
        // TẠO GROUP MỚI
        const xuongTruoc1 = this.getMeshByName(
          clonedModule.group,
          'XUONG-TRUOC-1'
        );
        const xuongTruoc2 = this.getMeshByName(
          clonedModule.group,
          'XUONG-TRUOC-2'
        );
        const xuongSau = this.getMeshByName(clonedModule.group, 'XUONG-SAU');

        const sizeXuongTruoc1 = this.getSize(xuongTruoc1);
        const sizeXuongTruoc2 = this.getSize(xuongTruoc2);

        const sizeXModel1 = savedSize.x;
        const sizeYModel1 = savedSize.y - this.getSize(xuongSau).y;

        const sizeXModel2 =
          xuongTruoc2.position.x -
          xuongSau.position.x -
          xuongSau.userData.originalSize.x;
        const sizeYModel2 = sizeXuongTruoc2.y;

        const cloneGroup = new THREE.Group();
        const clone1 = model.clone();
        const clone2 = model.clone();

        // scale theo originalSize của MESH GỐC (model)
        clone1.scale.x = (sizeXModel1 * model.scale.x) / originalSize.x;
        clone1.scale.y = (sizeYModel1 * model.scale.y) / originalSize.y;

        clone2.scale.x = (sizeXModel2 * model.scale.x) / originalSize.x;
        clone2.scale.y = (sizeYModel2 * model.scale.y) / originalSize.y;

        clone1.position.set(0, 0, 0);
        clone2.position.set(0, 0, 0);

        clone2.position.x =
          clone2.position.x + xuongSau.userData.originalSize.x;

        const sizeClone1 = this.getSize(clone1);
        clone2.position.y = clone1.position.y + sizeClone1.y;

        cloneGroup.add(clone1);
        cloneGroup.add(clone2);

        // chuyển type sang group
        cloneGroup.userData = { ...(model.userData || {}), type: 11 };
        cloneGroup.name = 'MAIN-HPANEL';
        model.userData.type = type;
        return cloneGroup; // <<< TRẢ VỀ GROUP MỚI
      }

      case InsideType.ACCESSORY_FRIDGE: {
        // clonedModule.scaleModuleWithFormular({
        //   width: originalSize.x + 0.1,
        //   height: originalSize.y + 0.05,
        // });
      }

      case InsideType.ACCESSORY_SINK:
      case InsideType.ACCESSORY_COOKER: {
        const [, ryAccessory] = localTRS.r || [0, 0, 0];

        if (ryAccessory === 0) {
          model.scale.x = savedSize.x / originalSize.x;
          model.scale.z = savedSize.z / originalSize.z;
        } else if (ryAccessory === Math.PI / 2) {
          model.scale.z = savedSize.x / originalSize.z;
          model.scale.x = savedSize.z / originalSize.x;
        }

        model.userData.type = type;

        return model;
      }

      case InsideType.CLOTHES_HANGER: // Thanh treo quần áo
        const [, ryClothesHanger] = localTRS.r || [0, 0, 0];

        if (ryClothesHanger === 0) {
          model.scale.x = savedSize.x / originalSize.x;
          model.scale.z = savedSize.z / originalSize.z;
        } else if (ryClothesHanger === Math.PI / 2) {
          model.scale.z = savedSize.x / originalSize.z;
          model.scale.x = savedSize.z / originalSize.x;
        }
        model.userData.type = type;

        return model;

      case InsideType.LED_RECESSED: // Led âm
        const [, ryLedRec] = localTRS.r || [0, 0, 0];

        if (ryLedRec === 0) {
          model.scale.x = savedSize.x / originalSize.x;
          model.scale.z = savedSize.z / originalSize.z;
        } else if (ryLedRec === Math.PI / 2) {
          model.scale.z = savedSize.x / originalSize.z;
          model.scale.x = savedSize.z / originalSize.x;
        }
        model.userData.type = type;

        this.addInsideLeds(model);
        return model;

      case InsideType.LED_CORNER: // Led góc
        const [, ryLedCor] = localTRS.r || [0, 0, 0];

        if (ryLedCor === 0) {
          model.scale.x = savedSize.x / originalSize.x;
          model.scale.z = savedSize.z / originalSize.z;
        } else if (ryLedCor === Math.PI / 2) {
          model.scale.z = savedSize.x / originalSize.z;
          model.scale.x = savedSize.z / originalSize.x;
        }
        model.userData.type = type;

        this.addInsideLeds(model);
        return model;

      case InsideType.KHUNG_SAT:
        model.scale.x = savedSize.x / originalSize.x;
        model.scale.z = savedSize.z / originalSize.z;
        model.userData.type = type;
        return model;

      case InsideType.LIFT_SUPPORT:
        model.scale.x = 0.0001 / originalSize.x;
        model.scale.y = 0.0001 / originalSize.y;
        model.scale.z = 0.0001 / originalSize.z;
        model.userData.type = type;
        return model;

      default:
        return model;
    }
  }

  drawBoxBorder = (mesh, currentCabinet, module) => {
    const ids = this.getUuidInMesh(mesh);
    const boundingBox = new THREE.Box3().setFromObject(mesh);
    const center = new THREE.Vector3();
    boundingBox.getCenter(center);
    const meshPoints = currentCabinet.getActiveMeshPoints(module);

    const orientation = this.getModuleOrientation(module?.group);

    // Trục ngang giữ nguyên quy ước cũ: module xoay 90° dùng chung chiều với 270°
    const sideOrientation =
      orientation === 'ROTATED_90' ? 'ROTATED_270' : orientation;

    // Tìm cả 6 cạnh bằng ray từ tâm bounding box qua tâm từng mặt
    const { leftEdge, rightEdge, topEdge, bottomEdge, frontEdge, backEdge } =
      this.findEdgesAroundMesh({
        mesh,
        meshPoints,
        ids,
        orientation,
        sideOrientation,
        module,
      });
    const sizeRight = this.getSize(rightEdge?.mesh);
    const sizeLeft = this.getSize(leftEdge?.mesh);
    const sizeBottom = this.getSize(bottomEdge?.mesh);
    const sizeTop = this.getSize(topEdge?.mesh);
    const sizeFront = this.getSize(frontEdge?.mesh);
    const sizeBack = this.getSize(backEdge?.mesh);

    this.distanceMeshToEdges({
      mesh,
      currentCabinet,
      prevPos: mesh.position.clone(),
      bottomEdge,
      topEdge,
      leftEdge,
      rightEdge,
      frontEdge,
      backEdge,
      orientation,
      sideOrientation,
      module,
    });

    return {
      edges: {
        rightEdge,
        leftEdge,
        bottomEdge,
        topEdge,
        frontEdge,
        backEdge,
      },
      sizes: {
        sizeRight,
        sizeLeft,
        sizeBottom,
        sizeTop,
        sizeFront,
        sizeBack,
      },
    };
  };

  scaleHeight(gltfScene, sizeScale, cabinet, movePos) {
    const { type } = gltfScene.userData;

    // Early return for unsupported types
    if ([InsideType.HPANEL, InsideType.DRAWER].includes(type)) {
      return;
    }

    switch (type) {
      case InsideType.VPANEL:
        this.scaleVPanel(gltfScene, sizeScale, movePos);
        break;

      case InsideType.VPANEL2:
        this.scaleVPanel2(gltfScene, sizeScale);
        break;

      default:
        console.warn(`Unknown panel type: ${type}`);
    }
  }

  scaleVPanel(gltfScene, sizeScale, movePos) {
    if (!gltfScene.isGroup) {
      this.scaleSimpleMesh(gltfScene, sizeScale);
      return;
    }

    const sizeParent = this.getSize(gltfScene);

    gltfScene.traverse(child => {
      if (!child.isMesh || !child.userData.sizeDefault?.y) return;

      const shouldScale = movePos || this.isLastChild(child);
      if (!shouldScale) return;

      const size = this.getSize(child);
      const delta = sizeParent.y - size.y;
      const sizeY = sizeScale - delta;
      const scaleY = sizeY / child.userData.sizeDefault.y;

      child.scale.y = scaleY;

      if (movePos) {
        child.position.y -= sizeScale - sizeParent.y;
      }
    });
  }

  scaleVPanel2(gltfScene, sizeScale) {
    if (!gltfScene.isGroup) {
      this.scaleSimpleMesh(gltfScene, sizeScale);
      return;
    }

    const [firstChild, lastChild] = gltfScene.children;
    if (!firstChild || !lastChild) return;

    // --- STEP 1: Lấy box3 theo world space ---
    const boxFirst = new THREE.Box3().setFromObject(firstChild);
    const boxLast = new THREE.Box3().setFromObject(lastChild);

    const firstHeight = boxFirst.max.y - boxFirst.min.y;
    const lastHeight = boxLast.max.y - boxLast.min.y;
    if (firstHeight === 0) return;

    // Lưu vị trí world của lastChild (tọa độ đáy)
    const lastBottom = new THREE.Vector3(
      (boxLast.min.x + boxLast.max.x) / 2,
      boxLast.min.y,
      (boxLast.min.z + boxLast.max.z) / 2
    );

    // --- STEP 2: Tính tỉ lệ scale cho firstChild ---
    const scaleRatio = sizeScale / firstHeight;

    // --- STEP 3: Scale firstChild quanh đúng tâm của nó ---
    const firstCenter = new THREE.Vector3();
    boxFirst.getCenter(firstCenter);

    // Dịch firstChild về gốc trước khi scale (để scale quanh trung tâm thật)
    firstChild.position.sub(firstCenter);
    firstChild.scale.y *= scaleRatio;
    firstChild.position.add(firstCenter);

    firstChild.updateMatrixWorld(true);

    // --- STEP 4: Cập nhật lại group ---
    gltfScene.updateMatrixWorld(true);

    // --- STEP 5: Tính toán offset cần bù để giữ lastChild đứng yên ---
    const newBoxLast = new THREE.Box3().setFromObject(lastChild);
    const newLastBottom = new THREE.Vector3(
      (newBoxLast.min.x + newBoxLast.max.x) / 2,
      newBoxLast.min.y,
      (newBoxLast.min.z + newBoxLast.max.z) / 2
    );

    const offsetY = lastBottom.y - newLastBottom.y;
    gltfScene.position.y += offsetY;
    lastChild.position.y = sizeScale;

    lastChild.updateMatrixWorld(true);
    gltfScene.updateMatrixWorld(true);
  }

  scaleSimpleMesh(mesh, sizeScale) {
    const defaultSize = mesh.userData.sizeDefault?.y;
    if (!defaultSize || defaultSize === 0) {
      console.warn('Invalid sizeDefault.y for mesh');
      return;
    }
    mesh.scale.y = sizeScale / defaultSize;
  }

  isLastChild(child) {
    return child.parent?.children[child.parent.children.length - 1] === child;
  }

  scaleWidth(gltfScene, sizeScale, module) {
    const originalSize = this.getSizeRotation(module.group, gltfScene);
    InsideCabinetHandler.scaleWidth(
      gltfScene,
      sizeScale,
      originalSize,
      module,
      this.scaleCabinetShelfWidth
    );
  }

  // Lấy các tâm của các mặt của hình hộp (box) dựa trên center và size
  getFaceCenters = (center, size) => {
    const hx = size.x / 2;
    const hy = size.y / 2;
    const hz = size.z / 2;

    const { x: cx, y: cy, z: cz } = center;

    return {
      X1: new THREE.Vector3(cx + hx, cy, cz),
      X2: new THREE.Vector3(cx - hx, cy, cz),
      Y1: new THREE.Vector3(cx, cy + hy, cz),
      Y2: new THREE.Vector3(cx, cy - hy, cz),
      Z1: new THREE.Vector3(cx, cy, cz + hz),
      Z2: new THREE.Vector3(cx, cy, cz - hz),
    };
  };

  getUuidInMesh = mesh => {
    const ids = [];
    mesh.traverse(x => {
      ids.push(x.uuid);
    });
    return ids;
  };

  bboxDistance(mesh1, mesh2) {
    const box1 = new THREE.Box3().setFromObject(mesh1);
    const box2 = new THREE.Box3().setFromObject(mesh2);

    if (box1.intersectsBox(box2)) {
      return 0; // chạm nhau
    }

    // khoảng cách ngắn nhất giữa 2 bounding box
    const v1 = new THREE.Vector3();
    const v2 = new THREE.Vector3();
    box1.clampPoint(box2.getCenter(v1), v1);
    box2.clampPoint(box1.getCenter(v2), v2);

    return v1.distanceTo(v2);
  }

  moveMesh = (
    mesh,
    point,
    prePos,
    myScene,
    currentCabinet,
    module,
    options = {}
  ) => {
    try {
      const ids = this.getUuidInMesh(mesh);
      // Fallback nếu chưa truyền vào
      currentCabinet = currentCabinet || this.currentCabinet;
      if (!currentCabinet) {
        console.warn('moveMesh: currentCabinet is required.');
        mesh.position.set(prePos.x, prePos.y, prePos.z);
        return;
      }

      // 1) Lấy các "điểm lưới" đang active trong cabinet (chuẩn InsideLoader)
      let meshPoints = currentCabinet.getActiveMeshPoints(module);
      if (mesh.userData.typeHau === TypeConnerModule.SUB) {
        meshPoints = EdgeDetector.findMeshPointsR90(module.group);
      }

      const pointLocal = (module?.group ?? module)?.worldToLocal(point.clone());

      const orientation = this.getModuleOrientation(module?.group ?? module);

      const isRotated =
        orientation === 'ROTATED_90' || orientation === 'ROTATED_270';
      // Module góc phụ (SUB) xoay 90° so với group nên trục local tính riêng
      const axisOrientation =
        !isRotated && mesh.userData?.typeHau === TypeConnerModule.SUB
          ? 'ROTATED_90'
          : orientation;
      // Trục ngang giữ nguyên quy ước cũ: module xoay 90° dùng chung chiều với 270°
      const sideOrientation = isRotated ? 'ROTATED_270' : axisOrientation;

      // Tìm cả 6 cạnh bằng ray từ tâm bounding box của mesh qua tâm từng mặt
      const { leftEdge, rightEdge, topEdge, bottomEdge, frontEdge, backEdge } =
        this.findEdgesAroundMesh({
          mesh,
          meshPoints,
          ids,
          orientation: axisOrientation,
          sideOrientation,
          module,
        });

      // Di chuyển theo chiều sâu (sửa label mặt trước / mặt sau).
      // Không phụ thuộc 4 edge trái/phải/trên/dưới nên xử lý trước bước kiểm tra.
      if (options.axis === 'depth') {
        const moved = this.moveMeshAlongDepth({
          mesh,
          point,
          module,
          meshPoints,
          ids,
          frontEdge,
          backEdge,
          depthOrientation: axisOrientation,
        });

        if (moved) {
          this.distanceMeshToEdges({
            mesh,
            currentCabinet,
            prevPos: mesh.position.clone(),
            bottomEdge,
            topEdge,
            leftEdge,
            rightEdge,
            frontEdge,
            backEdge,
            orientation,
            axisOrientation,
            sideOrientation,
            module,
          });

          this.rescaleDoor(currentCabinet?.cloneModule ?? module);

          if (typeof this.findPoint === 'function') {
            this.findPoint();
          }
          this.adjustBackPanelForHPanel(module, this.scene);
        }
        return;
      }

      // 2) Xác định edges gần nhất theo chuẩn InsideLoader
      // - Z edges: right/left
      //   Nếu kéo "xà dọc" (type === 1) thì giới hạn tìm 4 điểm (giống drawBoxBorder).

      if (
        !(rightEdge && leftEdge && bottomEdge) &&
        mesh.userData?.type !== InsideType.HPANEL_RIGHT &&
        mesh.userData?.type !== InsideType.HPANEL_LEFT &&
        mesh.userData?.type !== InsideType.VPANEL &&
        !module.cabinetType.toLowerCase().includes('corner')
      ) {
        // Không đủ edge – trả mesh về vị trí cũ
        mesh.position.set(prePos.x, prePos.y, prePos.z);
        openMessageError({
          content: i18n.t(
            'Insufficient edges detected. Resetting to previous position.'
          ),
        });
        return;
      }

      // 3) Tính kích thước (chuẩn InsideLoader: dùng getSize)
      const sizeRight = this.getSize(rightEdge?.mesh);
      const sizeLeft = this.getSize(leftEdge?.mesh);
      const sizeBottom = this.getSize(bottomEdge?.mesh);
      const sizeTop = this.getSize(topEdge?.mesh);
      const sizeMesh = this.getSize(mesh);

      // 4) Lấy worldPosition để tính ràng buộc
      const topY = topEdge?.mesh.getWorldPosition(new THREE.Vector3()).y;
      const bottomY = bottomEdge?.mesh.getWorldPosition(new THREE.Vector3()).y;
      const leftZ = leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).z;
      const rightZ = rightEdge?.mesh.getWorldPosition(new THREE.Vector3()).z;

      // Ràng buộc theo chuẩn InsideLoader:
      const maxPosY = topY; // trần bởi top edge
      const minPosY = bottomY + sizeBottom.y; // sàn + bề dày bottom
      const maxPosZ = leftZ - sizeLeft?.z; // sát về bên trái trừ bề dày left
      const minPosZ = rightZ + sizeMesh.z; // sát về bên phải + dày của chính mesh

      // 5) Di chuyển theo type (mapping giữ nguyên: 0 = xà ngang (move Y), 1 = xà dọc (move Z), 3 = đợt kệ (move Y), 5 = something theo Z)
      switch (mesh.userData?.type) {
        case InsideType.HPANEL: {
          // Xà ngang – trượt theo Y, scale dọc theo dependent
          if (
            greaterOrEqual(point.y, minPosY) &&
            lessOrEqual(point.y + sizeMesh.y, maxPosY)
          ) {
            if (pointLocal) {
              mesh.position.y = pointLocal.y;
            } else {
              mesh.position.y = point.y;
            }

            // đoạn này kiểm tra xem có inside nào nằm ngang có khoảng cách gần inside hiện tại nhất
            let minDistance = Infinity;
            let closedMesh = null;
            if (mesh.userData?.leftEdgeUuid) {
              const leftEdgeMesh = myScene.findGLTFByUuid(
                mesh.userData?.leftEdgeUuid
              );
              if (leftEdgeMesh?.userData?.leftDependent?.length) {
                leftEdgeMesh.userData.leftDependent.filter(id => {
                  if (id !== mesh.uuid) {
                    const other = myScene.findGLTFByUuid(id);
                    const distance = this.bboxDistance(other, mesh);
                    if (distance < minDistance) {
                      minDistance = distance;
                      closedMesh = other;
                    }
                  }
                });
              }
            }

            if (mesh.userData?.rightEdgeUuid) {
              const rightEdgeMesh = myScene.findGLTFByUuid(
                mesh.userData?.rightEdgeUuid
              );
              if (rightEdgeMesh?.userData?.rightDependent?.length) {
                rightEdgeMesh.userData.rightDependent.filter(id => {
                  if (id !== mesh.uuid) {
                    const other = myScene.findGLTFByUuid(id);
                    const distance = this.bboxDistance(other, mesh);
                    if (distance < minDistance) {
                      minDistance = distance;
                      closedMesh = other;
                    }
                  }
                });
              }
            }

            // nếu khoảng cách bé hơn 0.02 thì đưa inside lại vị trí đó inside gần nhất đó
            if (minDistance <= 0.02) {
              mesh.position.y = closedMesh.position.y;
            }

            // Tính khoảng cách giữa mesh đến các cạnh:
            this.distanceMeshToEdges({
              mesh,
              currentCabinet,
              prevPos: mesh.position.clone(),
              bottomEdge,
              topEdge,
              leftEdge,
              rightEdge,
              frontEdge,
              backEdge,
              orientation,
              axisOrientation,
              sideOrientation,
              module,
            });

            // bottomDependent (các mô-đun “tựa” lên cạnh dưới của thanh này)
            if (mesh.userData?.bottomDependent?.length) {
              mesh.userData.bottomDependent =
                mesh.userData.bottomDependent.filter(id => {
                  const dep = myScene.findGLTFByUuid(id);
                  if (!dep || !dep.userData) return false;
                  if (
                    dep.userData.type === InsideType.CLOTHES_HANGER ||
                    dep.userData.type === InsideType.LED_RECESSED ||
                    dep.userData.type === InsideType.LED_CORNER
                  ) {
                    const topPos = module.group.worldToLocal(
                      mesh.getWorldPosition(new THREE.Vector3()).clone()
                    );
                    const sizeDep = this.getSize(dep);
                    dep.position.y =
                      topPos.y -
                      (dep.userData.type === InsideType.CLOTHES_HANGER
                        ? sizeDep.y
                        : 0);
                    return true;
                  }

                  const depBottom = myScene.findGLTFByUuid(
                    dep.userData.bottomEdgeUuid
                  );
                  if (!depBottom) return false;

                  const sizeB = this.getSize(depBottom);
                  const bottomWorldY = depBottom.getWorldPosition(
                    new THREE.Vector3()
                  ).y;
                  const newHeight =
                    mesh.position.y - depBottom.position.y - sizeB.y; // khoảng còn lại phía dưới

                  this.scaleHeight(dep, newHeight, this);
                  return true;
                });
            }

            // topDependent (các mô-đun “tựa” lên cạnh trên của thanh này)
            if (mesh.userData?.topDependent?.length) {
              mesh.userData.topDependent = mesh.userData.topDependent.filter(
                id => {
                  const dep = myScene.findGLTFByUuid(id);
                  if (!dep || !dep.userData) return false;

                  const depTop = myScene.findGLTFByUuid(
                    dep.userData.topEdgeUuid
                  );
                  if (!depTop) return false;

                  // khoảng còn lại phía trên
                  const sizeT = this.getSize(depTop);
                  const sizeSelf = sizeMesh; // đã tính ở trên
                  const newHeight =
                    depTop.position.y - mesh.position.y - sizeSelf.y;

                  this.scaleHeight(dep, newHeight, this);

                  // Nếu là group (ví dụ type 1 group clone), chuẩn hoá lại local gốc
                  if (dep.userData.type === 1) {
                    // this.resetGroupPositionToCurrentPosition(dep);
                    dep.position.y = mesh.position.y + sizeSelf.y;
                  } else {
                    // đặt lại ngay bên trên thanh
                    // dep.position.y = mesh.position.y + sizeSelf.y;
                  }
                  return true;
                }
              );
            }
          }
          break;
        }
        case InsideType.HPANEL_RIGHT: {
          // Xà ngang – trượt theo Y, scale dọc theo dependent
          if (
            greaterOrEqual(point.y, minPosY) &&
            lessOrEqual(point.y + sizeMesh.y, maxPosY)
          ) {
            if (pointLocal) {
              mesh.position.y = pointLocal.y;
            } else {
              mesh.position.y = point.y;
            }

            // đoạn này kiểm tra xem có inside nào nằm ngang có khoảng cách gần inside hiện tại nhất
            let minDistance = Infinity;
            let closedMesh = null;
            if (mesh.userData?.leftEdgeUuid) {
              const leftEdgeMesh = myScene.findGLTFByUuid(
                mesh.userData?.leftEdgeUuid
              );
              if (leftEdgeMesh?.userData?.leftDependent?.length) {
                leftEdgeMesh.userData.leftDependent.filter(id => {
                  if (id !== mesh.uuid) {
                    const other = myScene.findGLTFByUuid(id);
                    const distance = this.bboxDistance(other, mesh);
                    if (distance < minDistance) {
                      minDistance = distance;
                      closedMesh = other;
                    }
                  }
                });
              }
            }

            // nếu khoảng cách bé hơn 0.02 thì đưa inside lại vị trí đó inside gần nhất đó
            if (minDistance <= 0.02) {
              mesh.position.y = closedMesh.position.y;
            }

            // Tính khoảng cách giữa mesh đến các cạnh:
            this.distanceMeshToEdges({
              mesh,
              currentCabinet,
              prevPos: mesh.position.clone(),
              bottomEdge,
              topEdge,
              leftEdge,
              rightEdge,
              frontEdge,
              backEdge,
              orientation,
              axisOrientation,
              sideOrientation,
              module,
            });

            // bottomDependent (các mô-đun “tựa” lên cạnh dưới của thanh này)
            if (mesh.userData?.bottomDependent?.length) {
              mesh.userData.bottomDependent =
                mesh.userData.bottomDependent.filter(id => {
                  const dep = myScene.findGLTFByUuid(id);
                  if (!dep || !dep.userData) return false;
                  const depBottom = myScene.findGLTFByUuid(
                    dep.userData.bottomEdgeUuid
                  );
                  if (!depBottom) return false;

                  const sizeB = this.getSize(depBottom);
                  const bottomWorldY = depBottom.getWorldPosition(
                    new THREE.Vector3()
                  ).y;
                  const newHeight = mesh.position.y - bottomWorldY - sizeB.y; // khoảng còn lại phía dưới

                  this.scaleHeight(dep, newHeight, this);
                  return true;
                });
            }

            // topDependent (các mô-đun “tựa” lên cạnh trên của thanh này)
            if (mesh.userData?.topDependent?.length) {
              mesh.userData.topDependent = mesh.userData.topDependent.filter(
                id => {
                  const dep = myScene.findGLTFByUuid(id);
                  if (!dep || !dep.userData) return false;

                  const depTop = myScene.findGLTFByUuid(
                    dep.userData.topEdgeUuid
                  );
                  if (!depTop) return false;

                  // khoảng còn lại phía trên
                  const sizeT = this.getSize(depTop);
                  const sizeSelf = sizeMesh; // đã tính ở trên
                  const newHeight =
                    depTop.position.y - mesh.position.y - sizeSelf.y;

                  this.scaleHeight(dep, newHeight, this);

                  // Nếu là group (ví dụ type 1 group clone), chuẩn hoá lại local gốc
                  if (dep.userData.type === 1 && dep.isGroup) {
                    this.resetGroupPositionToCurrentPosition(dep);
                  } else {
                    // đặt lại ngay bên trên thanh
                    dep.position.y = mesh.position.y + sizeSelf.y;
                  }
                  return true;
                }
              );
            }
          }
          break;
        }
        case InsideType.HPANEL_LEFT: {
          // Xà ngang – trượt theo Y, scale dọc theo dependent
          if (
            greaterOrEqual(point.y, minPosY) &&
            lessOrEqual(point.y + sizeMesh.y, maxPosY)
          ) {
            if (pointLocal) {
              mesh.position.y = pointLocal.y;
            } else {
              mesh.position.y = point.y;
            }

            // đoạn này kiểm tra xem có inside nào nằm ngang có khoảng cách gần inside hiện tại nhất
            let minDistance = Infinity;
            let closedMesh = null;
            if (mesh.userData?.leftEdgeUuid) {
              const leftEdgeMesh = myScene.findGLTFByUuid(
                mesh.userData?.leftEdgeUuid
              );
              if (leftEdgeMesh?.userData?.leftDependent?.length) {
                leftEdgeMesh.userData.leftDependent.filter(id => {
                  if (id !== mesh.uuid) {
                    const other = myScene.findGLTFByUuid(id);
                    const distance = this.bboxDistance(other, mesh);
                    if (distance < minDistance) {
                      minDistance = distance;
                      closedMesh = other;
                    }
                  }
                });
              }
            }

            // nếu khoảng cách bé hơn 0.02 thì đưa inside lại vị trí đó inside gần nhất đó
            if (minDistance <= 0.02) {
              mesh.position.y = closedMesh.position.y;
            }

            // Tính khoảng cách giữa mesh đến các cạnh:
            this.distanceMeshToEdges({
              mesh,
              currentCabinet,
              prevPos: mesh.position.clone(),
              bottomEdge,
              topEdge,
              leftEdge,
              rightEdge,
              frontEdge,
              backEdge,
              orientation,
              axisOrientation,
              sideOrientation,
              module,
            });

            // bottomDependent (các mô-đun “tựa” lên cạnh dưới của thanh này)
            if (mesh.userData?.bottomDependent?.length) {
              mesh.userData.bottomDependent =
                mesh.userData.bottomDependent.filter(id => {
                  const dep = myScene.findGLTFByUuid(id);
                  if (!dep || !dep.userData) return false;
                  const depBottom = myScene.findGLTFByUuid(
                    dep.userData.bottomEdgeUuid
                  );
                  if (!depBottom) return false;

                  const sizeB = this.getSize(depBottom);
                  const bottomWorldY = depBottom.getWorldPosition(
                    new THREE.Vector3()
                  ).y;
                  const newHeight = mesh.position.y - bottomWorldY - sizeB.y; // khoảng còn lại phía dưới

                  this.scaleHeight(dep, newHeight, this);
                  return true;
                });
            }

            // topDependent (các mô-đun “tựa” lên cạnh trên của thanh này)
            if (mesh.userData?.topDependent?.length) {
              mesh.userData.topDependent = mesh.userData.topDependent.filter(
                id => {
                  const dep = myScene.findGLTFByUuid(id);
                  if (!dep || !dep.userData) return false;

                  const depTop = myScene.findGLTFByUuid(
                    dep.userData.topEdgeUuid
                  );
                  if (!depTop) return false;

                  // khoảng còn lại phía trên
                  const sizeT = this.getSize(depTop);
                  const sizeSelf = sizeMesh; // đã tính ở trên
                  const newHeight =
                    depTop.position.y - mesh.position.y - sizeSelf.y;

                  this.scaleHeight(dep, newHeight, this);

                  // Nếu là group (ví dụ type 1 group clone), chuẩn hoá lại local gốc
                  if (dep.userData.type === 1 && dep.isGroup) {
                    this.resetGroupPositionToCurrentPosition(dep);
                  } else {
                    // đặt lại ngay bên trên thanh
                    dep.position.y = mesh.position.y + sizeSelf.y;
                  }
                  return true;
                }
              );
            }
          }
          break;
        }
        case InsideType.VPANEL: {
          const autoMoveToNearMesh = () => {
            // Phần tìm closest mesh (giữ nguyên logic, nhưng có thể cần điều chỉnh distance calculation)
            let minDistance = Infinity;
            let closedMesh = null;
            if (mesh.userData?.topEdgeUuid) {
              const topEdgeMesh = myScene.findGLTFByUuid(
                mesh.userData?.topEdgeUuid
              );
              if (topEdgeMesh?.userData?.topDependent?.length) {
                topEdgeMesh.userData.topDependent.filter(id => {
                  if (id !== mesh.uuid) {
                    const other = myScene.findGLTFByUuid(id);
                    const distance = this.bboxDistance(other, mesh);
                    if (distance < minDistance) {
                      minDistance = distance;
                      closedMesh = other;
                    }
                  }
                });
              }
            }

            if (mesh.userData?.bottomEdgeUuid) {
              const bottomEdgeMesh = myScene.findGLTFByUuid(
                mesh.userData?.bottomEdgeUuid
              );
              if (bottomEdgeMesh?.userData?.bottomDependent?.length) {
                bottomEdgeMesh.userData.bottomDependent.filter(id => {
                  if (id !== mesh.uuid) {
                    const other = myScene.findGLTFByUuid(id);
                    const distance = this.bboxDistance(other, mesh);
                    if (distance < minDistance) {
                      minDistance = distance;
                      closedMesh = other;
                    }
                  }
                });
              }
            }

            // Snap to closest mesh nếu distance nhỏ hơn 0.02
            if (minDistance <= 0.02) {
              // if (orientation === "NORMAL") {
              mesh.position.z = closedMesh.position.z;
              // } else if (
              //   orientation === "ROTATED_270" ||
              //   orientation === "ROTATED_90"
              // ) {
              //   mesh.position.x = closedMesh.position.x;
              // }
            }
          };
          // Xà dọc – xử lý theo orientation
          if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
            // Trường hợp bình thường: trượt theo Z
            if (
              ((minPosZ != null && greaterOrEqual(point.z, minPosZ)) ||
                !minPosZ) &&
              ((maxPosZ != null &&
                lessOrEqual(point.z - sizeMesh.z, maxPosZ)) ||
                !maxPosZ)
            ) {
              const pointLocal = module?.group?.worldToLocal(point);
              mesh.position.z = pointLocal.z;
              if (orientation === 'ROTATED_180') {
                mesh.position.z = pointLocal.z + sizeMesh.z;
              }

              autoMoveToNearMesh();

              // Xử lý leftDependent và rightDependent như code gốc
              if (mesh.userData?.leftDependent?.length) {
                mesh.userData.leftDependent =
                  mesh.userData.leftDependent.filter(id => {
                    const dep = myScene.findGLTFByUuid(id);
                    if (!dep || !dep.userData) return false;
                    const depLeft = myScene.findGLTFByUuid(
                      dep.userData.leftEdgeUuid
                    );
                    if (!depLeft) return false;

                    const sizeB = this.getSize(depLeft);
                    const leftWorldZ = depLeft.getWorldPosition(
                      new THREE.Vector3()
                    ).z;
                    const newWidth = -mesh.position.z + leftWorldZ - sizeB.z;

                    this.scaleWidth(dep, newWidth, module);
                    return true;
                  });
              }

              if (mesh.userData?.rightDependent?.length) {
                mesh.userData.rightDependent =
                  mesh.userData.rightDependent.filter(id => {
                    const dep = myScene.findGLTFByUuid(id);
                    if (!dep || !dep.userData) return false;
                    const depRight = myScene.findGLTFByUuid(
                      dep.userData.rightEdgeUuid
                    );
                    if (!depRight) return false;

                    const sizeB = this.getSize(depRight);
                    const rightWorldZ = depRight.getWorldPosition(
                      new THREE.Vector3()
                    ).z;
                    const newWidth = mesh.position.z - rightWorldZ - sizeB.z;

                    this.scaleWidth(dep, newWidth, module);
                    dep.position.z = mesh.position.z - sizeB.z;
                    return true;
                  });
              }
            }
          } else if (
            orientation === 'ROTATED_270' ||
            orientation === 'ROTATED_90'
          ) {
            // Trường hợp xoay 270/90 độ: trượt theo X
            // Cần tính lại ràng buộc cho trục X
            const minPosX =
              leftEdge?.mesh?.getWorldPosition?.(new THREE.Vector3())?.x +
              sizeLeft?.x;
            const maxPosX =
              rightEdge?.mesh?.getWorldPosition?.(new THREE.Vector3())?.x -
              sizeMesh?.x;

            if (
              ((minPosX != null && greaterOrEqual(point.x, minPosX)) ||
                !minPosX) &&
              ((maxPosX != null && lessOrEqual(point.x, maxPosX)) || !maxPosX)
            ) {
              const pointLocal = module.group.worldToLocal(point);
              mesh.position.z = pointLocal.z;
              if (orientation === 'ROTATED_90') {
                mesh.position.z = pointLocal.z + sizeMesh.x;
              }

              autoMoveToNearMesh();

              // Xử lý dependent theo trục X thay vì Z
              if (mesh.userData?.leftDependent?.length) {
                mesh.userData.leftDependent =
                  mesh.userData.leftDependent.filter(id => {
                    const dep = myScene.findGLTFByUuid(id);
                    if (!dep || !dep.userData) return false;
                    const depLeft = myScene.findGLTFByUuid(
                      dep.userData.leftEdgeUuid
                    );
                    if (!depLeft) return false;

                    const sizeB = this.getSize(depLeft);
                    const leftWorldX = depLeft.getWorldPosition(
                      new THREE.Vector3()
                    );

                    const newWidth =
                      mesh.getWorldPosition(new THREE.Vector3()).x -
                      leftWorldX.x -
                      sizeB.x; // Đổi từ z sang x

                    this.scaleWidth(dep, newWidth, module);
                    return true;
                  });
              }

              if (mesh.userData?.rightDependent?.length) {
                mesh.userData.rightDependent =
                  mesh.userData.rightDependent.filter(id => {
                    const dep = myScene.findGLTFByUuid(id);
                    if (!dep || !dep.userData) return false;
                    const depRight = myScene.findGLTFByUuid(
                      dep.userData.rightEdgeUuid
                    );
                    if (!depRight) return false;

                    const sizeB = this.getSize(depRight);
                    const rightWorldX = depRight.getWorldPosition(
                      new THREE.Vector3()
                    );
                    const newWidth =
                      -mesh.getWorldPosition(new THREE.Vector3()).x +
                      rightWorldX.x -
                      sizeB.x; // Đổi từ z sang x

                    this.scaleWidth(dep, newWidth, module);
                    dep.position.z = mesh.position.z - sizeB.x; // Đổi từ z sang x
                    return true;
                  });
              }
            }
          }
          // else if (orientation === 'ROTATED_90') {
          //   // Trường hợp xoay 270/90 độ: trượt theo X
          //   // Cần tính lại ràng buộc cho trục X
          //   const minPosX =
          //     -leftEdge?.mesh?.getWorldPosition?.(new THREE.Vector3())?.x -
          //     sizeLeft?.x;
          //   const maxPosX =
          //     -rightEdge?.mesh?.getWorldPosition?.(new THREE.Vector3())?.x +
          //     sizeMesh?.x;

          //   if (
          //     ((minPosX != null && greaterOrEqual(point.x, minPosX)) ||
          //       !minPosX) &&
          //     ((maxPosX != null && lessOrEqual(point.x, maxPosX)) || !maxPosX)
          //   ) {
          //     const pointLocal = module.group.worldToLocal(point);
          //     mesh.position.z = pointLocal.z + sizeMesh.x;

          //     autoMoveToNearMesh();

          //     // Xử lý dependent theo trục X thay vì Z
          //     if (mesh.userData?.leftDependent?.length) {
          //       mesh.userData.leftDependent =
          //         mesh.userData.leftDependent.filter(id => {
          //           const dep = myScene.findGLTFByUuid(id);
          //           if (!dep || !dep.userData) return false;
          //           const depLeft = myScene.findGLTFByUuid(
          //             dep.userData.leftEdgeUuid
          //           );
          //           if (!depLeft) return false;

          //           const sizeB = this.getSize(depLeft);
          //           const leftWorldX = depLeft.getWorldPosition(
          //             new THREE.Vector3()
          //           );

          //           const newWidth =
          //             mesh.getWorldPosition(new THREE.Vector3()).x -
          //             leftWorldX.x -
          //             sizeB.x; // Đổi từ z sang x

          //           this.scaleWidth(dep, newWidth, module);
          //           return true;
          //         });
          //     }

          //     if (mesh.userData?.rightDependent?.length) {
          //       mesh.userData.rightDependent =
          //         mesh.userData.rightDependent.filter(id => {
          //           const dep = myScene.findGLTFByUuid(id);
          //           if (!dep || !dep.userData) return false;
          //           const depRight = myScene.findGLTFByUuid(
          //             dep.userData.rightEdgeUuid
          //           );
          //           if (!depRight) return false;

          //           const sizeB = this.getSize(depRight);
          //           const rightWorldX = depRight.getWorldPosition(
          //             new THREE.Vector3()
          //           );
          //           const newWidth =
          //             -mesh.getWorldPosition(new THREE.Vector3()).x +
          //             rightWorldX.x -
          //             sizeB.x; // Đổi từ z sang x

          //           this.scaleWidth(dep, newWidth, module);
          //           dep.position.z = mesh.position.z - sizeB.x; // Đổi từ z sang x
          //           return true;
          //         });
          //     }
          //   }
          // }

          this.distanceMeshToEdges({
            mesh,
            currentCabinet,
            prevPos: mesh.position.clone(),
            bottomEdge,
            topEdge,
            leftEdge,
            rightEdge,
            frontEdge,
            backEdge,
            orientation,
            axisOrientation,
            sideOrientation,
            module,
          });
          this.adjustBackPanelForHPanel(module, this.scene);
          break;
        }

        case InsideType.VPANEL2: {
          const autoMoveToNearMesh = () => {
            // Phần tìm closest mesh (giữ nguyên logic, nhưng có thể cần điều chỉnh distance calculation)
            let minDistance = Infinity;
            let closedMesh = null;
            if (mesh.userData?.topEdgeUuid) {
              const topEdgeMesh = myScene.findGLTFByUuid(
                mesh.userData?.topEdgeUuid
              );
              if (topEdgeMesh?.userData?.topDependent?.length) {
                topEdgeMesh.userData.topDependent.filter(id => {
                  if (id !== mesh.uuid) {
                    const other = myScene.findGLTFByUuid(id);
                    const distance = this.bboxDistance(other, mesh);
                    if (distance < minDistance) {
                      minDistance = distance;
                      closedMesh = other;
                    }
                  }
                });
              }
            }

            if (mesh.userData?.bottomEdgeUuid) {
              const bottomEdgeMesh = myScene.findGLTFByUuid(
                mesh.userData?.bottomEdgeUuid
              );
              if (bottomEdgeMesh?.userData?.bottomDependent?.length) {
                bottomEdgeMesh.userData.bottomDependent.filter(id => {
                  if (id !== mesh.uuid) {
                    const other = myScene.findGLTFByUuid(id);
                    const distance = this.bboxDistance(other, mesh);
                    if (distance < minDistance) {
                      minDistance = distance;
                      closedMesh = other;
                    }
                  }
                });
              }
            }

            // Snap to closest mesh nếu distance nhỏ hơn 0.02
            if (minDistance <= 0.02) {
              // if (orientation === "NORMAL") {
              mesh.position.z = closedMesh.position.z;
              // } else if (
              //   orientation === "ROTATED_270" ||
              //   orientation === "ROTATED_90"
              // ) {
              //   mesh.position.x = closedMesh.position.x;
              // }
            }
          };
          // Xà dọc – xử lý theo orientation
          if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
            // Trường hợp bình thường: trượt theo Z
            if (
              greaterOrEqual(point.z, minPosZ) &&
              lessOrEqual(point.z - sizeMesh.z, maxPosZ)
            ) {
              mesh.position.z = point.z;

              autoMoveToNearMesh();

              // Xử lý leftDependent và rightDependent như code gốc
              if (mesh.userData?.leftDependent?.length) {
                mesh.userData.leftDependent =
                  mesh.userData.leftDependent.filter(id => {
                    const dep = myScene.findGLTFByUuid(id);
                    if (!dep || !dep.userData) return false;
                    const depLeft = myScene.findGLTFByUuid(
                      dep.userData.leftEdgeUuid
                    );
                    if (!depLeft) return false;

                    const sizeB = this.getSize(depLeft);
                    const leftWorldZ = depLeft.getWorldPosition(
                      new THREE.Vector3()
                    ).z;
                    const newWidth = -mesh.position.z + leftWorldZ - sizeB.z;

                    this.scaleWidth(dep, newWidth, module);
                    return true;
                  });
              }

              if (mesh.userData?.rightDependent?.length) {
                mesh.userData.rightDependent =
                  mesh.userData.rightDependent.filter(id => {
                    const dep = myScene.findGLTFByUuid(id);
                    if (!dep || !dep.userData) return false;
                    const depRight = myScene.findGLTFByUuid(
                      dep.userData.rightEdgeUuid
                    );
                    if (!depRight) return false;

                    const sizeB = this.getSize(depRight);
                    const rightWorldZ = depRight.getWorldPosition(
                      new THREE.Vector3()
                    ).z;
                    const newWidth = mesh.position.z - rightWorldZ - sizeB.z;

                    this.scaleWidth(dep, newWidth, module);
                    dep.position.z = mesh.position.z - sizeB.z;
                    return true;
                  });
              }
            }
          } else if (
            orientation === 'ROTATED_270' ||
            orientation === 'ROTATED_90'
          ) {
            // Trường hợp xoay 270/90 độ: trượt theo X
            // Cần tính lại ràng buộc cho trục X
            const minPosX =
              leftEdge.mesh.getWorldPosition(new THREE.Vector3()).x +
              sizeLeft.x;
            const maxPosX =
              rightEdge.mesh.getWorldPosition(new THREE.Vector3()).x -
              sizeMesh.x;

            if (
              greaterOrEqual(point.x, minPosX) &&
              lessOrEqual(point.x, maxPosX)
            ) {
              const pointLocal = module.group.worldToLocal(point);
              mesh.position.z = pointLocal.z;

              autoMoveToNearMesh();

              // Xử lý dependent theo trục X thay vì Z
              if (mesh.userData?.leftDependent?.length) {
                mesh.userData.leftDependent =
                  mesh.userData.leftDependent.filter(id => {
                    const dep = myScene.findGLTFByUuid(id);
                    if (!dep || !dep.userData) return false;
                    const depLeft = myScene.findGLTFByUuid(
                      dep.userData.leftEdgeUuid
                    );
                    if (!depLeft) return false;

                    const sizeB = this.getSize(depLeft);
                    const leftWorldX = depLeft.getWorldPosition(
                      new THREE.Vector3()
                    );

                    const newWidth =
                      mesh.getWorldPosition(new THREE.Vector3()).x -
                      leftWorldX.x -
                      sizeB.x; // Đổi từ z sang x

                    this.scaleWidth(dep, newWidth, module);
                    return true;
                  });
              }

              if (mesh.userData?.rightDependent?.length) {
                mesh.userData.rightDependent =
                  mesh.userData.rightDependent.filter(id => {
                    const dep = myScene.findGLTFByUuid(id);
                    if (!dep || !dep.userData) return false;
                    const depRight = myScene.findGLTFByUuid(
                      dep.userData.rightEdgeUuid
                    );
                    if (!depRight) return false;

                    const sizeB = this.getSize(depRight);
                    const rightWorldX = depRight.getWorldPosition(
                      new THREE.Vector3()
                    );
                    const newWidth =
                      -mesh.getWorldPosition(new THREE.Vector3()).x +
                      rightWorldX.x -
                      sizeB.x; // Đổi từ z sang x

                    this.scaleWidth(dep, newWidth, module);
                    dep.position.z = mesh.position.z - sizeB.x; // Đổi từ z sang x
                    return true;
                  });
              }
            }
          }

          this.distanceMeshToEdges({
            mesh,
            currentCabinet,
            prevPos: mesh.position.clone(),
            bottomEdge,
            topEdge,
            leftEdge,
            rightEdge,
            frontEdge,
            backEdge,
            orientation,
            axisOrientation,
            sideOrientation,
            module,
          });
          break;
        }

        case InsideType.DRAWER: {
          // Đợt kệ – thường chỉ trượt theo Y trong khung
          if (
            greaterOrEqual(point.y, minPosY) &&
            lessOrEqual(point.y + sizeMesh.y, maxPosY)
          ) {
            mesh.position.y = point.y;
            this.distanceMeshToEdges({
              mesh,
              currentCabinet,
              prevPos: mesh.position.clone(),
              bottomEdge,
              topEdge,
              leftEdge,
              rightEdge,
              frontEdge,
              backEdge,
              orientation,
              axisOrientation,
              sideOrientation,
              module,
            });

            // topDependent (các mô-đun “tựa” lên cạnh trên của thanh này)
            if (mesh.userData?.topDependent?.length) {
              mesh.userData.topDependent = mesh.userData.topDependent.filter(
                id => {
                  const dep = myScene.findGLTFByUuid(id);
                  if (!dep || !dep.userData) return false;

                  const depTop = myScene.findGLTFByUuid(
                    dep.userData.topEdgeUuid
                  );
                  if (!depTop) return false;

                  // khoảng còn lại phía trên
                  const sizeT = this.getSize(depTop);
                  const sizeSelf = sizeMesh; // đã tính ở trên
                  const newHeight =
                    depTop.position.y - mesh.position.y - sizeSelf.y;

                  this.scaleHeight(dep, newHeight, this, true);

                  // Nếu là group (ví dụ type 1 group clone), chuẩn hoá lại local gốc
                  if (dep.userData.type === 1 && dep.isGroup) {
                    this.resetGroupPositionToCurrentPosition(dep);
                  } else {
                    // đặt lại ngay bên trên thanh
                    dep.position.y = mesh.position.y + sizeSelf.y;
                  }
                  return true;
                }
              );
            }
          }
          break;
        }

        case InsideType.VCURVE: {
          // Một số phụ kiện theo Z (giữ như cũ)
          if (
            greaterOrEqual(point.z, minPosZ) &&
            lessOrEqual(point.z - sizeMesh.z, maxPosZ)
          ) {
            mesh.position.z = point.z;
            this.distanceMeshToEdges({
              mesh,
              currentCabinet,
              prevPos: mesh.position.clone(),
              bottomEdge,
              topEdge,
              leftEdge,
              rightEdge,
              frontEdge,
              backEdge,
              orientation,
              axisOrientation,
              sideOrientation,
              module,
            });
          }
          break;
        }

        default: {
          console.log('moveMesh: unsupported type => no-op');
        }
      }

      //Rescale cửa
      this.rescaleDoor(currentCabinet?.cloneModule ?? module);

      // (tuỳ hệ thống của bạn) Cập nhật lại các điểm bắt dính / hiển thị
      if (typeof this.findPoint === 'function') {
        this.findPoint();
      }
    } catch (e) {
      console.log('Vị trí thay đổi không hợp lệ: ' + e);
      openMessageError({ content: i18n.t('Invalid Position!') });
    } finally {
      this.scene.calculateTotalCost?.({});
    }
  };

  findColumnPanel = clonedModule => {
    let rightColPanel;
    let leftColPanel;

    clonedModule.group.traverse(child => {
      if (leftColPanel && rightColPanel) return;

      if (child.isMesh) {
        if (child.name === 'BIA-COT-1') {
          leftColPanel = child;
        } else if (child.name === 'BIA-COT-2') {
          rightColPanel = child;
        }
      }
    });

    return { leftColPanel, rightColPanel };
  };

  createPanelColumn(clonedModule, model, leftEdge, leftPos, leftColPos) {
    let leftPosition, leftColPosition;
    const columnPoints = this.findColumnPanel(clonedModule);
    const leftColPanel = columnPoints.leftColPanel;

    if (!leftPos && !leftColPos) {
      leftPosition = this.getLocalPosOf(clonedModule.group, leftEdge?.mesh);
      leftColPosition = this.getLocalPosOf(clonedModule.group, leftColPanel);
    } else {
      leftPosition = leftPos;
      leftColPosition = leftColPos;
    }

    let modelGeometry;
    const KCCot = clonedModule.formularController.currentOptions.KCCot;
    const columnSize = {
      x: clonedModule.formularController.currentOptions.dayCot,
      y: clonedModule.formularController.currentDimensions.height,
      z: clonedModule.formularController.currentOptions.rongCot,
    };

    //Lưu lại thông tin cột để lúc load lại xắn
    model.userData.columnInfo = {
      leftPos: leftPosition,
      leftColPos: leftColPosition,
      // leftEdge,
    };

    if (model.geometry) {
      modelGeometry = model.geometry;
    } else if (model.children && model.children.length > 0) {
      modelGeometry = model.children[0].geometry;
    } else {
      throw new Error('Không tìm thấy geometry trong model');
    }

    const modelSize = this.getSize(model);

    // Model cho CSG
    const modelForCSG = new THREE.Mesh(modelGeometry);

    modelForCSG.scale.copy(model.scale);
    modelForCSG.position.set(0, 0, 0);
    modelForCSG.updateMatrixWorld(true);

    // Cut box
    const cutGeometry = new THREE.BoxGeometry(
      columnSize.x + thicknessBase,
      thicknessBase * 2,
      columnSize.z + thicknessBase * 2
    );
    const cutForCSG = new THREE.Mesh(cutGeometry);

    cutForCSG.position.set(
      columnSize.x / 2,
      0,
      -(
        leftPosition.z -
        leftColPosition.z +
        columnSize.z / 2 -
        (leftPosition.z === leftColPosition.z ? thicknessBase : 0)
      )
    );
    cutForCSG.updateMatrixWorld(true);

    try {
      const resultMesh = CSG.subtract(modelForCSG, cutForCSG);

      if (
        resultMesh &&
        resultMesh.geometry &&
        resultMesh.geometry.attributes.position.count > 0
      ) {
        resultMesh.position.copy(
          model.getWorldPosition(new THREE.Vector3()).clone()
        );
        resultMesh.rotation.copy(model.rotation);
        resultMesh.name = model.name;
        resultMesh.parent = model.parent;
        resultMesh.userData = { ...model.userData };

        return resultMesh;
      }

      return model;
    } catch (error) {
      console.error('Alternative CSG failed:', error);
      return model;
    }
  }

  getLocalPosOf = (group, obj3D) => {
    if (!group || !obj3D) return null;
    // đảm bảo matrixWorld mới nhất
    group.updateWorldMatrix(true, false);
    obj3D.updateWorldMatrix(true, false);
    // Không cần .clone() vì getWorldPosition đã ghi vào vector mới
    const world = obj3D.getWorldPosition(new THREE.Vector3());
    return group.worldToLocal(world);
  };

  removeAccessoryByType(module, accessoryType) {
    if (!module || !module.listInside || !module.group) {
      console.warn('Invalid module for removing accessories');
      return;
    }

    let removedCount = 0;

    // Duyệt ngược để xóa an toàn
    for (let i = module.listInside.length - 1; i >= 0; i--) {
      const inside = module.listInside[i];

      if (inside.type === accessoryType) {
        console.log(`Removing accessory at index ${i}:`, {
          type: inside.type,
          name: inside.name,
          uuid: inside.gltfUuid,
        });

        try {
          const mesh = inside.gltf;

          // Xóa khỏi scene 3D
          if (mesh && module.group) {
            module.group.remove(mesh);

            // Dispose để giải phóng bộ nhớ
            mesh.traverse(child => {
              if (child.geometry) {
                child.geometry.dispose();
              }
              if (child.material) {
                if (Array.isArray(child.material)) {
                  child.material.forEach(mat => mat.dispose());
                } else {
                  child.material.dispose();
                }
              }
            });
          }

          // Xóa khỏi listInside
          module.listInside.splice(i, 1);
          removedCount++;
        } catch (error) {
          console.error(`Error removing accessory at index ${i}:`, error);
        }
      }
    }

    console.log(
      `Removed ${removedCount} accessory(ies) of type ${accessoryType}`
    );
    return removedCount;
  }

  switchModelAccessory = (
    moduleData,
    clonedModule,
    currentCabinet,
    currentInside
  ) => {
    return new Promise((resolve, reject) => {
      const loader = new GLTFLoader();
      loader.load(
        moduleData.model,
        async gltf => {
          try {
            const model = gltf.scene;

            // BƯỚC 1: Tìm accessory cũ trong listInside theo type
            const oldAccessoryIndex = clonedModule.listInside.findIndex(
              inside =>
                inside.type === moduleData.type &&
                inside.gltf.userData.isInside === true &&
                inside.gltf.uuid === currentInside.gltfUuid
            );

            let oldPosition = null;
            let oldPositionLocal = null;
            let oldRotation = null;
            let oldSize = null;

            let leftEdge, rightEdge;
            let sizeLeft, sizeRight;
            let leftPos, rightPos;

            // BƯỚC 2: Nếu tìm thấy accessory cũ, lưu position và xóa nó
            if (oldAccessoryIndex !== -1) {
              const oldAccessory = clonedModule.listInside[oldAccessoryIndex];
              const oldMesh = oldAccessory.gltf;

              if (oldMesh?.userData?.leftEdgeUuid) {
                leftEdge = this.findMeshByUuid(
                  clonedModule.group,
                  oldMesh.userData.leftEdgeUuid
                );
                sizeLeft = this.getSizeRotation(clonedModule.group, leftEdge);
                leftPos = clonedModule.group.worldToLocal(
                  leftEdge?.getWorldPosition(new THREE.Vector3()).clone()
                );
              }

              if (oldMesh?.userData?.rightEdgeUuid) {
                rightEdge = this.findMeshByUuid(
                  clonedModule.group,
                  oldMesh.userData.rightEdgeUuid
                );
                sizeRight = this.getSizeRotation(clonedModule.group, rightEdge);
                rightPos = clonedModule.group.worldToLocal(
                  rightEdge?.getWorldPosition(new THREE.Vector3()).clone()
                );
              }

              if (moduleData.type === InsideType.ACCESSORY_SINK) {
                let sizeZ;
                const leftWorldPos = new THREE.Vector3();
                const rightWorldPos = new THREE.Vector3();
                leftEdge.getWorldPosition(leftWorldPos);
                rightEdge.getWorldPosition(rightWorldPos);
                const orientation = this.getModuleOrientation(
                  clonedModule.group
                );
                if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
                  sizeZ =
                    Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.x;
                } else if (
                  orientation === 'ROTATED_90' ||
                  orientation === 'ROTATED_270'
                ) {
                  sizeZ = Math.abs(
                    rightWorldPos.x - leftWorldPos.x - sizeLeft.z
                  );
                }
                if (sizeZ < moduleData?.width / 1000 + 0.04) {
                  openNotification({
                    key: 'cant-add-door-existing-door',
                    icon: <Icon.ICWarning />,
                    iconColor: '#F97D16',
                    title: 'Không thể thêm phụ kiện',
                    description: `Phụ kiện này chỉ phù hợp với khung tủ có kích thước lớn hơn ${
                      moduleData.width + 74
                    } mm, hãy điều chỉnh kích thước khung tủ trước khi thêm phụ kiện.`,
                    placement: 'bottomRight',
                    closable: true,
                    duration: 4,
                  });
                  resolve();
                  return;
                }
              }

              if (oldMesh) {
                // Lưu position và rotation của accessory cũ
                oldPosition = oldMesh.position.clone();
                oldPositionLocal = clonedModule.group.worldToLocal(
                  oldMesh.getWorldPosition(new THREE.Vector3()).clone()
                );
                oldRotation = oldMesh.rotation.clone();
                // oldSize = this.getSizeRotation(clonedModule.group , oldMesh)
                oldSize = this.getSizeRotation(clonedModule.group, oldMesh);

                // Xóa khỏi scene 3D
                clonedModule.group.remove(oldMesh);

                // Dispose để giải phóng bộ nhớ
                oldMesh.traverse(child => {
                  if (child.geometry) {
                    child.geometry.dispose();
                  }
                  if (child.material) {
                    if (Array.isArray(child.material)) {
                      child.material.forEach(mat => mat.dispose());
                    } else {
                      child.material.dispose();
                    }
                  }
                });
              }

              // Xóa khỏi listInside
              clonedModule.listInside.splice(oldAccessoryIndex, 1);
              console.log('Old accessory removed from listInside and 3D');
            }

            if (moduleData.type === InsideType.ACCESSORY_SINK) {
              this.removeAccessoryByType(
                clonedModule,
                InsideType.ACCESSORY_FAUCET
              );
            }

            // BƯỚC 3: Thêm model mới vào cabinet
            const addModelToCabinet = group => {
              const data = {
                _id: moduleData.id,
                name: moduleData.name,
                price: moduleData.price,
                child: moduleData.child,
                glb: moduleData.model,
                type: model.userData.type ?? moduleData?.type,
                dependent: moduleData?.dependent,
                doorDistance: moduleData?.doorDistance,
                thumbnail_url: moduleData?.thumbnail_url ?? moduleData?.icon,
                limit: moduleData?.limit,
                beveled: moduleData?.beveled ?? false,
                gltfUuid: model.uuid,
                gltf: group ?? model,
                group: moduleData.group,
              };

              model.userData.isInside = true;
              model.userData.model = moduleData.model;
              model.userData.default_handle = moduleData?.default_handle;
              model.userData.default_lock = moduleData?.default_lock;
              model.userData.default_glass_type =
                moduleData?.default_glass_type;

              if (moduleData?.userData) {
                model.userData = {
                  ...model.userData,
                  ...moduleData.userData,
                };
              }
              model.userData.limit = moduleData?.limit;

              clonedModule.listInside.push(data);
              clonedModule.group.add(group ?? model);

              return data;
            };

            const originalSize = this.getSize(model);
            let resultData;

            switch (moduleData.type) {
              case InsideType.ACCESSORY_DISH_UPPER:
                model.userData.type = moduleData.type;
                this.setInfoModelChild(model);
                if (oldPosition) {
                  const originalSize = this.getSize(model);
                  model.position.copy(oldPosition);
                  if (oldRotation) {
                    model.rotation.copy(oldRotation);
                  }
                  if (oldSize) {
                    model.scale.z = oldSize.z / originalSize.z;
                  }
                  resultData = addModelToCabinet();
                }
                break;

              case InsideType.ACCESSORY_DISH_LOWER:
                model.userData.type = moduleData.type;
                this.setInfoModelChild(model);
                if (oldPosition) {
                  const originalSize = this.getSize(model);
                  model.position.copy(oldPosition);
                  if (oldRotation) {
                    model.rotation.copy(oldRotation);
                  }
                  if (oldSize) {
                    model.scale.z = oldSize.z / originalSize.z;
                    model.scale.y = oldSize.y / originalSize.y;
                  }
                  resultData = addModelToCabinet();
                }
                break;

              case InsideType.ACCESSORY_GENERAL:
                model.userData.type = moduleData.type;
                this.setInfoModelChild(model);
                if (!moduleData?.width) {
                  openNotification({
                    key: 'cant-add-door-existing-door',
                    icon: <Icon.ICWarning />,
                    iconColor: '#F97D16',
                    title: 'Không thể thêm phụ kiện',
                    description:
                      'Phụ kiện này đang thiếu dữ liệu (không có thông số width), nên không thể thêm vào. Vui lòng liên hệ admin để được hỗ trợ.',
                    placement: 'bottomRight',
                    closable: true,
                    duration: 4,
                  });
                  return;
                }
                const width = (moduleData?.width + 90) / 1000;
                const oldDimensions =
                  clonedModule.formularController.currentDimensions;
                const oldOptions =
                  clonedModule.formularController.currentOptions;
                clonedModule.scaleModuleWithFormular({
                  width,
                });
                const newDimensions =
                  clonedModule.formularController.currentDimensions;
                const newOptions =
                  clonedModule.formularController.currentOptions;
                this.scaleInsideAfterModuleScale(
                  clonedModule,
                  oldDimensions,
                  newDimensions,
                  oldOptions,
                  newOptions
                );
                model.scale.z = moduleData?.width / 1000 / originalSize.z;

                // const

                if (leftEdge && rightEdge) {
                  leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  rightPos = clonedModule.group.worldToLocal(
                    rightEdge?.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  model.position.copy(oldPosition);
                  const newSize = this.getSize(model);
                  const sizeZ = Math.abs(rightPos.z - (leftPos.z - sizeLeft.z));
                  const gapLeft = (sizeZ - newSize.z) / 2;
                  model.position.z = leftPos.z - sizeLeft.z - gapLeft;
                  model.userData.leftEdgeUuid = leftEdge.uuid;
                  model.userData.rightEdgeUuid = rightEdge.uuid;
                  resultData = addModelToCabinet();
                }
                break;

              case InsideType.ACCESSORY_HOOD:
                model.userData.type = moduleData.type;
                this.setInfoModelChild(model);

                const tamHau = this.getMeshByName(clonedModule.group, 'HAU');
                if (tamHau) {
                  const sizeHood = this.getSize(model);
                  let caoMHM = (moduleData.height + 5) / 1000;
                  const width = (moduleData.width + 10) / 1000;

                  if (moduleData.group === 'CODIEN') {
                    caoMHM = 0.1;
                  }

                  clonedModule.scaleModuleWithFormular(
                    {
                      width,
                    },
                    {
                      caoMHM,
                    }
                  );
                  const tamHauPos = clonedModule.group.worldToLocal(
                    tamHau?.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const sizeTamHau = this.getSizeRotation(
                    clonedModule.group,
                    tamHau
                  );
                  if (moduleData.group === 'CODIEN') {
                    model.position.y = tamHauPos.y - (sizeHood.y - 0.1 - 0.025);
                  } else {
                    model.position.y = tamHauPos.y;
                  }
                  model.position.z = tamHauPos.z;
                  model.scale.z = width / model.userData.sizeDefault.z;
                  model.position.x = tamHauPos.x + sizeTamHau.x;
                  resultData = addModelToCabinet();
                  this.rescaleDoor(clonedModule);
                }
                break;

              case InsideType.ACCESSORY_FAUCET:
                model.userData.type = moduleData.type;
                this.setInfoModelChild(model);

                // BƯỚC 4: Áp dụng position
                if (oldPosition) {
                  // Nếu có position cũ, dùng position cũ
                  console.log('Using old position for new accessory');
                  model.position.copy(oldPosition);
                  if (oldRotation) {
                    model.rotation.copy(oldRotation);
                  }
                } else {
                  // Nếu không có position cũ, tìm vị trí mặc định từ POS-VOI
                  const posVoiMesh = this.getMeshByName(
                    clonedModule.group,
                    'POS-VOI'
                  );
                  if (posVoiMesh) {
                    const posVoi = clonedModule.group.worldToLocal(
                      posVoiMesh.getWorldPosition(new THREE.Vector3()).clone()
                    );
                    model.position.y = posVoi.y;
                    model.position.z = posVoi.z;
                    model.position.x = posVoi.x;
                    console.log('Using default position from POS-VOI');
                  } else {
                    console.warn('POS-VOI mesh not found');
                  }
                }

                resultData = addModelToCabinet();
                break;

              case InsideType.ACCESSORY_COOKER:
                model.userData.type = moduleData.type;
                this.setInfoModelChild(model);

                if (oldPosition) {
                  const biaTrai = this.getMeshByName(
                    clonedModule.group,
                    'BIA-TRAI'
                  );
                  const biaPhai = this.getMeshByName(
                    clonedModule.group,
                    'BIA-PHAI'
                  );
                  const xuongTruoc1 = this.getMeshByName(
                    clonedModule.group,
                    'XUONG-TRUOC-1'
                  );
                  const tamHau = this.getMeshByName(clonedModule.group, 'HAU');
                  const biaTraiPos = clonedModule.group.worldToLocal(
                    biaTrai?.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const biaPhaiPos = clonedModule.group.worldToLocal(
                    biaPhai?.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const xuongTruoc1Pos = clonedModule.group.worldToLocal(
                    xuongTruoc1?.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const tamHauPos = clonedModule.group.worldToLocal(
                    tamHau?.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const size = this.getSize(model);
                  const sizeBiaTrai = this.getSize(biaTrai);
                  const sizeHau = this.getSize(tamHau);
                  const sizeXuongTruoc1 = this.getSize(xuongTruoc1);

                  if (biaTrai && biaPhai && xuongTruoc1 && tamHau) {
                    const orientation = this.getModuleOrientation(
                      clonedModule.group
                    );
                    if (
                      orientation === 'NORMAL' ||
                      orientation === 'ROTATED_180'
                    ) {
                      const sizeZ = Math.abs(
                        biaTraiPos.z - (biaPhaiPos.z - sizeBiaTrai.z)
                      );
                      const gapLeft = (sizeZ - size.z) / 2;
                      model.position.z = biaTraiPos.z - sizeBiaTrai.z - gapLeft;
                      const gapBottom =
                        xuongTruoc1Pos.x +
                        sizeXuongTruoc1.x -
                        tamHauPos.x -
                        size.x;
                      const posX = gapBottom / 2;
                      model.position.x = tamHauPos.x + posX + sizeHau.x;
                      model.position.y =
                        xuongTruoc1Pos.y + sizeXuongTruoc1.y - size.y + 0.022;
                    } else {
                      const sizeZ = Math.abs(
                        biaPhaiPos.z - (biaTraiPos.z - sizeBiaTrai.x)
                      );
                      const gapLeft = (sizeZ - size.z) / 2;
                      model.position.z = biaTraiPos.z - sizeBiaTrai.x - gapLeft;
                      const gapBottom =
                        xuongTruoc1Pos.x +
                        sizeXuongTruoc1.z -
                        tamHauPos.x -
                        size.x;
                      const posX = gapBottom / 2;
                      model.position.x = tamHauPos.x + posX + sizeHau.z;
                      model.position.y =
                        xuongTruoc1Pos.y + sizeXuongTruoc1.y - size.y + 0.022;
                    }
                  }
                }

                resultData = addModelToCabinet();
                break;

              case InsideType.ACCESSORY_SINK:
                model.userData.type = moduleData.type;
                this.setInfoModelChild(model);

                if (oldPosition) {
                  const biaTrai = this.getMeshByName(
                    clonedModule.group,
                    'BIA-TRAI'
                  );
                  const biaPhai = this.getMeshByName(
                    clonedModule.group,
                    'BIA-PHAI'
                  );
                  const xuongTruoc1 = this.getMeshByName(
                    clonedModule.group,
                    'XUONG-TRUOC-1'
                  );
                  const cloneMesh = model.clone();
                  cloneMesh.scale.set(1, 1, 1);
                  const sinkSize = this.getSize(cloneMesh);
                  model.scale.z = moduleData?.width / 1000 / sinkSize.z;
                  const tamHau = this.getMeshByName(clonedModule.group, 'HAU');
                  const biaTraiPos = clonedModule.group.worldToLocal(
                    biaTrai?.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const biaPhaiPos = clonedModule.group.worldToLocal(
                    biaPhai?.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const xuongTruoc1Pos = clonedModule.group.worldToLocal(
                    xuongTruoc1?.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const tamHauPos = clonedModule.group.worldToLocal(
                    tamHau?.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const size = this.getSize(model);
                  const sizeBiaTrai = this.getSize(biaTrai);
                  const sizeHau = this.getSize(tamHau);
                  const sizeXuongTruoc1 = this.getSize(xuongTruoc1);

                  if (biaTrai && biaPhai && xuongTruoc1 && tamHau) {
                    const orientation = this.getModuleOrientation(
                      clonedModule.group
                    );
                    if (
                      orientation === 'NORMAL' ||
                      orientation === 'ROTATED_180'
                    ) {
                      const sizeZ = Math.abs(
                        biaTraiPos.z - (biaPhaiPos.z - sizeBiaTrai.z)
                      );
                      const gapLeft = (sizeZ - size.z) / 2;
                      model.position.z = biaTraiPos.z - sizeBiaTrai.z - gapLeft;
                      const gapBottom =
                        xuongTruoc1Pos.x +
                        sizeXuongTruoc1.x -
                        tamHauPos.x -
                        size.x;
                      const posX = gapBottom / 2;
                      model.position.x = tamHauPos.x + posX + sizeHau.x;
                      model.position.y =
                        xuongTruoc1Pos.y + sizeXuongTruoc1.y - size.y + 0.022;
                    } else {
                      const sizeZ = Math.abs(
                        biaPhaiPos.z - (biaTraiPos.z - sizeBiaTrai.x)
                      );
                      const gapLeft = (sizeZ - size.z) / 2;
                      model.position.z = biaTraiPos.z - sizeBiaTrai.x - gapLeft;
                      const gapBottom =
                        xuongTruoc1Pos.x +
                        sizeXuongTruoc1.z -
                        tamHauPos.x -
                        size.x;
                      const posX = gapBottom / 2;
                      model.position.x = tamHauPos.x + posX + sizeHau.z;
                      model.position.y =
                        xuongTruoc1Pos.y + sizeXuongTruoc1.y - size.y + 0.022;
                      model.userData.leftEdgeUuid = leftEdge.uuid;
                      model.userData.rightEdgeUuid = rightEdge.uuid;
                    }
                  }
                }

                resultData = addModelToCabinet();
                break;

              case InsideType.ACCESSORY_GENERAL:
                model.userData.type = moduleData.type;
                this.setInfoModelChild(model);

                if (oldPosition) {
                  console.log('Using old position for new fixed accessory');
                  model.position.copy(oldPosition);
                  if (oldRotation) {
                    model.rotation.copy(oldRotation);
                  }
                } else {
                  // Logic mặc định cho fixed accessory nếu cần
                  console.warn('No old position found for fixed accessory');
                }

                resultData = addModelToCabinet();
                break;

              case InsideType.ACCESSORY_FRIDGE:
                model.userData.type = moduleData.type;
                this.setInfoModelChild(model);

                // const originalSize = this.getSize(model);

                clonedModule.scaleModuleWithFormular({
                  width: originalSize.z + 0.1,
                  height: originalSize.y + 0.05,
                });

                if (oldPosition) {
                  console.log('Using old position for new fixed accessory');
                  model.position.copy(oldPosition);
                  if (oldRotation) {
                    model.rotation.copy(oldRotation);
                  }
                } else {
                  // Logic mặc định cho fixed accessory nếu cần
                  console.warn('No old position found for fixed accessory');
                }

                resultData = addModelToCabinet();
                break;

              case InsideType.ACCESSORY_WASHER:
                model.userData.type = moduleData.type;
                this.setInfoModelChild(model);

                if (oldPosition) {
                  console.log('Using old position for new fixed accessory');
                  model.position.copy(oldPosition);
                  if (oldRotation) {
                    model.rotation.copy(oldRotation);
                  }
                } else {
                  // Logic mặc định cho fixed accessory nếu cần
                  console.warn('No old position found for fixed accessory');
                }

                await this.changeSize(clonedModule, model, originalSize);

                resultData = addModelToCabinet();
                break;

              default:
                console.error('Invalid module type:', moduleData.type);
                reject(
                  new Error(`Unsupported module type: ${moduleData.type}`)
                );
                return;
            }

            console.log('New accessory added successfully:', resultData);
            resolve(resultData);
          } catch (error) {
            console.error('Error in switchModelAccessory:', error);
            reject(error);
          }
        },
        undefined,
        err => {
          console.error('Error loading model:', err);
          reject(err);
        }
      );
    });
  };

  loadRealModel = (
    moduleData,
    intersection,
    clonedModule,
    currentCabinet,
    handleData,
    typeHau,
    defaultLiftSupport
  ) => {
    return new Promise((resolve, reject) => {
      const loader = new GLTFLoader();
      let meshPoints = currentCabinet.getActiveMeshPoints(clonedModule);
      if (typeHau === TypeConnerModule.SUB) {
        meshPoints = EdgeDetector.findMeshPointsR90(clonedModule.group);
      }
      if (typeHau) {
        const ori = this.getModuleOrientation(clonedModule.group);
        const ignoreXanBa =
          moduleData.type === InsideType.DOOR ||
          moduleData.type === InsideType.DRAWER ||
          moduleData.type === InsideType.ACCESSORY_DISH_LOWER;
        const checkXanBa = moduleData.type !== InsideType.DOOR;

        if (ori === 'NORMAL') {
          if (typeHau === TypeConnerModule.MAIN) {
            meshPoints = EdgeDetector.findMeshPoints(
              clonedModule.group,
              ignoreXanBa,
              checkXanBa
            );
          } else if (typeHau === TypeConnerModule.SUB) {
            meshPoints = EdgeDetector.findMeshPointsR90(
              clonedModule.group,
              ignoreXanBa,
              checkXanBa
            );
          }
        }
        if (ori === 'ROTATED_270') {
          if (typeHau === TypeConnerModule.MAIN) {
            meshPoints = EdgeDetector.findMeshPointsRotation(
              clonedModule.group,
              ignoreXanBa,
              checkXanBa
            );
          } else if (typeHau === TypeConnerModule.SUB) {
            meshPoints = EdgeDetector.findMeshPoints(
              clonedModule.group,
              ignoreXanBa,
              checkXanBa
            );
          }
        }
      }
      const columnPoints = this.findColumnPanel(clonedModule);

      loader.load(
        moduleData.model,
        async gltf => {
          try {
            //Chỉnh lại độ bóng của cửa
            if (gltf?.scene && moduleData.type === InsideType.DOOR) {
              gltf.scene.traverse(child => {
                if (child.isMesh && child.material) {
                  child.material.metalness = 0; // 0 = không kim loại, 1 = kim loại hoàn toàn
                  child.material.roughness = 0.8; // 0 = bóng gương, 1 = mờ hoàn toàn
                }
              });
            }

            // Màu cho gương soi gắn cánh cửa
            if (gltf?.scene && moduleData.type === InsideType.MIRROR_DOOR) {
              gltf.scene.traverse(child => {
                if (child.isMesh && child.material) {
                  child.material.metalness = 0.2; // kim loại hoàn toàn
                  child.material.roughness = 0.0; // bóng gương hoàn toàn
                  child.material.envMapIntensity = 1.0; // phản chiếu môi trường
                  child.material.color.set(0xffffff); // màu trắng để phản chiếu đúng màu
                }
              });
            }

            let model;
            if (
              moduleData.type === InsideType.LOCK ||
              moduleData.type === InsideType.DOOR ||
              moduleData.type === InsideType.DRAWER ||
              moduleData.type === InsideType.ACCESSORY_GENERAL ||
              moduleData.type === InsideType.ACCESSORY_DISH_UPPER ||
              moduleData.type === InsideType.ACCESSORY_DISH_LOWER ||
              moduleData.type === InsideType.CLOTHES_HANGER ||
              moduleData.type === InsideType.LED_RECESSED ||
              moduleData.type === InsideType.LED_CORNER ||
              moduleData.type === InsideType.ACCESSORY_SINK ||
              moduleData.type === InsideType.ACCESSORY_FAUCET ||
              moduleData.type === InsideType.ACCESSORY_FRIDGE ||
              moduleData.type === InsideType.ACCESSORY_WASHER ||
              moduleData.type === InsideType.ACCESSORY_COOKER ||
              moduleData.type === InsideType.ACCESSORY_HOOD ||
              moduleData.type === InsideType.ACCESSORY_FLIP ||
              moduleData.type === InsideType.CHAN_ROI_TV ||
              moduleData.type === InsideType.KHUNG_SAT ||
              moduleData.type === InsideType.U_DRAWER
            ) {
              model = gltf.scene;
            } else {
              gltf.scene?.traverse(child => {
                if (child.isMesh) {
                  model = child;
                  if (moduleData.type !== InsideType.ACCESSORY_DISH_UPPER) {
                    child.userData.isMesh = true;
                  }
                } else if (child.isGroup) {
                }
              });
            }

            const addModelToCabinet = group => {
              const data = {
                _id: moduleData.id,
                name: moduleData.name,
                price: moduleData.price,
                child: moduleData.child,
                // glb: moduleData.model,
                glb:
                  group?.userData.type === InsideType.BRACE
                    ? '/assets/glb/BRACE.glb'
                    : group?.userData.type === InsideType.HANDLE
                      ? moduleData.default_handle.model
                      : group?.userData.type === InsideType.LOCK
                        ? moduleData.default_lock.model
                        : moduleData.model,
                type:
                  group?.userData?.type ??
                  model?.userData?.type ??
                  moduleData?.type,
                materialType:
                  moduleData?.material_type ?? moduleData?.materialType,
                useAcrylic: moduleData?.use_acrylic ?? moduleData?.useAcrylic,
                dependent: moduleData?.dependent,
                doorDistance: moduleData?.doorDistance,
                groupId: moduleData?.groupId,
                thumbnail_url: moduleData?.thumbnail_url ?? moduleData?.icon,
                limit: moduleData?.limit,
                beveled: moduleData?.beveled ?? false,
                gltfUuid: group?.uuid ?? model.uuid,
                gltf: group ?? model,
                group: moduleData.group,
              };

              model.userData.isInside = true;
              model.userData.model = moduleData.model;
              model.userData.default_handle = moduleData?.default_handle;
              model.userData.default_lock = moduleData?.default_lock;
              model.userData.default_glass_type =
                moduleData?.default_glass_type;

              if (moduleData?.userData) {
                model.userData = {
                  ...model.userData,
                  ...moduleData.userData,
                };
              }
              model.userData.limit = moduleData?.limit;
              clonedModule.listInside.push(data);

              clonedModule.group.add(group ?? model);

              return data;
            };

            //Thêm bạ
            const addBraceToCabinet = brace => {
              const data = {
                _id: brace.userData?.id,
                glb:
                  brace.userData.type === InsideType.BRACE
                    ? '/assets/glb/BRACE.glb'
                    : moduleData.default_handle.model,
                type: brace.userData.type,
                name: brace.userData.name,
                price: brace.userData.price,
                material: brace.userData.material,
                thumbnail_url: brace.userData.thumbnail_url,
                gltfUuid: brace.uuid,
                gltf: brace,
              };

              model.userData.isInside = true;
              model.userData.model =
                moduleData.type === InsideType.BRACE
                  ? '/assets/glb/BRACE.glb'
                  : moduleData.model;
              clonedModule.listInside.push(data);
              clonedModule.group.add(brace);

              return data;
            };

            const originalSize = this.getSize(model);

            if (moduleData.localTRS && !moduleData.userData.noInside) {
              model.userData.type = moduleData.type;
              model.userData.materialType =
                moduleData.userData?.materialType ?? moduleData?.materialType;
              model.userData.doorDirection =
                moduleData.userData?.doorDirection ?? moduleData?.doorDirection;
              model.userData.horizontalType =
                moduleData.userData?.horizontalType ??
                moduleData?.horizontalType;
              model.userData.useAcrylic =
                moduleData.userData?.useAcrylic ?? moduleData?.useAcrylic;
              model.userData.columnInfo = moduleData.userData?.columnInfo;
              model.userData.sizeData = moduleData.userData?.sizeData;

              this.setInfoModelChild?.(model);
              const newModel = await this.changeSize(
                clonedModule,
                model,
                originalSize,
                moduleData.savedSize,
                moduleData.type,
                moduleData.localTRS
              );
              if (newModel) model = newModel;
              const data = addModelToCabinet();
              this.applyLocalTRS(model, moduleData.localTRS);
              resolve(data);
              return;
            }

            if (moduleData.localTRS && moduleData.userData.noInside) {
              resolve(null);
              return;
            }

            let rightEdgeDoor, leftEdgeDoor;
            let rightEdge, leftEdge, backEdge, bottomEdge, topEdge;
            let drawerClicked, accessoryFlipClicked;

            const orientation = this.getModuleOrientation(clonedModule.group);
            //cu
            // if (
            //   ((orientation === 'NORMAL' || orientation === 'ROTATED_180') &&
            //     typeHau === TypeConnerModule.MAIN) ||
            //   (orientation === 'ROTATED_270' &&
            //     typeHau === TypeConnerModule.SUB) ||
            //   ((orientation === 'NORMAL' || orientation === 'ROTATED_180') &&
            //     !typeHau)
            // )
            //moi
            if (
              (orientation === 'NORMAL' &&
                (typeHau === TypeConnerModule.MAIN || !typeHau)) ||
              (orientation === 'ROTATED_270' &&
                typeHau === TypeConnerModule.SUB)
            ) {
              const resultZ = EdgeDetector.findClosestZEdges(
                undefined,
                meshPoints,
                intersection,
                [InsideType.COL_PANEL]
              );
              rightEdge = resultZ.rightEdge;
              leftEdge = resultZ.leftEdge;
              backEdge = EdgeDetector.findClosestXEdges(
                meshPoints,
                intersection,
                moduleData.type !== InsideType.DOOR &&
                  moduleData.type !== InsideType.MIRROR_DOOR
                  ? [InsideType.DOOR]
                  : [] //Nếu inside đang kéo không phải cửa thì bỏ qua cánh cửa
              );

              const resultY = EdgeDetector.findClosestYEdges(
                undefined,
                meshPoints,
                intersection,
                [
                  InsideType.BRACE,
                  InsideType.DOOR,
                  InsideType.CLOTHES_HANGER,
                  InsideType.ACCESSORY_DISH_UPPER,
                  InsideType.ACCESSORY_GENERAL,
                  InsideType.ACCESSORY_FRIDGE,
                  InsideType.ACCESSORY_HOOD,
                  InsideType.COL_PANEL,
                ],
                null,
                false,
                typeHau
              );
              bottomEdge = resultY.bottomEdge;
              topEdge = resultY.topEdge;

              if (
                moduleData.type === InsideType.DOOR ||
                moduleData.type === InsideType.MIRROR_DOOR
              ) {
                const resultEdgeDoor = EdgeDetector.findClosestEdgesForDoor(
                  undefined,
                  meshPoints,
                  intersection,
                  // null,
                  [InsideType.COL_PANEL, InsideType.ACCESSORY_GENERAL],
                  undefined,
                  true
                );
                rightEdgeDoor = resultEdgeDoor.rightEdge;
                leftEdgeDoor = resultEdgeDoor.leftEdge;

                drawerClicked = EdgeDetector.findDrawerClick(
                  meshPoints,
                  intersection
                );

                if (!drawerClicked) {
                  // accessoryFlipClicked = EdgeDetector.findAccessoryFlipClick(
                  drawerClicked = EdgeDetector.findAccessoryFlipClick(
                    meshPoints,
                    intersection
                  );
                }

                const resultYDoor = EdgeDetector.findClosestYEdges(
                  undefined,
                  meshPoints,
                  intersection,
                  [
                    InsideType.ACCESSORY_HOOD,
                    InsideType.ACCESSORY_GENERAL,
                    InsideType.ACCESSORY_DISH_UPPER,
                    InsideType.HPANEL_SHOE,
                    InsideType.COL_PANEL,
                  ],
                  undefined,
                  true
                );

                bottomEdge = resultYDoor.bottomEdge;
                topEdge = resultYDoor.topEdge;
              }
            } else if (
              (orientation === 'ROTATED_270' &&
                (typeHau === TypeConnerModule.MAIN || !typeHau)) ||
              (orientation === 'ROTATED_180' &&
                typeHau === TypeConnerModule.SUB)
            ) {
              const resultZ = EdgeDetector.findClosestZEdgesR270(
                undefined,
                meshPoints,
                intersection,
                [InsideType.COL_PANEL] //Loại trừ bìa cột
              );
              rightEdge = resultZ.rightEdge;
              leftEdge = resultZ.leftEdge;
              backEdge = EdgeDetector.findClosestXEdgesR270(
                meshPoints,
                intersection,
                moduleData.type !== InsideType.DOOR &&
                  moduleData.type !== InsideType.MIRROR_DOOR
                  ? [InsideType.DOOR]
                  : [] //Nếu inside đang kéo không phải cửa thì bỏ qua cánh cửa
              );

              const resultY = EdgeDetector.findClosestYEdgesR270(
                undefined,
                meshPoints,
                intersection,
                [
                  InsideType.BRACE,
                  InsideType.DOOR,
                  InsideType.CLOTHES_HANGER,
                  InsideType.ACCESSORY_DISH_UPPER,
                  InsideType.ACCESSORY_GENERAL,
                  InsideType.ACCESSORY_FRIDGE,
                  InsideType.ACCESSORY_HOOD,
                  InsideType.COL_PANEL,
                ],
                undefined,
                false
              );
              bottomEdge = resultY.bottomEdge;
              topEdge = resultY.topEdge;

              if (
                moduleData.type === InsideType.DOOR ||
                moduleData.type === InsideType.MIRROR_DOOR
              ) {
                const resultEdgeDoor = EdgeDetector.findClosestEdgesForDoorR270(
                  undefined,
                  meshPoints,
                  intersection,
                  // null,
                  [InsideType.COL_PANEL],
                  undefined,
                  true
                );
                rightEdgeDoor = resultEdgeDoor.rightEdge;
                leftEdgeDoor = resultEdgeDoor.leftEdge;

                drawerClicked = EdgeDetector.findDrawerClickR270(
                  meshPoints,
                  intersection
                );

                if (!drawerClicked) {
                  // accessoryFlipClicked = EdgeDetector.findAccessoryFlipClickR270(
                  drawerClicked = EdgeDetector.findAccessoryFlipClickR270(
                    meshPoints,
                    intersection
                  );
                }

                const resultYDoor = EdgeDetector.findClosestYEdgesR270(
                  undefined,
                  meshPoints,
                  intersection,
                  [
                    InsideType.ACCESSORY_HOOD,
                    InsideType.ACCESSORY_GENERAL,
                    InsideType.ACCESSORY_DISH_UPPER,
                    InsideType.HPANEL_SHOE,
                    InsideType.COL_PANEL,
                  ],
                  undefined,
                  true
                );
                bottomEdge = resultYDoor.bottomEdge;
                topEdge = resultYDoor.topEdge;
              }
            } else if (
              orientation === 'ROTATED_90' ||
              typeHau === TypeConnerModule.SUB
            ) {
              const resultZ = EdgeDetector.findClosestZEdgesR90(
                undefined,
                meshPoints,
                intersection,
                [InsideType.COL_PANEL] //Loại trừ bìa cột
              );

              rightEdge = resultZ.rightEdge;
              leftEdge = resultZ.leftEdge;
              backEdge = EdgeDetector.findClosestXEdgesR90(
                meshPoints,
                intersection,
                moduleData.type !== InsideType.DOOR &&
                  moduleData.type !== InsideType.MIRROR_DOOR
                  ? [InsideType.DOOR]
                  : [] //Nếu inside đang kéo không phải cửa thì bỏ qua cánh cửa
              );

              const resultY = EdgeDetector.findClosestYEdgesR90(
                undefined,
                meshPoints,
                intersection,
                [
                  InsideType.BRACE,
                  InsideType.DOOR,
                  InsideType.CLOTHES_HANGER,
                  InsideType.ACCESSORY_DISH_UPPER,
                  InsideType.ACCESSORY_GENERAL,
                  InsideType.ACCESSORY_FRIDGE,
                  InsideType.ACCESSORY_HOOD,
                  InsideType.COL_PANEL,
                ],
                undefined,
                false
              );
              bottomEdge = resultY.bottomEdge;
              topEdge = resultY.topEdge;

              if (
                moduleData.type === InsideType.DOOR ||
                moduleData.type === InsideType.MIRROR_DOOR
              ) {
                const resultEdgeDoor = EdgeDetector.findClosestEdgesForDoorR90(
                  undefined,
                  meshPoints,
                  intersection,
                  // null,
                  [InsideType.COL_PANEL, InsideType.ACCESSORY_GENERAL],
                  undefined,
                  true
                );
                rightEdgeDoor = resultEdgeDoor.rightEdge;
                leftEdgeDoor = resultEdgeDoor.leftEdge;

                drawerClicked = EdgeDetector.findDrawerClickR90(
                  meshPoints,
                  intersection
                );

                if (!drawerClicked) {
                  drawerClicked = EdgeDetector.findAccessoryFlipClick(
                    meshPoints,
                    intersection
                  );
                }

                const resultYDoor = EdgeDetector.findClosestYEdgesR90(
                  undefined,
                  meshPoints,
                  intersection,
                  [
                    InsideType.ACCESSORY_HOOD,
                    InsideType.ACCESSORY_GENERAL,
                    InsideType.ACCESSORY_DISH_UPPER,
                    InsideType.HPANEL_SHOE,
                    InsideType.COL_PANEL,
                  ],
                  undefined,
                  true
                );

                bottomEdge = resultYDoor.bottomEdge;
                topEdge = resultYDoor.topEdge;
              }
            }

            let sizeRight;
            let sizeLeft;
            let sizeBottom;
            let sizeTop;
            let sizeBack;

            if (topEdge) {
              sizeTop = this.getSize(topEdge?.mesh);
            }

            if (bottomEdge) {
              sizeBottom = this.getSize(bottomEdge?.mesh);
            }

            if (rightEdge) {
              sizeRight = this.getSize(rightEdge?.mesh);
            }

            if (leftEdge) {
              sizeLeft = this.getSize(leftEdge?.mesh);
            }

            // if (backEdge) {
            //   sizeBack = this.getSize(backEdge?.mesh);
            //   if (orientation === "NORMAL"|| orientation === "ROTATED_180") {
            //     const localBackEdgePos = clonedModule.group.worldToLocal(
            //       backEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
            //     );
            //     model.position.x = localBackEdgePos.x + sizeBack.x;
            //   } else if (
            //     orientation === "ROTATED_90" ||
            //     orientation === "ROTATED_270"
            //   ) {
            //     const localBackEdgePos = clonedModule.group.worldToLocal(
            //       backEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
            //     );
            //     model.position.x = localBackEdgePos.x + sizeBack.z;
            //   }
            // }

            const leftPos = this.getLocalPosOf(
              clonedModule.group,
              leftEdge?.mesh
            );
            const rightPos = this.getLocalPosOf(
              clonedModule.group,
              rightEdge?.mesh
            );
            const backPos = this.getLocalPosOf(
              clonedModule.group,
              backEdge?.mesh
            );

            if (backEdge && leftEdge && rightEdge) {
              //Xắn ngang sử dụng 2 cạnh trái phải để định vị
              sizeBack = this.getSize(backEdge?.mesh);
              model.position.x = Math.min(leftPos.x, rightPos.x);

              //Các loại còn lại sử dụng tấm hậu để định vị
              if (moduleData.type !== InsideType.HPANEL) {
                if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
                  const localBackEdgePos = clonedModule.group.worldToLocal(
                    backEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  model.position.x = localBackEdgePos.x + sizeBack.x;
                } else if (
                  orientation === 'ROTATED_90' ||
                  orientation === 'ROTATED_270'
                ) {
                  const localBackEdgePos = clonedModule.group.worldToLocal(
                    backEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  model.position.x = localBackEdgePos.x + sizeBack.z;
                }
              }
            }
            //Logic mới cho vị trí theo trục X

            let resultData;

            switch (moduleData.type) {
              case InsideType.HPANEL: //Xan ngang
                // let check = false;
                if (clonedModule.moduleType.toLowerCase().includes('shoe')) {
                  if (
                    clonedModule.formularController.currentDimensions.depth <
                    0.333
                  ) {
                    // check = true;
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: 'Độ sâu tủ không phù hợp',
                      description:
                        'Chiều sâu tủ hiện tại không phù hợp để làm kệ thẳng cho giày dép. Gợi ý: chuyển sang kệ xéo để tối ưu không gian.',
                      placement: 'bottomRight',
                      closable: true,
                      duration: 4,
                    });
                  }
                }
                if (rightEdge && leftEdge && !typeHau) {
                  model.userData.type = moduleData.type;
                  model.userData.typeHau = typeHau;
                  this.setInfoModelChild(model);

                  const posLocal = clonedModule.group.worldToLocal(
                    intersection.clone()
                  );
                  model.position.y = posLocal.y;

                  const isOverlapHauNgang = this.checkOverlapHauNgang(
                    clonedModule,
                    model.position.y
                  );

                  const originalScale = model.scale.clone();

                  //Bia cot start
                  let leftColPos, rightColPos;

                  if (columnPoints.leftColPanel || columnPoints.rightColPanel) {
                    const leftColPanel = columnPoints.leftColPanel;
                    const rightColPanel = columnPoints.rightColPanel;

                    // leftColPos = clonedModule.group.worldToLocal(
                    //   leftColPanel.getWorldPosition(new THREE.Vector3()).clone()
                    // );
                    //  rightColPos = clonedModule.group.worldToLocal(
                    //   rightColPanel
                    //     .getWorldPosition(new THREE.Vector3())
                    //     .clone()
                    // );
                    leftColPos = this.getLocalPosOf(
                      clonedModule.group,
                      leftColPanel
                    );
                    rightColPos = this.getLocalPosOf(
                      clonedModule.group,
                      rightColPanel
                    );
                  }
                  //Bia cot end
                  let sizeXTV;
                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const scaleZ = Math.abs(
                      rightEdge?.mesh.userData.position.z -
                        leftEdge?.mesh.userData.position.z
                    );
                    model.position.z = leftPos.z - sizeLeft.z;
                    model.scale.z =
                      ((scaleZ - sizeLeft.z) * originalScale.z) /
                      originalSize.z;

                    //Scale X
                    let sizeX =
                      Number(
                        clonedModule.formularController.currentDimensions.depth
                      ) -
                      (backEdge.mesh.position.x + sizeBack.x);
                    if (
                      Number(this.doorType) === DoorType.INNER &&
                      (leftEdge.mesh.userData.type === undefined ||
                        rightEdge.mesh.userData.type === undefined)
                    ) {
                      sizeX = sizeX - 0.019;
                    }
                    if (
                      Number(this.doorType) === DoorType.SLIDING &&
                      (leftEdge.mesh.userData.type === undefined ||
                        rightEdge.mesh.userData.type === undefined)
                    ) {
                      sizeX = sizeX - 0.08;
                    }
                    if (
                      clonedModule.cabinetType.toLowerCase().includes('curve')
                    ) {
                      sizeX = sizeX - 0.017;
                    }

                    if (isOverlapHauNgang) {
                      sizeX = sizeX - 0.017;
                    }

                    if (
                      leftEdge.mesh.userData.type ||
                      rightEdge.mesh.userData.type
                    ) {
                      sizeX = Math.min(sizeLeft.x, sizeRight.x);
                    }
                    const scaleX = sizeX / originalSize.x;
                    model.scale.x = scaleX;
                    sizeXTV = sizeBottom?.x;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    const leftWorldPos = new THREE.Vector3();
                    const rightWorldPos = new THREE.Vector3();
                    leftEdge.mesh.getWorldPosition(leftWorldPos);
                    rightEdge.mesh.getWorldPosition(rightWorldPos);

                    model.position.z = leftPos.z - sizeLeft.x;

                    const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                    model.scale.z =
                      ((scaleZ - sizeLeft.x) * originalScale.z) /
                      originalSize.z;

                    //Scale X
                    let sizeX =
                      Number(
                        clonedModule.formularController.currentDimensions.depth
                      ) -
                      (backEdge.mesh.position.x + sizeBack.z);

                    if (
                      Number(this.doorType) === DoorType.INNER &&
                      (leftEdge.mesh.userData.type === undefined ||
                        rightEdge.mesh.userData.type === undefined)
                    ) {
                      sizeX = sizeX - 0.019;
                    }
                    if (
                      Number(this.doorType) === DoorType.SLIDING &&
                      (leftEdge.mesh.userData.type === undefined ||
                        rightEdge.mesh.userData.type === undefined)
                    ) {
                      sizeX = sizeX - 0.08;
                    }
                    if (
                      clonedModule.cabinetType.toLowerCase().includes('curve')
                    ) {
                      sizeX = sizeX - 0.017;
                    }

                    if (
                      leftEdge.mesh.userData.type ||
                      rightEdge.mesh.userData.type
                    ) {
                      sizeX = Math.min(sizeLeft.z, sizeRight.z);
                    }

                    const scaleX = sizeX / originalSize.x;
                    model.scale.x = scaleX;
                    sizeXTV = sizeBottom?.z;
                  }

                  if (
                    clonedModule.cabinetType.toLowerCase().includes('tv') ||
                    clonedModule.cabinetType
                      .toLowerCase()
                      .includes('shoe_lower_bench')
                  ) {
                    if (
                      (clonedModule.cabinetType
                        .toLowerCase()
                        .includes('upper') &&
                        bottomEdge.mesh.userData.type === undefined &&
                        bottomEdge.mesh.userData.type === undefined) ||
                      clonedModule.cabinetType
                        .toLowerCase()
                        .includes('shoe_lower_bench')
                    ) {
                      sizeXTV -= thicknessBase;
                    }
                    model.scale.x = sizeXTV / originalSize.x;
                  }
                  if (clonedModule.cabinetType.toLowerCase().includes('tv')) {
                    if (
                      clonedModule.cabinetType.toLowerCase().includes('upper')
                    ) {
                      const localBackEdgePos = clonedModule.group.worldToLocal(
                        backEdge?.mesh
                          .getWorldPosition(new THREE.Vector3())
                          .clone()
                      );
                      model.position.x = localBackEdgePos.x + sizeBack.z;
                    }
                  }
                  if (
                    clonedModule.cabinetType
                      .toLowerCase()
                      .includes('shoe_lower_bench')
                  ) {
                    const localBackEdgePos = clonedModule.group.worldToLocal(
                      backEdge?.mesh
                        .getWorldPosition(new THREE.Vector3())
                        .clone()
                    );
                    model.position.x = localBackEdgePos.x + sizeBack.z;
                  }

                  const verticalEdgeRatio =
                    (topEdge?.mesh?.position.y -
                      model.position.y -
                      originalSize.y) /
                    (model.position.y -
                      bottomEdge?.mesh?.position.y -
                      sizeBottom?.y);

                  model.userData.verticalEdgeRatio = verticalEdgeRatio;

                  //Trái nằm trước bìa phải cột và phải nằm sau bìa trái cột
                  if (
                    (columnPoints.leftColPanel || columnPoints.rightColPanel) &&
                    leftPos.z > rightColPos.z &&
                    rightPos.z < leftColPos.z
                  ) {
                    const isInsideColumn =
                      leftPos.z < leftColPos.z && rightPos.z > rightColPos.z;

                    if (!isInsideColumn) {
                      //Tính lại độ sâu của inside cho tủ khuyết cột
                      let sizeXForColMd;

                      if (
                        orientation === 'NORMAL' ||
                        orientation === 'ROTATED_180'
                      ) {
                        sizeXForColMd = Math.max(sizeLeft.x, sizeRight.x);

                        const scaleX = sizeXForColMd / originalSize.x;
                        model.scale.x = scaleX;
                      } else if (
                        orientation === 'ROTATED_90' ||
                        orientation === 'ROTATED_270'
                      ) {
                        sizeXForColMd = Math.max(sizeLeft.z, sizeRight.z);

                        const scaleX = sizeXForColMd / originalSize.x;
                        model.scale.x = scaleX;
                      }

                      // const newMesh = this.createPanelColumn(
                      //   clonedModule,
                      //   model,
                      //   leftPos,
                      //   leftColPos
                      // );
                      const newMesh = this.createPanelColumn(
                        clonedModule,
                        model,
                        leftEdge
                      );

                      model = newMesh;
                    }
                  }

                  ///XU LY CHO TRUONG HOP TU KHUYET COT DOC

                  //// phu thuoc
                  // if (!leftEdge.mesh.userData.rightDependent) {
                  //   leftEdge.mesh.userData.rightDependent = [];
                  // }
                  // leftEdge.mesh.userData.rightDependent.push(model.uuid);
                  if (leftEdge.mesh.userData.type !== undefined) {
                    if (!leftEdge.mesh.userData.rightDependent) {
                      leftEdge.mesh.userData.rightDependent = [];
                    }
                    leftEdge.mesh.userData.rightDependent.push(model.uuid);
                  } else if (leftEdge.mesh.parent.userData.type !== undefined) {
                    if (!leftEdge.mesh.parent.userData.rightDependent) {
                      leftEdge.mesh.parent.userData.rightDependent = [];
                    }
                    leftEdge.mesh.parent.userData.rightDependent.push(
                      model.uuid
                    );
                  }

                  // if (!rightEdge.mesh.userData.leftDependent) {
                  //   rightEdge.mesh.userData.leftDependent = [];
                  // }
                  // rightEdge.mesh.userData.leftDependent.push(model.uuid);
                  if (rightEdge.mesh.userData.type !== undefined) {
                    if (!rightEdge.mesh.userData.leftDependent) {
                      rightEdge.mesh.userData.leftDependent = [];
                    }
                    rightEdge.mesh.userData.leftDependent.push(model.uuid);
                  } else if (
                    rightEdge.mesh.parent.userData.type !== undefined
                  ) {
                    if (!rightEdge.mesh.parent.userData.leftDependent) {
                      rightEdge.mesh.parent.userData.leftDependent = [];
                    }
                    rightEdge.mesh.parent.userData.leftDependent.push(
                      model.uuid
                    );
                  }
                  model.userData.leftEdgeUuid = leftEdge?.mesh.uuid;
                  model.userData.rightEdgeUuid = rightEdge?.mesh.uuid;
                  model.userData.topEdgeUuid = topEdge?.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge?.mesh.uuid;
                  ////
                } else if (
                  leftEdge &&
                  rightEdge === undefined &&
                  backEdge &&
                  clonedModule.cabinetType.toLowerCase().includes('curve') &&
                  !typeHau
                ) {
                  //Trường hợp chỉ có cạnh trái (tu bo cong)
                  model.userData.type = InsideType.HPANEL_RIGHT;
                  this.setInfoModelChild(model);

                  const posLocal = clonedModule.group.worldToLocal(
                    intersection.clone()
                  );
                  model.position.y = posLocal.y;

                  const originalScale = model.scale.clone();

                  sizeBack = this.getSize(backEdge?.mesh);

                  model.position.x = leftPos.x;

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const backBox = new THREE.Box3().setFromObject(
                      backEdge.mesh
                    );
                    const leftBox = new THREE.Box3().setFromObject(
                      leftEdge.mesh
                    );
                    model.position.z = leftPos.z - sizeLeft.z;
                    const sizeZ = leftBox.min.z - backBox.min.z;
                    const scaleZ = sizeZ / originalSize.z;
                    model.scale.z = scaleZ;

                    //Scale X
                    const sizeX = sizeLeft.x;
                    const scaleX = sizeX / originalSize.x;
                    model.scale.x = scaleX;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    const backBox = new THREE.Box3().setFromObject(
                      backEdge.mesh
                    );
                    const leftBox = new THREE.Box3().setFromObject(
                      leftEdge.mesh
                    );

                    const leftWorldPos = new THREE.Vector3();
                    leftEdge.mesh.getWorldPosition(leftWorldPos);

                    model.position.z = leftPos.z - sizeLeft.x;

                    const sizeZ = backBox.max.x - leftBox.max.x;
                    const scaleZ = sizeZ / originalSize.z;
                    model.scale.z = scaleZ;

                    //Scale X
                    const sizeX = sizeLeft.z;
                    const scaleX = sizeX / originalSize.x;
                    model.scale.x = scaleX;
                  }

                  ///XU LY CHO TRUONG HOP TU KHUYET COT DOC

                  //// phu thuoc
                  // if (!leftEdge.mesh.userData.rightDependent) {
                  //   leftEdge.mesh.userData.rightDependent = [];
                  // }
                  // leftEdge.mesh.userData.rightDependent.push(model.uuid);
                  if (leftEdge.mesh.userData.type !== undefined) {
                    if (!leftEdge.mesh.userData.rightDependent) {
                      leftEdge.mesh.userData.rightDependent = [];
                    }
                    leftEdge.mesh.userData.rightDependent.push(model.uuid);
                  } else if (leftEdge.mesh.parent.userData.type !== undefined) {
                    if (!leftEdge.mesh.parent.userData.rightDependent) {
                      leftEdge.mesh.parent.userData.rightDependent = [];
                    }
                    leftEdge.mesh.parent.userData.rightDependent.push(
                      model.uuid
                    );
                  }
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  ////
                } else if (
                  leftEdge === undefined &&
                  rightEdge &&
                  backEdge &&
                  clonedModule.cabinetType.toLowerCase().includes('curve') &&
                  !typeHau
                ) {
                  //Trường hợp chỉ có cạnh phải (tu bo cong)
                  model.userData.type = InsideType.HPANEL_LEFT;
                  this.setInfoModelChild(model);

                  const posLocal = clonedModule.group.worldToLocal(
                    intersection.clone()
                  );
                  model.position.y = posLocal.y;

                  const originalScale = model.scale.clone();

                  sizeBack = this.getSize(backEdge?.mesh);

                  model.position.x = rightPos.x;

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const backBox = new THREE.Box3().setFromObject(
                      backEdge.mesh
                    );
                    const rightBox = new THREE.Box3().setFromObject(
                      rightEdge.mesh
                    );

                    const sizeZ = backBox.max.z - rightBox.max.z;
                    const scaleZ = sizeZ / originalSize.z;
                    model.scale.z = scaleZ;

                    model.position.z = backPos?.z;
                    //Scale X
                    const sizeX = sizeRight.x;
                    const scaleX = sizeX / originalSize.x;
                    model.scale.x = scaleX;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    const backBox = new THREE.Box3().setFromObject(
                      backEdge.mesh
                    );
                    const rightBox = new THREE.Box3().setFromObject(
                      rightEdge.mesh
                    );

                    const sizeZ = rightBox.min.x - backBox.min.x;
                    const scaleZ = sizeZ / originalSize.z;
                    model.scale.z = scaleZ;
                    model.position.z = backPos?.z;

                    //Scale X
                    const sizeX = sizeRight.z;
                    const scaleX = sizeX / originalSize.x;
                    model.scale.x = scaleX;
                  }

                  ///XU LY CHO TRUONG HOP TU KHUYET COT DOC

                  //// phu thuoc
                  if (rightEdge.mesh.userData.type !== undefined) {
                    if (!rightEdge.mesh.userData.leftDependent) {
                      rightEdge.mesh.userData.leftDependent = [];
                    }
                    rightEdge.mesh.userData.leftDependent.push(model.uuid);
                  } else if (
                    rightEdge.mesh.parent.userData.type !== undefined
                  ) {
                    if (!rightEdge.mesh.parent.userData.leftDependent) {
                      rightEdge.mesh.parent.userData.leftDependent = [];
                    }
                    rightEdge.mesh.parent.userData.leftDependent.push(
                      model.uuid
                    );
                  }
                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  ////
                } else if (leftEdge && rightEdge && typeHau) {
                  model.userData.type = moduleData.type;
                  model.userData.typeHau = typeHau;
                  this.setInfoModelChild(model);

                  const posLocal = clonedModule.group.worldToLocal(
                    intersection.clone()
                  );
                  model.position.y = posLocal.y;

                  const originalScale = model.scale.clone();

                  let sizeXTV;
                  const isMain = typeHau === TypeConnerModule.MAIN;
                  const leftWorldPos = new THREE.Vector3();
                  const rightWorldPos = new THREE.Vector3();
                  leftEdge.mesh.getWorldPosition(leftWorldPos);
                  rightEdge.mesh.getWorldPosition(rightWorldPos);

                  if (orientation === 'NORMAL') {
                    if (isMain) {
                      //Scale Z
                      const scaleZ = Math.abs(rightWorldPos.z - leftWorldPos.z);
                      model.position.z = leftPos.z - sizeLeft.z;
                      model.scale.z =
                        ((scaleZ - sizeLeft.z) * originalScale.z) /
                        originalSize.z;

                      //Scale X

                      const scaleX = sizeRight.x / originalSize.x;
                      model.scale.x = scaleX;
                      sizeXTV = sizeBottom?.x;
                    } else {
                      // scale Z
                      model.rotation.y = Math.PI / 2;
                      model.position.z = leftPos.z;
                      model.position.x = leftPos.x;

                      const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                      let sizeL = 0;
                      if (
                        leftEdge?.mesh.userData.type &&
                        rightEdge?.mesh.userData.type
                      ) {
                        model.position.x = leftPos.x - sizeLeft.x;
                        sizeL = sizeLeft.x;
                      }
                      if (
                        leftEdge?.mesh.userData.type &&
                        !rightEdge?.mesh.userData.type
                      ) {
                        model.position.x = leftPos.x - sizeLeft.x;
                        sizeL = sizeLeft.x * 2;
                      }
                      if (
                        !leftEdge?.mesh.userData.type &&
                        rightEdge?.mesh.userData.type
                      ) {
                        model.position.x = leftPos.x;
                        sizeL = 0;
                      }
                      if (
                        !leftEdge?.mesh.userData.type &&
                        !rightEdge?.mesh.userData.type
                      ) {
                        model.position.x = leftPos.x;
                        sizeL = sizeLeft.x;
                      }
                      model.scale.z =
                        ((scaleZ - sizeL) * originalScale.z) / originalSize.z;

                      //Scale X

                      const scaleX = sizeLeft.z / originalSize.x;
                      model.scale.x = scaleX;
                      sizeXTV = sizeBottom?.z;
                    }
                  }
                  if (orientation === 'ROTATED_270') {
                    if (!isMain) {
                      //Scale Z
                      model.rotation.y = Math.PI / 2;
                      const scaleZ = Math.abs(
                        leftEdge?.mesh.userData.position.z -
                          rightEdge?.mesh.userData.position.z
                      );
                      model.position.z = leftPos.z;
                      let sizeL = 0;
                      if (
                        leftEdge?.mesh.userData.type &&
                        rightEdge?.mesh.userData.type
                      ) {
                        model.position.x = leftPos.x - sizeLeft.z;
                        sizeL = sizeLeft.z;
                      }
                      if (
                        leftEdge?.mesh.userData.type &&
                        !rightEdge?.mesh.userData.type
                      ) {
                        model.position.x = leftPos.x - sizeLeft.z;
                        sizeL = sizeLeft.z * 2;
                      }
                      if (
                        !leftEdge?.mesh.userData.type &&
                        rightEdge?.mesh.userData.type
                      ) {
                        model.position.x = leftPos.x;
                        sizeL = 0;
                      }
                      if (
                        !leftEdge?.mesh.userData.type &&
                        !rightEdge?.mesh.userData.type
                      ) {
                        model.position.x = leftPos.x;
                        sizeL = sizeLeft.z;
                      }
                      model.scale.z =
                        ((scaleZ - sizeL) * originalScale.z) / originalSize.z;

                      //Scale X

                      const scaleX = sizeLeft.x / originalSize.x;
                      model.scale.x = scaleX;
                      sizeXTV = sizeBottom?.x;
                    } else {
                      // scale Z

                      model.position.z = leftPos.z - sizeLeft.x;
                      model.position.x = rightPos.x;

                      const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                      model.scale.z =
                        ((scaleZ - sizeLeft.x) * originalScale.z) /
                        originalSize.z;

                      //Scale X

                      const scaleX = sizeRight.z / originalSize.x;
                      model.scale.x = scaleX;
                      sizeXTV = sizeBottom?.z;
                    }
                  }
                }

                resultData = addModelToCabinet();
                ///XU LY CHO TRUONG HOP TU KHUYET COT DOC

                //// phu thuoc
                // if (!leftEdge.mesh.userData.rightDependent) {
                //   leftEdge.mesh.userData.rightDependent = [];
                // }
                // leftEdge.mesh.userData.rightDependent.push(model.uuid);
                if (leftEdge?.mesh.userData.type !== undefined) {
                  if (!leftEdge.mesh.userData.rightDependent) {
                    leftEdge.mesh.userData.rightDependent = [];
                  }
                  leftEdge.mesh.userData.rightDependent.push(model.uuid);
                } else if (leftEdge?.mesh.parent.userData.type !== undefined) {
                  if (!leftEdge.mesh.parent.userData.rightDependent) {
                    leftEdge.mesh.parent.userData.rightDependent = [];
                  }
                  leftEdge.mesh.parent.userData.rightDependent.push(model.uuid);
                }

                // if (!rightEdge.mesh.userData.leftDependent) {
                //   rightEdge.mesh.userData.leftDependent = [];
                // }
                // rightEdge.mesh.userData.leftDependent.push(model.uuid);
                if (rightEdge?.mesh.userData.type !== undefined) {
                  if (!rightEdge.mesh.userData.leftDependent) {
                    rightEdge.mesh.userData.leftDependent = [];
                  }
                  rightEdge.mesh.userData.leftDependent.push(model.uuid);
                } else if (rightEdge?.mesh.parent.userData.type !== undefined) {
                  if (!rightEdge.mesh.parent.userData.leftDependent) {
                    rightEdge.mesh.parent.userData.leftDependent = [];
                  }
                  rightEdge.mesh.parent.userData.leftDependent.push(model.uuid);
                }
                model.userData.leftEdgeUuid = leftEdge?.mesh.uuid;
                model.userData.rightEdgeUuid = rightEdge?.mesh.uuid;
                model.userData.topEdgeUuid = topEdge?.mesh.uuid;
                model.userData.bottomEdgeUuid = bottomEdge?.mesh.uuid;
                ////

                break;

              case InsideType.HPANEL_SHOE: //Xan ngang
                if (rightEdge && leftEdge) {
                  model.userData.type = InsideType.HPANEL_SHOE;
                  this.setInfoModelChild(model);

                  const leftWorldPos = new THREE.Vector3();
                  const rightWorldPos = new THREE.Vector3();
                  leftEdge.mesh.getWorldPosition(leftWorldPos);
                  rightEdge.mesh.getWorldPosition(rightWorldPos);

                  let sizeZ, sizeX;

                  const posLocal = clonedModule.group.worldToLocal(
                    intersection.clone()
                  );
                  model.position.y = posLocal.y;

                  let positionZ = 0;

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    positionZ = leftPos.z - sizeLeft.z;

                    sizeZ =
                      Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;

                    //Scale X
                    sizeX =
                      Number(
                        clonedModule.formularController.currentDimensions.depth
                      ) -
                      (backEdge.mesh.position.x + sizeBack.x);

                    console.log('sizeZ', sizeZ);
                    console.log('sizeX', sizeX);
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    positionZ = leftPos.z - sizeLeft.x;
                    sizeZ = Math.abs(
                      rightWorldPos.x - leftWorldPos.x - sizeLeft.x
                    );

                    //Scale X
                    sizeX =
                      Number(
                        clonedModule.formularController.currentDimensions.depth
                      ) -
                      (backEdge.mesh.position.x + sizeBack.z);
                  }

                  const cloneGroup = new THREE.Group();
                  const clone1 = model.clone();
                  clone1.name = 'HPANEL-SHOE-MAIN';
                  const clone2 = model.clone();
                  clone2.name = 'HPANEL-SHOE-SUB';

                  clone1.position.set(0, 0, 0);
                  clone2.position.set(0, 0, 0);

                  const sizeClone1 =
                    (2 * (sizeX - 0.04)) / Math.sqrt(2) - 0.047;
                  clone1.scale.x = sizeClone1 / originalSize.x;
                  clone1.scale.z = sizeZ / originalSize.z;

                  const sizeClone2 = 0.03;
                  clone2.scale.x = sizeClone2 / originalSize.x;
                  clone2.scale.z = sizeZ / originalSize.z;

                  clone2.position.y = clone1.position.y + originalSize.y;
                  clone2.position.x = clone1.position.x + sizeClone1;
                  clone2.rotation.z += THREE.MathUtils.degToRad(90);

                  cloneGroup.add(clone1);
                  cloneGroup.add(clone2);

                  model = cloneGroup;
                  model.userData.type = InsideType.HPANEL_SHOE;
                  this.setInfoModelChild(model);

                  model.position.x = Math.min(leftPos.x, rightPos.x);
                  model.position.z = positionZ;
                  model.position.y = posLocal.y;
                  model.rotation.z += THREE.MathUtils.degToRad(-45);

                  //// phu thuoc
                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.topEdgeUuid = topEdge.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;
                  ////

                  const cloneData = model.clone();
                  cloneData.rotation.set(0, 0, 0);
                  const sizeData = [];

                  cloneData.traverse(child => {
                    if (!child.isMesh) return;
                    // Lấy rotation (rad)
                    const rotation = {
                      x: child.rotation.x,
                      y: child.rotation.y,
                      z: child.rotation.z,
                    };

                    const position = {
                      x: child.position.x,
                      y: child.position.y,
                      z: child.position.z,
                    };

                    child.rotation.set(0, 0, 0);

                    // Lấy size (bounding box)
                    const box = new THREE.Box3().setFromObject(child);
                    const size = new THREE.Vector3();
                    box.getSize(size);

                    sizeData.push({
                      name: child.name || 'unnamed',
                      size: {
                        x: size.x,
                        y: size.y,
                        z: size.z,
                      },
                      rotation,
                      position,
                    });
                  });

                  this.disposeObject3D(cloneData);
                  model.userData.sizeData = sizeData;

                  const sizeModel = this.getSize(model);

                  // model.scale.z = sizeZ / originalSize.z;
                  // const scaleX = sizeHypotenuse / originalSize.x;
                  // model.scale.x = scaleX;

                  resultData = addModelToCabinet();

                  if (bottomEdge && topEdge) {
                    const bottomWorldPos = new THREE.Vector3();
                    const topWorldPos = new THREE.Vector3();
                    bottomEdge.mesh.getWorldPosition(bottomWorldPos);
                    topEdge.mesh.getWorldPosition(topWorldPos);

                    const posBottomLocal = clonedModule.group.worldToLocal(
                      bottomWorldPos.clone()
                    );

                    const posTopLocal = clonedModule.group.worldToLocal(
                      topWorldPos.clone()
                    );

                    const box = new THREE.Box3().setFromObject(model);
                    const posModel = model.getWorldPosition(
                      new THREE.Vector3()
                    );
                    const min = box.min.clone();
                    const deltaY = posModel.y - min.y;

                    model.position.y =
                      posBottomLocal.y + sizeBottom.y + deltaY + 0.001;

                    const clone3 = model.clone();
                    this.setInfoModelChild(clone3);

                    const hpanelSub = this.getMeshByName(
                      clone3,
                      'HPANEL-SHOE-SUB'
                    );
                    const box2 = new THREE.Box3().setFromObject(hpanelSub);
                    const max = box2.max.clone();
                    const posModel2 = clone3.getWorldPosition(
                      new THREE.Vector3()
                    );
                    const deltaY2 = posModel2.y - max.y;

                    addModelToCabinet(clone3);

                    clone3.position.y = posTopLocal.y - (0.255 - deltaY2);

                    const sizeY =
                      clone3.position.y - model.position.y - sizeModel.y;

                    const numMesh = Math.floor(sizeY / 0.255);

                    if (numMesh >= 1) {
                      const gap =
                        (sizeY - numMesh * sizeModel.y) / (numMesh + 1);
                      for (let i = 1; i < numMesh + 1; i++) {
                        const clone4 = model.clone();
                        this.setInfoModelChild(clone4);
                        clone4.position.y =
                          model.position.y + i * sizeModel.y + i * gap;
                        addModelToCabinet(clone4);
                      }
                    }
                  }
                }
                break;

              case InsideType.VPANEL: //Xan doc
                if (
                  clonedModule.cabinetType.toLowerCase().includes('tv') &&
                  bottomEdge.orientation === 2
                ) {
                  return resultData;
                }
                if (
                  bottomEdge &&
                  topEdge !== undefined &&
                  topEdge.orientation !== 2 &&
                  bottomEdge.orientation !== 2 &&
                  !typeHau
                ) {
                  model.userData.type = moduleData.type;
                  this.setInfoModelChild(model);

                  const localBackEdgePos = clonedModule.group.worldToLocal(
                    backEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const posLocal = clonedModule.group.worldToLocal(
                    bottomEdge?.mesh
                      .getWorldPosition(new THREE.Vector3())
                      .clone()
                  );
                  model.position.y = posLocal.y + sizeBottom.y;

                  // model.position.y =
                  //   bottomEdge?.mesh.userData.position.y + sizeBottom.y;

                  const localPoint = clonedModule.group.worldToLocal(
                    intersection.clone()
                  );

                  model.position.z = localPoint.z;
                  const scaleY1 = Math.abs(
                    bottomEdge?.mesh.userData.position.y -
                      topEdge?.mesh.userData.position.y
                  );

                  const originalScale1 = model.scale.clone();

                  sizeBack = this.getSize(backEdge?.mesh);

                  model.scale.y =
                    ((scaleY1 - sizeBottom.y) * originalScale1.y) /
                    originalSize.y;

                  let sizeXTV;

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    let sizeX =
                      Number(
                        clonedModule.formularController.currentDimensions.depth
                      ) -
                      (backEdge.mesh.position.x + sizeBack.x);
                    if (
                      Number(this.doorType) === 1 &&
                      (leftEdge.mesh.userData.type === undefined ||
                        rightEdge.mesh.userData.type === undefined)
                    ) {
                      sizeX = sizeX - 0.02;
                    }
                    if (!leftEdge || !rightEdge) {
                      model.position.x = localBackEdgePos.x + sizeBack.x;
                    }
                    if (
                      clonedModule.cabinetType
                        .toLowerCase()
                        .includes('curve') &&
                      clonedModule.formularController.currentOptions.caoKhungHoc
                    ) {
                      sizeX = sizeX - 0.017;
                    }
                    if (
                      topEdge.mesh.userData.type ||
                      bottomEdge.mesh.userData.type
                    ) {
                      sizeX =
                        sizeTop.x < sizeBottom.x ? sizeTop.x : sizeBottom.x;
                    }
                    const scaleX1 = sizeX / originalSize.x;

                    model.scale.x = scaleX1;

                    sizeXTV = sizeBottom.x;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    let sizeX =
                      Number(
                        clonedModule.formularController.currentDimensions.depth
                      ) -
                      (backEdge.mesh.position.x + sizeBack.z);
                    if (
                      Number(this.doorType) === 1 &&
                      (leftEdge.mesh.userData.type === undefined ||
                        rightEdge.mesh.userData.type === undefined)
                    ) {
                      sizeX = sizeX - 0.02;
                    }
                    if (!leftEdge || !rightEdge) {
                      model.position.x = localBackEdgePos.x + sizeBack.z;
                    }
                    if (
                      clonedModule.cabinetType
                        .toLowerCase()
                        .includes('curve') &&
                      clonedModule.formularController.currentOptions.caoKhungHoc
                    ) {
                      sizeX = sizeX - 0.017;
                    }
                    if (
                      topEdge.mesh.userData.type ||
                      bottomEdge.mesh.userData.type
                    ) {
                      sizeX =
                        sizeTop.z < sizeBottom.z ? sizeTop.z : sizeBottom.z;
                    }
                    const scaleX1 = sizeX / originalSize.x;

                    model.scale.x = scaleX1;
                    sizeXTV = sizeBottom.z;
                  }
                  if (
                    clonedModule.cabinetType.toLowerCase().includes('tv') ||
                    clonedModule.cabinetType
                      .toLowerCase()
                      .includes('shoe_lower_bench')
                  ) {
                    if (
                      (clonedModule.cabinetType
                        .toLowerCase()
                        .includes('upper') &&
                        bottomEdge.mesh.userData.type === undefined) ||
                      clonedModule.cabinetType
                        .toLowerCase()
                        .includes('shoe_lower_bench')
                    ) {
                      sizeXTV -= thicknessBase;
                    }
                    model.scale.x = sizeXTV / originalSize.x;
                  }
                }

                const xuongTruoc1 = this.getMeshByName(
                  clonedModule.group,
                  'XUONG-TRUOC-1'
                );
                const xuongTruoc2 = this.getMeshByName(
                  clonedModule.group,
                  'XUONG-TRUOC-2'
                );
                let xuongSau;
                xuongSau = this.getMeshByName(clonedModule.group, 'XUONG-SAU');

                if (!xuongSau) {
                  xuongSau = this.getMeshByName(
                    clonedModule.group,
                    'XUONG-SAU-2'
                  );
                }

                if (
                  bottomEdge &&
                  (topEdge === undefined || topEdge.orientation === 2) &&
                  !typeHau
                ) {
                  model.userData.type = moduleData.type;

                  const sizeXuongTruoc1 = this.getSize(xuongTruoc1);
                  const sizeXuongTruoc2 = this.getSize(xuongTruoc2);
                  const sizeXuongSau = this.getSize(xuongSau);

                  const positionXuongTruoc1 = xuongTruoc1.getWorldPosition(
                    new THREE.Vector3()
                  );
                  const positionXuongTruoc2 = xuongTruoc2.getWorldPosition(
                    new THREE.Vector3()
                  );
                  const positionXuongSau = xuongSau?.getWorldPosition(
                    new THREE.Vector3()
                  );
                  const tsst = bottomEdge.mesh.getWorldPosition(
                    new THREE.Vector3()
                  );
                  const sizeXModel1 =
                    positionXuongTruoc1.x +
                    sizeXuongTruoc1.x -
                    backEdge.mesh.getWorldPosition(new THREE.Vector3()).x -
                    sizeBack.x;
                  const sizeYModel1 =
                    positionXuongTruoc1.y -
                    bottomEdge.mesh.getWorldPosition(new THREE.Vector3()).y -
                    sizeBottom.y;

                  let sizeXModel2 =
                    xuongTruoc2.position.x -
                    xuongSau.position.x -
                    sizeXuongSau.x -
                    backEdge.mesh.getWorldPosition(new THREE.Vector3()).x;
                  const sizeYModel2 = sizeXuongTruoc2.y;

                  if (backEdge.mesh.name === 'HAU-2') {
                    sizeXModel2 += sizeXuongSau.x / 2;
                  }

                  const cloneGroup = new THREE.Group();
                  const clone1 = model.clone();
                  const clone2 = model.clone();

                  clone1.scale.x =
                    (sizeXModel1 * model.scale.x) / originalSize.x;
                  clone1.scale.y =
                    (sizeYModel1 * model.scale.y) / originalSize.y;

                  clone2.scale.x =
                    (sizeXModel2 * model.scale.x) / originalSize.x;
                  clone2.scale.y =
                    (sizeYModel2 * model.scale.y) / originalSize.y;

                  clone1.position.set(0, 0, 0);
                  clone2.position.set(0, 0, 0);

                  const sizeClone1 = this.getSize(clone1);

                  clone2.position.y = clone1.position.y + sizeClone1.y;
                  clone2.position.x = clone2.position.x + sizeXuongSau.x;

                  if (backEdge.mesh.name === 'HAU-2') {
                    clone2.position.x = clone1.position.x;
                  }

                  cloneGroup.add(clone1);
                  cloneGroup.add(clone2);

                  model = cloneGroup;
                  model.name = 'MAIN-HPANEL';
                  this.setInfoModelChild(model);

                  model.userData.type = InsideType.VPANEL2; //Xà dọc 2 mảnh

                  model.position.z = intersection.z;
                  const localxuongSauPos = clonedModule.group.worldToLocal(
                    xuongSau.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  model.position.x = localxuongSauPos.x;
                  if (backEdge.mesh.name === 'HAU-2') {
                    const localbackPos = clonedModule.group.worldToLocal(
                      backEdge.mesh
                        .getWorldPosition(new THREE.Vector3())
                        .clone()
                    );
                    model.position.x = localbackPos.x + sizeBack.x;
                  }
                  model.position.y =
                    bottomEdge.mesh.getWorldPosition(new THREE.Vector3()).y +
                    sizeBottom.y;
                }

                if (bottomEdge && topEdge && typeHau) {
                  model.userData.type = moduleData.type;
                  model.userData.typeHau = typeHau;
                  this.setInfoModelChild(model);

                  const posLocal = clonedModule.group.worldToLocal(
                    bottomEdge?.mesh
                      .getWorldPosition(new THREE.Vector3())
                      .clone()
                  );
                  model.position.y = posLocal.y + sizeBottom.y;

                  const localPoint = clonedModule.group.worldToLocal(
                    intersection.clone()
                  );

                  const originalScale = model.scale.clone();

                  const scaleY1 = Math.abs(
                    bottomEdge?.mesh.userData.position.y -
                      topEdge?.mesh.userData.position.y
                  );

                  model.scale.y =
                    ((scaleY1 - sizeBottom.y) * originalScale.y) /
                    originalSize.y;

                  let sizeXTV;
                  const isMain = typeHau === TypeConnerModule.MAIN;
                  const leftWorldPos = new THREE.Vector3();
                  const rightWorldPos = new THREE.Vector3();
                  leftEdge.mesh.getWorldPosition(leftWorldPos);
                  rightEdge.mesh.getWorldPosition(rightWorldPos);
                  if (orientation === 'NORMAL') {
                    if (isMain) {
                      //Scale Z
                      model.position.x = localPoint.x;
                      model.position.z = localPoint.z;
                      const scaleX = sizeRight.x / originalSize.x;
                      model.scale.x = scaleX;

                      const sizeGLR =
                        Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;
                      model.userData.sizeGLR = sizeGLR;
                    } else {
                      // scale Z
                      model.rotation.y = Math.PI / 2;
                      model.position.x = localPoint.x;
                      model.position.z = leftPos.z;
                      const scaleX = sizeRight.z / originalSize.x;
                      model.scale.x = scaleX;

                      const sizeGLR =
                        Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;
                      model.userData.sizeGLR = sizeGLR;
                    }
                  }
                  if (orientation === 'ROTATED_270') {
                    if (!isMain) {
                      //Scale Z
                      model.rotation.y = Math.PI / 2;
                      model.position.x = localPoint.x;
                      model.position.z = leftPos.z;
                      const scaleX = sizeRight.x / originalSize.x;
                      model.scale.x = scaleX;

                      const sizeGLR =
                        Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;
                      model.userData.sizeGLR = sizeGLR;
                    } else {
                      // scale Z
                      model.position.x = leftPos.x;
                      model.position.z = localPoint.z;
                      const scaleX = sizeRight.z / originalSize.x;
                      model.scale.x = scaleX;

                      const sizeGLR =
                        Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;
                      model.userData.sizeGLR = sizeGLR;
                    }
                  }
                }

                resultData = addModelToCabinet();

                //// phu thuoc
                let topEdgeId = null;
                // if (!topEdge.mesh.userData.bottomDependent) {
                //   topEdge.mesh.userData.bottomDependent = [];
                // }
                // topEdge.mesh.userData.bottomDependent.push(model.uuid);
                // topEdgeId = topEdge.mesh.uuid;
                if (topEdge?.mesh?.userData.type !== undefined) {
                  if (!topEdge.mesh.userData.bottomDependent) {
                    topEdge.mesh.userData.bottomDependent = [];
                  }
                  topEdge.mesh.userData.bottomDependent.push(model.uuid);
                  topEdgeId = topEdge.mesh.uuid;
                } else if (xuongTruoc1) {
                  if (!xuongTruoc1.userData.bottomDependent) {
                    xuongTruoc1.userData.bottomDependent = [];
                  }
                  xuongTruoc1.userData.bottomDependent.push(model.uuid);
                  topEdgeId = xuongTruoc1.uuid;
                }

                if (bottomEdge?.mesh?.userData.type !== undefined) {
                  if (!bottomEdge.mesh.userData.topDependent) {
                    bottomEdge.mesh.userData.topDependent = [];
                  }
                  bottomEdge.mesh.userData.topDependent.push(model.uuid);
                }
                model.userData.topEdgeUuid = topEdgeId || topEdge?.mesh.uuid;
                model.userData.bottomEdgeUuid = bottomEdge?.mesh?.uuid;
                model.userData.rightEdgeUuid = rightEdge?.mesh?.uuid;
                model.userData.leftEdgeUuid = leftEdge?.mesh?.uuid;
                ////
                break;

              case InsideType.DRAWER: //Hoc keo
                if (rightEdge && leftEdge && !typeHau) {
                  if (bottomEdge.mesh.userData.type === InsideType.DRAWER) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: 'Không thể thêm hộc kéo',
                      description:
                        'Hộc kéo đã tồn tại. Vui lòng nhân bản hộc hiện có thay vì thêm mới.',
                      placement: 'bottomRight',
                      closable: true,
                      duration: 4,
                    });
                    return;
                  }
                  model.userData.type = moduleData.type;
                  model.userData.indexDuplicate = 0;

                  this.setInfoModelChild(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const bottomPos = clonedModule.group.worldToLocal(
                    bottomEdge?.mesh
                      .getWorldPosition(new THREE.Vector3())
                      .clone()
                  );
                  const topPos = clonedModule.group.worldToLocal(
                    topEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const sizeYDrawer = topPos.y - bottomPos.y - sizeBottom.y;

                  if (
                    bottomEdge.mesh.userData.type ===
                    InsideType.ACCESSORY_WASHER
                  ) {
                    model.userData.topGap = 0.01;
                    model.userData.bottomGap = 0.01;
                  } else if (
                    clonedModule.cabinetType.toLowerCase().includes('tv')
                  ) {
                    model.userData.bottomGap = 0.02;
                    model.userData.topGap = 0.03;
                  } else {
                    model.userData.topGap = DRAWER_DEFAULT_TOP_GAP;
                    model.userData.bottomGap = DRAWER_DEFAULT_BOTTOM_GAP;
                  }
                  let deltaY = 0;
                  if (bottomEdge.mesh.userData?.topGap) {
                    deltaY = Number(bottomEdge.mesh.userData.topGap);
                  }
                  // model.position.x = Math.max(
                  //   rightEdge?.mesh.userData.position.x,
                  //   leftEdge?.mesh.userData.position.x
                  // );

                  let sizeY = 0;
                  if (
                    !bottomEdge.mesh.userData.type &&
                    !topEdge.mesh.userData.type &&
                    topEdge.mesh.userData.type !== InsideType.HPANEL
                  ) {
                    const sizeDoor =
                      (topPos.y -
                        bottomPos.y +
                        doorInsetOnPanel45 -
                        hinge45Clearance) /
                      2;
                    sizeY =
                      sizeDoor -
                      sizeBottom.y -
                      model.userData.bottomGap -
                      model.userData.topGap -
                      doorInsetOnPanel;
                  }

                  if (topEdge.mesh.userData.type === InsideType.HPANEL) {
                    sizeY =
                      topPos.y -
                      bottomPos.y -
                      sizeBottom.y -
                      model.userData.bottomGap -
                      model.userData.topGap -
                      sizeBarce;
                  }

                  if (
                    bottomEdge.mesh.userData.type === InsideType.DRAWER &&
                    !topEdge.mesh.userData.type
                  ) {
                    const sizeDoor =
                      topPos.y -
                      bottomPos.y -
                      sizeBottom.y -
                      bottomEdge.mesh.userData.topGap -
                      sizeBarce;
                    sizeY =
                      sizeDoor -
                      model.userData.bottomGap -
                      model.userData.topGap;
                  }

                  model.position.z =
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).z -
                    sizeLeft.z;
                  let gapBarce = 0;
                  if (bottomEdge.mesh.userData.type === InsideType.DRAWER) {
                    gapBarce = this.braceHeight;
                  }
                  model.position.y =
                    bottomPos.y +
                    sizeBottom.y +
                    model.userData.bottomGap +
                    deltaY +
                    gapBarce;

                  const originalSize3 = this.getSize(model);
                  let sizeZ3 = 0;
                  let sizeX = 0;
                  let sizeXTV;
                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    sizeZ3 =
                      leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).z -
                      rightEdge?.mesh.getWorldPosition(new THREE.Vector3()).z -
                      sizeLeft.z;

                    model.position.z = leftPos.z - sizeLeft.z;

                    sizeX =
                      Number(
                        clonedModule.formularController.currentDimensions.depth
                      ) -
                      (backEdge.mesh.position.x + sizeBack.x);

                    if (Number(this.doorType) === 1) {
                      sizeX = sizeX - 0.02;
                    }

                    if (
                      clonedModule.cabinetType.toLowerCase().includes('curve')
                    ) {
                      sizeX = sizeX - 0.017;
                    }

                    if (
                      leftEdge.mesh.userData.type ||
                      rightEdge.mesh.userData.type
                    ) {
                      sizeX =
                        sizeLeft.x < sizeRight.x ? sizeLeft.x : sizeRight.x;
                    }

                    // Tru hao hoc tu
                    sizeX = sizeX - 0.02;
                    model.position.x = model.position.x + 0.02;
                    sizeXTV = sizeBottom.x;
                    // Tru hao hoc tu
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    const leftWorldPos = new THREE.Vector3();
                    const rightWorldPos = new THREE.Vector3();
                    leftEdge.mesh.getWorldPosition(leftWorldPos);
                    rightEdge.mesh.getWorldPosition(rightWorldPos);

                    sizeZ3 =
                      Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;

                    model.position.z = leftPos.z - sizeLeft.x;

                    sizeX =
                      Number(
                        clonedModule.formularController.currentDimensions.depth
                      ) -
                      (backEdge.mesh.position.x + sizeBack.z);

                    if (Number(this.doorType) === 1) {
                      sizeX = sizeX - 0.02;
                    }

                    if (
                      clonedModule.cabinetType.toLowerCase().includes('curve')
                    ) {
                      sizeX = sizeX - 0.017;
                    }
                    if (
                      leftEdge.mesh.userData.type ||
                      rightEdge.mesh.userData.type
                    ) {
                      sizeX =
                        sizeLeft.z < sizeRight.z ? sizeLeft.z : sizeRight.z;
                    }
                    // Tru hao hoc tu
                    sizeX = sizeX - 0.02;
                    model.position.x = model.position.x + 0.02;
                    sizeXTV = sizeBottom.z;
                    // Tru hao hoc tu
                  }

                  if (
                    clonedModule.cabinetType
                      .toLowerCase()
                      .includes('shoe_lower_bench')
                  ) {
                    sizeX = sizeXTV - thicknessBase - 0.02;
                  }

                  this.scaleCabinetShelfWidth(model, originalSize3, sizeZ3);
                  this.scaleCabinetShelfDepth(model, originalSize3, sizeX);

                  if (clonedModule.cabinetType.toLowerCase().includes('shoe')) {
                    sizeY =
                      topPos.y -
                      bottomPos.y -
                      sizeBottom.y -
                      model.userData.bottomGap -
                      model.userData.topGap;
                  }

                  if (clonedModule.cabinetType.toLowerCase().includes('tv')) {
                    if (bottomEdge.mesh.userData.type === InsideType.DRAWER) {
                      model.userData.indexDuplicate = 1;
                      sizeY =
                        topPos.y -
                        bottomPos.y -
                        sizeBottom.y -
                        model.userData.bottomGap -
                        2 * model.userData.topGap -
                        0.1;
                    } else {
                      model.userData.indexDuplicate = 0;
                      if (topPos.y - bottomPos.y - sizeBottom.y > 0.32) {
                        sizeY =
                          (topPos.y -
                            bottomPos.y -
                            sizeBottom.y -
                            2 * model.userData.bottomGap -
                            2 * model.userData.topGap -
                            0.1) /
                          2;
                      } else {
                        sizeY =
                          topPos.y -
                          bottomPos.y -
                          sizeBottom.y -
                          model.userData.bottomGap -
                          model.userData.topGap;
                      }
                    }
                  }

                  InsideCabinetHandler.scaleHeight(
                    model,
                    (sizeY > 0.3 ? 0.3 : sizeY) - 0.008
                  );

                  resultData = addModelToCabinet();
                  if (
                    sizeYDrawer -
                      model.userData.topGap -
                      model.userData.bottomGap <
                      originalSize3.y &&
                    topEdge.mesh.userData.type
                  ) {
                    InsideCabinetHandler.scaleHeight(
                      model,
                      sizeYDrawer -
                        model.userData.topGap -
                        model.userData.bottomGap
                    );
                  }

                  //// phu thuoc
                  if (leftEdge.mesh.userData.type !== undefined) {
                    if (!leftEdge.mesh.userData.rightDependent) {
                      leftEdge.mesh.userData.rightDependent = [];
                    }
                    leftEdge.mesh.userData.rightDependent.push(model.uuid);
                  }

                  if (rightEdge.mesh.userData.type !== undefined) {
                    if (!rightEdge.mesh.userData.leftDependent) {
                      rightEdge.mesh.userData.leftDependent = [];
                    }
                    rightEdge.mesh.userData.leftDependent.push(model.uuid);
                  }
                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.topEdgeUuid = topEdge.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;
                  ////
                }
                if (rightEdge && leftEdge && typeHau) {
                  if (bottomEdge.mesh.userData.type === InsideType.DRAWER) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: 'Không thể thêm hộc kéo',
                      description:
                        'Hộc kéo đã tồn tại. Vui lòng nhân bản hộc hiện có thay vì thêm mới.',
                      placement: 'bottomRight',
                      closable: true,
                      duration: 4,
                    });
                    return;
                  }
                  if (
                    leftEdge.mesh.name.includes('XAN') ||
                    rightEdge.mesh.name.includes('XAN')
                  ) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: 'Không thể thêm hộc kéo',
                      description: 'Không thể thêm hộc kéo tại vị trí này.',
                      placement: 'bottomRight',
                      closable: true,
                      duration: 4,
                    });
                    return;
                  }
                  model.userData.type = moduleData.type;
                  model.userData.typeHau = typeHau;
                  model.userData.indexDuplicate = 0;

                  this.setInfoModelChild(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const bottomPos = clonedModule.group.worldToLocal(
                    bottomEdge?.mesh
                      .getWorldPosition(new THREE.Vector3())
                      .clone()
                  );
                  const topPos = clonedModule.group.worldToLocal(
                    topEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const sizeYDrawer = topPos.y - bottomPos.y - sizeBottom.y;

                  if (
                    bottomEdge.mesh.userData.type ===
                    InsideType.ACCESSORY_WASHER
                  ) {
                    model.userData.topGap = 0.01;
                    model.userData.bottomGap = 0.01;
                  } else if (
                    clonedModule.cabinetType.toLowerCase().includes('tv')
                  ) {
                    model.userData.bottomGap = 0.02;
                    model.userData.topGap = 0.03;
                  } else {
                    model.userData.topGap = DRAWER_DEFAULT_TOP_GAP;
                    model.userData.bottomGap = DRAWER_DEFAULT_BOTTOM_GAP;
                  }
                  let deltaY = 0;
                  if (bottomEdge.mesh.userData?.topGap) {
                    deltaY = Number(bottomEdge.mesh.userData.topGap);
                  }
                  // model.position.x = Math.max(
                  //   rightEdge?.mesh.userData.position.x,
                  //   leftEdge?.mesh.userData.position.x
                  // );

                  let sizeY = 0;
                  if (
                    !bottomEdge.mesh.userData.type &&
                    !topEdge.mesh.userData.type &&
                    topEdge.mesh.userData.type !== InsideType.HPANEL
                  ) {
                    const sizeDoor =
                      (topPos.y -
                        bottomPos.y +
                        doorInsetOnPanel45 -
                        hinge45Clearance) /
                      2;
                    sizeY =
                      sizeDoor -
                      sizeBottom.y -
                      model.userData.bottomGap -
                      model.userData.topGap -
                      doorInsetOnPanel;
                  }

                  if (topEdge.mesh.userData.type === InsideType.HPANEL) {
                    sizeY =
                      topPos.y -
                      bottomPos.y -
                      sizeBottom.y -
                      model.userData.bottomGap -
                      model.userData.topGap -
                      sizeBarce;
                  }

                  if (
                    bottomEdge.mesh.userData.type === InsideType.DRAWER &&
                    !topEdge.mesh.userData.type
                  ) {
                    const sizeDoor =
                      topPos.y -
                      bottomPos.y -
                      sizeBottom.y -
                      bottomEdge.mesh.userData.topGap -
                      sizeBarce;
                    sizeY =
                      sizeDoor -
                      model.userData.bottomGap -
                      model.userData.topGap;
                  }

                  model.position.z =
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).z -
                    sizeLeft.z;
                  let gapBarce = 0;
                  if (bottomEdge.mesh.userData.type === InsideType.DRAWER) {
                    gapBarce = this.braceHeight;
                  }
                  model.position.y =
                    bottomPos.y +
                    sizeBottom.y +
                    model.userData.bottomGap +
                    deltaY +
                    gapBarce;

                  const originalSize3 = this.getSize(model);
                  let sizeZ3 = 0;
                  let sizeX = 0;
                  let sizeXTV;
                  const isMain = typeHau === TypeConnerModule.MAIN;
                  if (orientation === 'NORMAL') {
                    if (isMain) {
                      //Scale Z
                      sizeZ3 =
                        Math.abs(
                          rightEdge?.mesh.userData.position.z -
                            leftEdge?.mesh.userData.position.z
                        ) - sizeLeft.z;
                      model.position.z = leftPos.z - sizeLeft.z;
                      model.position.x = leftPos.x + 0.02;
                      sizeX = sizeRight.x - 0.02;
                    } else {
                      // scale Z
                      model.rotation.y = Math.PI / 2;

                      const leftWorldPos = new THREE.Vector3();
                      const rightWorldPos = new THREE.Vector3();
                      leftEdge.mesh.getWorldPosition(leftWorldPos);
                      rightEdge.mesh.getWorldPosition(rightWorldPos);

                      model.position.z = leftPos.z - 0.02;
                      model.position.x = leftPos.x;

                      sizeZ3 =
                        Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;

                      sizeX = sizeLeft.z - 0.02;
                    }
                  }
                  if (orientation === 'ROTATED_270') {
                    if (!isMain) {
                      //Scale Z
                      model.rotation.y = Math.PI / 2;
                      sizeZ3 =
                        Math.abs(
                          rightEdge?.mesh.userData.position.z -
                            leftEdge?.mesh.userData.position.z
                        ) - sizeLeft.z;
                      model.position.z = leftPos.z - 0.02;
                      model.position.x = leftPos.x;
                      sizeX = sizeLeft.x - 0.02;
                    } else {
                      // scale Z

                      const leftWorldPos = new THREE.Vector3();
                      const rightWorldPos = new THREE.Vector3();
                      leftEdge.mesh.getWorldPosition(leftWorldPos);
                      rightEdge.mesh.getWorldPosition(rightWorldPos);

                      model.position.z = leftPos.z - sizeLeft.x;
                      model.position.x = rightPos.x + 0.02;

                      sizeZ3 =
                        Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;

                      sizeX = sizeRight.z - 0.02;
                    }
                  }

                  if (
                    clonedModule.cabinetType
                      .toLowerCase()
                      .includes('shoe_lower_bench')
                  ) {
                    sizeX = sizeXTV - thicknessBase - 0.02;
                  }

                  this.scaleCabinetShelfWidth(model, originalSize3, sizeZ3);
                  this.scaleCabinetShelfDepth(model, originalSize3, sizeX);

                  if (clonedModule.cabinetType.toLowerCase().includes('shoe')) {
                    sizeY =
                      topPos.y -
                      bottomPos.y -
                      sizeBottom.y -
                      model.userData.bottomGap -
                      model.userData.topGap;
                  }

                  if (clonedModule.cabinetType.toLowerCase().includes('tv')) {
                    if (bottomEdge.mesh.userData.type === InsideType.DRAWER) {
                      model.userData.indexDuplicate = 1;
                      sizeY =
                        topPos.y -
                        bottomPos.y -
                        sizeBottom.y -
                        model.userData.bottomGap -
                        2 * model.userData.topGap -
                        0.1;
                    } else {
                      model.userData.indexDuplicate = 0;
                      if (topPos.y - bottomPos.y - sizeBottom.y > 0.32) {
                        sizeY =
                          (topPos.y -
                            bottomPos.y -
                            sizeBottom.y -
                            2 * model.userData.bottomGap -
                            2 * model.userData.topGap -
                            0.1) /
                          2;
                      } else {
                        sizeY =
                          topPos.y -
                          bottomPos.y -
                          sizeBottom.y -
                          model.userData.bottomGap -
                          model.userData.topGap;
                      }
                    }
                  }

                  InsideCabinetHandler.scaleHeight(
                    model,
                    (sizeY > 0.3 ? 0.3 : sizeY) - 0.008
                  );

                  resultData = addModelToCabinet();
                  if (
                    sizeYDrawer -
                      model.userData.topGap -
                      model.userData.bottomGap <
                      originalSize3.y &&
                    topEdge.mesh.userData.type
                  ) {
                    InsideCabinetHandler.scaleHeight(
                      model,
                      sizeYDrawer -
                        model.userData.topGap -
                        model.userData.bottomGap
                    );
                  }

                  //// phu thuoc
                  if (leftEdge.mesh.userData.type !== undefined) {
                    if (!leftEdge.mesh.userData.rightDependent) {
                      leftEdge.mesh.userData.rightDependent = [];
                    }
                    leftEdge.mesh.userData.rightDependent.push(model.uuid);
                  }

                  if (rightEdge.mesh.userData.type !== undefined) {
                    if (!rightEdge.mesh.userData.leftDependent) {
                      rightEdge.mesh.userData.leftDependent = [];
                    }
                    rightEdge.mesh.userData.leftDependent.push(model.uuid);
                  }
                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.topEdgeUuid = topEdge.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;
                  ////
                }
                break;

              case InsideType.U_DRAWER: //Hoc keo chu U
                if (rightEdge && leftEdge) {
                  model.userData.type = moduleData.type;
                  model.userData.child = moduleData.child;
                  this.setInfoModelChild(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const bottomPos = clonedModule.group.worldToLocal(
                    bottomEdge?.mesh
                      .getWorldPosition(new THREE.Vector3())
                      .clone()
                  );

                  model.position.y = bottomPos.y + sizeBottom.y;

                  const originalScale = model.scale.clone();

                  let sizeXDrawerChild = 0;

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const scaleZ = Math.abs(
                      rightEdge?.mesh.userData.position.z -
                        leftEdge?.mesh.userData.position.z
                    );
                    model.position.z = leftPos.z - sizeLeft.z;
                    InsideCabinetHandler.scaleWidth(model, scaleZ - sizeLeft.z);
                    const sizeX =
                      Number(
                        clonedModule.formularController.currentDimensions.depth
                      ) -
                      (backEdge.mesh.position.x + sizeBack.x) -
                      0.1;
                    sizeXDrawerChild = sizeX - 0.045 - 0.017;
                    InsideCabinetHandler.scaleDepth(model, sizeX);
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    const leftWorldPos = new THREE.Vector3();
                    const rightWorldPos = new THREE.Vector3();
                    leftEdge.mesh.getWorldPosition(leftWorldPos);
                    rightEdge.mesh.getWorldPosition(rightWorldPos);

                    model.position.z = leftPos.z - sizeLeft.x;

                    const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                    InsideCabinetHandler.scaleWidth(model, scaleZ - sizeLeft.x);

                    const sizeX =
                      Number(
                        clonedModule.formularController.currentDimensions.depth
                      ) -
                      (backEdge.mesh.position.z + sizeBack.z) -
                      0.1;
                    sizeXDrawerChild = sizeX - 0.045 - 0.017;
                    InsideCabinetHandler.scaleDepth(model, sizeX);
                  }
                  resultData = addModelToCabinet();
                  await this.settingUDrawer(
                    clonedModule,
                    sizeXDrawerChild,
                    defaultNumDrawer,
                    defaultBottomGap,
                    defaultTopGap,
                    originalSize,
                    model,
                    orientation
                  );
                  ///XU LY CHO TRUONG HOP TU KHUYET COT DOC

                  //// phu thuoc
                  if (leftEdge.mesh.userData.type !== undefined) {
                    if (!leftEdge.mesh.userData.rightDependent) {
                      leftEdge.mesh.userData.rightDependent = [];
                    }
                    leftEdge.mesh.userData.rightDependent.push(model.uuid);
                  } else if (leftEdge.mesh.parent.userData.type !== undefined) {
                    if (!leftEdge.mesh.parent.userData.rightDependent) {
                      leftEdge.mesh.parent.userData.rightDependent = [];
                    }
                    leftEdge.mesh.parent.userData.rightDependent.push(
                      model.uuid
                    );
                  }

                  if (rightEdge.mesh.userData.type !== undefined) {
                    if (!rightEdge.mesh.userData.leftDependent) {
                      rightEdge.mesh.userData.leftDependent = [];
                    }
                    rightEdge.mesh.userData.leftDependent.push(model.uuid);
                  } else if (
                    rightEdge.mesh.parent.userData.type !== undefined
                  ) {
                    if (!rightEdge.mesh.parent.userData.leftDependent) {
                      rightEdge.mesh.parent.userData.leftDependent = [];
                    }
                    rightEdge.mesh.parent.userData.leftDependent.push(
                      model.uuid
                    );
                  }
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  ////
                }
                break;

              case InsideType.ACCESSORY_GENERAL: //Phu kien kh scale
                if (rightEdge && leftEdge && !typeHau) {
                  this.removeAccessoryByType(
                    clonedModule,
                    InsideType.ACCESSORY_GENERAL
                  );
                  model.userData.type = moduleData.type;
                  this.setInfoModelChild(model);

                  if (!moduleData?.width) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: 'Không thể thêm phụ kiện',
                      description:
                        'Phụ kiện này đang thiếu dữ liệu (không có thông số width), nên không thể thêm vào. Vui lòng liên hệ admin để được hỗ trợ.',
                      placement: 'bottomRight',
                      closable: true,
                      duration: 4,
                    });
                    return;
                  }

                  // if (
                  //   !clonedModule.cabinetType.toLowerCase().includes('sink')
                  // ) {
                  //   const width = (moduleData?.width + 90) / 1000;
                  //   clonedModule.scaleModuleWithFormular({
                  //     width,
                  //   });
                  // }

                  model.scale.z = moduleData?.width / 1000 / originalSize.z;

                  const newSize = this.getSize(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const rightPos = clonedModule.group.worldToLocal(
                    rightEdge?.mesh
                      .getWorldPosition(new THREE.Vector3())
                      .clone()
                  );

                  const bottomPos = clonedModule.group.worldToLocal(
                    bottomEdge?.mesh
                      .getWorldPosition(new THREE.Vector3())
                      .clone()
                  );

                  model.position.y =
                    bottomPos.y + sizeBottom.y + (moduleData.bottomGap || 0);
                  model.userData.bottomGap = moduleData.bottomGap || 0;

                  const originalScale = model.scale.clone();

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const sizeZ = Math.abs(
                      rightPos.z - (leftPos.z - sizeLeft.z)
                    );
                    const gapLeft = (sizeZ - newSize.z) / 2;
                    model.position.z = leftPos.z - sizeLeft.z - gapLeft;
                    const posX = bottomPos.x + sizeBottom.x - newSize.x;
                    model.position.x = posX;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    const leftWorldPos = new THREE.Vector3();
                    const rightWorldPos = new THREE.Vector3();
                    leftEdge.mesh.getWorldPosition(leftWorldPos);
                    rightEdge.mesh.getWorldPosition(rightWorldPos);

                    model.position.z = leftPos.z - sizeLeft.x;
                  }
                  resultData = addModelToCabinet();
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  ///XU LY CHO TRUONG HOP TU KHUYET COT DOC
                }
                if (rightEdge && leftEdge && typeHau) {
                  this.removeAccessoryByType(
                    clonedModule,
                    InsideType.ACCESSORY_GENERAL
                  );
                  model.userData.type = moduleData.type;
                  this.setInfoModelChild(model);

                  if (!moduleData?.width) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: 'Không thể thêm phụ kiện',
                      description:
                        'Phụ kiện này đang thiếu dữ liệu (không có thông số width), nên không thể thêm vào. Vui lòng liên hệ admin để được hỗ trợ.',
                      placement: 'bottomRight',
                      closable: true,
                      duration: 4,
                    });
                    return;
                  }

                  const isMain = typeHau === TypeConnerModule.MAIN;

                  if (isMain) {
                    const sizeMd = clonedModule.formularController;
                    const width =
                      sizeMd.currentDimensions.depth +
                      sizeMd.currentOptions.DDTBia +
                      sizeMd.currentOptions.truHaoGoc +
                      (moduleData?.width + 90) / 1000;
                    clonedModule.scaleModuleWithFormular({}, { rongK2: width });
                  } else {
                    const sizeMd = clonedModule.formularController;
                    const width =
                      sizeMd.currentDimensions.depth +
                      sizeMd.currentOptions.DDTBia +
                      sizeMd.currentOptions.truHaoGoc +
                      (moduleData?.width + 90) / 1000;
                    clonedModule.scaleModuleWithFormular({}, { rongK1: width });
                  }

                  model.scale.z = moduleData?.width / 1000 / originalSize.z;

                  const newSize = this.getSize(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const rightPos = clonedModule.group.worldToLocal(
                    rightEdge?.mesh
                      .getWorldPosition(new THREE.Vector3())
                      .clone()
                  );

                  const bottomPos = clonedModule.group.worldToLocal(
                    bottomEdge?.mesh
                      .getWorldPosition(new THREE.Vector3())
                      .clone()
                  );

                  model.position.y =
                    bottomPos.y + sizeBottom.y + (moduleData.bottomGap || 0);
                  model.userData.bottomGap = moduleData.bottomGap || 0;

                  const originalScale = model.scale.clone();

                  if (orientation === 'NORMAL') {
                    if (isMain) {
                      //Scale Z
                      const leftWorldPos = new THREE.Vector3();
                      const rightWorldPos = new THREE.Vector3();
                      leftEdge.mesh.getWorldPosition(leftWorldPos);
                      rightEdge.mesh.getWorldPosition(rightWorldPos);

                      const delta =
                        Math.abs(rightWorldPos.z - leftWorldPos.z) - newSize.z;

                      model.position.z = leftPos.z - delta / 2;
                      model.position.x = leftPos.x + sizeLeft.x - newSize.x;
                    } else {
                      // scale Z
                      model.rotation.y = Math.PI / 2;

                      const leftWorldPos = new THREE.Vector3();
                      const rightWorldPos = new THREE.Vector3();
                      leftEdge.mesh.getWorldPosition(leftWorldPos);
                      rightEdge.mesh.getWorldPosition(rightWorldPos);

                      const delta =
                        Math.abs(rightWorldPos.x - leftWorldPos.x) - newSize.z;

                      model.position.z = leftPos.z;
                      model.position.x = leftPos.x - delta / 2;

                      //Scale X
                      model.position.z = leftPos.z - sizeLeft.z + newSize.x;
                    }
                  }
                  if (orientation === 'ROTATED_270') {
                    if (!isMain) {
                      model.rotation.y = Math.PI / 2;
                      //Scale Z
                      const scaleZ = Math.abs(
                        rightEdge?.mesh.userData.position.z -
                          leftEdge?.mesh.userData.position.z
                      );
                      model.position.z = leftPos.z;
                      model.position.x = leftPos.x;
                      model.scale.z =
                        ((scaleZ - sizeLeft.z) * originalScale.z) /
                        originalSize.z;

                      //Scale X

                      model.position.z = model.position.z - sizeLeft.x / 2;
                    } else {
                      // scale Z

                      const leftWorldPos = new THREE.Vector3();
                      const rightWorldPos = new THREE.Vector3();
                      leftEdge.mesh.getWorldPosition(leftWorldPos);
                      rightEdge.mesh.getWorldPosition(rightWorldPos);

                      const delta =
                        Math.abs(rightWorldPos.x - leftWorldPos.x) - newSize.z;

                      model.position.z = leftPos.z - sizeLeft.x;
                      model.position.x = leftPos.x + delta / 2;

                      //Scale X
                      // model.position.x = model.position.x + sizeLeft.z / 2;
                    }
                  }
                  resultData = addModelToCabinet();
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  ///XU LY CHO TRUONG HOP TU KHUYET COT DOC
                }
                break;

              case InsideType.ACCESSORY_FRIDGE: //Phu kien kh scale
                if (rightEdge && leftEdge) {
                  model.userData.type = moduleData.type;
                  this.setInfoModelChild(model);
                  this.removeAccessoryByType(clonedModule, moduleData.type);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const backPos = clonedModule.group.worldToLocal(
                    backEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  model.position.y = leftPos.y;
                  model.userData.bottomGap = moduleData.bottomGap || 0;

                  const originalScale = model.scale.clone();
                  clonedModule.scaleModuleWithFormular({
                    width: Number((originalSize.z + 0.1).toFixed(3)),
                    height: Number((originalSize.y + 0.05).toFixed(3)),
                  });

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const sizeZ = Math.abs(
                      rightEdge?.mesh.userData.position.z -
                        (leftEdge?.mesh.userData.position.z - sizeLeft.z)
                    );
                    const gapLeft = (sizeZ - originalSize.z) / 2;
                    model.position.z = leftPos.z - sizeLeft.z - gapLeft;
                    const posX = originalSize.x + leftPos.x - sizeLeft.x;
                    model.position.x = posX;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    const leftWorldPos = new THREE.Vector3();
                    const rightWorldPos = new THREE.Vector3();
                    leftEdge.mesh.getWorldPosition(leftWorldPos);
                    rightEdge.mesh.getWorldPosition(rightWorldPos);

                    model.position.z = leftPos.z - sizeLeft.x;
                  }
                  resultData = addModelToCabinet();
                  ///XU LY CHO TRUONG HOP TU KHUYET COT DOC

                  //// phu thuoc
                  if (leftEdge.mesh.userData.type !== undefined) {
                    if (!leftEdge.mesh.userData.rightDependent) {
                      leftEdge.mesh.userData.rightDependent = [];
                    }
                    leftEdge.mesh.userData.rightDependent.push(model.uuid);
                  } else if (leftEdge.mesh.parent.userData.type !== undefined) {
                    if (!leftEdge.mesh.parent.userData.rightDependent) {
                      leftEdge.mesh.parent.userData.rightDependent = [];
                    }
                    leftEdge.mesh.parent.userData.rightDependent.push(
                      model.uuid
                    );
                  }

                  if (rightEdge.mesh.userData.type !== undefined) {
                    if (!rightEdge.mesh.userData.leftDependent) {
                      rightEdge.mesh.userData.leftDependent = [];
                    }
                    rightEdge.mesh.userData.leftDependent.push(model.uuid);
                  } else if (
                    rightEdge.mesh.parent.userData.type !== undefined
                  ) {
                    if (!rightEdge.mesh.parent.userData.leftDependent) {
                      rightEdge.mesh.parent.userData.leftDependent = [];
                    }
                    rightEdge.mesh.parent.userData.leftDependent.push(
                      model.uuid
                    );
                  }
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  ////
                }
                break;

              case InsideType.ACCESSORY_WASHER: //Phu kien kh scale
                if (rightEdge && leftEdge) {
                  model.userData.type = moduleData.type;
                  this.setInfoModelChild(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  model.position.y = leftPos.y;
                  model.userData.bottomGap = moduleData.bottomGap || 0;

                  const originalScale = model.scale.clone();

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const sizeZ = Math.abs(
                      rightEdge?.mesh.userData.position.z -
                        (leftEdge?.mesh.userData.position.z - sizeLeft.z)
                    );
                    const gapLeft = (sizeZ - originalSize.z) / 2;
                    model.position.z = leftPos.z - sizeLeft.z - gapLeft;
                    const posX = leftPos.x;
                    model.position.x = posX;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    const leftWorldPos = new THREE.Vector3();
                    const rightWorldPos = new THREE.Vector3();
                    leftEdge.mesh.getWorldPosition(leftWorldPos);
                    rightEdge.mesh.getWorldPosition(rightWorldPos);

                    model.position.z = leftPos.z - sizeLeft.x;
                  }
                  resultData = addModelToCabinet();
                  ///XU LY CHO TRUONG HOP TU KHUYET COT DOC
                }
                break;

              case InsideType.ACCESSORY_SINK: //Phu kien  bon rua
                if (
                  rightEdge &&
                  leftEdge &&
                  backEdge.mesh.name.toLowerCase().includes('hau') &&
                  !typeHau
                ) {
                  model.userData.type = moduleData.type;
                  this.setInfoModelChild(model);
                  this.removeAccessoryByType(
                    clonedModule,
                    InsideType.ACCESSORY_SINK
                  );

                  this.removeAccessoryByType(
                    clonedModule,
                    InsideType.ACCESSORY_FAUCET
                  );

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const backPos = clonedModule.group.worldToLocal(
                    backEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const leftWorldPos = new THREE.Vector3();
                  const rightWorldPos = new THREE.Vector3();
                  leftEdge.mesh.getWorldPosition(leftWorldPos);
                  rightEdge.mesh.getWorldPosition(rightWorldPos);

                  const xuongTruoc1 = this.getMeshByName(
                    clonedModule.group,
                    'XUONG-TRUOC-1'
                  );

                  if (!xuongTruoc1) break;

                  let sizeZSink;

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    sizeZSink =
                      Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    sizeZSink = Math.abs(
                      rightWorldPos.x - leftWorldPos.x - sizeLeft.x
                    );
                  }

                  if (sizeZSink < moduleData.width / 1000 + 0.04) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: 'Không thể thêm phụ kiện',
                      description: `Phụ kiện này chỉ phù hợp với khung tủ có kích thước lớn hơn ${
                        moduleData.width + 40
                      } mm, hãy điều chỉnh kích thước khung tủ trước khi thêm phụ kiện.`,
                      placement: 'bottomRight',
                      closable: true,
                      duration: 4,
                    });
                    break;
                  }

                  const bonRua = this.getMeshByName(model, 'BON-RUA');

                  const sizeBonRua = this.getSize(bonRua);

                  const positionXuongTruoc1 = xuongTruoc1.getWorldPosition(
                    new THREE.Vector3()
                  );

                  const sizeXuongTruoc1 = this.getSize(xuongTruoc1);

                  const xuongTruoc1Pos = clonedModule.group.worldToLocal(
                    xuongTruoc1?.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const tamHauPos = clonedModule.group.worldToLocal(
                    backEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  model.position.y =
                    positionXuongTruoc1.y +
                    sizeXuongTruoc1.y -
                    sizeBonRua.y +
                    0.022;

                  model.scale.z = moduleData.width / 1000 / originalSize.z;
                  model.userData.sizeWidth = moduleData.width / 1000;

                  const sizeZ = Math.abs(
                    rightEdge?.mesh.userData.position.z -
                      (leftEdge?.mesh.userData.position.z - sizeLeft.z)
                  );
                  const newSize = this.getSize(model);
                  const gapLeft = (sizeZ - newSize.z) / 2;
                  model.position.z = leftPos.z - sizeLeft.z - gapLeft;
                  const gapBottom =
                    xuongTruoc1Pos.x +
                    sizeXuongTruoc1.x -
                    tamHauPos.x -
                    originalSize.x;
                  const posX = gapBottom / 2;
                  model.position.x = backPos.x + posX + sizeBack.x;
                  resultData = addModelToCabinet();
                  //// phu thuoc
                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  ////
                } else if (typeHau) {
                  model.userData.type = moduleData.type;
                  model.userData.typeHau = typeHau;
                  this.setInfoModelChild(model);
                  this.removeAccessoryByType(
                    clonedModule,
                    InsideType.ACCESSORY_SINK
                  );

                  let frontEdgeMesh, backEdgeMesh, leftEdgeMesh, rightEdgeMesh;

                  const isMain = typeHau === TypeConnerModule.MAIN;

                  clonedModule.group.traverse(mesh => {
                    if (!mesh.isMesh) return;
                    if (isMain) {
                      if (mesh.name.includes('MF')) frontEdgeMesh = mesh;
                      if (mesh.name.includes('MB')) backEdgeMesh = mesh;
                      if (mesh.name.includes('ML')) leftEdgeMesh = mesh;
                      if (mesh.name.includes('MR')) rightEdgeMesh = mesh;
                    } else {
                      if (mesh.name.includes('SF')) frontEdgeMesh = mesh;
                      if (mesh.name.includes('SB')) backEdgeMesh = mesh;
                      if (mesh.name.includes('SL')) leftEdgeMesh = mesh;
                      if (mesh.name.includes('SR')) rightEdgeMesh = mesh;
                    }
                  });

                  if (
                    !frontEdgeMesh ||
                    !backEdgeMesh ||
                    !leftEdgeMesh ||
                    !rightEdgeMesh
                  )
                    break;

                  const frontPos = clonedModule.group.worldToLocal(
                    frontEdgeMesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const backPos = clonedModule.group.worldToLocal(
                    backEdgeMesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdgeMesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const rightPos = clonedModule.group.worldToLocal(
                    rightEdgeMesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const sizeLeft = this.getSize(leftEdgeMesh);
                  const sizeBack = this.getSize(backEdgeMesh);
                  const sizeFront = this.getSize(frontEdgeMesh);
                  const sizeRight = this.getSize(rightEdgeMesh);

                  const bonRua = this.getMeshByName(model, 'BON-RUA');
                  const sizeBonRua = this.getSize(bonRua);

                  if (isMain) {
                    // Tính Y
                    model.position.y =
                      frontPos.y + sizeFront.y - sizeBonRua.y + 0.022;

                    // Tính Z (giữa left và right)
                    const sizeZ = Math.abs(
                      rightPos.z - (leftPos.z - sizeLeft.z)
                    );
                    const gapLeft = (sizeZ - originalSize.z) / 2;
                    model.position.z = leftPos.z - sizeLeft.z - gapLeft;

                    // Tính X (giữa front và back)
                    const gapBottom =
                      frontPos.x - backPos.x - sizeBack.x - originalSize.x;
                    const posX = gapBottom / 2;
                    model.position.x = backPos.x + posX + sizeBack.x;
                  } else {
                    model.rotation.y = Math.PI / 2;
                    // Tính Y
                    model.position.y =
                      frontPos.y + sizeFront.y - sizeBonRua.y + 0.022;

                    // Tính Z (giữa left và right)
                    const sizeZ = Math.abs(
                      frontPos.z - (backPos.z - sizeBack.z)
                    );
                    const gapBottom = (sizeZ - originalSize.x) / 2;
                    model.position.z = backPos.z - gapBottom - sizeBack.z;

                    // Tính X (giữa front và back)
                    const sizeX = Math.abs(
                      rightPos.x - (leftPos.x - sizeLeft.x)
                    );
                    const gapLeft = (sizeX - originalSize.z) / 2;
                    model.position.x = leftPos.x - gapLeft;
                  }

                  resultData = addModelToCabinet();
                }
                model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                break;

              case InsideType.ACCESSORY_FAUCET: //Phu kien  bon rua
                const posVoiMesh = this.getMeshByName(
                  clonedModule.group,
                  'POS-VOI'
                );
                this.removeAccessoryByType(
                  clonedModule,
                  InsideType.ACCESSORY_FAUCET
                );
                if (posVoiMesh) {
                  model.userData.type = moduleData.type;
                  this.setInfoModelChild(model);
                  const posVoi = clonedModule.group.worldToLocal(
                    posVoiMesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  model.position.y = posVoi.y;
                  model.position.z = posVoi.z;
                  model.position.x = posVoi.x;
                  if (typeHau) {
                    const rotation = posVoiMesh.parent.rotation.clone();
                    model.rotation.copy(rotation);
                  }
                  resultData = addModelToCabinet();
                }

                break;

              case InsideType.ACCESSORY_COOKER: //Phu kien  bon rua
                if (rightEdge && leftEdge && !typeHau) {
                  model.userData.type = moduleData.type;
                  this.setInfoModelChild(model);

                  this.removeAccessoryByType(
                    clonedModule,
                    InsideType.ACCESSORY_COOKER
                  );

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const backPos = clonedModule.group.worldToLocal(
                    backEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const xuongTruoc1 = this.getMeshByName(
                    clonedModule.group,
                    'XUONG-TRUOC-1'
                  );

                  if (!xuongTruoc1) break;

                  const bonRua = this.getMeshByName(model, 'BEP-TU');

                  const sizeBonRua = this.getSize(bonRua);

                  const positionXuongTruoc1 = xuongTruoc1.getWorldPosition(
                    new THREE.Vector3()
                  );

                  const sizeXuongTruoc1 = this.getSize(xuongTruoc1);

                  const xuongTruoc1Pos = clonedModule.group.worldToLocal(
                    xuongTruoc1?.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const tamHauPos = clonedModule.group.worldToLocal(
                    backEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  model.position.y =
                    xuongTruoc1Pos.y + sizeXuongTruoc1.y - sizeBonRua.y + 0.022;

                  const sizeZ = Math.abs(
                    rightEdge?.mesh.userData.position.z -
                      (leftEdge?.mesh.userData.position.z - sizeLeft.z)
                  );
                  const gapLeft = (sizeZ - originalSize.z) / 2;
                  model.position.z = leftPos.z - sizeLeft.z - gapLeft;
                  const gapBottom =
                    xuongTruoc1Pos.x -
                    tamHauPos.x -
                    sizeBack.x -
                    originalSize.x;
                  const posX = gapBottom / 2;
                  model.position.x = backPos.x + posX + sizeBack.x;
                  resultData = addModelToCabinet();
                  ///XU LY CHO TRUONG HOP TU KHUYET COT DOC
                } else if (typeHau) {
                  model.userData.type = moduleData.type;
                  model.userData.typeHau = typeHau;
                  this.setInfoModelChild(model);
                  this.removeAccessoryByType(
                    clonedModule,
                    InsideType.ACCESSORY_COOKER
                  );

                  let frontEdgeMesh, backEdgeMesh, leftEdgeMesh, rightEdgeMesh;

                  const isMain = typeHau === 'MAIN';

                  clonedModule.group.traverse(mesh => {
                    if (!mesh.isMesh) return;
                    if (isMain) {
                      if (mesh.name.includes('MF')) frontEdgeMesh = mesh;
                      if (mesh.name.includes('MB')) backEdgeMesh = mesh;
                      if (mesh.name.includes('ML')) leftEdgeMesh = mesh;
                      if (mesh.name.includes('MR')) rightEdgeMesh = mesh;
                    } else {
                      if (mesh.name.includes('SF')) frontEdgeMesh = mesh;
                      if (mesh.name.includes('SB')) backEdgeMesh = mesh;
                      if (mesh.name.includes('SL')) leftEdgeMesh = mesh;
                      if (mesh.name.includes('SR')) rightEdgeMesh = mesh;
                    }
                  });

                  if (
                    !frontEdgeMesh ||
                    !backEdgeMesh ||
                    !leftEdgeMesh ||
                    !rightEdgeMesh
                  )
                    break;

                  const frontPos = clonedModule.group.worldToLocal(
                    frontEdgeMesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const backPos = clonedModule.group.worldToLocal(
                    backEdgeMesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdgeMesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const rightPos = clonedModule.group.worldToLocal(
                    rightEdgeMesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const sizeLeft = this.getSize(leftEdgeMesh);
                  const sizeBack = this.getSize(backEdgeMesh);
                  const sizeFront = this.getSize(frontEdgeMesh);
                  const sizeRight = this.getSize(rightEdgeMesh);

                  const bonRua = this.getMeshByName(model, 'BEP-TU');
                  const sizeBonRua = this.getSize(bonRua);

                  if (isMain) {
                    // Tính Y
                    model.position.y =
                      frontPos.y + sizeFront.y - sizeBonRua.y + 0.022;

                    // Tính Z (giữa left và right)
                    const sizeZ = Math.abs(
                      rightPos.z - (leftPos.z - sizeLeft.z)
                    );
                    const gapLeft = (sizeZ - originalSize.z) / 2;
                    model.position.z = leftPos.z - sizeLeft.z - gapLeft;

                    // Tính X (giữa front và back)
                    const gapBottom =
                      frontPos.x - backPos.x - sizeBack.x - originalSize.x;
                    const posX = gapBottom / 2;
                    model.position.x = backPos.x + posX + sizeBack.x;
                  } else {
                    model.rotation.y = Math.PI / 2;
                    // Tính Y
                    model.position.y =
                      frontPos.y + sizeFront.y - sizeBonRua.y + 0.022;

                    // Tính Z (giữa left và right)
                    const sizeZ = Math.abs(
                      frontPos.z - (backPos.z - sizeBack.z)
                    );
                    const gapBottom = (sizeZ - originalSize.x) / 2;
                    model.position.z = leftPos.z - gapBottom;

                    // Tính X (giữa front và back)
                    const sizeX = Math.abs(
                      rightPos.x - (leftPos.x - sizeLeft.x)
                    );
                    const gapLeft = (sizeX - originalSize.z) / 2;
                    model.position.x = leftPos.x - gapLeft;
                  }

                  resultData = addModelToCabinet();
                }
                model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                break;

              case InsideType.ACCESSORY_HOOD: //Phu kien  bon rua
                model.userData.type = moduleData.type;
                this.setInfoModelChild(model);
                const tamHau = this.getMeshByName(clonedModule.group, 'HAU');
                if (tamHau) {
                  this.removeAccessoryByType(
                    clonedModule,
                    InsideType.ACCESSORY_HOOD
                  );
                  const caoMHM = (moduleData.height + 5) / 1000;
                  const width = (moduleData.width + 10) / 1000;
                  const sizeHood = this.getSize(model);

                  clonedModule.scaleModuleWithFormular(
                    {
                      width,
                    },
                    {
                      caoMHM,
                    }
                  );
                  const tamHauPos = clonedModule.group.worldToLocal(
                    tamHau?.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const sizeTamHau = this.getSize(tamHau);
                  if (moduleData.group === 'CODIEN') {
                    model.position.y = tamHauPos.y - (sizeHood.y - 0.1 - 0.025);
                  } else {
                    model.position.y = tamHauPos.y;
                  }
                  model.scale.z = width / model.userData.sizeDefault.z;
                  model.position.z = tamHauPos.z;
                  model.position.x = tamHauPos.x + sizeTamHau.x;
                  resultData = addModelToCabinet();
                }
                break;

              case InsideType.ACCESSORY_DISH_UPPER: //Phu kien scale
                if (rightEdge && leftEdge) {
                  this.removeAccessoryByType(
                    clonedModule,
                    InsideType.ACCESSORY_DISH_UPPER
                  );
                  if (
                    clonedModule.formularController.currentDimensions.width *
                      1000 !==
                    moduleData.width + 34
                  ) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: 'Không thể thêm phụ kiện',
                      description: `Phụ kiện này chỉ phù hợp với khung tủ có kích thước ${
                        moduleData.width + 34
                      } mm, hãy điều chỉnh kích thước khung tủ trước khi thêm phụ kiện.`,
                      placement: 'bottomRight',
                      closable: true,
                      duration: 4,
                    });
                    break;
                  }
                  model.userData.type = moduleData.type;
                  this.setInfoModelChild(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  model.userData.bottomGap = 0.06;

                  // model.position.z =
                  // leftEdge?.mesh.userData.position.z - sizeLeft.z;

                  const bottomPos = clonedModule.group.worldToLocal(
                    bottomEdge?.mesh
                      .getWorldPosition(new THREE.Vector3())
                      .clone()
                  );

                  model.position.y =
                    bottomPos.y +
                    sizeBottom.y +
                    (model.userData.bottomGap || 0);
                  const originalScale = model.scale.clone();

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const scaleZ = Math.abs(
                      rightEdge?.mesh.userData.position.z -
                        leftEdge?.mesh.userData.position.z
                    );
                    model.position.z = leftPos.z - sizeLeft.z;
                    model.scale.z =
                      ((scaleZ - sizeLeft.z) * originalScale.z) /
                      originalSize.z;
                    const posX = bottomPos.x + sizeBottom.x - originalSize.x;
                    model.position.x = posX;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    const leftWorldPos = new THREE.Vector3();
                    const rightWorldPos = new THREE.Vector3();
                    leftEdge.mesh.getWorldPosition(leftWorldPos);
                    rightEdge.mesh.getWorldPosition(rightWorldPos);

                    model.position.z = leftPos.z - sizeLeft.x;

                    const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                    model.scale.z =
                      ((scaleZ - sizeLeft.x) * originalScale.z) /
                      originalSize.z;
                  }
                  resultData = addModelToCabinet();
                  ///XU LY CHO TRUONG HOP TU KHUYET COT DOC
                }
                break;

              case InsideType.ACCESSORY_DISH_LOWER: //Phu kien scale
                if (rightEdge && leftEdge && !typeHau) {
                  if (
                    clonedModule.formularController.currentDimensions.width *
                      1000 !==
                    moduleData.width + 34
                  ) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: 'Không thể thêm phụ kiện',
                      description: `Phụ kiện này chỉ phù hợp với khung tủ có kích thước ${
                        moduleData.width + 34
                      } mm, hãy điều chỉnh kích thước khung tủ trước khi thêm phụ kiện.`,
                      placement: 'bottomRight',
                      closable: true,
                      duration: 4,
                    });
                    break;
                  }
                  model.userData.type = moduleData.type;
                  this.setInfoModelChild(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  model.userData.topGap = DRAWER_DEFAULT_TOP_GAP;
                  model.userData.bottomGap = DRAWER_DEFAULT_BOTTOM_GAP;

                  // model.position.z =
                  // leftEdge?.mesh.userData.position.z - sizeLeft.z;

                  const topPos = clonedModule.group.worldToLocal(
                    topEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const bottomPos = clonedModule.group.worldToLocal(
                    bottomEdge?.mesh
                      .getWorldPosition(new THREE.Vector3())
                      .clone()
                  );

                  let deltaY = 0;
                  if (bottomEdge.mesh.userData?.topGap) {
                    deltaY = Number(bottomEdge.mesh.userData.topGap);
                  }

                  let gapBarce = 0;
                  if (
                    bottomEdge.mesh.userData.type ===
                    InsideType.ACCESSORY_DISH_LOWER
                  ) {
                    gapBarce = this.braceHeight;
                  }

                  //tinh size phu kien
                  let sizeY = moduleData?.height / 1000 || 0;
                  if (
                    !bottomEdge.mesh.userData.type &&
                    bottomEdge.mesh.userData.type !== InsideType.HPANEL &&
                    !topEdge.mesh.userData.type
                  ) {
                    const sizeDoor =
                      (topPos.y -
                        bottomPos.y +
                        doorInsetOnPanel45 -
                        hinge45Clearance) /
                      2;
                    sizeY =
                      sizeDoor -
                      sizeBottom.y -
                      model.userData.bottomGap -
                      model.userData.topGap -
                      doorInsetOnPanel;
                  }

                  if (
                    bottomEdge.mesh.userData.type ===
                      InsideType.ACCESSORY_DISH_LOWER &&
                    !topEdge.mesh.userData.type
                  ) {
                    const sizeDoor =
                      topPos.y -
                      bottomPos.y -
                      sizeBottom.y -
                      bottomEdge.mesh.userData.topGap -
                      sizeBarce;
                    sizeY =
                      sizeDoor -
                      model.userData.bottomGap -
                      model.userData.topGap;
                  }

                  model.position.y =
                    bottomPos.y +
                    sizeBottom.y +
                    (model.userData.bottomGap || 0) +
                    deltaY +
                    gapBarce;
                  model.scale.y = sizeY / originalSize.y;
                  const originalScale = model.scale.clone();

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const scaleZ = Math.abs(
                      rightEdge?.mesh.userData.position.z -
                        leftEdge?.mesh.userData.position.z
                    );
                    model.position.z = leftPos.z - sizeLeft.z;
                    model.scale.z =
                      ((scaleZ - sizeLeft.z) * originalScale.z) /
                      originalSize.z;
                    const posX = bottomPos.x + sizeBottom.x - originalSize.x;
                    model.position.x = posX;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    const leftWorldPos = new THREE.Vector3();
                    const rightWorldPos = new THREE.Vector3();
                    leftEdge.mesh.getWorldPosition(leftWorldPos);
                    rightEdge.mesh.getWorldPosition(rightWorldPos);

                    model.position.z = leftPos.z - sizeLeft.x;

                    const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                    model.scale.z =
                      ((scaleZ - sizeLeft.x) * originalScale.z) /
                      originalSize.z;
                  }
                  if (
                    bottomEdge.mesh.userData.type ===
                    InsideType.ACCESSORY_DISH_LOWER
                  ) {
                    model.userData.indexDuplicate =
                      bottomEdge.mesh.userData.indexDuplicate + 1;
                  } else {
                    model.userData.indexDuplicate = 0;
                  }
                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.topEdgeUuid = topEdge.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;
                  resultData = addModelToCabinet();
                  ///XU LY CHO TRUONG HOP TU KHUYET COT DOC
                }
                if (rightEdge && leftEdge && typeHau) {
                  if (
                    leftEdge.mesh.name.includes('XAN') ||
                    rightEdge.mesh.name.includes('XAN')
                  ) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: 'Không thể thêm hộc kéo',
                      description: 'Không thể thêm hộc kéo tại vị trí này.',
                      placement: 'bottomRight',
                      closable: true,
                      duration: 4,
                    });
                    return;
                  }
                  model.userData.typeHau = typeHau;
                  model.userData.type = moduleData.type;
                  this.setInfoModelChild(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  model.userData.topGap = DRAWER_DEFAULT_TOP_GAP;
                  model.userData.bottomGap = DRAWER_DEFAULT_BOTTOM_GAP;

                  // model.position.z =
                  // leftEdge?.mesh.userData.position.z - sizeLeft.z;

                  const topPos = clonedModule.group.worldToLocal(
                    topEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const bottomPos = clonedModule.group.worldToLocal(
                    bottomEdge?.mesh
                      .getWorldPosition(new THREE.Vector3())
                      .clone()
                  );

                  let deltaY = 0;
                  if (bottomEdge.mesh.userData?.topGap) {
                    deltaY = Number(bottomEdge.mesh.userData.topGap);
                  }

                  let gapBarce = 0;
                  if (
                    bottomEdge.mesh.userData.type ===
                    InsideType.ACCESSORY_DISH_LOWER
                  ) {
                    gapBarce = this.braceHeight;
                  }

                  //tinh size phu kien
                  let sizeY = moduleData?.height / 1000 || 0;
                  if (
                    !bottomEdge.mesh.userData.type &&
                    bottomEdge.mesh.userData.type !== InsideType.HPANEL &&
                    !topEdge.mesh.userData.type
                  ) {
                    const sizeDoor =
                      (topPos.y -
                        bottomPos.y +
                        doorInsetOnPanel45 -
                        hinge45Clearance) /
                      2;
                    sizeY =
                      sizeDoor -
                      sizeBottom.y -
                      model.userData.bottomGap -
                      model.userData.topGap -
                      doorInsetOnPanel;
                  }

                  if (
                    bottomEdge.mesh.userData.type ===
                    InsideType.ACCESSORY_DISH_LOWER
                  ) {
                    const sizeDoor =
                      topPos.y -
                      bottomPos.y -
                      sizeBottom.y -
                      bottomEdge.mesh.userData.topGap -
                      sizeBarce;
                    sizeY =
                      sizeDoor -
                      model.userData.bottomGap -
                      model.userData.topGap;
                  }

                  model.position.y =
                    bottomPos.y +
                    sizeBottom.y +
                    (model.userData.bottomGap || 0) +
                    deltaY +
                    gapBarce;
                  model.scale.y = sizeY / originalSize.y;
                  const originalScale = model.scale.clone();
                  const isMain = typeHau === TypeConnerModule.MAIN;
                  let checkWidth = false;
                  if (orientation === 'NORMAL') {
                    if (isMain) {
                      //Scale Z
                      const scaleZ = Math.abs(
                        rightEdge?.mesh.userData.position.z -
                          leftEdge?.mesh.userData.position.z
                      );
                      if (
                        scaleZ - sizeLeft.z * 1000 ===
                        moduleData.width + 34
                      ) {
                        checkWidth = true;
                      }
                      model.position.z = leftPos.z - sizeLeft.z;
                      model.scale.z =
                        ((scaleZ - sizeLeft.z) * originalScale.z) /
                        originalSize.z;

                      //Scale X

                      const scaleX = sizeRight.x / originalSize.x;
                      model.scale.x = scaleX;
                    } else {
                      // scale Z
                      model.rotation.y = Math.PI / 2;

                      const leftWorldPos = new THREE.Vector3();
                      const rightWorldPos = new THREE.Vector3();
                      leftEdge.mesh.getWorldPosition(leftWorldPos);
                      rightEdge.mesh.getWorldPosition(rightWorldPos);

                      model.position.z = leftPos.z;
                      model.position.x = leftPos.x;

                      const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                      model.scale.z =
                        ((scaleZ - sizeLeft.x) * originalScale.z) /
                        originalSize.z;

                      //Scale X

                      const scaleX = sizeLeft.z / originalSize.x;
                      model.scale.x = scaleX;
                    }
                  }
                  if (orientation === 'ROTATED_270') {
                    if (!isMain) {
                      //Scale Z
                      model.rotation.y = Math.PI / 2;
                      const scaleZ = Math.abs(
                        rightEdge?.mesh.userData.position.z -
                          leftEdge?.mesh.userData.position.z
                      );
                      model.position.z = leftPos.z;
                      model.position.x = leftPos.x;
                      model.scale.z =
                        ((scaleZ - sizeLeft.z) * originalScale.z) /
                        originalSize.z;

                      //Scale X

                      const scaleX = sizeLeft.x / originalSize.x;
                      model.scale.x = scaleX;
                    } else {
                      // scale Z

                      const leftWorldPos = new THREE.Vector3();
                      const rightWorldPos = new THREE.Vector3();
                      leftEdge.mesh.getWorldPosition(leftWorldPos);
                      rightEdge.mesh.getWorldPosition(rightWorldPos);

                      model.position.z = leftPos.z - sizeLeft.x;
                      model.position.x = rightPos.x;

                      const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                      model.scale.z =
                        ((scaleZ - sizeLeft.x) * originalScale.z) /
                        originalSize.z;

                      //Scale X

                      const scaleX = sizeRight.z / originalSize.x;
                      model.scale.x = scaleX;
                    }
                  }
                  if (
                    bottomEdge.mesh.userData.type ===
                    InsideType.ACCESSORY_DISH_LOWER
                  ) {
                    model.userData.indexDuplicate =
                      bottomEdge.mesh.userData.indexDuplicate + 1;
                  } else {
                    model.userData.indexDuplicate = 0;
                  }
                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.topEdgeUuid = topEdge.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;
                  resultData = addModelToCabinet();
                  ///XU LY CHO TRUONG HOP TU KHUYET COT DOC
                }
                break;

              case InsideType.CLOTHES_HANGER: //Phu kien scale
                if (
                  rightEdge?.mesh &&
                  leftEdge?.mesh &&
                  topEdge?.mesh &&
                  !typeHau
                ) {
                  model.userData.type = moduleData.type;
                  model.userData.price = moduleData.price;
                  this.setInfoModelChild(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  // model.position.z =
                  // leftEdge?.mesh.userData.position.z - sizeLeft.z;

                  const topPos = clonedModule.group.worldToLocal(
                    topEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  model.position.y = topPos.y - originalSize.y;
                  model.userData.bottomGap = moduleData.bottomGap || 0;
                  const originalScale = model.scale.clone();

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const scaleZ = Math.abs(
                      rightEdge.mesh.userData.position.z -
                        leftEdge.mesh.userData.position.z
                    );
                    model.position.z = leftPos.z - sizeLeft.z;
                    model.scale.z =
                      ((scaleZ - sizeLeft.z) * originalScale.z) /
                      originalSize.z;
                    const posX = leftPos.x + sizeLeft.x / 2;
                    model.position.x = posX;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    const leftWorldPos = new THREE.Vector3();
                    const rightWorldPos = new THREE.Vector3();
                    leftEdge.mesh.getWorldPosition(leftWorldPos);
                    rightEdge.mesh.getWorldPosition(rightWorldPos);

                    model.position.z = leftPos.z - sizeLeft.x;
                    model.position.x = leftPos.x + sizeLeft.z / 2;

                    const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                    model.scale.z =
                      ((scaleZ - sizeLeft.x) * originalScale.z) /
                      originalSize.z;
                  }
                  if (topEdge?.mesh?.userData.type !== undefined) {
                    if (!topEdge.mesh.userData.bottomDependent) {
                      topEdge.mesh.userData.bottomDependent = [];
                    }
                    topEdge.mesh.userData.bottomDependent.push(model.uuid);
                  }

                  if (leftEdge?.mesh.userData.type !== undefined) {
                    if (!leftEdge.mesh.userData.rightDependent) {
                      leftEdge.mesh.userData.rightDependent = [];
                    }
                    leftEdge.mesh.userData.rightDependent.push(model.uuid);
                  } else if (
                    leftEdge?.mesh.parent.userData.type !== undefined
                  ) {
                    if (!leftEdge.mesh.parent.userData.rightDependent) {
                      leftEdge.mesh.parent.userData.rightDependent = [];
                    }
                    leftEdge.mesh.parent.userData.rightDependent.push(
                      model.uuid
                    );
                  }

                  if (rightEdge?.mesh.userData.type !== undefined) {
                    if (!rightEdge.mesh.userData.leftDependent) {
                      rightEdge.mesh.userData.leftDependent = [];
                    }
                    rightEdge.mesh.userData.leftDependent.push(model.uuid);
                  } else if (
                    rightEdge?.mesh.parent.userData.type !== undefined
                  ) {
                    if (!rightEdge.mesh.parent.userData.leftDependent) {
                      rightEdge.mesh.parent.userData.leftDependent = [];
                    }
                    rightEdge.mesh.parent.userData.leftDependent.push(
                      model.uuid
                    );
                  }

                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.topEdgeUuid = topEdge.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;

                  resultData = addModelToCabinet();
                } else if (leftEdge && rightEdge && typeHau) {
                  model.userData.type = moduleData.type;
                  model.userData.typeHau = typeHau;
                  this.setInfoModelChild(model);

                  const posLocal = clonedModule.group.worldToLocal(
                    intersection.clone()
                  );
                  model.position.y = posLocal.y;

                  const topPos = clonedModule.group.worldToLocal(
                    topEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const originalScale = model.scale.clone();

                  let sizeXTV;
                  const isMain = typeHau === TypeConnerModule.MAIN;
                  model.position.y = topPos.y - originalSize.y;
                  if (orientation === 'NORMAL') {
                    if (isMain) {
                      //Scale Z
                      const scaleZ = Math.abs(
                        rightEdge?.mesh.userData.position.z -
                          leftEdge?.mesh.userData.position.z
                      );
                      model.position.z = leftPos.z - sizeLeft.z;
                      model.scale.z =
                        ((scaleZ - sizeLeft.z) * originalScale.z) /
                        originalSize.z;

                      //Scale X

                      model.position.x = model.position.x + sizeLeft.x / 2;
                      sizeXTV = sizeBottom.x;
                    } else {
                      // scale Z
                      model.rotation.y = Math.PI / 2;

                      const leftWorldPos = new THREE.Vector3();
                      const rightWorldPos = new THREE.Vector3();
                      leftEdge.mesh.getWorldPosition(leftWorldPos);
                      rightEdge.mesh.getWorldPosition(rightWorldPos);

                      model.position.z = leftPos.z;
                      model.position.x = leftPos.x;

                      const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                      model.scale.z =
                        ((scaleZ - sizeLeft.x) * originalScale.z) /
                        originalSize.z;

                      //Scale X
                      model.position.z = model.position.z - sizeLeft.z / 2;
                      sizeXTV = sizeBottom.z;
                    }
                  }
                  if (orientation === 'ROTATED_270') {
                    if (!isMain) {
                      model.rotation.y = Math.PI / 2;
                      //Scale Z
                      const scaleZ = Math.abs(
                        rightEdge?.mesh.userData.position.z -
                          leftEdge?.mesh.userData.position.z
                      );
                      model.position.z = leftPos.z;
                      model.position.x = leftPos.x;
                      model.scale.z =
                        ((scaleZ - sizeLeft.z) * originalScale.z) /
                        originalSize.z;

                      //Scale X

                      model.position.z = model.position.z - sizeLeft.x / 2;
                      sizeXTV = sizeBottom.x;
                    } else {
                      // scale Z

                      const leftWorldPos = new THREE.Vector3();
                      const rightWorldPos = new THREE.Vector3();
                      leftEdge.mesh.getWorldPosition(leftWorldPos);
                      rightEdge.mesh.getWorldPosition(rightWorldPos);

                      model.position.z = leftPos.z - sizeLeft.x;
                      model.position.x = leftPos.x;

                      const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                      model.scale.z =
                        ((scaleZ - sizeLeft.x) * originalScale.z) /
                        originalSize.z;

                      //Scale X
                      model.position.x = model.position.x + sizeRight.z / 2;
                      sizeXTV = sizeBottom.z;
                    }
                  }

                  resultData = addModelToCabinet();
                  ///XU LY CHO TRUONG HOP TU KHUYET COT DOC

                  //// phu thuoc
                  // if (!leftEdge.mesh.userData.rightDependent) {
                  //   leftEdge.mesh.userData.rightDependent = [];
                  // }
                  // leftEdge.mesh.userData.rightDependent.push(model.uuid);
                  if (leftEdge.mesh.userData.type !== undefined) {
                    if (!leftEdge.mesh.userData.rightDependent) {
                      leftEdge.mesh.userData.rightDependent = [];
                    }
                    leftEdge.mesh.userData.rightDependent.push(model.uuid);
                  } else if (leftEdge.mesh.parent.userData.type !== undefined) {
                    if (!leftEdge.mesh.parent.userData.rightDependent) {
                      leftEdge.mesh.parent.userData.rightDependent = [];
                    }
                    leftEdge.mesh.parent.userData.rightDependent.push(
                      model.uuid
                    );
                  }

                  // if (!rightEdge.mesh.userData.leftDependent) {
                  //   rightEdge.mesh.userData.leftDependent = [];
                  // }
                  // rightEdge.mesh.userData.leftDependent.push(model.uuid);
                  if (rightEdge.mesh.userData.type !== undefined) {
                    if (!rightEdge.mesh.userData.leftDependent) {
                      rightEdge.mesh.userData.leftDependent = [];
                    }
                    rightEdge.mesh.userData.leftDependent.push(model.uuid);
                  } else if (
                    rightEdge.mesh.parent.userData.type !== undefined
                  ) {
                    if (!rightEdge.mesh.parent.userData.leftDependent) {
                      rightEdge.mesh.parent.userData.leftDependent = [];
                    }
                    rightEdge.mesh.parent.userData.leftDependent.push(
                      model.uuid
                    );
                  }
                  model.userData.leftEdgeUuid = leftEdge?.mesh.uuid;
                  model.userData.rightEdgeUuid = rightEdge?.mesh.uuid;
                  model.userData.topEdgeUuid = topEdge?.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge?.mesh.uuid;
                  ////
                }

                break;

              case InsideType.LED_CORNER: //Led góc
                if (
                  rightEdge?.mesh &&
                  leftEdge?.mesh &&
                  topEdge?.mesh &&
                  !typeHau
                ) {
                  if (topEdge.mesh.userData?.type === InsideType.LED_CORNER) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: i18n.t('Unable to add the product.'),
                      description: i18n.t(
                        'A product already exists in this position.'
                      ),
                      placement: 'bottomRight',
                      closable: true,
                      duration: 2,
                    });
                    break;
                  }

                  model.userData.type = moduleData.type;
                  model.userData.price = moduleData.price;
                  this.setInfoModelChild(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const topPos = clonedModule.group.worldToLocal(
                    topEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  model.position.y = topPos.y;
                  model.userData.bottomGap = moduleData.bottomGap || 0;
                  const originalScale = model.scale.clone();

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const sizeZ = Math.abs(
                      rightEdge.mesh.userData.position.z -
                        leftEdge.mesh.userData.position.z
                    );
                    model.position.z = leftPos.z - sizeLeft.z;
                    model.scale.z =
                      ((sizeZ - sizeLeft.z) * originalScale.z) / originalSize.z;
                    // const posX = leftPos.x + sizeLeft.x / 2;
                    const posX = leftPos.x;
                    model.position.x = posX;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    const leftWorldPos = new THREE.Vector3();
                    const rightWorldPos = new THREE.Vector3();
                    leftEdge.mesh.getWorldPosition(leftWorldPos);
                    rightEdge.mesh.getWorldPosition(rightWorldPos);

                    model.position.z = leftPos.z - sizeLeft.x;
                    // model.position.x = leftPos.x + sizeLeft.z / 2;
                    model.position.x = leftPos.x;

                    const sizeZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                    model.scale.z =
                      ((sizeZ - sizeLeft.x) * originalScale.z) / originalSize.z;
                  }
                  if (topEdge?.mesh?.userData.type !== undefined) {
                    if (!topEdge.mesh.userData.bottomDependent) {
                      topEdge.mesh.userData.bottomDependent = [];
                    }
                    topEdge.mesh.userData.bottomDependent.push(model.uuid);
                  }

                  if (leftEdge?.mesh.userData.type !== undefined) {
                    if (!leftEdge.mesh.userData.rightDependent) {
                      leftEdge.mesh.userData.rightDependent = [];
                    }
                    leftEdge.mesh.userData.rightDependent.push(model.uuid);
                  } else if (
                    leftEdge?.mesh.parent.userData.type !== undefined
                  ) {
                    if (!leftEdge.mesh.parent.userData.rightDependent) {
                      leftEdge.mesh.parent.userData.rightDependent = [];
                    }
                    leftEdge.mesh.parent.userData.rightDependent.push(
                      model.uuid
                    );
                  }

                  if (rightEdge?.mesh.userData.type !== undefined) {
                    if (!rightEdge.mesh.userData.leftDependent) {
                      rightEdge.mesh.userData.leftDependent = [];
                    }
                    rightEdge.mesh.userData.leftDependent.push(model.uuid);
                  } else if (
                    rightEdge?.mesh.parent.userData.type !== undefined
                  ) {
                    if (!rightEdge.mesh.parent.userData.leftDependent) {
                      rightEdge.mesh.parent.userData.leftDependent = [];
                    }
                    rightEdge.mesh.parent.userData.leftDependent.push(
                      model.uuid
                    );
                  }

                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.topEdgeUuid = topEdge.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge?.mesh?.uuid;

                  this.addInsideLeds(model);

                  resultData = addModelToCabinet();
                } else if (
                  leftEdge?.mesh &&
                  rightEdge === undefined &&
                  topEdge?.mesh &&
                  backEdge?.mesh &&
                  clonedModule.moduleType.toLowerCase().includes('curve') &&
                  !typeHau
                ) {
                  if (topEdge.mesh.userData?.type === InsideType.LED_CORNER) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: i18n.t('Unable to add the product.'),
                      description: i18n.t(
                        'A product already exists in this position.'
                      ),
                      placement: 'bottomRight',
                      closable: true,
                      duration: 2,
                    });
                    break;
                  }

                  model.userData.type = moduleData.type;
                  model.userData.price = moduleData.price;
                  this.setInfoModelChild(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const backPos = clonedModule.group.worldToLocal(
                    backEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const topPos = clonedModule.group.worldToLocal(
                    topEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const backSize = this.getSize(backEdge.mesh);

                  model.position.y = topPos.y;
                  model.userData.bottomGap = moduleData.bottomGap || 0;
                  const originalScale = model.scale.clone();

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const sizeZ =
                      backSize.z - Math.abs(backPos.z - leftPos.z) - sizeLeft.z;
                    model.position.z = leftPos.z - sizeLeft.z;
                    model.scale.z = (sizeZ * originalScale.z) / originalSize.z;

                    const posX = leftPos.x;
                    model.position.x = posX;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    const leftWorldPos = new THREE.Vector3();
                    leftEdge.mesh.getWorldPosition(leftWorldPos);

                    model.position.z = leftPos.z - sizeLeft.x;
                    model.position.x = leftPos.x;

                    const sizeZ =
                      backSize.x - Math.abs(backPos.z - leftPos.z) - sizeLeft.x;
                    model.scale.z = (sizeZ * originalScale.z) / originalSize.z;
                  }
                  if (topEdge?.mesh?.userData.type !== undefined) {
                    if (!topEdge.mesh.userData.bottomDependent) {
                      topEdge.mesh.userData.bottomDependent = [];
                    }
                    topEdge.mesh.userData.bottomDependent.push(model.uuid);
                  }

                  model.userData.rightEdgeUuid = undefined;
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.topEdgeUuid = topEdge.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;

                  this.addInsideLeds(model);

                  resultData = addModelToCabinet();
                } else if (
                  leftEdge === undefined &&
                  rightEdge?.mesh &&
                  topEdge?.mesh &&
                  backEdge?.mesh &&
                  clonedModule.moduleType.toLowerCase().includes('curve') &&
                  !typeHau
                ) {
                  if (topEdge.mesh.userData?.type === InsideType.LED_CORNER) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: i18n.t('Unable to add the product.'),
                      description: i18n.t(
                        'A product already exists in this position.'
                      ),
                      placement: 'bottomRight',
                      closable: true,
                      duration: 2,
                    });
                    break;
                  }

                  model.userData.type = moduleData.type;
                  model.userData.price = moduleData.price;
                  this.setInfoModelChild(model);

                  const rightPos = clonedModule.group.worldToLocal(
                    rightEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const topPos = clonedModule.group.worldToLocal(
                    topEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const backPos = clonedModule.group.worldToLocal(
                    backEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const backSize = this.getSize(backEdge.mesh);

                  model.position.y = topPos.y;
                  model.userData.bottomGap = moduleData.bottomGap || 0;
                  const originalScale = model.scale.clone();

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const sizeZ = Math.abs(rightPos.z - backPos.z);
                    model.position.z = topPos.z;
                    model.scale.z = (sizeZ * originalScale.z) / originalSize.z;

                    const posX = rightPos.x;
                    model.position.x = posX;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    model.position.z = topPos.z;
                    model.position.x = rightPos.x;

                    const sizeZ = Math.abs(rightPos.z - backPos.z);
                    model.scale.z = (sizeZ * originalScale.z) / originalSize.z;
                  }
                  if (topEdge?.mesh?.userData.type !== undefined) {
                    if (!topEdge.mesh.userData.bottomDependent) {
                      topEdge.mesh.userData.bottomDependent = [];
                    }
                    topEdge.mesh.userData.bottomDependent.push(model.uuid);
                  }

                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  model.userData.leftEdgeUuid = undefined;
                  model.userData.topEdgeUuid = topEdge.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;

                  this.addInsideLeds(model);

                  resultData = addModelToCabinet();
                } else if (
                  rightEdge?.mesh &&
                  leftEdge?.mesh &&
                  topEdge?.mesh &&
                  typeHau
                ) {
                  if (topEdge.mesh.userData?.type === InsideType.LED_CORNER) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: i18n.t('Unable to add the product.'),
                      description: i18n.t(
                        'A product already exists in this position.'
                      ),
                      placement: 'bottomRight',
                      closable: true,
                      duration: 2,
                    });
                    break;
                  }

                  model.userData.type = moduleData.type;
                  model.userData.typeHau = typeHau;
                  model.userData.price = moduleData.price;
                  this.setInfoModelChild(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const topPos = clonedModule.group.worldToLocal(
                    topEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  model.position.y = topPos.y;
                  model.userData.bottomGap = moduleData.bottomGap || 0;
                  const originalScale = model.scale.clone();
                  const isMain = typeHau === TypeConnerModule.MAIN;
                  if (orientation === 'NORMAL') {
                    if (isMain) {
                      //Scale Z
                      const scaleZ = Math.abs(
                        rightEdge?.mesh.userData.position.z -
                          leftEdge?.mesh.userData.position.z
                      );
                      model.position.z = leftPos.z - sizeLeft.z;
                      model.scale.z =
                        ((scaleZ - sizeLeft.z) * originalScale.z) /
                        originalSize.z;

                      //Scale X

                      model.position.x = model.position.x + sizeBack.x;
                    } else {
                      // scale Z
                      model.rotation.y = Math.PI / 2;

                      const leftWorldPos = new THREE.Vector3();
                      const rightWorldPos = new THREE.Vector3();
                      leftEdge.mesh.getWorldPosition(leftWorldPos);
                      rightEdge.mesh.getWorldPosition(rightWorldPos);

                      model.position.z = leftPos.z;
                      model.position.x = leftPos.x;

                      const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                      model.scale.z =
                        ((scaleZ - sizeLeft.x) * originalScale.z) /
                        originalSize.z;

                      //Scale X
                      model.position.z = model.position.z - sizeBack.z;
                    }
                  }
                  if (orientation === 'ROTATED_270') {
                    if (!isMain) {
                      model.rotation.y = Math.PI / 2;
                      //Scale Z
                      const scaleZ = Math.abs(
                        rightEdge?.mesh.userData.position.z -
                          leftEdge?.mesh.userData.position.z
                      );
                      model.position.z = leftPos.z;
                      model.position.x = leftPos.x;
                      model.scale.z =
                        ((scaleZ - sizeLeft.z) * originalScale.z) /
                        originalSize.z;

                      //Scale X

                      model.position.z = model.position.z - sizeBack.x;
                    } else {
                      // scale Z

                      const leftWorldPos = new THREE.Vector3();
                      const rightWorldPos = new THREE.Vector3();
                      leftEdge.mesh.getWorldPosition(leftWorldPos);
                      rightEdge.mesh.getWorldPosition(rightWorldPos);

                      model.position.z = leftPos.z - sizeLeft.x;
                      model.position.x = leftPos.x;

                      const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                      model.scale.z =
                        ((scaleZ - sizeLeft.x) * originalScale.z) /
                        originalSize.z;

                      //Scale X
                      model.position.x = model.position.x + sizeBack.z;
                    }
                  }
                  if (topEdge?.mesh?.userData.type !== undefined) {
                    if (!topEdge.mesh.userData.bottomDependent) {
                      topEdge.mesh.userData.bottomDependent = [];
                    }
                    topEdge.mesh.userData.bottomDependent.push(model.uuid);
                  }
                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.topEdgeUuid = topEdge.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;

                  this.addInsideLeds(model);

                  resultData = addModelToCabinet();
                }
                break;

              case InsideType.LED_RECESSED: //Led âm
                if (
                  rightEdge?.mesh &&
                  leftEdge?.mesh &&
                  topEdge?.mesh &&
                  !typeHau
                ) {
                  if (topEdge.mesh.userData?.type === InsideType.LED_RECESSED) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: i18n.t('Unable to add the product.'),
                      description: i18n.t(
                        'A product already exists in this position.'
                      ),
                      placement: 'bottomRight',
                      closable: true,
                      duration: 2,
                    });
                    break;
                  }

                  model.userData.type = moduleData.type;
                  model.userData.price = moduleData.price;
                  this.setInfoModelChild(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const topPos = clonedModule.group.worldToLocal(
                    topEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  model.position.y = topPos.y;
                  model.userData.bottomGap = moduleData.bottomGap || 0;
                  const originalScale = model.scale.clone();

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const scaleZ = Math.abs(
                      rightEdge.mesh.userData.position.z -
                        leftEdge.mesh.userData.position.z
                    );
                    model.position.z = leftPos.z - sizeLeft.z;
                    model.scale.z =
                      ((scaleZ - sizeLeft.z) * originalScale.z) /
                      originalSize.z;
                    const posX = leftPos.x + sizeLeft.x / 2;
                    model.position.x = posX;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    const leftWorldPos = new THREE.Vector3();
                    const rightWorldPos = new THREE.Vector3();
                    leftEdge.mesh.getWorldPosition(leftWorldPos);
                    rightEdge.mesh.getWorldPosition(rightWorldPos);

                    model.position.z = leftPos.z - sizeLeft.x;
                    model.position.x = leftPos.x + sizeLeft.z / 2;

                    const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                    model.scale.z =
                      ((scaleZ - sizeLeft.x) * originalScale.z) /
                      originalSize.z;
                  }
                  if (topEdge?.mesh?.userData.type !== undefined) {
                    if (!topEdge.mesh.userData.bottomDependent) {
                      topEdge.mesh.userData.bottomDependent = [];
                    }
                    topEdge.mesh.userData.bottomDependent.push(model.uuid);
                  }

                  if (leftEdge?.mesh.userData.type !== undefined) {
                    if (!leftEdge.mesh.userData.rightDependent) {
                      leftEdge.mesh.userData.rightDependent = [];
                    }
                    leftEdge.mesh.userData.rightDependent.push(model.uuid);
                  } else if (
                    leftEdge?.mesh.parent.userData.type !== undefined
                  ) {
                    if (!leftEdge.mesh.parent.userData.rightDependent) {
                      leftEdge.mesh.parent.userData.rightDependent = [];
                    }
                    leftEdge.mesh.parent.userData.rightDependent.push(
                      model.uuid
                    );
                  }

                  if (rightEdge?.mesh.userData.type !== undefined) {
                    if (!rightEdge.mesh.userData.leftDependent) {
                      rightEdge.mesh.userData.leftDependent = [];
                    }
                    rightEdge.mesh.userData.leftDependent.push(model.uuid);
                  } else if (
                    rightEdge?.mesh.parent.userData.type !== undefined
                  ) {
                    if (!rightEdge.mesh.parent.userData.leftDependent) {
                      rightEdge.mesh.parent.userData.leftDependent = [];
                    }
                    rightEdge.mesh.parent.userData.leftDependent.push(
                      model.uuid
                    );
                  }

                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.topEdgeUuid = topEdge.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge?.mesh?.uuid;

                  this.addInsideLeds(model);

                  resultData = addModelToCabinet();
                } else if (
                  leftEdge?.mesh &&
                  rightEdge === undefined &&
                  topEdge?.mesh &&
                  backEdge?.mesh &&
                  clonedModule.moduleType.toLowerCase().includes('curve') &&
                  !typeHau
                ) {
                  if (topEdge.mesh.userData?.type === InsideType.LED_RECESSED) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: i18n.t('Unable to add the product.'),
                      description: i18n.t(
                        'A product already exists in this position.'
                      ),
                      placement: 'bottomRight',
                      closable: true,
                      duration: 2,
                    });
                    break;
                  }

                  model.userData.type = moduleData.type;
                  model.userData.price = moduleData.price;
                  this.setInfoModelChild(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const topPos = clonedModule.group.worldToLocal(
                    topEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const backSize = this.getSize(backEdge.mesh);

                  model.position.y = topPos.y;
                  model.userData.bottomGap = moduleData.bottomGap || 0;
                  const originalScale = model.scale.clone();

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const sizeZ =
                      backSize.z - Math.abs(backPos.z - leftPos.z) - sizeLeft.z;
                    model.position.z = leftPos.z - sizeLeft.z;
                    model.scale.z = (sizeZ * originalScale.z) / originalSize.z;

                    const posX = leftPos.x + sizeLeft.x / 2;
                    model.position.x = posX;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    const leftWorldPos = new THREE.Vector3();
                    leftEdge.mesh.getWorldPosition(leftWorldPos);

                    model.position.z = leftPos.z - sizeLeft.x;
                    model.position.x = leftPos.x + sizeLeft.z / 2;

                    const sizeZ =
                      backSize.x - Math.abs(backPos.z - leftPos.z) - sizeLeft.x;
                    model.scale.z = (sizeZ * originalScale.z) / originalSize.z;
                  }
                  if (topEdge?.mesh?.userData.type !== undefined) {
                    if (!topEdge.mesh.userData.bottomDependent) {
                      topEdge.mesh.userData.bottomDependent = [];
                    }
                    topEdge.mesh.userData.bottomDependent.push(model.uuid);
                  }

                  model.userData.rightEdgeUuid = undefined;
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.topEdgeUuid = topEdge.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;

                  this.addInsideLeds(model);

                  resultData = addModelToCabinet();
                } else if (
                  leftEdge === undefined &&
                  rightEdge?.mesh &&
                  topEdge?.mesh &&
                  backEdge?.mesh &&
                  clonedModule.moduleType.toLowerCase().includes('curve') &&
                  !typeHau
                ) {
                  if (topEdge.mesh.userData?.type === InsideType.LED_RECESSED) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: i18n.t('Unable to add the product.'),
                      description: i18n.t(
                        'A product already exists in this position.'
                      ),
                      placement: 'bottomRight',
                      closable: true,
                      duration: 2,
                    });
                    break;
                  }

                  model.userData.type = moduleData.type;
                  model.userData.price = moduleData.price;
                  this.setInfoModelChild(model);

                  const rightPos = clonedModule.group.worldToLocal(
                    rightEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const topPos = clonedModule.group.worldToLocal(
                    topEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );
                  const backSize = this.getSize(backEdge.mesh);

                  model.position.y = topPos.y;
                  model.userData.bottomGap = moduleData.bottomGap || 0;
                  const originalScale = model.scale.clone();

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    const sizeZ = Math.abs(rightPos.z - backPos.z);
                    model.position.z = topPos.z;
                    model.scale.z = (sizeZ * originalScale.z) / originalSize.z;

                    const posX = rightPos.x + sizeRight.x / 2;
                    model.position.x = posX;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    model.position.z = topPos.z;
                    model.position.x = rightPos.x + sizeRight.z / 2;

                    const sizeZ = Math.abs(rightPos.z - backPos.z);
                    model.scale.z = (sizeZ * originalScale.z) / originalSize.z;
                  }
                  if (topEdge?.mesh?.userData.type !== undefined) {
                    if (!topEdge.mesh.userData.bottomDependent) {
                      topEdge.mesh.userData.bottomDependent = [];
                    }
                    topEdge.mesh.userData.bottomDependent.push(model.uuid);
                  }

                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  model.userData.leftEdgeUuid = undefined;
                  model.userData.topEdgeUuid = topEdge.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;

                  this.addInsideLeds(model);

                  resultData = addModelToCabinet();
                } else if (
                  rightEdge?.mesh &&
                  leftEdge?.mesh &&
                  topEdge?.mesh &&
                  typeHau
                ) {
                  if (topEdge.mesh.userData?.type === InsideType.LED_RECESSED) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: i18n.t('Unable to add the product.'),
                      description: i18n.t(
                        'A product already exists in this position.'
                      ),
                      placement: 'bottomRight',
                      closable: true,
                      duration: 2,
                    });
                    break;
                  }

                  model.userData.type = moduleData.type;
                  model.userData.typeHau = typeHau;
                  model.userData.price = moduleData.price;
                  this.setInfoModelChild(model);

                  const leftPos = clonedModule.group.worldToLocal(
                    leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const topPos = clonedModule.group.worldToLocal(
                    topEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  model.position.y = topPos.y;
                  model.userData.bottomGap = moduleData.bottomGap || 0;
                  const originalScale = model.scale.clone();
                  const isMain = typeHau === TypeConnerModule.MAIN;
                  if (orientation === 'NORMAL') {
                    if (isMain) {
                      //Scale Z
                      const scaleZ = Math.abs(
                        rightEdge?.mesh.userData.position.z -
                          leftEdge?.mesh.userData.position.z
                      );
                      model.position.z = leftPos.z - sizeLeft.z;
                      model.scale.z =
                        ((scaleZ - sizeLeft.z) * originalScale.z) /
                        originalSize.z;

                      //Scale X

                      model.position.x = model.position.x + sizeRight.x / 2;
                    } else {
                      // scale Z
                      model.rotation.y = Math.PI / 2;

                      const leftWorldPos = new THREE.Vector3();
                      const rightWorldPos = new THREE.Vector3();
                      leftEdge.mesh.getWorldPosition(leftWorldPos);
                      rightEdge.mesh.getWorldPosition(rightWorldPos);

                      model.position.z = leftPos.z;
                      model.position.x = leftPos.x;

                      const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                      model.scale.z =
                        ((scaleZ - sizeLeft.x) * originalScale.z) /
                        originalSize.z;

                      //Scale X
                      model.position.z = model.position.z - sizeLeft.z / 2;
                    }
                  }
                  if (orientation === 'ROTATED_270') {
                    if (!isMain) {
                      model.rotation.y = Math.PI / 2;
                      //Scale Z
                      const scaleZ = Math.abs(
                        rightEdge?.mesh.userData.position.z -
                          leftEdge?.mesh.userData.position.z
                      );
                      model.position.z = leftPos.z;
                      model.position.x = leftPos.x;
                      model.scale.z =
                        ((scaleZ - sizeLeft.z) * originalScale.z) /
                        originalSize.z;

                      //Scale X

                      model.position.z = model.position.z - sizeLeft.x / 2;
                    } else {
                      // scale Z

                      const leftWorldPos = new THREE.Vector3();
                      const rightWorldPos = new THREE.Vector3();
                      leftEdge.mesh.getWorldPosition(leftWorldPos);
                      rightEdge.mesh.getWorldPosition(rightWorldPos);

                      model.position.z = leftPos.z - sizeLeft.x;
                      model.position.x = leftPos.x;

                      const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x);
                      model.scale.z =
                        ((scaleZ - sizeLeft.x) * originalScale.z) /
                        originalSize.z;

                      //Scale X
                      model.position.x = model.position.x + sizeRight.z / 2;
                    }
                  }
                  if (topEdge?.mesh?.userData.type !== undefined) {
                    if (!topEdge.mesh.userData.bottomDependent) {
                      topEdge.mesh.userData.bottomDependent = [];
                    }
                    topEdge.mesh.userData.bottomDependent.push(model.uuid);
                  }
                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.topEdgeUuid = topEdge.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;

                  this.addInsideLeds(model);

                  resultData = addModelToCabinet();
                }
                break;

              case InsideType.MIRROR_DOOR: //Gương gắn cửa tủ
                if (
                  rightEdge?.mesh &&
                  leftEdge?.mesh &&
                  topEdge?.mesh &&
                  backEdge?.mesh
                ) {
                  if (backEdge.mesh.userData?.type !== InsideType.DOOR) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: i18n.t('Unable to add the product.'),
                      description:
                        'Gương cần được thả vào vị trí cánh cửa mà bạn muốn thêm gương.',
                      placement: 'bottomRight',
                      closable: true,
                      duration: 2,
                    });
                    break;
                  }
                  if (backEdge.mesh.userData?.mirrorUuid) {
                    const mirrorModel = this.findMeshByUuid(
                      clonedModule.group,
                      backEdge.mesh.userData.mirrorUuid
                    );

                    if (mirrorModel) {
                      openNotification({
                        key: 'cant-add-door-existing-door',
                        icon: <Icon.ICWarning />,
                        iconColor: '#F97D16',
                        title: i18n.t('Unable to add the product.'),
                        description:
                          'Vị trí này đã tồn tại gương, không thể thêm trùng nhau.',
                        placement: 'bottomRight',
                        closable: true,
                        duration: 2,
                      });
                      break;
                    }
                  }

                  model.userData.type = moduleData.type;
                  model.userData.price = moduleData.price;
                  this.setInfoModelChild(model);

                  const backPos = clonedModule.group.worldToLocal(
                    backEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                  );

                  const originalScale = model.scale.clone();

                  const offsetMirror = 0.06;
                  const bboxDoor = new THREE.Box3().setFromObject(
                    backEdge.mesh
                  );
                  const sizeDoor = new THREE.Vector3();
                  bboxDoor.getSize(sizeDoor);

                  let sizeZ;
                  const sizeX = 0.005;
                  const sizeY =
                    Math.min(1.5 + offsetMirror * 2, sizeDoor.y) -
                    offsetMirror * 2;

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    sizeZ = sizeDoor.z - 2 * offsetMirror;
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    sizeZ = sizeDoor.x - 2 * offsetMirror;
                  }

                  model.position.x = backPos.x - 0.005;
                  model.position.y = backPos.y + offsetMirror;
                  model.position.z = backPos.z - offsetMirror;

                  model.scale.x = (sizeX * originalScale.x) / originalSize.x;
                  model.scale.y = (sizeY * originalScale.y) / originalSize.y;
                  model.scale.z = (sizeZ * originalScale.z) / originalSize.z;

                  model.userData.linkedDoorUuid = backEdge.mesh.uuid;
                  model.userData.handlePosition = {
                    current: backEdge.mesh.userData.handlePosition.current,
                  };

                  backEdge.mesh.userData.mirrorUuid = model.uuid;

                  resultData = addModelToCabinet();
                }
                break;

              case InsideType.ACCESSORY_FLIP: //Xan ngang
                if (rightEdge && leftEdge && topEdge && bottomEdge) {
                  model.userData.type = InsideType.ACCESSORY_FLIP;
                  this.setInfoModelChild(model);

                  const leftWorldPos = new THREE.Vector3();
                  const rightWorldPos = new THREE.Vector3();
                  const bottomWorldPos = new THREE.Vector3();
                  const topWorldPos = new THREE.Vector3();
                  leftEdge.mesh.getWorldPosition(leftWorldPos);
                  rightEdge.mesh.getWorldPosition(rightWorldPos);
                  bottomEdge.mesh.getWorldPosition(bottomWorldPos);
                  topEdge.mesh.getWorldPosition(topWorldPos);

                  let sizeZ, sizeX;

                  const posLocal = clonedModule.group.worldToLocal(
                    intersection.clone()
                  );
                  model.position.y = posLocal.y;

                  let positionZ = 0;

                  if (
                    orientation === 'NORMAL' ||
                    orientation === 'ROTATED_180'
                  ) {
                    //Scale Z
                    positionZ = leftPos.z - sizeLeft.z;

                    sizeZ =
                      Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;

                    //Scale X
                    sizeX =
                      Number(
                        clonedModule.formularController.currentDimensions.depth
                      ) -
                      (backEdge.mesh.position.x + sizeBack.x);

                    console.log('sizeZ', sizeZ);
                    console.log('sizeX', sizeX);
                  } else if (
                    orientation === 'ROTATED_90' ||
                    orientation === 'ROTATED_270'
                  ) {
                    // scale Z
                    positionZ = leftPos.z - sizeLeft.x;
                    sizeZ = Math.abs(
                      rightWorldPos.x - leftWorldPos.x - sizeLeft.x
                    );

                    //Scale X
                    sizeX =
                      Number(
                        clonedModule.formularController.currentDimensions.depth
                      ) -
                      (backEdge.mesh.position.x + sizeBack.z);
                  }

                  model.scale.z = sizeZ / originalSize.z;
                  model.position.x = Math.min(leftPos.x, rightPos.x);
                  model.position.z = positionZ;
                  // model.position.y = posLocal.y;
                  const sizeModel = this.getSize(model);
                  const deltaGap = (0.315 - sizeModel.y) / 2;
                  model.position.y = topWorldPos.y - 0.33 + deltaGap;
                  //// phu thuoc
                  model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
                  model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
                  model.userData.topEdgeUuid = topEdge.mesh.uuid;
                  model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;
                  ////
                  resultData = addModelToCabinet();

                  const sizeY = topWorldPos.y - bottomWorldPos.y - sizeBottom.y;

                  const numMesh = Math.floor(sizeY / 0.33);

                  if (numMesh > 1) {
                    for (let i = 1; i < numMesh; i++) {
                      const clone4 = model.clone();
                      this.setInfoModelChild(clone4);
                      clone4.position.y =
                        topWorldPos.y - 0.33 * (i + 1) + deltaGap;
                      addModelToCabinet(clone4);
                    }
                  }
                }
                break;

              case InsideType.DOOR: //Cửa tủ
                if (
                  rightEdge?.mesh &&
                  leftEdge?.mesh &&
                  backEdge?.mesh &&
                  bottomEdge?.mesh
                ) {
                  if (backEdge.mesh.userData?.type === InsideType.DOOR) {
                    openNotification({
                      key: 'cant-add-door-existing-door',
                      icon: <Icon.ICWarning />,
                      iconColor: '#F97D16',
                      title: i18n.t('Unable to add the product.'),
                      description:
                        'Vị trí đã có sản phẩm hoặc kích thước không phù hợp.',
                      placement: 'bottomRight',
                      closable: true,
                      duration: 2,
                    });
                    break;
                  }

                  model.userData.type = moduleData.type;
                  this.setInfoModelChild(model);

                  this.scaleDoor(
                    drawerClicked,
                    model,
                    {
                      bottomEdge,
                      rightEdge,
                      leftEdge,
                      backEdge,
                      topEdge,
                      rightEdgeDoor,
                      leftEdgeDoor,
                    },
                    moduleData,
                    clonedModule,
                    originalSize,
                    addModelToCabinet,
                    addBraceToCabinet,
                    handleData,
                    typeHau,
                    defaultLiftSupport
                  );
                  this.setInfoModelChild(model);
                }
                break;

              default:
                console.error('Invalid module type:', moduleData.type);
                break;
            }
            resolve(resultData);
          } catch (error) {
            reject(error);
          }
        },
        undefined,
        err => {
          reject(err);
        }
      );
    });
  };

  checkOverlapHauNgang(clonedModule, modelLocalPosY) {
    let isOverlap = false;
    clonedModule.group.traverse(child => {
      if (isOverlap) return;
      if (!child.isMesh) return;
      if (!child.name.includes('HAU-NGANG')) return;

      const size = this.getSize(child);
      const localPos = clonedModule.group.worldToLocal(
        child.getWorldPosition(new THREE.Vector3()).clone()
      );

      const yMin = localPos.y;
      const yMax = localPos.y + size.y;

      if (modelLocalPosY >= yMin && modelLocalPosY <= yMax) {
        isOverlap = true;
      }
    });
    return isOverlap;
  }

  adjustBackPanelForHPanel(module, myScene) {
    const sizeGroup = this.getSizeRotation(module.group, module.group);
    if (module.cabinetType.includes('corner')) {
      // module goc
      if (sizeGroup.z > 1.2 || sizeGroup.x > 1.2) {
        const backPanel2 = this.getMeshByNameOrIncludes(module.group, 'HAU-2');
        if (backPanel2) {
          // Danh sách các tấm đứng ảnh hưởng đến tấm hậu
          const affectingPanels = [];

          // Thêm BIA-TRAI (tấm ngoài cùng bên trái)
          const biaTraiMesh = this.getMeshByNameOrIncludes(
            module.group,
            'BIA-3'
          );
          if (biaTraiMesh) {
            affectingPanels.push({
              mesh: biaTraiMesh,
              type: 'BIA-3',
              position: biaTraiMesh
                .getWorldPosition(new THREE.Vector3())
                .clone(),
            });
          }

          // Duyệt qua các inside để tìm VPANEL thỏa mãn điều kiện
          module?.listInside.forEach(inside => {
            if (
              inside.type === InsideType.VPANEL ||
              inside.type === InsideType.VPANEL2
            ) {
              if (inside.gltf && inside.gltf.rotation.y === 0) {
                const topMesh = myScene.findGLTFByUuid(
                  inside.gltf.userData?.topEdgeUuid
                );
                const bottomMesh = myScene.findGLTFByUuid(
                  inside.gltf.userData?.bottomEdgeUuid
                );

                // Kiểm tra điều kiện: cả top và bottom đều không phải inside
                if (
                  topMesh &&
                  bottomMesh
                  // topMesh.userData?.isInside !== true &&
                  // bottomMesh.userData?.isInside !== true
                ) {
                  affectingPanels.push({
                    mesh: inside.gltf,
                    type: 'VPANEL',
                    position: inside.gltf
                      .getWorldPosition(new THREE.Vector3())
                      .clone(),
                  });
                }
              }
            }
          });

          // Thêm BIA-PHAI (tấm ngoài cùng bên phải)
          const biaPhaiMesh = this.getMeshByNameOrIncludes(
            module.group,
            'BIA-4'
          );
          if (biaPhaiMesh) {
            affectingPanels.push({
              mesh: biaPhaiMesh,
              type: 'BIA-4',
              position: biaPhaiMesh
                .getWorldPosition(new THREE.Vector3())
                .clone(),
            });
          }

          // Sắp xếp danh sách theo orientation
          const orientation = this.getModuleOrientation(module?.group);

          if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
            // Sort theo chiều Z (từ lớn đến nhỏ - reversed)
            affectingPanels.sort((a, b) => b.position.z - a.position.z);
          } else if (
            orientation === 'ROTATED_90' ||
            orientation === 'ROTATED_270'
          ) {
            // Sort theo chiều X (từ nhỏ đến lớn)
            affectingPanels.sort((a, b) => a.position.x - b.position.x);
          }

          // Tính khoảng cách giữa các panels
          const MAX_BACK_PANEL_SIZE = 1.2; // Khoảng cách lớn nhất để tạo tấm hậu
          const distances = [];

          for (let i = 0; i < affectingPanels.length - 1; i++) {
            const currentPanel = affectingPanels[i];
            const nextPanel = affectingPanels[i + 1];

            let distance;
            if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
              distance = Math.abs(
                currentPanel.position.z - nextPanel.position.z
              );
            } else {
              distance = Math.abs(
                currentPanel.position.x - nextPanel.position.x
              );
            }

            distances.push({
              fromIndex: i,
              toIndex: i + 1,
              distance: distance,
              fromPanel: currentPanel,
              toPanel: nextPanel,
            });
          }

          // Tạo các nhóm tấm hậu dựa trên khoảng cách
          const backPanelGroups = [];
          let currentGroup = {
            startIndex: 0,
            endIndex: 0,
            totalDistance: 0,
            panels: [affectingPanels[0]],
          };

          for (let i = 0; i < distances.length; i++) {
            const dist = distances[i];
            const potentialTotalDistance =
              currentGroup.totalDistance + dist.distance;

            // Nếu tổng khoảng cách vượt quá MAX hoặc là panel cuối cùng
            if (potentialTotalDistance > MAX_BACK_PANEL_SIZE) {
              // Lưu nhóm hiện tại
              currentGroup.panels.push(dist.fromPanel);
              backPanelGroups.push(currentGroup);

              // Bắt đầu nhóm mới
              currentGroup = {
                startIndex: dist.toIndex,
                endIndex: dist.toIndex,
                totalDistance: dist.distance,
                panels: [dist.fromPanel],
              };
            } else {
              // Thêm vào nhóm hiện tại
              currentGroup.endIndex = dist.toIndex;
              currentGroup.totalDistance = potentialTotalDistance;
              // currentGroup.panels.push(dist.toPanel);
            }
            if (i === distances.length - 1) {
              currentGroup.panels.push(dist.toPanel);
            }
          }

          // Thêm nhóm cuối cùng
          backPanelGroups.push(currentGroup);

          // Tính size xyz cho từng tấm hậu và gán vào userData
          const backPanelSizes = backPanelGroups.map((group, index) => {
            const firstPanel = group.panels[0];
            const lastPanel = group.panels[group.panels.length - 1];

            // Lấy size của backPanel gốc để lấy chiều cao và độ dày
            const originalBackPanelSize = this.getSize(backPanel2);

            let sizeData;
            if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
              let width = Math.abs(
                lastPanel.position.z - firstPanel.position.z
              );
              if (lastPanel.mesh.userData.isInside !== true) {
                width += 0.017 / 2;
              }
              if (firstPanel.mesh.userData.isInside !== true) {
                width += 0.017 / 2;
              }
              sizeData = {
                x: originalBackPanelSize.x, // Độ dày
                y: originalBackPanelSize.y, // Chiều cao
                z: width, // Chiều rộng theo trục Z
                groupIndex: index,
                startIndex: group.startIndex,
                endIndex: group.endIndex,
                panelCount: group.panels.length,
              };
            } else {
              let width = Math.abs(
                lastPanel?.position.x - firstPanel?.position.x
              );
              if (lastPanel?.mesh.userData.isInside !== true) {
                width += 0.017 / 2;
              }
              if (firstPanel?.mesh.userData.isInside !== true) {
                width += 0.017 / 2;
              }
              sizeData = {
                x: width, // Chiều rộng theo trục X
                y: originalBackPanelSize.y, // Chiều cao
                z: originalBackPanelSize.z, // Độ dày
                groupIndex: index,
                startIndex: group.startIndex,
                endIndex: group.endIndex,
                panelCount: group.panels.length,
              };
            }

            return sizeData;
          });

          // Gán vào userData của backPanel
          if (!backPanel2.userData) {
            backPanel2.userData = {};
          }
          backPanel2.userData.backPanelSizes = backPanelSizes;

          // return {
          //   affectingPanels,
          //   distances,
          //   backPanelGroups,
          //   backPanelSizes,
          // };
        }
        const backPanel1 = this.getMeshByNameOrIncludes(module.group, 'HAU-1');
        if (backPanel1) {
          // Danh sách các tấm đứng ảnh hưởng đến tấm hậu
          const affectingPanels = [];

          // Thêm BIA-TRAI (tấm ngoài cùng bên trái)
          const biaTraiMesh = this.getMeshByNameOrIncludes(
            module.group,
            'BIA-1'
          );
          if (biaTraiMesh) {
            affectingPanels.push({
              mesh: biaTraiMesh,
              type: 'BIA-1',
              position: biaTraiMesh
                .getWorldPosition(new THREE.Vector3())
                .clone(),
            });
          }

          // Duyệt qua các inside để tìm VPANEL thỏa mãn điều kiện
          module?.listInside.forEach(inside => {
            if (
              inside.type === InsideType.VPANEL ||
              inside.type === InsideType.VPANEL2
            ) {
              if (inside.gltf && inside.gltf.rotation.y !== 0) {
                const topMesh = myScene.findGLTFByUuid(
                  inside.gltf.userData?.topEdgeUuid
                );
                const bottomMesh = myScene.findGLTFByUuid(
                  inside.gltf.userData?.bottomEdgeUuid
                );

                // Kiểm tra điều kiện: cả top và bottom đều không phải inside
                if (
                  topMesh &&
                  bottomMesh
                  // topMesh.userData?.isInside !== true &&
                  // bottomMesh.userData?.isInside !== true
                ) {
                  affectingPanels.push({
                    mesh: inside.gltf,
                    type: 'VPANEL',
                    position: inside.gltf
                      .getWorldPosition(new THREE.Vector3())
                      .clone(),
                  });
                }
              }
            }
          });

          // Thêm BIA-PHAI (tấm ngoài cùng bên phải)
          const biaPhaiMesh = this.getMeshByNameOrIncludes(
            module.group,
            'BIA-2'
          );
          if (biaPhaiMesh) {
            affectingPanels.push({
              mesh: biaPhaiMesh,
              type: 'BIA-2',
              position: biaPhaiMesh
                .getWorldPosition(new THREE.Vector3())
                .clone(),
            });
          }

          // Sắp xếp danh sách theo orientation
          const orientation = this.getModuleOrientation(module?.group);

          if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
            // Sort theo chiều Z (từ lớn đến nhỏ - reversed)
            affectingPanels.sort((a, b) => a.position.x - b.position.x);
          } else if (
            orientation === 'ROTATED_90' ||
            orientation === 'ROTATED_270'
          ) {
            // Sort theo chiều X (từ nhỏ đến lớn)
            affectingPanels.sort((a, b) => b.position.z - a.position.z);
          }

          // Tính khoảng cách giữa các panels
          const MAX_BACK_PANEL_SIZE = 1.2; // Khoảng cách lớn nhất để tạo tấm hậu
          const distances = [];

          for (let i = 0; i < affectingPanels.length - 1; i++) {
            const currentPanel = affectingPanels[i];
            const nextPanel = affectingPanels[i + 1];

            let distance;
            if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
              distance = Math.abs(
                currentPanel.position.x - nextPanel.position.x
              );
            } else {
              distance = Math.abs(
                currentPanel.position.z - nextPanel.position.z
              );
            }

            distances.push({
              fromIndex: i,
              toIndex: i + 1,
              distance: distance,
              fromPanel: currentPanel,
              toPanel: nextPanel,
            });
          }

          // Tạo các nhóm tấm hậu dựa trên khoảng cách
          const backPanelGroups = [];
          let currentGroup = {
            startIndex: 0,
            endIndex: 0,
            totalDistance: 0,
            panels: [affectingPanels[0]],
          };

          for (let i = 0; i < distances.length; i++) {
            const dist = distances[i];
            const potentialTotalDistance =
              currentGroup.totalDistance + dist.distance;

            // Nếu tổng khoảng cách vượt quá MAX hoặc là panel cuối cùng
            if (potentialTotalDistance > MAX_BACK_PANEL_SIZE) {
              // Lưu nhóm hiện tại
              currentGroup.panels.push(dist.fromPanel);
              backPanelGroups.push(currentGroup);

              // Bắt đầu nhóm mới
              currentGroup = {
                startIndex: dist.toIndex,
                endIndex: dist.toIndex,
                totalDistance: dist.distance,
                panels: [dist.fromPanel],
              };
            } else {
              // Thêm vào nhóm hiện tại
              currentGroup.endIndex = dist.toIndex;
              currentGroup.totalDistance = potentialTotalDistance;
              // currentGroup.panels.push(dist.toPanel);
            }
            if (i === distances.length - 1) {
              currentGroup.panels.push(dist.toPanel);
            }
          }

          // Thêm nhóm cuối cùng
          backPanelGroups.push(currentGroup);

          // Tính size xyz cho từng tấm hậu và gán vào userData
          const backPanelSizes = backPanelGroups.map((group, index) => {
            const firstPanel = group.panels[0];
            const lastPanel = group.panels[group.panels.length - 1];

            // Lấy size của backPanel gốc để lấy chiều cao và độ dày
            const originalBackPanelSize = this.getSize(backPanel1);

            let sizeData;
            if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
              let width = Math.abs(
                lastPanel.position.x - firstPanel.position.x
              );
              if (lastPanel.mesh.userData.isInside !== true) {
                width += 0.017 / 2;
              }
              if (firstPanel.mesh.userData.isInside !== true) {
                width += 0.017 / 2;
              }
              sizeData = {
                x: Math.min(originalBackPanelSize.x, originalBackPanelSize.z), // Độ dày
                y: originalBackPanelSize.y, // Chiều cao
                z: width, // Chiều rộng theo trục Z
                groupIndex: index,
                startIndex: group.startIndex,
                endIndex: group.endIndex,
                panelCount: group.panels.length,
              };
            } else {
              let width = Math.abs(
                lastPanel.position.z - firstPanel.position.z
              );
              if (lastPanel.mesh.userData.isInside !== true) {
                width += 0.017 / 2;
              }
              if (firstPanel.mesh.userData.isInside !== true) {
                width += 0.017 / 2;
              }
              sizeData = {
                x: width, // Chiều rộng theo trục X
                y: originalBackPanelSize.y, // Chiều cao
                z: Math.min(originalBackPanelSize.x, originalBackPanelSize.z), // Độ dày
                groupIndex: index,
                startIndex: group.startIndex,
                endIndex: group.endIndex,
                panelCount: group.panels.length,
              };
            }

            return sizeData;
          });

          // Gán vào userData của backPanel
          if (!backPanel1.userData) {
            backPanel1.userData = {};
          }
          backPanel1.userData.backPanelSizes = backPanelSizes;

          return {
            affectingPanels,
            distances,
            backPanelGroups,
            backPanelSizes,
          };
        }
      }
    } else {
      // module thuong
      if (sizeGroup.z > 1.2) {
        const backPanel = this.getMeshByName(module.group, 'HAU');
        if (backPanel) {
          // Danh sách các tấm đứng ảnh hưởng đến tấm hậu
          const affectingPanels = [];

          // Thêm BIA-TRAI (tấm ngoài cùng bên trái)
          const biaTraiMesh = this.getMeshByName(module.group, 'BIA-TRAI');
          if (biaTraiMesh) {
            affectingPanels.push({
              mesh: biaTraiMesh,
              type: 'BIA-TRAI',
              position: biaTraiMesh
                .getWorldPosition(new THREE.Vector3())
                .clone(),
            });
          }

          // Duyệt qua các inside để tìm VPANEL thỏa mãn điều kiện
          module?.listInside.forEach(inside => {
            if (
              inside.type === InsideType.VPANEL ||
              inside.type === InsideType.VPANEL2
            ) {
              if (inside.gltf) {
                const topMesh = myScene.findGLTFByUuid(
                  inside.gltf.userData?.topEdgeUuid
                );
                const bottomMesh = myScene.findGLTFByUuid(
                  inside.gltf.userData?.bottomEdgeUuid
                );

                // Kiểm tra điều kiện: cả top và bottom đều không phải inside
                if (
                  topMesh &&
                  bottomMesh
                  // topMesh.userData?.isInside !== true &&
                  // bottomMesh.userData?.isInside !== true
                ) {
                  affectingPanels.push({
                    mesh: inside.gltf,
                    type: 'VPANEL',
                    position: inside.gltf
                      .getWorldPosition(new THREE.Vector3())
                      .clone(),
                  });
                }
              }
            }
          });

          // Thêm BIA-PHAI (tấm ngoài cùng bên phải)
          const biaPhaiMesh = this.getMeshByName(module.group, 'BIA-PHAI');
          if (biaPhaiMesh) {
            affectingPanels.push({
              mesh: biaPhaiMesh,
              type: 'BIA-PHAI',
              position: biaPhaiMesh
                .getWorldPosition(new THREE.Vector3())
                .clone(),
            });
          }

          // Sắp xếp danh sách theo orientation
          const orientation = this.getModuleOrientation(module?.group);

          if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
            // Sort theo chiều Z (từ lớn đến nhỏ - reversed)
            affectingPanels.sort((a, b) => b.position.z - a.position.z);
          } else if (
            orientation === 'ROTATED_90' ||
            orientation === 'ROTATED_270'
          ) {
            // Sort theo chiều X (từ nhỏ đến lớn)
            affectingPanels.sort((a, b) => a.position.x - b.position.x);
          }

          // Tính khoảng cách giữa các panels
          const MAX_BACK_PANEL_SIZE = 1.2; // Khoảng cách lớn nhất để tạo tấm hậu
          const distances = [];

          for (let i = 0; i < affectingPanels.length - 1; i++) {
            const currentPanel = affectingPanels[i];
            const nextPanel = affectingPanels[i + 1];

            let distance;
            if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
              distance = Math.abs(
                currentPanel.position.z - nextPanel.position.z
              );
            } else {
              distance = Math.abs(
                currentPanel.position.x - nextPanel.position.x
              );
            }

            distances.push({
              fromIndex: i,
              toIndex: i + 1,
              distance: distance,
              fromPanel: currentPanel,
              toPanel: nextPanel,
            });
          }

          // Tạo các nhóm tấm hậu dựa trên khoảng cách
          const backPanelGroups = [];
          let currentGroup = {
            startIndex: 0,
            endIndex: 0,
            totalDistance: 0,
            panels: [affectingPanels[0]],
          };

          for (let i = 0; i < distances.length; i++) {
            const dist = distances[i];
            const potentialTotalDistance =
              currentGroup.totalDistance + dist.distance;

            // Nếu tổng khoảng cách vượt quá MAX hoặc là panel cuối cùng
            if (potentialTotalDistance > MAX_BACK_PANEL_SIZE) {
              // Lưu nhóm hiện tại
              currentGroup.panels.push(dist.fromPanel);
              backPanelGroups.push(currentGroup);

              // Bắt đầu nhóm mới
              currentGroup = {
                startIndex: dist.toIndex,
                endIndex: dist.toIndex,
                totalDistance: dist.distance,
                panels: [dist.fromPanel],
              };
            } else {
              // Thêm vào nhóm hiện tại
              currentGroup.endIndex = dist.toIndex;
              currentGroup.totalDistance = potentialTotalDistance;
              // currentGroup.panels.push(dist.toPanel);
            }
            if (i === distances.length - 1) {
              currentGroup.panels.push(dist.toPanel);
            }
          }

          // Thêm nhóm cuối cùng
          backPanelGroups.push(currentGroup);

          // Tính size xyz cho từng tấm hậu và gán vào userData
          const backPanelSizes = backPanelGroups.map((group, index) => {
            const firstPanel = group.panels[0];
            const lastPanel = group.panels[group.panels.length - 1];

            // Lấy size của backPanel gốc để lấy chiều cao và độ dày
            const originalBackPanelSize = this.getSize(backPanel);

            let sizeData;
            if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
              let width = Math.abs(
                lastPanel.position.z - firstPanel.position.z
              );
              if (lastPanel.mesh.userData.isInside !== true) {
                width += 0.017 / 2;
              }
              if (firstPanel.mesh.userData.isInside !== true) {
                width += 0.017 / 2;
              }
              sizeData = {
                x: originalBackPanelSize.x, // Độ dày
                y: originalBackPanelSize.y, // Chiều cao
                z: width, // Chiều rộng theo trục Z
                groupIndex: index,
                startIndex: group.startIndex,
                endIndex: group.endIndex,
                panelCount: group.panels.length,
              };
            } else {
              let width = Math.abs(
                lastPanel.position.x - firstPanel.position.x
              );
              if (lastPanel.mesh.userData.isInside !== true) {
                width += 0.017 / 2;
              }
              if (firstPanel.mesh.userData.isInside !== true) {
                width += 0.017 / 2;
              }
              sizeData = {
                x: width, // Chiều rộng theo trục X
                y: originalBackPanelSize.y, // Chiều cao
                z: originalBackPanelSize.z, // Độ dày
                groupIndex: index,
                startIndex: group.startIndex,
                endIndex: group.endIndex,
                panelCount: group.panels.length,
              };
            }

            return sizeData;
          });

          // Gán vào userData của backPanel
          if (!backPanel.userData) {
            backPanel.userData = {};
          }
          backPanel.userData.backPanelSizes = backPanelSizes;

          return {
            affectingPanels,
            distances,
            backPanelGroups,
            backPanelSizes,
          };
        }
      }
    }

    return null;
  }

  removeAllChildUDrawers(module, gltfScene) {
    if (!module || !module.listInside || !module.group) {
      console.warn('Invalid module for removing child drawers');
      return;
    }

    // BƯỚC 1: Tìm tất cả drawer con cần xóa
    const drawersToRemove = [];

    module.listInside.forEach((inside, index) => {
      // Kiểm tra xem inside có phải là drawer con không
      if (
        inside.gltf?.userData?.isChildUDrawer === true &&
        inside.gltf.userData?.parentUDrawerUuid === gltfScene.uuid
      ) {
        drawersToRemove.push({
          inside: inside,
          index: index,
          gltf: inside.gltf,
        });
      }
    });

    console.log(`Found ${drawersToRemove.length} child drawers to remove`);

    // BƯỚC 2: Xóa từ scene 3D (từ cuối về đầu để không ảnh hưởng index)
    drawersToRemove.forEach(item => {
      try {
        // Xóa khỏi group
        if (item.gltf && module.group) {
          module.group.remove(item.gltf);

          // Dispose geometry và material để giải phóng bộ nhớ
          item.gltf.traverse(child => {
            if (child.geometry) {
              child.geometry.dispose();
            }
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(mat => mat.dispose());
              } else {
                child.material.dispose();
              }
            }
          });
        }
      } catch (error) {
        console.error('Error removing drawer from scene:', error);
      }
    });

    // BƯỚC 3: Xóa khỏi listInside (filter để tạo mảng mới)
    const removeUuids = drawersToRemove.map(item => item.gltf.uuid);
    module.listInside = module.listInside.filter(
      inside => !removeUuids.includes(inside.gltf.uuid)
    );

    console.log(
      `Removed all child drawers. Remaining inside items: ${module.listInside.length}`
    );
  }

  async scaleUDrawerHeight(module, uDrawerMesh, newHeight) {
    if (!module || !uDrawerMesh) {
      console.error('Invalid parameters for scaleUDrawerHeight');
      throw new Error('Module hoặc U-Drawer mesh không hợp lệ');
    }

    try {
      // BƯỚC 1: Lấy thông tin hiện tại
      const currentInfo = uDrawerMesh.userData.infoChildDrawer || {};
      const numDrawer = currentInfo.numDrawer || 2;
      const bottomGap = currentInfo.bottomGap || 0;
      const topGap = currentInfo.topGap || 0;
      const sizeXDrawerChild = currentInfo.sizeXDrawerChild || 0.45;

      // Lấy size hiện tại
      const currentSize = GeometryUtils.getSizeRotation(
        module.group,
        uDrawerMesh
      );
      const oldHeight = currentSize.y;

      // BƯỚC 2: Scale U-Drawer mesh theo chiều cao mới
      InsideCabinetHandler.scaleHeight(uDrawerMesh, newHeight);
      uDrawerMesh.updateMatrixWorld(true);

      // BƯỚC 6: Lấy orientation và size mới
      const orientation = GeometryUtils.getModuleOrientation(module.group);
      const updatedSize = GeometryUtils.getSizeRotation(
        module.group,
        uDrawerMesh
      );

      // BƯỚC 7: Gọi settingUDrawer để tạo lại các drawer con với chiều cao mới
      await this.settingUDrawer(
        module,
        sizeXDrawerChild,
        numDrawer,
        bottomGap,
        topGap,
        updatedSize,
        uDrawerMesh,
        orientation
      );

      return true;
    } catch (error) {
      console.error('Error scaling U-Drawer height:', error);
      throw error;
    }
  }

  async fetchDrawerModule(moduleId) {
    try {
      const response = await callApi({
        endpoint: `modules/v3/get_item_detail/${moduleId}`,
        method: 'GET',
      });

      if (!response?.data?.success) {
        throw new Error('API không trả về dữ liệu hợp lệ');
      }

      return response.data.result?.data || response.data.data;
    } catch (error) {
      console.error('Error fetching drawer module:', error);
      throw error;
    }
  }

  settingUDrawer(
    clonedModule,
    sizeXDrawerChild,
    numDrawer,
    bottomGap,
    topGap,
    meshSize,
    gltfScene,
    orientation
  ) {
    return new Promise(async (resolve, reject) => {
      this.removeAllChildUDrawers(clonedModule, gltfScene);
      const infoChildDrawer = {
        numDrawer: numDrawer,
        bottomGap: bottomGap,
        topGap: topGap,
        sizeXDrawerChild: sizeXDrawerChild,
      };
      gltfScene.userData.infoChildDrawer = infoChildDrawer;

      const childModel = gltfScene.userData.child;

      const module = await this.fetchDrawerModule(childModel);

      const addModelToCabinet = model => {
        const data = {
          _id: module.id,
          name: module.name,
          glb: module.model,
          type: module.type,
          thumbnail_url: module?.thumbnail_url ?? module?.icon,
          gltfUuid: model.uuid,
          gltf: model,
        };
        this.setInfoModelChild(model);
        clonedModule.listInside.push(data);
        clonedModule.group.add(model);
      };

      const loader = new GLTFLoader();

      loader.load(
        module.model,
        async gltf => {
          try {
            const model = gltf.scene;
            model.userData.type = InsideType.DRAWER;
            model.userData.isChildUDrawer = true;
            model.userData.parentUDrawerUuid = gltfScene.uuid;
            addModelToCabinet(model);
            const sizeDefault = this.getSize(model);

            // Đợi settingUDrawerChild hoàn thành
            await InsideCabinetHandler.settingUDrawerChild(
              clonedModule,
              sizeDefault,
              gltfScene,
              model,
              module,
              orientation,
              addModelToCabinet
            );

            // Gọi lại hàm load texture cho hộc tủ
            setTimeout(async () => {
              await this.scene.textureController.reloadTexture({
                module: clonedModule,
              });
            }, 100);

            // QUAN TRỌNG: resolve sau khi tất cả hoàn thành
            resolve();
          } catch (error) {
            reject(error);
          }
        },
        undefined,
        error => {
          // Xử lý lỗi khi load model
          reject(error);
        }
      );
    });
  }

  loadLiftSupportModel = (
    liftSupports,
    clonedModule,
    parentModel,
    addModelToCabinet
  ) => {
    return new Promise((resolve, reject) => {
      const loader = new GLTFLoader();

      loader.load(
        '/assets/glb/LIFT-SUPPORT.glb',
        gltf => {
          let model;

          gltf.scene?.traverse(child => {
            if (child.isMesh) {
              model = child;
            }
          });

          model.scale.set(0.0001, 0.0001, 0.0001);
          const box = new THREE.Box3().setFromObject(parentModel);
          const center = new THREE.Vector3();
          box.getCenter(center);

          // model.position.copy(center);
          model.position.set(0, 0.05, 0);

          const parentSize = this.getSize(parentModel);

          if (parentModel.userData.liftSupportUuid) {
            //Thay thế tay nâng cũ
            const oldLiftSupport = this.findMeshByUuid(
              clonedModule.group,
              parentModel.userData.liftSupportUuid
            );

            this.removeInside(oldLiftSupport, clonedModule);
          }

          model.userData.isInside = true;
          model.userData.type = InsideType.LIFT_SUPPORT;
          model.userData.typeHau = parentModel.userData?.typeHau;
          model.userData.doorUuid = parentModel.uuid;
          model.userData.linkedDoorUuid = parentModel.uuid;

          model.userData.notCollision = true;

          parentModel.userData.liftSupportUuid = model.uuid;

          const addDataToListInside = dt => {
            const data = {
              _id: dt.id,
              glb: '/assets/glb/LIFT-SUPPORT.glb',
              type: InsideType.LIFT_SUPPORT,
              name: dt.name,
              price: dt.price,
              material: dt.material,
              thumbnail_url: dt.thumbnail_url,
              gltfUuid: model.uuid,
              gltf: model,
            };

            clonedModule.listInside.push(data);

            clonedModule.group.add(model);
          };

          if (parentModel?.userData?.horizontalType === HorizontalType.ONE_D) {
            //Của ti hơi
            if (Math.max(parentSize.x, parentSize.z) < 0.8) {
              addDataToListInside(liftSupports[HorizontalType.ONE_D][0]);
            } else {
              addDataToListInside(liftSupports[HorizontalType.ONE_D][1]);
            }
          } else {
            //Cửa tay nâng
            if (parentSize.y <= 0.9) {
              addDataToListInside(liftSupports[HorizontalType.TWO_D][0]);
            } else if (parentSize.y > 0.9 && parentSize.y <= 1.05) {
              addDataToListInside(liftSupports[HorizontalType.TWO_D][2]);
            } else {
              openNotification({
                key: 'door-height',
                icon: <Icon.ICWarning />,
                iconColor: '#F97D16',
                title: i18n.t('Unable to add the product.'),
                description: 'Kích thước cửa quá lớn.',
                placement: 'bottomRight',
                closable: true,
                duration: 2,
              });
            }
          }

          resolve(model.uuid);
          return model.uuid;
        },
        undefined,
        err => {
          reject(err);
        }
      );
    });
  };

  loadHandleModel = (
    moduleData,
    clonedModule,
    parentModel,
    addModelToCabinet,
    layer
  ) => {
    return new Promise((resolve, reject) => {
      const loader = new GLTFLoader();
      if (!moduleData?.model) return;

      loader.load(
        moduleData.model,
        gltf => {
          let model;

          gltf.scene?.traverse(child => {
            if (child.isMesh) {
              model = child;
            }
          });

          if (parentModel.userData?.materialType?.includes('nhom_kinh')) {
            parentModel.traverse(object => {
              if (object.name === 'TAY-NAM-NHOM-KINH') {
                model = object;
              }
            });

            resolve();
            return;
          }

          const orientation = this.getModuleOrientation(clonedModule.group);

          const parentPos = clonedModule.group.worldToLocal(
            parentModel.getWorldPosition(new THREE.Vector3()).clone()
          );

          const parentSize = this.getSize(parentModel);
          const handleSize = this.getSize(model);

          model.position.copy(parentModel.position);

          // if (parentModel.rotation.y !== 0) {
          //   model.rotation.copy(parentModel.rotation);
          //   model.rotation.y += Math.PI;
          // }

          //Vị trí theo độ cao (chung)
          if (
            parentModel.position.y >
            handleYRcm - handleSize.y / 2 - handlePadding
          ) {
            model.position.y =
              parentModel.position.y + handlePadding + handleSize.y / 2;
          } else if (
            parentModel.position.y + parentSize.y <
            handleYRcm + handleSize.y / 2 + handlePadding
          ) {
            model.position.y =
              parentModel.position.y +
              parentSize.y -
              handlePadding -
              handleSize.y / 2;
          } else {
            model.position.y = handleYRcm;
          }

          //Set lại độ cao của tay nắm nếu nó không thuộc tủ dưới
          if (!clonedModule?.moduleType?.includes('lower')) {
            model.position.y = parentPos.y + handlePadding + handleSize.y / 2;
          }

          //Đối với tủ TV vị trí tay nắm nằm giữa cửa theo chiều cao
          if (clonedModule?.moduleType?.includes('tv')) {
            model.position.y = parentPos.y + parentSize.y / 2;
          }

          //Vị trí theo phương ngang xử lý luôn cho hộc kéo
          if (parentModel.userData.cornerDoor) {
            if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
              switch (parentModel.userData.cornerDoor.toUpperCase()) {
                case 'DOOR-1':
                  model.rotation.y = Math.PI / 2;
                  model.position.x = parentPos.x - parentSize.x + handlePadding;
                  model.position.z = parentPos.z - parentSize.z;
                  break;

                case 'DOOR-2':
                  model.rotation.y = Math.PI / 2;
                  model.position.x = parentPos.x - handlePadding;
                  model.position.z = parentPos.z - parentSize.z;
                  break;

                case 'DOOR-3':
                  model.position.x = parentPos.x + parentSize.x;
                  model.position.z = parentPos.z - parentSize.z + handlePadding;
                  break;

                case 'DOOR-4':
                  model.position.x = parentPos.x + parentSize.x;
                  model.position.z = parentPos.z - handlePadding;
                  break;

                default:
                  console.warn(
                    'Unknown corner door type:',
                    parentModel.userData.cornerDoor
                  );
                  break;
              }
            } else if (
              orientation === 'ROTATED_90' ||
              orientation === 'ROTATED_270'
            ) {
              switch (parentModel.userData.cornerDoor.toUpperCase()) {
                case 'DOOR-1':
                  model.rotation.y = Math.PI / 2;
                  model.position.x = parentPos.x - parentSize.z + handlePadding;
                  model.position.z = parentPos.z - parentSize.x;
                  break;

                case 'DOOR-2':
                  model.rotation.y = Math.PI / 2;
                  model.position.x = parentPos.x - handlePadding;
                  model.position.z = parentPos.z - parentSize.x;
                  break;

                case 'DOOR-3':
                  model.position.x = parentPos.x + parentSize.z;
                  model.position.z = parentPos.z - parentSize.x + handlePadding;
                  break;

                case 'DOOR-4':
                  model.position.x = parentPos.x + parentSize.z;
                  model.position.z = parentPos.z - handlePadding;
                  break;

                default:
                  console.warn(
                    'Unknown corner door type:',
                    parentModel.userData.cornerDoor
                  );
                  break;
              }
            }
          } else if (parentModel.userData.typeHau === TypeConnerModule.SUB) {
            switch (orientation) {
              case 'NORMAL':
              case 'ROTATED_180': {
                model.position.z -= parentSize.z;

                if (parentModel.userData.drawerUuid) {
                  model.rotation.x = Math.PI / 2;
                  model.rotation.z = Math.PI / 2;

                  model.position.x = parentPos.x - parentSize.x / 2;

                  if (parentModel.userData.materialType.includes('tron')) {
                    model.position.y = parentPos.y + parentSize.y / 2;
                  } else {
                    model.position.y =
                      parentPos.y + parentSize.y - handlePadding;
                  }
                } else if (
                  parentModel.userData.doorDirection ===
                  DoorDirection.HORIZONTAL
                ) {
                  model.rotation.x = Math.PI / 2;
                  model.rotation.z = Math.PI / 2;

                  model.position.x = parentPos.x - parentSize.x / 2;

                  model.position.y = parentPos.y + handlePadding;
                } else {
                  if (parentModel.rotation.y !== 0) {
                    model.rotation.copy(parentModel.rotation);
                    model.rotation.y += Math.PI;
                  }

                  if (!parentModel.userData.handlePosition) {
                    return;
                  }

                  if (
                    parentModel.userData.handlePosition.left &&
                    parentModel.userData.handlePosition.current === 'left'
                  ) {
                    model.position.x = parentPos.x - handlePadding;
                  } else if (
                    parentModel.userData.handlePosition.right &&
                    parentModel.userData.handlePosition.current === 'right'
                  ) {
                    model.position.x =
                      parentPos.x - (parentSize.x - handlePadding);
                  } else {
                    openMessageError({
                      content: i18n.t(
                        'No suitable position found for the handle'
                      ),
                    });
                  }
                }
                break;
              }

              case 'ROTATED_90':
              case 'ROTATED_270': {
                model.position.z -= parentSize.x;

                if (parentModel.userData.drawerUuid) {
                  model.rotation.x = Math.PI / 2;
                  model.rotation.z = Math.PI / 2;

                  model.position.x = parentPos.x - parentSize.z / 2;

                  if (parentModel.userData.materialType.includes('tron')) {
                    model.position.y = parentPos.y + parentSize.y / 2;
                  } else {
                    model.position.y =
                      parentPos.y + parentSize.y - handlePadding;
                  }
                } else if (
                  parentModel.userData.doorDirection ===
                  DoorDirection.HORIZONTAL
                ) {
                  model.rotation.x = Math.PI / 2;
                  model.rotation.z = Math.PI / 2;

                  model.position.x = parentPos.x - parentSize.z / 2;

                  model.position.y = parentPos.y + handlePadding;
                } else {
                  if (parentModel.rotation.y !== 0) {
                    model.rotation.copy(parentModel.rotation);
                    model.rotation.y += Math.PI;
                  }

                  if (!parentModel.userData.handlePosition) {
                    return;
                  }

                  if (
                    parentModel.userData.handlePosition.left &&
                    parentModel.userData.handlePosition.current === 'left'
                  ) {
                    model.position.x = parentPos.x - handlePadding;
                  } else if (
                    parentModel.userData.handlePosition.right &&
                    parentModel.userData.handlePosition.current === 'right'
                  ) {
                    model.position.x =
                      parentPos.x - (parentSize.z - handlePadding);
                  } else {
                    openMessageError({
                      content: i18n.t(
                        'No suitable position found for the handle'
                      ),
                    });
                  }
                }
                break;
              }
            }
          } else {
            if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
              model.position.x += parentSize.x;

              if (parentModel.userData.drawerUuid) {
                model.rotation.x = Math.PI / 2;

                model.position.z = parentPos.z - parentSize.z / 2;

                if (parentModel.userData.materialType.includes('tron')) {
                  model.position.y = parentPos.y + parentSize.y / 2;
                } else {
                  model.position.y = parentPos.y + parentSize.y - handlePadding;
                }
              } else if (
                parentModel.userData.doorDirection === DoorDirection.HORIZONTAL
              ) {
                model.rotation.x = Math.PI / 2;

                model.position.z = parentPos.z - parentSize.z / 2;

                model.position.y = parentPos.y + handlePadding;
              } else {
                if (!parentModel.userData.handlePosition) {
                  return;
                }

                if (
                  parentModel.userData.handlePosition.left &&
                  parentModel.userData.handlePosition.current === 'left'
                ) {
                  model.position.z = parentPos.z - handlePadding;
                } else if (
                  parentModel.userData.handlePosition.right &&
                  parentModel.userData.handlePosition.current === 'right'
                ) {
                  model.position.z =
                    parentPos.z - (parentSize.z - handlePadding);
                } else {
                  openMessageError({
                    content: i18n.t(
                      'No suitable position found for the handle'
                    ),
                  });
                }
              }
            } else if (
              orientation === 'ROTATED_90' ||
              orientation === 'ROTATED_270'
            ) {
              model.position.x += parentSize.z;

              if (parentModel.userData.drawerUuid) {
                model.rotation.x = Math.PI / 2;

                model.position.z = parentPos.z - parentSize.x / 2;

                if (parentModel.userData.materialType.includes('tron')) {
                  model.position.y = parentPos.y + parentSize.y / 2;
                } else {
                  model.position.y = parentPos.y + parentSize.y - handlePadding;
                }
              } else if (
                parentModel.userData.doorDirection === DoorDirection.HORIZONTAL
              ) {
                model.rotation.x = Math.PI / 2;

                model.position.z = parentPos.z - parentSize.x / 2;

                model.position.y = parentPos.y + handlePadding;
              } else {
                if (!parentModel.userData.handlePosition) {
                  return;
                }

                if (
                  parentModel.userData.handlePosition.left &&
                  parentModel.userData.handlePosition.current === 'left'
                ) {
                  model.position.z = parentPos.z - handlePadding;
                } else if (
                  parentModel.userData.handlePosition.right &&
                  parentModel.userData.handlePosition.current === 'right'
                ) {
                  model.position.z =
                    parentPos.z - (parentSize.x - handlePadding);
                } else {
                  openMessageError({
                    content: i18n.t(
                      'No suitable position found for the handle'
                    ),
                  });
                }
              }
            }
          }
          //Vị trí theo phương ngang

          if (parentModel.userData.handleUuid) {
            //Thay thế tay nắm cũ
            const oldHandle = this.findMeshByUuid(
              clonedModule.group,
              parentModel.userData.handleUuid
            );

            this.removeInside(oldHandle, clonedModule);
          }

          model.userData.typeHau = parentModel.userData?.typeHau;
          model.userData.doorUuid = parentModel.uuid;
          model.userData.type = InsideType.HANDLE;
          model.userData.thumbnail_url =
            moduleData.thumbnail_url ?? moduleData.icon;

          model.userData.id = moduleData.id;
          model.userData.price = moduleData.price;
          model.userData.material = moduleData.material;
          model.userData.name = moduleData.name;
          model.userData.linkedDoorUuid = parentModel.uuid;
          model.userData.layer = layer ?? 0;
          parentModel.userData.handleUuid = model.uuid;

          addModelToCabinet(model);
          return model.uuid;
        },
        undefined,
        err => {
          reject(err);
        }
      );
    });
  };

  loadLockModel = (
    moduleData,
    clonedModule,
    parentModel,
    addModelToCabinet
  ) => {
    return new Promise((resolve, reject) => {
      const loader = new GLTFLoader();
      if (!moduleData?.model) return;

      loader.load(
        moduleData.model,
        gltf => {
          const model = gltf.scene;
          // let model;

          // gltf.scene?.traverse(child => {
          //   if (child.isMesh) {
          //     model = child;
          //   }
          // });

          if (parentModel.userData?.materialType?.includes('nhom_kinh')) {
            parentModel.traverse(object => {
              if (object.name === 'TAY-NAM-NHOM-KINH') {
                model = object;
              }
            });

            resolve();
            return;
          }

          const orientation = this.getModuleOrientation(clonedModule.group);

          const parentPos = clonedModule.group.worldToLocal(
            parentModel.getWorldPosition(new THREE.Vector3()).clone()
          );

          const parentSize = this.getSize(parentModel);
          let handleSize = handleSizeDefault;

          if (parentModel.userData.handleUuid) {
            const handle = this.findMeshByUuid(
              clonedModule.group,
              parentModel.userData.handleUuid
            );

            if (handle) {
              handleSize = this.getSize(handle);
            }
          }

          model.position.copy(parentModel.position);

          if (parentModel.rotation.y !== 0) {
            model.rotation.copy(parentModel.rotation);
          }

          //Vị trí theo độ cao (chung)
          if (
            parentModel.position.y >
            handleYRcm - handleSize.y / 2 - handlePadding - lockPadding
          ) {
            model.position.y =
              parentModel.position.y +
              handleSize.y +
              handlePadding +
              lockPadding;
          } else if (
            parentModel.position.y + parentSize.y <
            handleYRcm + handleSize.y / 2 + handlePadding
          ) {
            model.position.y =
              parentModel.position.y +
              parentSize.y -
              handlePadding -
              lockPadding -
              handleSize.y;
          } else {
            model.position.y = handleYRcm - handleSize.y / 2 - lockPadding;
          }

          //Set lại độ cao của tay nắm nếu nó không thuộc tủ dưới
          if (!clonedModule?.moduleType?.includes('lower')) {
            model.position.y =
              parentPos.y + handlePadding + lockPadding + handleSize.y;
          }

          //Đối với tủ TV vị trí tay nắm nằm giữa cửa theo chiều cao
          if (clonedModule?.moduleType?.includes('tv')) {
            model.position.y = parentPos.y + parentSize.y / 2;
          }

          //Vị trí theo phương ngang xử lý luôn cho hộc kéo
          if (parentModel.userData.cornerDoor) {
            if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
              switch (parentModel.userData.cornerDoor.toUpperCase()) {
                case 'DOOR-1':
                  model.rotation.y = Math.PI / 2;
                  model.position.x = parentPos.x - parentSize.x + handlePadding;
                  model.position.z = parentPos.z - parentSize.z;
                  break;

                case 'DOOR-2':
                  model.rotation.y = Math.PI / 2;
                  model.position.x = parentPos.x - handlePadding;
                  model.position.z = parentPos.z - parentSize.z;
                  break;

                case 'DOOR-3':
                  model.position.x = parentPos.x + parentSize.x;
                  model.position.z = parentPos.z - parentSize.z + handlePadding;
                  break;

                case 'DOOR-4':
                  model.position.x = parentPos.x + parentSize.x;
                  model.position.z = parentPos.z - handlePadding;
                  break;

                default:
                  console.warn(
                    'Unknown corner door type:',
                    parentModel.userData.cornerDoor
                  );
                  break;
              }
            } else if (
              orientation === 'ROTATED_90' ||
              orientation === 'ROTATED_270'
            ) {
              switch (parentModel.userData.cornerDoor.toUpperCase()) {
                case 'DOOR-1':
                  model.rotation.y = Math.PI / 2;
                  model.position.x = parentPos.x - parentSize.z + handlePadding;
                  model.position.z = parentPos.z - parentSize.x;
                  break;

                case 'DOOR-2':
                  model.rotation.y = Math.PI / 2;
                  model.position.x = parentPos.x - handlePadding;
                  model.position.z = parentPos.z - parentSize.x;
                  break;

                case 'DOOR-3':
                  model.position.x = parentPos.x + parentSize.z;
                  model.position.z = parentPos.z - parentSize.x + handlePadding;
                  break;

                case 'DOOR-4':
                  model.position.x = parentPos.x + parentSize.z;
                  model.position.z = parentPos.z - handlePadding;
                  break;

                default:
                  console.warn(
                    'Unknown corner door type:',
                    parentModel.userData.cornerDoor
                  );
                  break;
              }
            }
          } else if (parentModel.userData.typeHau === TypeConnerModule.SUB) {
            switch (orientation) {
              case 'NORMAL':
              case 'ROTATED_180': {
                model.position.z -= parentSize.z;

                if (parentModel.userData.drawerUuid) {
                  model.position.x = parentPos.x - parentSize.x / 2;

                  if (parentModel.userData.materialType.includes('tron')) {
                    model.position.y = parentPos.y + parentSize.y - lockPadding;
                  } else {
                    model.position.y = parentPos.y + parentSize.y - lockPadding;
                  }
                } else if (
                  parentModel.userData.doorDirection ===
                  DoorDirection.HORIZONTAL
                ) {
                  model.position.x = parentPos.x - parentSize.x / 2;

                  if (parentModel.userData.materialType.includes('tron')) {
                    model.position.y = parentPos.y + lockPadding;
                  } else {
                    model.position.y = parentPos.y + lockPadding;
                  }
                } else {
                  if (!parentModel.userData.handlePosition) {
                    return;
                  }

                  if (
                    parentModel.userData.handlePosition.left &&
                    parentModel.userData.handlePosition.current === 'left'
                  ) {
                    model.position.x = parentPos.x - handlePadding;
                  } else if (
                    parentModel.userData.handlePosition.right &&
                    parentModel.userData.handlePosition.current === 'right'
                  ) {
                    model.position.x =
                      parentPos.x - (parentSize.x - handlePadding);
                  } else {
                    openMessageError({
                      content: i18n.t(
                        'No suitable position found for the lock'
                      ),
                    });
                  }
                }
                break;
              }

              case 'ROTATED_90':
              case 'ROTATED_270': {
                model.position.z -= parentSize.x;

                if (parentModel.userData.drawerUuid) {
                  model.position.x = parentPos.x - parentSize.z / 2;

                  if (parentModel.userData.materialType.includes('tron')) {
                    model.position.y = parentPos.y + parentSize.y - lockPadding;
                  } else {
                    model.position.y = parentPos.y + parentSize.y - lockPadding;
                  }
                } else if (
                  parentModel.userData.doorDirection ===
                  DoorDirection.HORIZONTAL
                ) {
                  model.position.x = parentPos.x - parentSize.z / 2;

                  if (parentModel.userData.materialType.includes('tron')) {
                    model.position.y = parentPos.y + lockPadding;
                  } else {
                    model.position.y = parentPos.y + lockPadding;
                  }
                } else {
                  if (!parentModel.userData.handlePosition) {
                    return;
                  }

                  if (
                    parentModel.userData.handlePosition.left &&
                    parentModel.userData.handlePosition.current === 'left'
                  ) {
                    model.position.x = parentPos.x - handlePadding;
                  } else if (
                    parentModel.userData.handlePosition.right &&
                    parentModel.userData.handlePosition.current === 'right'
                  ) {
                    model.position.x =
                      parentPos.x - (parentSize.z - handlePadding);
                  } else {
                    openMessageError({
                      content: i18n.t(
                        'No suitable position found for the lock'
                      ),
                    });
                  }
                }
                break;
              }
            }
          } else {
            if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
              model.position.x += parentSize.x;

              if (parentModel.userData.drawerUuid) {
                model.rotation.x = Math.PI / 2;

                model.position.z = parentPos.z - parentSize.z / 2;

                if (parentModel.userData.materialType.includes('tron')) {
                  model.position.y = parentPos.y + parentSize.y - lockPadding;
                } else {
                  model.position.y =
                    parentPos.y + parentSize.y - handlePadding - lockPadding;
                }
              } else if (
                parentModel.userData.doorDirection === DoorDirection.HORIZONTAL
              ) {
                model.rotation.x = Math.PI / 2;

                model.position.z = parentPos.z - parentSize.z / 2;

                if (parentModel.userData.materialType.includes('tron')) {
                  model.position.y = parentPos.y + lockPadding;
                } else {
                  model.position.y = parentPos.y + handlePadding + lockPadding;
                }
              } else {
                if (!parentModel.userData.handlePosition) {
                  return;
                }

                if (
                  parentModel.userData.handlePosition.left &&
                  parentModel.userData.handlePosition.current === 'left'
                ) {
                  model.position.z = parentPos.z - handlePadding;
                } else if (
                  parentModel.userData.handlePosition.right &&
                  parentModel.userData.handlePosition.current === 'right'
                ) {
                  model.position.z =
                    parentPos.z - (parentSize.z - handlePadding);
                } else {
                  openMessageError({
                    content: i18n.t('No suitable position found for the lock'),
                  });
                }
              }
            } else if (
              orientation === 'ROTATED_90' ||
              orientation === 'ROTATED_270'
            ) {
              model.position.x += parentSize.z;

              if (parentModel.userData.drawerUuid) {
                model.rotation.x = Math.PI / 2;

                model.position.z = parentPos.z - parentSize.x / 2;

                if (parentModel.userData.materialType.includes('tron')) {
                  model.position.y = parentPos.y + parentSize.y - lockPadding;
                } else {
                  model.position.y =
                    parentPos.y + parentSize.y - handlePadding - lockPadding;
                }
              } else if (
                parentModel.userData.doorDirection === DoorDirection.HORIZONTAL
              ) {
                model.rotation.x = Math.PI / 2;

                model.position.z = parentPos.z - parentSize.x / 2;

                if (parentModel.userData.materialType.includes('tron')) {
                  model.position.y = parentPos.y + lockPadding;
                } else {
                  model.position.y = parentPos.y + handlePadding + lockPadding;
                }
              } else {
                if (!parentModel.userData.handlePosition) {
                  return;
                }

                if (
                  parentModel.userData.handlePosition.left &&
                  parentModel.userData.handlePosition.current === 'left'
                ) {
                  model.position.z = parentPos.z - handlePadding;
                } else if (
                  parentModel.userData.handlePosition.right &&
                  parentModel.userData.handlePosition.current === 'right'
                ) {
                  model.position.z =
                    parentPos.z - (parentSize.x - handlePadding);
                } else {
                  openMessageError({
                    content: i18n.t('No suitable position found for the lock'),
                  });
                }
              }
            }
          }
          //Vị trí theo phương ngang

          model.userData.doorUuid = parentModel.uuid;
          model.userData.type = InsideType.LOCK;
          model.userData.thumbnail_url =
            moduleData.thumbnail_url ?? moduleData.icon;

          model.userData.id = moduleData.id;
          model.userData.price = moduleData.price;
          model.userData.id = moduleData.id;
          model.userData.material = moduleData.material;
          model.userData.name = moduleData.name;
          model.userData.linkedDoorUuid = parentModel.uuid;
          model.userData.layer = parentModel.userData?.layer ?? 0;
          parentModel.userData.lockUuid = model.uuid;

          model.traverse(child => {
            child.userData.type = InsideType.LOCK;
            child.userData.layer = parentModel.userData?.layer ?? 0;
          });

          addModelToCabinet(model);
          return model.uuid;
        },
        undefined,
        err => {
          reject(err);
        }
      );
    });
  };

  loadLegModel = (moduleData, clonedModule, addModelToCabinet) => {
    return new Promise((resolve, reject) => {
      const loader = new GLTFLoader();
      loader.load(
        moduleData.model,
        gltf => {
          const model = gltf.scene;
          const orientation = this.getModuleOrientation(clonedModule.group);

          // Lấy kích thước của module
          const moduleWidth =
            clonedModule.formularController.currentDimensions.width;
          const moduleDepth =
            clonedModule.formularController.currentDimensions.depth;

          const offset = 0.08;
          const yPosition = 0;

          let legPositions;

          // if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
          //   const basePositions = [
          //     { x: moduleDepth / 2 - offset, z: moduleWidth / 2 - offset },
          //     { x: -moduleDepth / 2 + offset, z: moduleWidth / 2 - offset },
          //     { x: moduleDepth / 2 - offset, z: -moduleWidth / 2 + offset },
          //     { x: -moduleDepth / 2 + offset, z: -moduleWidth / 2 + offset },
          //   ];

          //   if (moduleWidth >= 1.6) {
          //     basePositions.push(
          //       { x: -moduleDepth / 2 + offset, z: 0 },
          //       { x: moduleDepth / 2 - offset, z: 0 }
          //     );
          //   }

          //   legPositions = basePositions;
          // } else if (
          //   orientation === 'ROTATED_90' ||
          //   orientation === 'ROTATED_270'
          // ) {
          //   const basePositions = [
          //     { x: moduleDepth / 2 - offset, z: moduleWidth / 2 - offset },
          //     { x: -moduleDepth / 2 + offset, z: moduleWidth / 2 - offset },
          //     { x: -moduleDepth / 2 + offset, z: -moduleWidth / 2 + offset },
          //     { x: moduleDepth / 2 - offset, z: -moduleWidth / 2 + offset },
          //   ];

          //   if (moduleWidth >= 1.6) {
          //     // Khi rotate, width chạy theo trục Z
          //     basePositions.push(
          //       { x: -moduleDepth / 2 + offset, z: 0 },
          //       { x: moduleDepth / 2 - offset, z: 0 }
          //     );
          //   }

          //   legPositions = basePositions;
          // }

          const checkBCT = clonedModule.formularController.currentOptions.bct;
          const checkBCP = clonedModule.formularController.currentOptions.bcp;
          const curveOffset =
            clonedModule.formularController.currentOptions.rBo +
            clonedModule.formularController.currentOptions.truHaoBoCong;

          if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
            const basePositions = [
              {
                x: moduleDepth / 2 - offset,
                z: moduleWidth / 2 - offset - (checkBCT ? curveOffset : 0),
              },
              {
                x: -moduleDepth / 2 + offset,
                z: moduleWidth / 2 - offset - (checkBCT ? curveOffset : 0),
              },
              {
                x: moduleDepth / 2 - offset,
                z: -moduleWidth / 2 + offset + (checkBCP ? curveOffset : 0),
              },
              {
                x: -moduleDepth / 2 + offset,
                z: -moduleWidth / 2 + offset + (checkBCP ? curveOffset : 0),
              },
            ];

            if (moduleWidth >= 1.6) {
              basePositions.push(
                {
                  x: -moduleDepth / 2 + offset,
                  z:
                    0 -
                    (checkBCT ? curveOffset / 2 : 0) +
                    (checkBCP ? curveOffset / 2 : 0),
                },
                {
                  x: moduleDepth / 2 - offset,
                  z:
                    0 -
                    (checkBCT ? curveOffset / 2 : 0) +
                    (checkBCP ? curveOffset / 2 : 0),
                }
              );
            }

            legPositions = basePositions;
          } else if (
            orientation === 'ROTATED_90' ||
            orientation === 'ROTATED_270'
          ) {
            const basePositions = [
              {
                x: moduleDepth / 2 - offset,
                z: moduleWidth / 2 - offset - (checkBCT ? curveOffset : 0),
              },
              {
                x: -moduleDepth / 2 + offset,
                z: moduleWidth / 2 - offset - (checkBCT ? curveOffset : 0),
              },
              {
                x: -moduleDepth / 2 + offset,
                z: -moduleWidth / 2 + offset + (checkBCP ? curveOffset : 0),
              },
              {
                x: moduleDepth / 2 - offset,
                z: -moduleWidth / 2 + offset + (checkBCP ? curveOffset : 0),
              },
            ];

            if (moduleWidth >= 1.6) {
              // Khi rotate, width chạy theo trục Z
              basePositions.push(
                {
                  x: -moduleDepth / 2 + offset,
                  z:
                    0 -
                    (checkBCT ? curveOffset / 2 : 0) +
                    (checkBCP ? curveOffset / 2 : 0),
                },
                {
                  x: moduleDepth / 2 - offset,
                  z:
                    0 -
                    (checkBCT ? curveOffset / 2 : 0) +
                    (checkBCP ? curveOffset / 2 : 0),
                }
              );
            }

            legPositions = basePositions;
          }

          const createdLegs = [];

          legPositions.forEach((pos, index) => {
            const legModel = index === 0 ? model : model.clone();

            const ROTATIONS = {
              special: {
                0: Math.PI / 4,
                1: -Math.PI / 4,
                2: (5 * Math.PI) / 4,
                3: (3 * Math.PI) / 4,
                4: -Math.PI / 2,
                5: Math.PI / 2,
              },
              normal: {
                0: 0,
                1: 0,
                2: Math.PI,
                3: Math.PI,
                4: -Math.PI / 2,
                5: Math.PI / 2,
              },
            };

            if (moduleData?.special) {
              const rotation = ROTATIONS.special[index];
              if (rotation !== undefined) legModel.rotation.y = rotation;
            } else {
              const rotation = ROTATIONS.normal[index];
              if (rotation !== undefined) legModel.rotation.y = rotation;
            }

            legModel.position.set(pos.x, yPosition, pos.z);

            legModel.userData.type = InsideType.CHAN_ROI_TV;
            legModel.userData.thumbnail_url =
              moduleData.thumbnail_url ?? moduleData.icon;
            legModel.userData.price = moduleData.price;
            legModel.userData.material = moduleData.material;
            legModel.userData.name = moduleData.name;
            legModel.userData.legIndex = index;
            legModel.userData.groupId = moduleData.group_id;

            addModelToCabinet(legModel);
            createdLegs.push(legModel.uuid);
          });

          resolve(createdLegs);
        },
        undefined,
        err => {
          reject(err);
        }
      );
    });
  };

  removeLegModel = clonedModule => {
    const legsToRemove = [];

    clonedModule.group.traverse(object => {
      if (
        object.userData.type === InsideType.CHAN_ROI_TV ||
        object.userData.type === InsideType.KHUNG_SAT
      ) {
        legsToRemove.push(object);
      }
    });

    // Xóa sau khi traverse xong
    legsToRemove.forEach(door => {
      this.removeInside(door, clonedModule);
    });
  };

  //Đổi loại cửa ngang
  async toggleHorizontalType(door, clonedModule, newType) {
    door.userData.horizontalType = newType;

    const materialType = door.userData?.materialType;
    const doorDirection = door.userData?.doorDirection;
    const horizontalType = newType;

    //Thêm tay nâng mặc định cho cửa ngang
    const liftSupportModelUuid = await this.loadLiftSupportModel(
      door.userData.defaultLiftSupport,
      clonedModule,
      door
    );

    const specialDoor = await this.cutSpecialDoor(
      clonedModule,
      door,
      materialType,
      doorDirection,
      horizontalType
    );

    // specialDoor.userData.liftSupportUuid = liftSupportModelUuid;

    return specialDoor;
  }

  //Đổi hướng mở cửa
  toggleDoorDirection(door, clonedModule, newPos) {
    if (door.userData.handlePosition[newPos]) {
      const handleModel = this.findMeshByUuid(
        clonedModule.group,
        door.userData.handleUuid
      );

      //Cua khong tay nam
      if (!handleModel && !door.userData.materialType?.includes('nhom_kinh')) {
        door.userData.handlePosition.current = newPos;
        this.toggleLockDirection(door, clonedModule, newPos);

        return;
      }

      const doorSize = this.getSize(door);
      const doorOrientation = this.getModuleOrientation(door);

      if (door.userData.materialType?.includes('nhom_kinh')) {
        //Cua nhom kinh
        door.traverse(object => {
          if (object.name === 'TAY-NAM-NHOM-KINH') {
            // if (
            //   doorOrientation === 'NORMAL' ||
            //   doorOrientation === 'ROTATED_180'
            // ) {
            //   if (newPos === 'left') {
            //     object.position.z = -aluminumHandleSize / 2;
            //   } else if (newPos === 'right') {
            //     object.position.z = -(doorSize.z - aluminumHandleSize / 2);
            //   }
            // } else if (
            //   doorOrientation === 'ROTATED_90' ||
            //   doorOrientation === 'ROTATED_270'
            // ) {
            //   if (newPos === 'left') {
            //     object.position.z = -aluminumHandleSize / 2;
            //   } else if (newPos === 'right') {
            //     object.position.z = -(doorSize.x - aluminumHandleSize / 2);
            //   }
            // }

            if (newPos === 'left') {
              object.position.z = -aluminumHandleSize / 2;
            } else if (newPos === 'right') {
              object.position.z = -(
                Math.max(doorSize.x, doorSize.z) -
                aluminumHandleSize / 2
              );
            }
          }
        });
      } else if (door.userData.typeHau === TypeConnerModule.SUB) {
        if (newPos === 'left') {
          handleModel.position.x = door.position.x - handlePadding;
        } else if (newPos === 'right') {
          handleModel.position.x =
            door.position.x -
            (Math.max(doorSize.x, doorSize.z) - handlePadding);
        }
      } else {
        if (newPos === 'left') {
          handleModel.position.z = door.position.z - handlePadding;
        } else if (newPos === 'right') {
          handleModel.position.z =
            door.position.z -
            (Math.max(doorSize.x, doorSize.z) - handlePadding);
        }
      }

      door.userData.handlePosition.current = newPos;
      this.toggleLockDirection(door, clonedModule, newPos);
    } else {
      openMessageError({
        content: i18n.t(
          'The selected opening direction cannot be applied to this door'
        ),
      });
    }
  }

  //Đổi hướng ổ khóa
  toggleLockDirection(door, clonedModule, newPos) {
    if (door.userData.handlePosition[newPos]) {
      const lockModel = this.findMeshByUuid(
        clonedModule.group,
        door.userData.lockUuid
      );

      if (!lockModel) {
        return;
      }

      const doorSize = this.getSize(door);
      const doorOrientation = this.getModuleOrientation(door);

      if (door.userData.typeHau === TypeConnerModule.SUB) {
        if (newPos === 'left') {
          lockModel.position.x = door.position.x - handlePadding;
        } else if (newPos === 'right') {
          lockModel.position.x =
            door.position.x -
            (Math.max(doorSize.x, doorSize.z) - handlePadding);
        }
      } else {
        if (newPos === 'left') {
          lockModel.position.z = door.position.z - handlePadding;
        } else if (newPos === 'right') {
          lockModel.position.z =
            door.position.z -
            (Math.max(doorSize.x, doorSize.z) - handlePadding);
        }
      }
    }
  }

  //Đổi cửa đụng trần có bạ/không bạ
  toggleCeilingDoorBrace(door, clonedModule, withBrace) {
    let braceModel = this.getMeshByName(clonedModule.group, 'BRACE');

    clonedModule.group.traverse(mesh => {
      if (
        mesh.userData.isInside === true &&
        mesh.userData.type === InsideType.BRACE
      ) {
        braceModel = mesh;
      }

      if (
        mesh.userData.isInside === true &&
        mesh.userData.type === InsideType.DOOR
      ) {
        mesh.userData.withBrace = withBrace;
      }
    });

    this.rescaleDoor(clonedModule);

    if (withBrace === 'khong_ba') {
      braceModel.scale.set(0, 0, 0);
    }
  }

  /** Xử lý cửa */
  movePointDoor(index, pointModel, arrays, door) {
    const doorOrientation = this.getModuleOrientation(door);

    if (pointModel.position.y < 0) return;

    if (pointModel && arrays?.length !== 0) {
      if (doorOrientation === 'NORMAL' || doorOrientation === 'ROTATED_180') {
        if (index == 0) {
          arrays[1].position.y = pointModel.position.y;
          arrays[3].position.z = pointModel.position.z;
        } else if (index == 1) {
          arrays[0].position.y = pointModel.position.y;
          arrays[2].position.z = pointModel.position.z;
        } else if (index == 2) {
          arrays[3].position.y = pointModel.position.y;
          arrays[1].position.z = pointModel.position.z;
        } else if (index == 3) {
          arrays[2].position.y = pointModel.position.y;
          arrays[0].position.z = pointModel.position.z;
        }
      } else if (
        doorOrientation === 'ROTATED_90' ||
        doorOrientation === 'ROTATED_270'
      ) {
        if (index == 0) {
          arrays[1].position.y = pointModel.position.y;
          arrays[3].position.x = pointModel.position.x;
        } else if (index == 1) {
          arrays[0].position.y = pointModel.position.y;
          arrays[2].position.x = pointModel.position.x;
        } else if (index == 2) {
          arrays[3].position.y = pointModel.position.y;
          arrays[1].position.x = pointModel.position.x;
        } else if (index == 3) {
          arrays[2].position.y = pointModel.position.y;
          arrays[0].position.x = pointModel.position.x;
        }
      }
    }
  }

  snapToPointDoorRecommend(index, recommend, arrays, door) {
    const doorOrientation = this.getModuleOrientation(door);

    if (recommend && arrays?.length !== 0) {
      if (doorOrientation === 'NORMAL' || doorOrientation === 'ROTATED_180') {
        if (index == 0) {
          arrays[1].position.y = recommend.position.y;
          arrays[3].position.z = recommend.position.z;
          arrays[0].position.y = recommend.position.y;
          arrays[0].position.z = recommend.position.z;
        } else if (index == 1) {
          arrays[0].position.y = recommend.position.y;
          arrays[2].position.z = recommend.position.z;
          arrays[1].position.y = recommend.position.y;
          arrays[1].position.z = recommend.position.z;
        } else if (index == 2) {
          arrays[3].position.y = recommend.position.y;
          arrays[1].position.z = recommend.position.z;
          arrays[2].position.y = recommend.position.y;
          arrays[2].position.z = recommend.position.z;
        } else if (index == 3) {
          arrays[2].position.y = recommend.position.y;
          arrays[0].position.z = recommend.position.z;
          arrays[3].position.y = recommend.position.y;
          arrays[3].position.z = recommend.position.z;
        }
      } else if (
        doorOrientation === 'ROTATED_90' ||
        doorOrientation === 'ROTATED_270'
      ) {
        if (index == 0) {
          arrays[1].position.y = recommend.position.y;
          arrays[3].position.x = recommend.position.x;
          arrays[0].position.y = recommend.position.y;
          arrays[0].position.x = recommend.position.x;
        } else if (index == 1) {
          arrays[0].position.y = recommend.position.y;
          arrays[2].position.x = recommend.position.x;
          arrays[1].position.y = recommend.position.y;
          arrays[1].position.x = recommend.position.x;
        } else if (index == 2) {
          arrays[3].position.y = recommend.position.y;
          arrays[1].position.x = recommend.position.x;
          arrays[2].position.y = recommend.position.y;
          arrays[2].position.x = recommend.position.x;
        } else if (index == 3) {
          arrays[2].position.y = recommend.position.y;
          arrays[0].position.x = recommend.position.x;
          arrays[3].position.y = recommend.position.y;
          arrays[3].position.x = recommend.position.x;
        }
      }
    }

    //Lưu lại vị trí snap để rescale door sau này
    let pointPos;

    if (recommend.userData.index >= 20) {
      pointPos = PointSnapPos.BOTTOM;
    } else if (recommend.userData.index >= 10) {
      pointPos = PointSnapPos.CENTER;
    } else {
      pointPos = PointSnapPos.TOP;
    }

    // Khởi tạo nếu chưa có
    if (!door.userData.customPoint) {
      door.userData.customPoint = { top: undefined, bottom: undefined };
    }

    if (index < 2) {
      door.userData.customPoint = {
        ...door.userData.customPoint,
        bottom: pointPos,
      };
    } else {
      door.userData.customPoint = {
        ...door.userData.customPoint,
        top: pointPos,
      };
    }
  }

  scaleDoorAdvanced(
    index,
    model,
    arrays,
    intersection,
    currentCabinet,
    module
  ) {
    let bottomEdge, topEdge, rightEdge, leftEdge;
    model.updateMatrixWorld(true);
    const doorOrientation = this.getModuleOrientation(model);

    const scaleOr = model.scale.clone();
    const modelBox = new THREE.Box3().setFromObject(model);
    const modelSize = modelBox.getSize(new THREE.Vector3());

    if (intersection) {
      if (index === 0 || index === 1) {
        intersection.y += 0.03;
      } else {
        intersection.y -= 0.03;
      }
    }

    const typeHau = model.userData.typeHau;
    let meshPoints = [];
    if (currentCabinet && module) {
      meshPoints = currentCabinet.getActiveMeshPoints(module);
      if (typeHau) {
        const ori = this.getModuleOrientation(module.group);
        const ignoreXanBa =
          model.userData.type === InsideType.DOOR ||
          model.userData.type === InsideType.DRAWER ||
          model.userData.type === InsideType.ACCESSORY_DISH_LOWER;
        const checkXanBa = model.userData.type !== InsideType.DOOR;

        if (ori === 'NORMAL') {
          if (typeHau === TypeConnerModule.MAIN) {
            meshPoints = EdgeDetector.findMeshPoints(
              module.group,
              ignoreXanBa,
              checkXanBa
            );
          } else if (typeHau === TypeConnerModule.SUB) {
            meshPoints = EdgeDetector.findMeshPointsR90(
              module.group,
              ignoreXanBa,
              checkXanBa
            );
          }
        }
        if (ori === 'ROTATED_270') {
          if (typeHau === TypeConnerModule.MAIN) {
            meshPoints = EdgeDetector.findMeshPointsRotation(
              module.group,
              ignoreXanBa,
              checkXanBa
            );
          } else if (typeHau === TypeConnerModule.SUB) {
            meshPoints = EdgeDetector.findMeshPoints(
              module.group,
              ignoreXanBa,
              checkXanBa
            );
          }
        }
      }
    }

    if (doorOrientation === 'NORMAL') {
      if (intersection && currentCabinet) {
        // const meshPoints = currentCabinet.getActiveMeshPoints(module);

        const resultY = EdgeDetector.findClosestYEdges(
          undefined,
          meshPoints,
          intersection,
          [InsideType.COL_PANEL, InsideType.BRACE],
          undefined,
          true
        );
        bottomEdge = resultY.bottomEdge;
        topEdge = resultY.topEdge;

        const result = EdgeDetector.findClosestZEdges(
          undefined,
          meshPoints,
          intersection,
          [InsideType.COL_PANEL, InsideType.BRACE],
          undefined,
          true
        );
        rightEdge = result.rightEdge;
        leftEdge = result.leftEdge;
      }

      if (model && arrays.length > 0) {
        const localPos = module?.group
          ? module.group.worldToLocal(arrays[1].position.clone())
          : arrays[1].position.clone();

        model.position.y = localPos.y;
        model.position.z = localPos.z;

        if (model.userData.typeHau === TypeConnerModule.SUB) {
          model.position.z = localPos.z + thicknessBase;
        }

        const sizeY = arrays[0].position.distanceTo(arrays[3].position);
        const sizeZ = arrays[0].position.distanceTo(arrays[1].position);

        //Kiểm tra cửa 1 cánh hay 2 cánh
        let sizeFull, leftEdgePos, rightEdgePos;

        if (leftEdge?.mesh && rightEdge?.mesh) {
          leftEdgePos = leftEdge.mesh.getWorldPosition(new THREE.Vector3());
          rightEdgePos = rightEdge.mesh.getWorldPosition(new THREE.Vector3());

          sizeFull = Math.abs(leftEdgePos.z - rightEdgePos.z);

          if (sizeFull && Math.abs(sizeFull - sizeZ) <= 2 * thicknessBase) {
            model.userData.isFullWidth = true;
            model.userData.handlePosition = {
              ...model.userData.handlePosition,
              left: true,
              right: true,
            };
          } else {
            model.userData.isFullWidth = false;

            const mdPos = model.getWorldPosition(new THREE.Vector3());

            if (nearlyEqual(mdPos.z, leftEdgePos.z, 2 * thicknessBase)) {
              model.userData.handlePosition = {
                ...model.userData.handlePosition,
                current: 'right',
                left: false,
                right: true,
              };
            } else {
              model.userData.handlePosition = {
                ...model.userData.handlePosition,
                current: 'left',
                left: true,
                right: false,
              };
            }
          }
        }
        //Kiểm tra cửa 1 cánh hay 2 cánh

        model.scale.y = (sizeY * scaleOr.y) / modelSize.y;
        model.scale.z = (sizeZ * scaleOr.z) / modelSize.z;

        if (sizeZ)
          if (index == 0 || index === 1) {
            model.userData.bottomEdgeUuid = bottomEdge?.mesh?.uuid;
          } else {
            model.userData.topEdgeUuid = topEdge?.mesh?.uuid;
          }

        if (index == 1 || index === 2) {
          model.userData.leftEdgeUuid = leftEdge?.mesh?.uuid;
        } else {
          model.userData.rightEdgeUuid = rightEdge?.mesh?.uuid;
        }
      }
    } else if (doorOrientation === 'ROTATED_90') {
      if (intersection && currentCabinet) {
        // const meshPoints = currentCabinet.getActiveMeshPoints(module);

        const resultY = EdgeDetector.findClosestYEdgesR90(
          undefined,
          meshPoints,
          intersection,
          [InsideType.COL_PANEL, InsideType.BRACE],
          undefined,
          true
        );
        bottomEdge = resultY.bottomEdge;
        topEdge = resultY.topEdge;

        const result = EdgeDetector.findClosestZEdgesR90(
          undefined,
          meshPoints,
          intersection,
          [InsideType.COL_PANEL, InsideType.BRACE],
          undefined,
          true
        );
        rightEdge = result.rightEdge;
        leftEdge = result.leftEdge;
      }

      if (model && arrays.length > 0) {
        const localPos = module?.group
          ? module.group.worldToLocal(arrays[0].position.clone())
          : arrays[0].position.clone();
        // const localPos = module.group.worldToLocal(arrays[0].position.clone());

        model.position.y = localPos.y;
        model.position.z = localPos.z;
        if (model.userData.typeHau === TypeConnerModule.SUB) {
          model.position.z = localPos.z + thicknessBase;
        }

        const sizeY = arrays[0].position.distanceTo(arrays[3].position);
        const sizeZ = arrays[0].position.distanceTo(arrays[1].position);

        //Kiểm tra cửa 1 cánh hay 2 cánh
        let sizeFull, leftEdgePos, rightEdgePos;

        if (leftEdge?.mesh && rightEdge?.mesh) {
          leftEdgePos = leftEdge.mesh.getWorldPosition(new THREE.Vector3());
          rightEdgePos = rightEdge.mesh.getWorldPosition(new THREE.Vector3());

          sizeFull = Math.abs(leftEdgePos.x - rightEdgePos.x);

          if (sizeFull && Math.abs(sizeFull - sizeZ) <= 2 * thicknessBase) {
            model.userData.isFullWidth = true;
            model.userData.handlePosition = {
              ...model.userData.handlePosition,
              left: true,
              right: true,
            };
          } else {
            model.userData.isFullWidth = false;

            const mdPos = model.getWorldPosition(new THREE.Vector3());

            if (nearlyEqual(mdPos.x, leftEdgePos.x, 2 * thicknessBase)) {
              model.userData.handlePosition = {
                ...model.userData.handlePosition,
                current: 'right',
                left: false,
                right: true,
              };
            } else {
              model.userData.handlePosition = {
                ...model.userData.handlePosition,
                current: 'left',
                left: true,
                right: false,
              };
            }
          }
        }
        //Kiểm tra cửa 1 cánh hay 2 cánh

        model.scale.y = (sizeY * scaleOr.y) / modelSize.y;
        model.scale.z = (sizeZ * scaleOr.z) / modelSize.x;

        if (index == 0 || index === 1) {
          model.userData.bottomEdgeUuid = bottomEdge?.mesh?.uuid;
        } else {
          model.userData.topEdgeUuid = topEdge?.mesh?.uuid;
        }

        if (index == 1 || index === 2) {
          model.userData.leftEdgeUuid = leftEdge?.mesh?.uuid;
        } else {
          model.userData.rightEdgeUuid = rightEdge?.mesh?.uuid;
        }
      }
    } else if (doorOrientation === 'ROTATED_180') {
      if (intersection && currentCabinet) {
        // const meshPoints = currentCabinet.getActiveMeshPoints(module);

        const resultY = EdgeDetector.findClosestYEdges(
          undefined,
          meshPoints,
          intersection,
          [InsideType.COL_PANEL, InsideType.BRACE],
          undefined,
          true
        );
        bottomEdge = resultY.bottomEdge;
        topEdge = resultY.topEdge;

        const result = EdgeDetector.findClosestZEdges(
          undefined,
          meshPoints,
          intersection,
          [InsideType.COL_PANEL, InsideType.BRACE],
          undefined,
          true
        );
        rightEdge = result.rightEdge;
        leftEdge = result.leftEdge;
      }

      if (model && arrays.length > 0) {
        const localPos = module?.group
          ? module.group.worldToLocal(arrays[0].position.clone())
          : arrays[0].position.clone();

        model.position.y = localPos.y;
        model.position.z = localPos.z;
        if (model.userData.typeHau === TypeConnerModule.SUB) {
          model.position.z = localPos.z + thicknessBase;
        }

        const sizeY = arrays[0].position.distanceTo(arrays[3].position);
        const sizeZ = arrays[0].position.distanceTo(arrays[1].position);

        //Kiểm tra cửa 1 cánh hay 2 cánh
        let sizeFull, leftEdgePos, rightEdgePos;

        if (leftEdge?.mesh && rightEdge?.mesh) {
          leftEdgePos = leftEdge.mesh.getWorldPosition(new THREE.Vector3());
          rightEdgePos = rightEdge.mesh.getWorldPosition(new THREE.Vector3());

          sizeFull = Math.abs(leftEdgePos.z - rightEdgePos.z);

          if (sizeFull && Math.abs(sizeFull - sizeZ) <= 2 * thicknessBase) {
            model.userData.isFullWidth = true;
            model.userData.handlePosition = {
              ...model.userData.handlePosition,
              left: true,
              right: true,
            };
          } else {
            model.userData.isFullWidth = false;

            const mdPos = model.getWorldPosition(new THREE.Vector3());

            if (nearlyEqual(mdPos.z, leftEdgePos.z, 2 * thicknessBase)) {
              model.userData.handlePosition = {
                ...model.userData.handlePosition,
                current: 'right',
                left: false,
                right: true,
              };
            } else {
              model.userData.handlePosition = {
                ...model.userData.handlePosition,
                current: 'left',
                left: true,
                right: false,
              };
            }
          }
        }
        //Kiểm tra cửa 1 cánh hay 2 cánh

        model.scale.y = (sizeY * scaleOr.y) / modelSize.y;
        model.scale.z = (sizeZ * scaleOr.z) / modelSize.z;

        if (sizeZ)
          if (index == 0 || index === 1) {
            model.userData.bottomEdgeUuid = bottomEdge?.mesh?.uuid;
          } else {
            model.userData.topEdgeUuid = topEdge?.mesh?.uuid;
          }

        if (index == 1 || index === 2) {
          model.userData.leftEdgeUuid = leftEdge?.mesh?.uuid;
        } else {
          model.userData.rightEdgeUuid = rightEdge?.mesh?.uuid;
        }
      }
    } else if (doorOrientation === 'ROTATED_270') {
      if (intersection && currentCabinet) {
        // const meshPoints = currentCabinet.getActiveMeshPoints(module);

        const resultY = EdgeDetector.findClosestYEdgesR270(
          undefined,
          meshPoints,
          intersection,
          [InsideType.COL_PANEL, InsideType.BRACE],
          undefined,
          true
        );
        bottomEdge = resultY.bottomEdge;
        topEdge = resultY.topEdge;

        const result = EdgeDetector.findClosestZEdgesR270(
          undefined,
          meshPoints,
          intersection,
          [InsideType.COL_PANEL, InsideType.BRACE],
          undefined,
          true
        );

        rightEdge = result.rightEdge;
        leftEdge = result.leftEdge;
      }

      if (model && arrays.length > 0) {
        // const localPos = module?.group
        //   ? module.group.worldToLocal(arrays[1].position.clone())
        //   : arrays[1].position.clone();
        const localPos = module.group.worldToLocal(arrays[1].position.clone());

        model.position.y = localPos.y;
        model.position.z = localPos.z;
        if (model.userData.typeHau === TypeConnerModule.SUB) {
          model.position.z = localPos.z + thicknessBase;
        }

        const sizeY = arrays[0].position.distanceTo(arrays[3].position);
        const sizeZ = arrays[0].position.distanceTo(arrays[1].position);

        //Kiểm tra cửa 1 cánh hay 2 cánh
        let sizeFull, leftEdgePos, rightEdgePos;

        if (leftEdge?.mesh && rightEdge?.mesh) {
          leftEdgePos = leftEdge.mesh.getWorldPosition(new THREE.Vector3());
          rightEdgePos = rightEdge.mesh.getWorldPosition(new THREE.Vector3());

          sizeFull = Math.abs(leftEdgePos.x - rightEdgePos.x);

          if (sizeFull && Math.abs(sizeFull - sizeZ) <= 2 * thicknessBase) {
            model.userData.isFullWidth = true;
            model.userData.handlePosition = {
              ...model.userData.handlePosition,
              left: true,
              right: true,
            };
          } else {
            model.userData.isFullWidth = false;

            const mdPos = model.getWorldPosition(new THREE.Vector3());

            if (nearlyEqual(mdPos.x, leftEdgePos.x, 2 * thicknessBase)) {
              model.userData.handlePosition = {
                ...model.userData.handlePosition,
                current: 'right',
                left: false,
                right: true,
              };
            } else {
              model.userData.handlePosition = {
                ...model.userData.handlePosition,
                current: 'left',
                left: true,
                right: false,
              };
            }
          }
        }
        //Kiểm tra cửa 1 cánh hay 2 cánh

        model.scale.y = (sizeY * scaleOr.y) / modelSize.y;
        model.scale.z = (sizeZ * scaleOr.z) / modelSize.x;

        if (index == 0 || index === 1) {
          model.userData.bottomEdgeUuid = bottomEdge?.mesh?.uuid;
        } else {
          model.userData.topEdgeUuid = topEdge?.mesh?.uuid;
        }

        if (index == 1 || index === 2) {
          model.userData.leftEdgeUuid = leftEdge?.mesh?.uuid;
        } else {
          model.userData.rightEdgeUuid = rightEdge?.mesh?.uuid;
        }
      }
    }

    const targetModule = currentCabinet?.cloneModule || module;
    if (targetModule) {
      this.updateHandlePosition(model, targetModule);
    }
  }

  scaleDoor(
    drawerClicked,
    model,
    edges,
    moduleData,
    clonedModule,
    originalSize,
    addModelToCabinet,
    addBraceToCabinet,
    handleData,
    typeHau,
    defaultLiftSupport
  ) {
    const {
      bottomEdge,
      rightEdge,
      leftEdge,
      backEdge,
      topEdge,
      rightEdgeDoor,
      leftEdgeDoor,
    } = edges;
    const orientation = this.getModuleOrientation(clonedModule.group);

    // Get edge sizes
    const sizeBottom = bottomEdge?.mesh ? this.getSize(bottomEdge.mesh) : null;
    const sizeTop = topEdge?.mesh ? this.getSize(topEdge.mesh) : null;
    const sizeRight = rightEdge?.mesh ? this.getSize(rightEdge.mesh) : null;
    const sizeLeft = leftEdge?.mesh ? this.getSize(leftEdge.mesh) : null;

    model.userData.type = moduleData.type;
    model.userData.materialType = moduleData.material_type;
    model.userData.useAcrylic = moduleData.use_acrylic;
    model.userData.defaultLiftSupport = defaultLiftSupport;

    //Xét type cửa là có bạ hay không bạ
    model.userData.withBrace = CeilingDoor.WITH_BRACE;

    //Kiểm tra nếu là cửa 2 cánh thì 2 cánh phải cùng loại
    if (
      rightEdgeDoor.mesh.userData?.type === InsideType.DOOR ||
      leftEdgeDoor.mesh.userData?.type === InsideType.DOOR
    ) {
      if (
        (rightEdgeDoor.mesh.userData?.beveled !== undefined &&
          rightEdgeDoor.mesh.userData?.beveled !== moduleData.beveled) ||
        (leftEdgeDoor.mesh.userData?.beveled !== undefined &&
          leftEdgeDoor.mesh.userData?.beveled !== moduleData.beveled)
      ) {
        openMessageError({
          content: i18n.t(
            'Invalid door selection. Both doors must be of the same type'
          ),
        });
        return;
      }
    }

    //Kiểm tra không cho thả cửa ngang vào hộc kéo
    if (
      drawerClicked &&
      moduleData?.door_direction === DoorDirection.HORIZONTAL
    ) {
      openMessageError({
        content: i18n.t(
          'Invalid door selection. Cannot use a horizontal door for a drawer'
        ),
      });
      return;
    }

    if (!drawerClicked) {
      //Nếu thả cửa vào khoảng không giữa 2 hộc kéo thì kh thêm cửa
      if (
        (bottomEdge?.mesh?.userData?.type === InsideType.DRAWER &&
          topEdge?.mesh?.userData?.type === InsideType.DRAWER) ||
        (bottomEdge?.mesh?.userData?.type === InsideType.ACCESSORY_DISH_LOWER &&
          topEdge?.mesh?.userData?.type === InsideType.ACCESSORY_DISH_LOWER)
      ) {
        openMessageError({
          content: i18n.t(
            'Please hover your mouse over the exact drawer where you want to add a door.'
          ),
        });
        return;
      }
    }

    //Kiểm tra cửa quá kích thước cho phép
    if (leftEdge?.mesh && rightEdge?.mesh) {
      //Kiểm tra kích thước
      let checkSize = true;

      if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
        if (
          Math.abs(
            rightEdge.mesh.getWorldPosition(new THREE.Vector3()).z -
              leftEdge.mesh.getWorldPosition(new THREE.Vector3()).z
          ) >
          2 * moduleData.limit.width.max - thicknessBase
        ) {
          checkSize = false;
        }
      } else if (
        orientation === 'ROTATED_90' ||
        orientation === 'ROTATED_270'
      ) {
        if (
          Math.abs(
            rightEdge.mesh.getWorldPosition(new THREE.Vector3()).x -
              leftEdge.mesh.getWorldPosition(new THREE.Vector3()).x
          ) >
          2 * moduleData.limit.width.max - thicknessBase
        ) {
          checkSize = false;
        }
      }

      if (checkSize === false) {
        openNotification({
          key: 'cant-add-door-existing-door',
          icon: <Icon.ICWarning />,
          iconColor: '#F97D16',
          title: i18n.t('Unable to add the product.'),
          description: 'Vị trí đã có sản phẩm hoặc kích thước không phù hợp.',
          placement: 'bottomRight',
          closable: true,
          duration: 2,
        });

        return;
      }
    }

    // Case 1: Không tìm thấy tấm trên
    if (
      bottomEdge?.mesh &&
      rightEdge?.mesh &&
      leftEdge?.mesh &&
      backEdge?.mesh &&
      (topEdge === undefined || topEdge.orientation === 2)
    ) {
      // if (this.beveled) {
      if (moduleData.beveled) {
        this.scale45DoorWithoutTopEdge(
          drawerClicked,
          model,
          {
            bottomEdge,
            rightEdge,
            leftEdge,
            backEdge,
            rightEdgeDoor,
            leftEdgeDoor,
          },
          { sizeBottom, sizeRight, sizeLeft },
          moduleData,
          clonedModule,
          typeHau,
          originalSize,
          { slit, countertopGap },
          addModelToCabinet
        );
      } else {
        this.scaleBasicDoorWithoutTopEdge(
          drawerClicked,
          model,
          {
            bottomEdge,
            rightEdge,
            leftEdge,
            backEdge,
            rightEdgeDoor,
            leftEdgeDoor,
          },
          { sizeBottom, sizeRight, sizeLeft },
          moduleData,
          clonedModule,
          typeHau,
          originalSize,
          { slit, countertopGap },
          addModelToCabinet,
          addBraceToCabinet
        );
      }
    }

    // Case 2: Tìm thấy tấm trên
    if (
      bottomEdge &&
      rightEdge &&
      leftEdge &&
      backEdge &&
      topEdge &&
      topEdge.orientation !== 2
    ) {
      // if (this.beveled) {
      if (moduleData.beveled) {
        this.scale45DoorWithTopEdge(
          drawerClicked,
          model,
          {
            bottomEdge,
            rightEdge,
            leftEdge,
            backEdge,
            topEdge,
            rightEdgeDoor,
            leftEdgeDoor,
          },
          { sizeBottom, sizeRight, sizeLeft, sizeTop },
          moduleData,
          clonedModule,
          typeHau,
          originalSize,
          { slit, countertopGap },
          addModelToCabinet,
          addBraceToCabinet
        );
      } else {
        this.scaleBasicDoorWithTopEdge(
          drawerClicked,
          model,
          {
            bottomEdge,
            rightEdge,
            leftEdge,
            backEdge,
            topEdge,
            rightEdgeDoor,
            leftEdgeDoor,
          },
          { sizeBottom, sizeRight, sizeLeft, sizeTop },
          moduleData,
          clonedModule,
          typeHau,
          originalSize,
          { slit, countertopGap },
          addModelToCabinet,
          addBraceToCabinet
        );
      }
    }
    if (moduleData?.default_glass_type) {
      clonedModule.textures.set('GLASS-PANEL', {
        textureId: moduleData?.default_glass_type?.id,
        textureCode:
          moduleData?.default_glass_type?.code ??
          moduleData?.default_glass_type?.name,
        texturePrice: moduleData?.default_glass_type?.price,
        textureUrl: moduleData?.default_glass_type?.image,
      });
    }
  }

  scaleBasicDoorWithoutTopEdge(
    drawerClicked,
    model,
    edges,
    sizes,
    moduleData,
    clonedModule,
    typeHau,
    originalSize,
    gaps,
    addModelToCabinet,
    addBraceToCabinet
  ) {
    const {
      bottomEdge,
      rightEdge,
      leftEdge,
      backEdge,
      rightEdgeDoor,
      leftEdgeDoor,
    } = edges;

    // //Cửa cho tủ góc
    // if (clonedModule.cabinetType.toLowerCase().includes('corner')) {
    //   this.scaleBasicDoorForCornerModule(
    //     moduleData,
    //     model,
    //     clonedModule,
    //     addModelToCabinet,
    //     addBraceToCabinet
    //   );

    //   return;
    // }

    // Nếu cả hai bên đều có type === InsideType.DOOR thì return
    if (
      rightEdgeDoor.mesh.userData?.type === InsideType.DOOR &&
      leftEdgeDoor.mesh.userData?.type === InsideType.DOOR
    ) {
      // message.error("Không thể thêm cửa ở vị trí này!!!");
      openNotification({
        key: 'cant-add-door-existing-door',
        icon: <Icon.ICWarning />,
        iconColor: '#F97D16',
        title: i18n.t('Unable to add the product.'),
        description: 'Vị trí đã có sản phẩm hoặc kích thước không phù hợp.',
        placement: 'bottomRight',
        closable: true,
        duration: 2,
      });
      return;
    }

    const { sizeBottom, sizeRight, sizeLeft } = sizes;
    const { slit, countertopGap } = gaps;

    // Tìm tấm top (xuongTruoc)
    // const possibleNames = ['XUONG-TRUOC-1','G-NEP-TRUOC-3'];
    const possibleNames = [
      'XUONG-TRUOC-1',
      'NEP-TRUOC-1',
      'G-NEP-TRUOC-3',
      'BA-1',
    ];
    let xuongTruoc = null;

    clonedModule.group.traverse(mesh => {
      if (mesh.isMesh && !xuongTruoc) {
        // Kiểm tra tên mesh có chứa bất kỳ tên nào trong danh sách
        if (possibleNames.some(name => mesh.name.includes(name))) {
          xuongTruoc = mesh;
        }
      }
    });

    if (!xuongTruoc) {
      return;
    }

    const sizeXuongTruoc = this.getSize(xuongTruoc);
    const xuongTruocPos = clonedModule.group.worldToLocal(
      xuongTruoc.getWorldPosition(new THREE.Vector3()).clone()
    );

    const orientation = this.getModuleOrientation(clonedModule.group);
    const leftPos = clonedModule.group.worldToLocal(
      leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
    );
    const rightPos = clonedModule.group.worldToLocal(
      rightEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
    );
    const bottomPos = clonedModule.group.worldToLocal(
      bottomEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
    );

    let drawerPos, qtyDrawer;

    if (drawerClicked) {
      qtyDrawer = drawerClicked.mesh.userData.drawerCount ?? 1;

      //Trường hợp kéo từng hộc kéo thì đếm số lượng
      if (!qtyDrawer) {
        let count = 0;
        clonedModule.group.traverse(object => {
          if (
            object.userData.isInside === true &&
            (object.userData.type === InsideType.DRAWER ||
              object.userData.type === InsideType.ACCESSORY_DISH_LOWER) &&
            nearlyEqual(
              object.position.x,
              drawerClicked.mesh.position.x,
              0.01
            ) &&
            nearlyEqual(object.position.z, drawerClicked.mesh.position.z, 0.01)
          ) {
            count++;
          }
        });

        qtyDrawer = count;
      }

      drawerPos = clonedModule.group.worldToLocal(
        drawerClicked?.mesh.getWorldPosition(new THREE.Vector3()).clone()
      );
    }

    // Size
    let sizeY;

    sizeY =
      xuongTruocPos.y -
      bottomPos.y +
      sizeXuongTruoc.y +
      (xuongTruoc?.mesh?.userData?.type !== undefined
        ? -sizeXuongTruoc.y / 2 - slit
        : 0) +
      (bottomEdge?.mesh?.userData?.type !== undefined
        ? -sizeBottom.y / 2 - slit
        : 0) -
      (countertopGap + minDoorGap);

    //Cửa hộc kéo
    if (drawerClicked) {
      if (
        bottomEdge.mesh.userData?.type === InsideType.DRAWER ||
        bottomEdge.mesh.userData?.type === InsideType.ACCESSORY_DISH_LOWER
      ) {
        sizeY =
          xuongTruocPos.y +
          sizeXuongTruoc.y -
          (drawerPos.y -
            drawerClicked.mesh.userData.bottomGap -
            this.braceIndent) -
          (countertopGap + minDoorGap) +
          //tu bep duoi
          (clonedModule.moduleType.toLowerCase().includes('kitchen')
            ? 0.002
            : 0);
      }

      //Lưu thông tin hộc kéo
      model.userData.drawerUuid = drawerClicked.mesh.uuid;
    }
    //Cửa hộc kéo

    // const sizeZ = this.setDoorZSize(
    //   orientation,
    //   leftEdge,
    //   leftPos,
    //   sizeLeft,
    //   rightEdge,
    //   rightPos,
    //   sizeRight,
    //   slit
    // );
    let sizeZ;
    if (typeHau === TypeConnerModule.SUB) {
      sizeZ = this.setDoorXSize(
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        rightEdge,
        rightPos,
        sizeRight,
        slit
      );
    } else {
      sizeZ = this.setDoorZSize(
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        rightEdge,
        rightPos,
        sizeRight,
        slit
      );
    }

    // Position
    // model.position.x =
    //   Number(clonedModule.formularController.currentDimensions.depth) / 2;
    if (!typeHau) {
      model.position.x =
        Number(clonedModule.formularController.currentDimensions.depth) / 2 -
        (drawerClicked?.mesh?.userData?.type === InsideType.ACCESSORY_FLIP
          ? thicknessBase
          : 0);
    } else if (typeHau === TypeConnerModule.SUB) {
      model.position.z =
        clonedModule.formularController.currentOptions.rongK2 / 2 -
        Number(clonedModule.formularController.currentDimensions.depth);

      model.rotation.y += Math.PI / 2;
    } else {
      model.position.x =
        Number(clonedModule.formularController.currentDimensions.depth) -
        clonedModule.formularController.currentOptions.rongK1 / 2;
    }

    model.position.y =
      bottomPos.y +
      (bottomEdge?.mesh?.userData?.type !== undefined
        ? sizeBottom.y / 2 + slit
        : 0);

    //Cửa hộc keos
    if (drawerClicked) {
      if (
        bottomEdge.mesh.userData?.type === InsideType.DRAWER ||
        bottomEdge.mesh.userData?.type === InsideType.ACCESSORY_DISH_LOWER
      ) {
        model.position.y =
          drawerPos.y -
          drawerClicked.mesh.userData.bottomGap -
          this.braceIndent -
          //tu bep duoi
          (clonedModule.moduleType.toLowerCase().includes('kitchen')
            ? 0.002
            : 0);
      }
    }
    //Cửa hộc keos

    // this.setDoorZPosition(
    //   model,
    //   leftEdgeDoor,
    //   orientation,
    //   leftEdge,
    //   leftPos,
    //   sizeLeft,
    //   sizeZ,
    //   slit
    // );
    if (typeHau === TypeConnerModule.SUB) {
      this.setDoorXPosition(
        model,
        leftEdgeDoor,
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        sizeZ,
        slit
      );
    } else {
      this.setDoorZPosition(
        model,
        leftEdgeDoor,
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        sizeZ,
        slit
      );
    }

    // Apply scale
    const scaleY = sizeY / originalSize.y;

    let scaleZ;

    if (drawerClicked) {
      scaleZ = sizeZ / originalSize.z;
    } else {
      // scaleZ =
      //   sizeZ >= moduleData.limit.width.min &&
      //   sizeZ < moduleData.limit.width.max
      //     ? sizeZ / 2 / originalSize.z
      //     : sizeZ >= moduleData.limit.width.max &&
      //         sizeZ / 2 >= moduleData.limit.width.min &&
      //         sizeZ / 2 <= moduleData.limit.width.max
      //       ? sizeZ / 2 / originalSize.z
      //       : moduleData.limit.width.max / originalSize.z;

      scaleZ =
        sizeZ <= moduleData.limit.width.max &&
        leftEdgeDoor?.mesh?.userData?.type !== InsideType.DOOR &&
        rightEdgeDoor?.mesh?.userData?.type !== InsideType.DOOR
          ? sizeZ / originalSize.z
          : sizeZ / 2 / originalSize.z;
    }

    model.scale.y = scaleY;
    model.scale.z = scaleZ;

    // Set userData
    model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
    model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
    model.userData.topEdgeUuid = xuongTruoc.uuid;
    model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;
    model.userData.leftEdgeDoorUuid = leftEdgeDoor.mesh.uuid;
    model.userData.rightEdgeDoorUuid = rightEdgeDoor.mesh.uuid;

    model.userData.beveled = moduleData?.beveled ?? false;
    model.userData.typeHau = typeHau;

    const check = this.handleCheckLeftRight(
      clonedModule,
      model,
      leftEdge.mesh,
      rightEdge.mesh,
      typeHau
    );

    model.userData.handlePosition = {
      left: check.isLeft,
      right: check.isRight,
      center: check.isCenter,
      // current: check.isRight ? 'right' : check.isLeft ? 'left' : null,
      // current: check.isRight ? 'right' : check.isLeft ? 'left' : 'center',
      current: check.isRight
        ? 'right'
        : check.isLeft
          ? 'left'
          : check.isCenter
            ? 'center'
            : 'drawer',
    };

    addModelToCabinet();

    //Thêm tay nắm mặc định
    const handleModel = this.loadHandleModel(
      moduleData.default_handle,
      clonedModule,
      model,
      addBraceToCabinet
    );

    //Scale cửa đặc biệt
    const materialType = moduleData.material_type;

    // if (!materialType?.includes('tron')) {
    setTimeout(async () => {
      await this.cutSpecialDoor(clonedModule, model, materialType);
    }, 200);
    // }
  }

  scaleBasicDoorWithTopEdge(
    drawerClicked,
    model,
    edges,
    sizes,
    moduleData,
    clonedModule,
    typeHau,
    originalSize,
    gaps,
    addModelToCabinet,
    addBraceToCabinet
  ) {
    const doorDirection = moduleData.door_direction;
    const isCurveModule =
      clonedModule.cabinetType.toLowerCase().includes('curve') &&
      clonedModule.formularController.currentOptions.caoKhungHoc;
    const isBenchModule =
      clonedModule.cabinetType.toLowerCase().includes('bench') ||
      clonedModule.cabinetType.toLowerCase().includes('tv');

    let checkSubZ = false;
    if (
      isCurveModule ||
      drawerClicked?.mesh?.userData?.type === InsideType.ACCESSORY_FLIP
    ) {
      checkSubZ = true;
    }

    // //Cửa cho tủ góc
    // if (clonedModule.cabinetType.toLowerCase().includes('corner')) {
    //   this.scaleBasicDoorForCornerModule(
    //     moduleData,
    //     model,
    //     clonedModule,
    //     addModelToCabinet,
    //     addBraceToCabinet
    //   );

    //   return;
    // }
    // //Cửa cho tủ góc

    const {
      bottomEdge,
      rightEdge,
      leftEdge,
      backEdge,
      topEdge,
      rightEdgeDoor,
      leftEdgeDoor,
    } = edges;
    // Nếu cả hai bên đều có type === InsideType.DOOR thì return
    if (
      rightEdgeDoor.mesh.userData?.type === InsideType.DOOR &&
      leftEdgeDoor.mesh.userData?.type === InsideType.DOOR
    ) {
      // message.error("Không thể thêm cửa ở vị trí này!!!");
      openNotification({
        key: 'cant-add-door-existing-door',
        icon: <Icon.ICWarning />,
        iconColor: '#F97D16',
        title: i18n.t('Unable to add the product.'),
        description: 'Vị trí đã có sản phẩm hoặc kích thước không phù hợp.',
        placement: 'bottomRight',
        closable: true,
        duration: 2,
      });
      return;
    }

    const { sizeBottom, sizeRight, sizeLeft, sizeTop } = sizes;
    const { slit, countertopGap } = gaps;

    const orientation = this.getModuleOrientation(clonedModule.group);
    const leftPos = clonedModule.group.worldToLocal(
      leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
    );
    const rightPos = clonedModule.group.worldToLocal(
      rightEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
    );
    const topPos = clonedModule.group.worldToLocal(
      topEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
    );
    const bottomPos = clonedModule.group.worldToLocal(
      bottomEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
    );

    let drawerPos, qtyDrawer;

    if (drawerClicked) {
      qtyDrawer = drawerClicked.mesh.userData.drawerCount ?? 1;

      //Trường hợp kéo từng hộc kéo thì đếm số lượng
      // if (!qtyDrawer) {
      if (
        drawerClicked.mesh.userData.type === InsideType.ACCESSORY_DISH_LOWER
      ) {
        let count = 0;
        clonedModule.group.traverse(object => {
          if (
            object.userData.isInside === true &&
            (object.userData.type === InsideType.DRAWER ||
              object.userData.type === InsideType.ACCESSORY_DISH_LOWER) &&
            nearlyEqual(
              object.position.x,
              drawerClicked.mesh.position.x,
              0.01
            ) &&
            nearlyEqual(object.position.z, drawerClicked.mesh.position.z, 0.01)
          ) {
            count++;
          }
        });

        qtyDrawer = count;
      }

      drawerPos = clonedModule.group.worldToLocal(
        drawerClicked?.mesh.getWorldPosition(new THREE.Vector3()).clone()
      );
    }

    //Size bao gồm tủ bo góc
    let sizeY =
      topPos.y -
      bottomPos.y +
      (topEdge.mesh.userData?.type !== undefined
        ? sizeTop.y / 2
        : isCurveModule || isBenchModule
          ? 0
          : sizeTop.y) +
      (bottomEdge.mesh.userData?.type !== undefined
        ? -sizeBottom.y / 2 - slit
        : 0) +
      (bottomEdge.mesh.userData?.type !== undefined &&
      (bottomEdge.mesh.userData?.type === InsideType.DRAWER ||
        bottomEdge.mesh.userData?.type === InsideType.ACCESSORY_DISH_LOWER)
        ? -sizeBottom.y / 2
        : 0);

    //Cửa hộc kéo
    let topFrameEdge, bottomFrameEdge, drawerHeight;
    let topFrameEdgeSize, bottomFrameEdgeSize;
    if (drawerClicked) {
      //Hộc kéo trong tủ
      if (drawerClicked.mesh.userData.type === InsideType.U_DRAWER) {
        this.scaleBasicDoorForUDrawer(
          drawerClicked,
          model,
          edges,
          sizes,
          moduleData,
          clonedModule,
          originalSize,
          gaps,
          addModelToCabinet,
          addBraceToCabinet
        );

        return;
      }

      //Hộc kéo thường
      topFrameEdge = this.findMeshByUuid(
        clonedModule.group,
        drawerClicked.mesh.userData.topEdgeUuid
      );
      topFrameEdgeSize = this.getSize(topFrameEdge);

      bottomFrameEdge = this.findMeshByUuid(
        clonedModule.group,
        drawerClicked.mesh.userData.bottomEdgeUuid
      );
      bottomFrameEdgeSize = this.getSize(bottomFrameEdge);

      let totalHeight =
        topFrameEdge.position.y -
        bottomFrameEdge.position.y +
        topFrameEdgeSize.y -
        (topFrameEdge.userData?.type !== undefined
          ? topFrameEdgeSize.y / 2
          : 0) -
        (bottomFrameEdge.userData?.type !== undefined
          ? bottomFrameEdgeSize.y / 2
          : 0) -
        //Tu bep duoi
        (['kitchen', 'lower'].every(item =>
          clonedModule.moduleType.includes(item)
        ) &&
        (topEdge.mesh.userData?.type === InsideType.DRAWER ||
          topEdge.mesh.userData?.type === InsideType.ACCESSORY_DISH_LOWER)
          ? countertopGap + minDoorGap
          : 0) -
        //Tu TV hoặc đôn ngồi
        ((clonedModule.moduleType.includes('tv') &&
          topFrameEdge.name?.toUpperCase()?.includes('NOC')) ||
        clonedModule.moduleType.includes('bench')
          ? topFrameEdgeSize.y
          : 0);

      drawerHeight = (totalHeight - 0.001 * (qtyDrawer - 1)) / qtyDrawer;

      sizeY = drawerHeight;

      // if (drawerClicked.mesh.userData.type === InsideType.ACCESSORY_FLIP) {
      //   sizeY = drawerClicked.mesh.userData.sizeDefault.y;
      // }

      //Lưu thông tin hộc kéo
      model.userData.drawerUuid = drawerClicked.mesh.uuid;
    }
    //Cửa hộc kéo

    // const sizeZ = this.setDoorZSize(
    //   orientation,
    //   leftEdge,
    //   leftPos,
    //   sizeLeft,
    //   rightEdge,
    //   rightPos,
    //   sizeRight,
    //   slit,
    //   checkSubZ
    // );

    let sizeZ;
    if (typeHau === TypeConnerModule.SUB) {
      sizeZ = this.setDoorXSize(
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        rightEdge,
        rightPos,
        sizeRight,
        slit,
        checkSubZ
      );
    } else {
      sizeZ = this.setDoorZSize(
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        rightEdge,
        rightPos,
        sizeRight,
        slit,
        checkSubZ
      );
    }

    //Position
    // model.position.x =
    // Number(clonedModule.formularController.currentDimensions.depth) / 2 -
    // (isCurveModule ? thicknessBase : 0) -
    // (isBenchModule ? thicknessBase : 0) -
    // (drawerClicked?.mesh?.userData?.type === InsideType.ACCESSORY_FLIP
    //   ? thicknessBase
    //   : 0);

    if (!typeHau) {
      model.position.x =
        Number(clonedModule.formularController.currentDimensions.depth) / 2 -
        (isCurveModule ? thicknessBase : 0) -
        (isBenchModule ? thicknessBase : 0) -
        (drawerClicked?.mesh?.userData?.type === InsideType.ACCESSORY_FLIP
          ? thicknessBase
          : 0);
    } else if (typeHau === TypeConnerModule.SUB) {
      model.position.z =
        clonedModule.formularController.currentOptions.rongK2 / 2 -
        Number(clonedModule.formularController.currentDimensions.depth);

      model.rotation.y += Math.PI / 2;
    } else {
      model.position.x =
        Number(clonedModule.formularController.currentDimensions.depth) -
        clonedModule.formularController.currentOptions.rongK1 / 2;
    }

    model.position.y =
      bottomPos.y +
      (bottomEdge.mesh.userData?.type !== undefined
        ? sizeBottom.y / 2 + slit
        : 0) +
      (bottomEdge.mesh.userData?.type === InsideType.DRAWER ||
      bottomEdge.mesh.userData?.type === InsideType.ACCESSORY_DISH_LOWER
        ? sizeBottom.y / 2 +
          bottomEdge.mesh.userData.topGap +
          this.braceHeight / 2
        : 0);

    //Cửa hộc keos
    if (drawerClicked) {
      model.position.y =
        drawerClicked.mesh.userData.indexDuplicate * (drawerHeight + 0.001) +
        (bottomFrameEdge.userData?.type === undefined
          ? bottomFrameEdge.position.y
          : bottomFrameEdge.position.y + bottomFrameEdgeSize.y / 2);

      // if (drawerClicked.mesh.userData.type === InsideType.ACCESSORY_FLIP) {
      //   model.position.y = drawerPos.y;
      // }
    }
    //Cửa hộc keos

    // this.setDoorZPosition(
    //   model,
    //   leftEdgeDoor,
    //   orientation,
    //   leftEdge,
    //   leftPos,
    //   sizeLeft,
    //   sizeZ,
    //   slit,
    //   checkSubZ
    // );
    if (typeHau === TypeConnerModule.SUB) {
      this.setDoorXPosition(
        model,
        leftEdgeDoor,
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        sizeZ,
        slit,
        checkSubZ
      );
    } else {
      this.setDoorZPosition(
        model,
        leftEdgeDoor,
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        sizeZ,
        slit,
        checkSubZ
      );
    }

    //Scale
    const scaleY = sizeY / originalSize.y;
    const horizontalType =
      sizeY > HorizontalType.LIM_HEIGHT
        ? HorizontalType.TWO_D
        : HorizontalType.ONE_D;

    let scaleZ;
    if (drawerClicked) {
      scaleZ = sizeZ / originalSize.z;
    } else {
      // scaleZ =
      //   sizeZ <= moduleData.limit.width.max &&
      //   leftEdgeDoor?.mesh?.userData?.type !== InsideType.DOOR &&
      //   rightEdgeDoor?.mesh?.userData?.type !== InsideType.DOOR
      //     ? sizeZ / originalSize.z
      //     : sizeZ / 2 / originalSize.z;

      scaleZ =
        doorDirection === DoorDirection.VERTICAL
          ? sizeZ <= moduleData.limit.width.max &&
            leftEdgeDoor?.mesh?.userData?.type !== InsideType.DOOR &&
            rightEdgeDoor?.mesh?.userData?.type !== InsideType.DOOR
            ? sizeZ / originalSize.z
            : sizeZ / 2 / originalSize.z
          : sizeZ / originalSize.z;
    }

    model.scale.y = scaleY;
    model.scale.z = scaleZ;

    model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
    model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
    model.userData.topEdgeUuid = topEdge.mesh.uuid;
    model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;
    model.userData.leftEdgeDoorUuid = leftEdgeDoor.mesh.uuid;
    model.userData.rightEdgeDoorUuid = rightEdgeDoor.mesh.uuid;

    model.userData.beveled = moduleData?.beveled ?? false;
    model.userData.typeHau = typeHau;
    model.userData.doorDirection = doorDirection;
    model.userData.horizontalType = horizontalType;

    const check = this.handleCheckLeftRight(
      clonedModule,
      model,
      leftEdge.mesh,
      rightEdge.mesh,
      typeHau
    );

    model.userData.handlePosition = {
      left: check.isLeft,
      right: check.isRight,
      center: check.isCenter,
      // current: check.isRight ? 'right' : check.isLeft ? 'left' : null,
      // current: check.isRight ? 'right' : check.isLeft ? 'left' : 'center',
      current: check.isRight
        ? 'right'
        : check.isLeft
          ? 'left'
          : check.isCenter
            ? 'center'
            : 'drawer',
    };

    addModelToCabinet();

    //Thêm tay nắm mặc định
    const handleModel = this.loadHandleModel(
      moduleData.default_handle,
      clonedModule,
      model,
      addBraceToCabinet
    );

    //Thêm tay nâng mặc định cho cửa ngang
    if (doorDirection === DoorDirection.HORIZONTAL) {
      const liftSupportModel = this.loadLiftSupportModel(
        model.userData.defaultLiftSupport,
        clonedModule,
        model,
        addBraceToCabinet
      );
    }

    //Scale cửa đặc biệt
    const materialType = moduleData.material_type;

    let doorModel = model;

    // if (!materialType?.includes('tron')) {
    setTimeout(async () => {
      // await this.cutSpecialDoor(clonedModule, model, materialType);
      doorModel = await this.cutSpecialDoor(
        clonedModule,
        model,
        materialType,
        doorDirection,
        horizontalType
      );
    }, 200);
    // }
  }

  scaleBasicDoorForCornerModule(
    moduleData,
    model,
    clonedModule,
    addModelToCabinet,
    addBraceToCabinet
  ) {
    const orientation = this.getModuleOrientation(clonedModule.group);
    const lstDoor = ['DOOR-1', 'DOOR-2', 'DOOR-3', 'DOOR-4'];
    const foundMeshes = lstDoor
      .map(edgeName => {
        let found = null;

        clonedModule.group.traverse(mesh => {
          if (
            mesh.isMesh &&
            mesh.name.includes(edgeName) &&
            mesh.scale.x > 0 &&
            mesh.scale.z > 0
          ) {
            found = mesh;
          }
        });

        return found;
      })
      .filter(mesh => mesh !== null);

    // Duyệt qua từng mesh tìm được
    for (const item of foundMeshes) {
      const targetMesh = item.clone();

      // Clone model để tạo door mới cho mỗi mesh
      const doorModel = model.clone();

      // Lấy size 3D của target mesh
      const boundingBox = new THREE.Box3().setFromObject(targetMesh);
      const targetSize = new THREE.Vector3();
      boundingBox.getSize(targetSize);

      // Lấy size 3D của md
      const mdBox = new THREE.Box3().setFromObject(clonedModule.group);
      const mdSize = new THREE.Vector3();
      mdBox.getSize(mdSize);

      // Reset scale của door model
      doorModel.scale.set(1, 1, 1);

      // Lấy size hiện tại của door model
      const doorBoundingBox = new THREE.Box3().setFromObject(doorModel);
      const doorSize = new THREE.Vector3();
      doorBoundingBox.getSize(doorSize);

      // Scale door model để match với target mesh
      if (item.name.includes('DOOR-1') || item.name.includes('DOOR-2')) {
        doorModel.rotation.y = Math.PI / 2;

        doorModel.scale.x = targetSize.z / doorSize.x;
        doorModel.scale.y = targetSize.y / doorSize.y;
        doorModel.scale.z = targetSize.x / doorSize.z;
      } else {
        doorModel.scale.x = targetSize.x / doorSize.x;
        doorModel.scale.y = targetSize.y / doorSize.y;
        doorModel.scale.z = targetSize.z / doorSize.z;
      }

      // Set position của door model
      doorModel.position.copy(targetMesh.position);

      if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
        doorModel.position.setX(targetMesh.position.x - mdSize.x / 2);
        doorModel.position.setZ(targetMesh.position.z + mdSize.z / 2);

        if (
          targetMesh.name.includes('DOOR-1') ||
          targetMesh.name.includes('DOOR-2')
        ) {
          doorModel.position.setX(
            targetMesh.position.x - mdSize.x / 2 + targetSize.x
          );
        }
      } else if (
        orientation === 'ROTATED_90' ||
        orientation === 'ROTATED_270'
      ) {
        doorModel.position.setX(targetMesh.position.x - mdSize.z / 2);
        doorModel.position.setZ(targetMesh.position.z + mdSize.x / 2);

        if (
          targetMesh.name.includes('DOOR-1') ||
          targetMesh.name.includes('DOOR-2')
        ) {
          doorModel.position.setX(
            targetMesh.position.x - mdSize.z / 2 + targetSize.x
          );
        }
      }

      targetMesh.visible = true;

      const isHandleLeft =
        targetMesh.name.includes('DOOR-2') ||
        targetMesh.name.includes('DOOR-4');

      doorModel.userData.handlePosition = {
        left: isHandleLeft,
        right: !isHandleLeft,
        current: isHandleLeft ? 'left' : 'right',
      };
      doorModel.userData.isInside = true;
      doorModel.userData.default_handle = moduleData.default_handle;
      doorModel.userData.default_lock = moduleData.default_lock;
      doorModel.userData.default_glass_type = moduleData.default_glass_type;
      doorModel.userData.cornerDoor = targetMesh.name;
      doorModel.userData.beveled = false;

      this.setInfoModelChild?.(doorModel);

      addModelToCabinet(doorModel);

      //Thêm tay nắm mặc định
      const handleModel = this.loadHandleModel(
        moduleData.default_handle,
        clonedModule,
        doorModel,
        addBraceToCabinet
      );

      //Scale cửa đặc biệt
      const materialType = moduleData.material_type;

      if (!materialType?.includes('tron')) {
        setTimeout(async () => {
          await this.cutSpecialDoor(clonedModule, doorModel, materialType);
        }, 200);
      }
    }
  }

  scale45DoorForCornerModule(
    moduleData,
    model,
    clonedModule,
    addModelToCabinet,
    addBraceToCabinet
  ) {
    const orientation = this.getModuleOrientation(clonedModule.group);
    const lstDoor = ['DOOR-1', 'DOOR-2', 'DOOR-3', 'DOOR-4'];
    const foundMeshes = lstDoor
      .map(edgeName => {
        let found = null;

        clonedModule.group.traverse(mesh => {
          if (mesh.isMesh && mesh.name.includes(edgeName) && mesh.scale.x > 0) {
            found = mesh;
          }
        });

        return found;
      })
      .filter(mesh => mesh !== null);

    // Duyệt qua từng mesh tìm được
    foundMeshes.forEach(item => {
      const targetMesh = item.clone();

      // Clone model để tạo door mới cho mỗi mesh
      const doorModel = model.clone();

      // Lấy size 3D của target mesh
      const boundingBox = new THREE.Box3().setFromObject(targetMesh);
      const targetSize = new THREE.Vector3();
      boundingBox.getSize(targetSize);

      // Lấy size 3D của md
      const mdBox = new THREE.Box3().setFromObject(clonedModule.group);
      const mdSize = new THREE.Vector3();
      mdBox.getSize(mdSize);

      // Reset scale của door model
      doorModel.scale.set(1, 1, 1);

      // Lấy size hiện tại của door model
      const doorBoundingBox = new THREE.Box3().setFromObject(doorModel);
      const doorSize = new THREE.Vector3();
      doorBoundingBox.getSize(doorSize);

      // Set position của door model
      doorModel.position.copy(targetMesh.position);

      if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
        doorModel.position.setX(targetMesh.position.x - mdSize.x / 2);
        doorModel.position.setZ(targetMesh.position.z + mdSize.z / 2);

        if (
          targetMesh.name.includes('DOOR-1') ||
          targetMesh.name.includes('DOOR-2')
        ) {
          doorModel.position.setX(
            targetMesh.position.x - mdSize.x / 2 + targetSize.x
          );
        }
      } else if (
        orientation === 'ROTATED_90' ||
        orientation === 'ROTATED_270'
      ) {
        doorModel.position.setX(targetMesh.position.x - mdSize.z / 2);
        doorModel.position.setZ(targetMesh.position.z + mdSize.x / 2);

        if (
          targetMesh.name.includes('DOOR-1') ||
          targetMesh.name.includes('DOOR-2')
        ) {
          doorModel.position.setX(
            targetMesh.position.x - mdSize.z / 2 + targetSize.x
          );
        }
      }

      if (
        clonedModule.moduleType.toLowerCase().includes('kitchen') &&
        clonedModule.cabinetType.toLowerCase().includes('lower')
      ) {
        //Chiều cao và vị trí cửa của riêng tủ góc bếp dưới
        targetSize.y -= 0.025;
      } else if (clonedModule.cabinetType.toLowerCase().includes('ceiling')) {
        //Chiều cao và vị trí cửa của tủ góc đụng trần
        targetSize.y -= topDoorGapBase + thicknessBase;

        doorModel.position.setY(
          targetMesh.position.y + topDoorGapBase + thicknessBase
        );
      }

      // Scale door model để match với target mesh
      if (item.name.includes('DOOR-1') || item.name.includes('DOOR-2')) {
        doorModel.rotation.y = Math.PI / 2;

        doorModel.scale.x = targetSize.z / doorSize.x;
        doorModel.scale.y = targetSize.y / doorSize.y;
        doorModel.scale.z = targetSize.x / doorSize.z;
      } else {
        doorModel.scale.x = targetSize.x / doorSize.x;
        doorModel.scale.y = targetSize.y / doorSize.y;
        doorModel.scale.z = targetSize.z / doorSize.z;
      }

      targetMesh.visible = true;

      const isHandleLeft =
        targetMesh.name.includes('DOOR-2') ||
        targetMesh.name.includes('DOOR-4');

      doorModel.userData.handlePosition = {
        left: isHandleLeft,
        right: !isHandleLeft,
        current: isHandleLeft ? 'left' : 'right',
      };
      doorModel.userData.isInside = true;
      doorModel.userData.default_handle = moduleData.default_handle;
      doorModel.userData.default_lock = moduleData.default_lock;
      doorModel.userData.default_glass_type = moduleData.default_glass_type;
      doorModel.userData.cornerDoor = targetMesh.name;
      doorModel.userData.beveled = true;

      this.setInfoModelChild?.(doorModel);

      addModelToCabinet(doorModel);

      //Scale cửa đặc biệt
      const materialType = moduleData.material_type;

      if (!materialType?.includes('tron')) {
        setTimeout(async () => {
          await this.cutSpecialDoor(clonedModule, doorModel, materialType);
        }, 200);
      }
    });

    //Thêm bạ cho tủ góc đối với tủ đụng trần
    if (clonedModule.cabinetType.toLowerCase().includes('ceiling')) {
      this.createAndAddBraceForCornerModule(
        model,
        clonedModule,
        addBraceToCabinet
      );
    }
  }
  // TỦ GÓC L END

  calculateUDrawerDoorTransform(
    originalSize,
    qtyDrawer,
    leftEdge,
    rightEdge,
    clonedModule,
    is45Door
  ) {
    // const doorClone = door.clone();
    // doorClone.scale.set(1, 1, 1);
    // const originalSize = this.getSize(doorClone);

    const leftEdgePos = clonedModule.group.worldToLocal(
      leftEdge.getWorldPosition(new THREE.Vector3()).clone()
    );
    const rightEdgePos = clonedModule.group.worldToLocal(
      rightEdge.getWorldPosition(new THREE.Vector3()).clone()
    );

    const leftEdgeBBox = new THREE.Box3().setFromObject(leftEdge);
    const leftEdgeSize = new THREE.Vector3();
    leftEdgeBBox.getSize(leftEdgeSize);

    const distance =
      leftEdgePos.distanceTo(rightEdgePos) -
      Math.min(leftEdgeSize.x, leftEdgeSize.z);

    const sizeZ = distance;
    const sizeY = leftEdgeSize.y / qtyDrawer - (is45Door ? topDoorGapBase : 0);

    const scaleZ = sizeZ / originalSize.z;
    const scaleY = sizeY / originalSize.y;

    return { leftEdgePos, leftEdgeSize, scaleZ, scaleY, sizeZ, sizeY };
  }

  //Cửa cho hộc kéo trong tủ - START
  scaleBasicDoorForUDrawer(
    drawerClicked,
    model,
    edges,
    sizes,
    moduleData,
    clonedModule,
    originalSize,
    gaps,
    addModelToCabinet,
    addBraceToCabinet
  ) {
    let vachTrai = null;
    let vachPhai = null;

    drawerClicked.mesh.traverse(child => {
      if (child.isMesh) {
        if (child.name === 'VACH-TRAI-U') {
          vachTrai = child;
        } else if (child.name === 'VACH-PHAI-U') {
          vachPhai = child;
        }
      }
    });

    if (!vachTrai || !vachPhai) {
      console.warn('Không tìm thấy VACH-TRAI-U hoặc VACH-PHAI-U');
      return;
    }

    const qtyDrawer = drawerClicked.mesh.userData.infoChildDrawer.numDrawer;
    const { leftEdgePos, leftEdgeSize, scaleZ, scaleY, sizeZ, sizeY } =
      this.calculateUDrawerDoorTransform(
        originalSize,
        qtyDrawer,
        vachTrai,
        vachPhai,
        clonedModule,
        false
      );

    let idxDrawer = 0;

    clonedModule.listInside.forEach(inside => {
      if (
        inside.gltf.userData.isChildUDrawer === true &&
        inside.gltf.userData.parentUDrawerUuid === drawerClicked.mesh.uuid
      ) {
        const mdClone = model.clone();
        const layer = 1;

        mdClone.scale.y = scaleY;
        mdClone.scale.z = scaleZ;

        mdClone.position.y = leftEdgePos.y + sizeY * idxDrawer;
        mdClone.position.x =
          leftEdgePos.x +
          Math.max(leftEdgeSize.x, leftEdgeSize.z) -
          Math.min(leftEdgeSize.x, leftEdgeSize.z);
        mdClone.position.z =
          leftEdgePos.z - Math.min(leftEdgeSize.x, leftEdgeSize.z);

        //Lưu thông tin hộc kéo tương ứng
        mdClone.userData.frameUuid = drawerClicked.mesh.uuid;
        mdClone.userData.drawerUuid = inside.gltf.uuid;
        mdClone.userData.idxDrawer = idxDrawer;

        mdClone.userData.leftEdgeUuid = vachTrai.uuid;
        mdClone.userData.rightEdgeUuid = vachPhai.uuid;

        mdClone.userData.isInside = true;
        mdClone.userData.beveled = moduleData.beveled;
        mdClone.userData.default_handle = moduleData.default_handle;
        mdClone.userData.default_lock = moduleData.default_lock;
        mdClone.userData.layer = layer;
        mdClone.userData.handlePosition = {
          left: false,
          right: false,
          current: 'center',
        };

        addModelToCabinet(mdClone);

        //Thêm tay nắm mặc định
        const handleModel = this.loadHandleModel(
          moduleData.default_handle,
          clonedModule,
          mdClone,
          addBraceToCabinet,
          layer
        );

        //Scale cửa đặc biệt
        const materialType = moduleData.material_type;

        // if (!materialType?.includes('tron')) {
        setTimeout(async () => {
          await this.cutSpecialDoor(clonedModule, mdClone, materialType);
        }, 200);
        // }

        idxDrawer++;
      }
    });
  }

  scale45DoorForUDrawer(
    drawerClicked,
    model,
    edges,
    sizes,
    moduleData,
    clonedModule,
    originalSize,
    gaps,
    addModelToCabinet,
    addBraceToCabinet
  ) {
    let vachTrai = null;
    let vachPhai = null;

    drawerClicked.mesh.traverse(child => {
      if (child.isMesh) {
        if (child.name === 'VACH-TRAI-U') {
          vachTrai = child;
        } else if (child.name === 'VACH-PHAI-U') {
          vachPhai = child;
        }
      }
    });

    if (!vachTrai || !vachPhai) {
      console.warn('Không tìm thấy VACH-TRAI-U hoặc VACH-PHAI-U');
      return;
    }

    const qtyDrawer = drawerClicked.mesh.userData.infoChildDrawer.numDrawer;
    const { leftEdgePos, leftEdgeSize, scaleZ, scaleY, sizeZ, sizeY } =
      this.calculateUDrawerDoorTransform(
        originalSize,
        qtyDrawer,
        vachTrai,
        vachPhai,
        clonedModule,
        true
      );

    let idxDrawer = 0;

    clonedModule.listInside.forEach(inside => {
      if (
        inside.gltf.userData.isChildUDrawer === true &&
        inside.gltf.userData.parentUDrawerUuid === drawerClicked.mesh.uuid
      ) {
        const mdClone = model.clone();
        const layer = 1;

        mdClone.scale.y = scaleY;
        mdClone.scale.z = scaleZ;

        mdClone.position.y =
          leftEdgePos.y + (sizeY + topDoorGapBase) * idxDrawer;
        mdClone.position.x =
          leftEdgePos.x +
          Math.max(leftEdgeSize.x, leftEdgeSize.z) -
          Math.min(leftEdgeSize.x, leftEdgeSize.z);
        mdClone.position.z =
          leftEdgePos.z - Math.min(leftEdgeSize.x, leftEdgeSize.z);

        //Lưu thông tin hộc kéo tương ứng
        mdClone.userData.frameUuid = drawerClicked.mesh.uuid;
        mdClone.userData.drawerUuid = inside.gltf.uuid;
        mdClone.userData.idxDrawer = idxDrawer;

        mdClone.userData.leftEdgeUuid = vachTrai.uuid;
        mdClone.userData.rightEdgeUuid = vachPhai.uuid;

        mdClone.userData.isInside = true;
        mdClone.userData.beveled = moduleData.beveled;
        mdClone.userData.default_handle = moduleData.default_handle;
        mdClone.userData.default_lock = moduleData.default_lock;
        mdClone.userData.layer = layer;
        mdClone.userData.handlePosition = {
          left: false,
          right: false,
          current: 'center',
        };

        addModelToCabinet(mdClone);

        // // Tạo và thêm tấm bạ
        // const braceUuid = this.createAndAddBrace(
        //   null, //kh truyen drawer
        //   mdClone,
        //   edges,
        //   sizes,
        //   clonedModule,
        //   originalSize,
        //   addBraceToCabinet,
        //   true //isUDrawerDoor=true
        // );

        // mdClone.userData.braceUuid = braceUuid;

        this.createAndAddBrace(
          null, //kh truyen drawer
          mdClone,
          edges,
          sizes,
          clonedModule,
          originalSize,
          addBraceToCabinet,
          true, //isUDrawerDoor=true
          null, //isCurveDoor,
          null, //isBenchDoor
          layer
        )
          .then(braceUuid => {
            mdClone.userData.braceUuid = braceUuid;
          })
          .catch(error => {
            console.error('Error creating brace:', error);
          });

        //Scale cửa đặc biệt
        const materialType = moduleData.material_type;

        // if (!materialType?.includes('tron')) {
        setTimeout(async () => {
          await this.cutSpecialDoor(clonedModule, mdClone, materialType);
        }, 200);
        // }

        idxDrawer++;
      }
    });
  }
  //Cửa cho hộc kéo trong tủ - END

  //Cửa cho phụ kiên lật tủ giày- START
  scaleBasicDoorForAccessoryFlip(
    drawerClicked,
    model,
    edges,
    sizes,
    moduleData,
    clonedModule,
    originalSize,
    gaps,
    addModelToCabinet,
    addBraceToCabinet
  ) {
    let vachTrai = null;
    let vachPhai = null;

    drawerClicked.mesh.traverse(child => {
      if (child.isMesh) {
        if (child.name === 'VACH-TRAI-U') {
          vachTrai = child;
        } else if (child.name === 'VACH-PHAI-U') {
          vachPhai = child;
        }
      }
    });

    if (!vachTrai || !vachPhai) {
      console.warn('Không tìm thấy VACH-TRAI-U hoặc VACH-PHAI-U');
      return;
    }

    const qtyDrawer = drawerClicked.mesh.userData.infoChildDrawer.numDrawer;
    const { leftEdgePos, leftEdgeSize, scaleZ, scaleY, sizeZ, sizeY } =
      this.calculateUDrawerDoorTransform(
        originalSize,
        qtyDrawer,
        vachTrai,
        vachPhai,
        clonedModule,
        false
      );

    let idxDrawer = 0;

    clonedModule.listInside.forEach(inside => {
      if (
        inside.gltf.userData.isChildUDrawer === true &&
        inside.gltf.userData.parentUDrawerUuid === drawerClicked.mesh.uuid
      ) {
        const mdClone = model.clone();

        mdClone.scale.y = scaleY;
        mdClone.scale.z = scaleZ;

        mdClone.position.y = leftEdgePos.y + sizeY * idxDrawer;
        mdClone.position.x =
          leftEdgePos.x +
          Math.max(leftEdgeSize.x, leftEdgeSize.z) -
          Math.min(leftEdgeSize.x, leftEdgeSize.z);
        mdClone.position.z =
          leftEdgePos.z - Math.min(leftEdgeSize.x, leftEdgeSize.z);

        //Lưu thông tin hộc kéo tương ứng
        mdClone.userData.frameUuid = drawerClicked.mesh.uuid;
        mdClone.userData.drawerUuid = inside.gltf.uuid;
        mdClone.userData.idxDrawer = idxDrawer;

        mdClone.userData.leftEdgeUuid = vachTrai.uuid;
        mdClone.userData.rightEdgeUuid = vachPhai.uuid;

        mdClone.userData.beveled = moduleData.beveled;

        addModelToCabinet(mdClone);

        //Thêm tay nắm mặc định
        const handleModel = this.loadHandleModel(
          moduleData.default_handle,
          clonedModule,
          mdClone,
          addBraceToCabinet
        );

        idxDrawer++;
      }
    });
  }

  //Cửa cho phụ kiên lật tủ giày- END

  scale45DoorWithoutTopEdge(
    drawerClicked,
    model,
    edges,
    sizes,
    moduleData,
    clonedModule,
    typeHau,
    originalSize,
    gaps,
    addModelToCabinet
  ) {
    // //Cửa cho tủ góc
    // if (clonedModule.cabinetType.toLowerCase().includes('corner')) {
    //   this.scale45DoorForCornerModule(
    //     moduleData,
    //     model,
    //     clonedModule,
    //     addModelToCabinet
    //     // addBraceToCabinet
    //   );

    //   return;
    // }
    // //Cửa cho tủ góc

    const {
      bottomEdge,
      rightEdge,
      leftEdge,
      backEdge,
      rightEdgeDoor,
      leftEdgeDoor,
    } = edges; // Nếu cả hai bên đều có type === InsideType.DOOR thì return
    if (
      rightEdgeDoor.mesh.userData?.type === InsideType.DOOR &&
      leftEdgeDoor.mesh.userData?.type === InsideType.DOOR
    ) {
      // message.error("Không thể thêm cửa ở vị trí này!!!");
      openNotification({
        key: 'cant-add-door-existing-door',
        icon: <Icon.ICWarning />,
        iconColor: '#F97D16',
        title: i18n.t('Unable to add the product.'),
        description: 'Vị trí đã có sản phẩm hoặc kích thước không phù hợp.',
        placement: 'bottomRight',
        closable: true,
        duration: 2,
      });
      return;
    }

    const { sizeBottom, sizeRight, sizeLeft } = sizes;
    const { slit, countertopGap } = gaps;

    // Tìm tấm top (xuongTruoc)
    // const possibleNames = ['XUONG-TRUOC-1', 'G-NEP-TRUOC-3'];
    const possibleNames = ['XUONG-TRUOC-1', 'NEP-TRUOC-1', 'G-NEP-TRUOC-3'];
    let xuongTruoc = null;

    clonedModule.group.traverse(mesh => {
      if (mesh.isMesh && !xuongTruoc) {
        // Kiểm tra tên mesh có chứa bất kỳ tên nào trong danh sách
        if (possibleNames.some(name => mesh.name.includes(name))) {
          xuongTruoc = mesh;
        }
      }
    });

    const sizeXuongTruoc = this.getSize(xuongTruoc);
    const xuongTruocPos = clonedModule.group.worldToLocal(
      xuongTruoc.getWorldPosition(new THREE.Vector3()).clone()
    );

    const orientation = this.getModuleOrientation(clonedModule.group);
    const leftPos = clonedModule.group.worldToLocal(
      leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
    );
    const rightPos = clonedModule.group.worldToLocal(
      rightEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
    );
    const bottomPos = clonedModule.group.worldToLocal(
      bottomEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
    );

    let drawerPos;
    if (drawerClicked) {
      drawerPos = clonedModule.group.worldToLocal(
        drawerClicked?.mesh.getWorldPosition(new THREE.Vector3()).clone()
      );
    }

    // Size
    let sizeY =
      xuongTruocPos.y +
      sizeXuongTruoc.y -
      bottomPos.y -
      (topDoorGapBase + countertopGap);

    //Cửa hộc kéo
    if (drawerClicked) {
      if (
        bottomEdge.mesh.userData?.type === InsideType.DRAWER ||
        bottomEdge.mesh.userData?.type === InsideType.ACCESSORY_DISH_LOWER
      ) {
        sizeY =
          xuongTruocPos.y +
          sizeXuongTruoc.y -
          (drawerPos.y -
            drawerClicked.mesh.userData.bottomGap -
            this.braceIndent) -
          (topDoorGapBase + countertopGap);
      }

      //Lưu thông tin hộc kéo
      model.userData.drawerUuid = drawerClicked.mesh.uuid;
    }
    //Cửa hộc kéo

    // const sizeZ = this.setDoorZSize(
    //   orientation,
    //   leftEdge,
    //   leftPos,
    //   sizeLeft,
    //   rightEdge,
    //   rightPos,
    //   sizeRight,
    //   slit
    // );
    let sizeZ;
    if (typeHau === TypeConnerModule.SUB) {
      sizeZ = this.setDoorXSize(
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        rightEdge,
        rightPos,
        sizeRight,
        slit
      );
    } else {
      sizeZ = this.setDoorZSize(
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        rightEdge,
        rightPos,
        sizeRight,
        slit
      );
    }

    // Position
    // model.position.x =
    //   Number(clonedModule.formularController.currentDimensions.depth) / 2;

    if (!typeHau) {
      model.position.x =
        Number(clonedModule.formularController.currentDimensions.depth) / 2 -
        (drawerClicked?.mesh?.userData?.type === InsideType.ACCESSORY_FLIP
          ? thicknessBase
          : 0);
    } else if (typeHau === TypeConnerModule.SUB) {
      model.position.z =
        clonedModule.formularController.currentOptions.rongK2 / 2 -
        Number(clonedModule.formularController.currentDimensions.depth);

      model.rotation.y += Math.PI / 2;
    } else {
      model.position.x =
        Number(clonedModule.formularController.currentDimensions.depth) -
        clonedModule.formularController.currentOptions.rongK1 / 2;
    }

    model.position.y = bottomPos.y;
    //Cửa hộc keos
    if (drawerClicked) {
      if (
        bottomEdge.mesh.userData?.type === InsideType.DRAWER ||
        bottomEdge.mesh.userData?.type === InsideType.ACCESSORY_DISH_LOWER
      ) {
        model.position.y =
          drawerPos.y -
          drawerClicked.mesh.userData.bottomGap -
          this.braceIndent;
      }
    }
    //Cửa hộc keos

    // this.setDoorZPosition(
    //   model,
    //   leftEdgeDoor,
    //   orientation,
    //   leftEdge,
    //   leftPos,
    //   sizeLeft,
    //   sizeZ,
    //   slit
    // );
    if (typeHau === TypeConnerModule.SUB) {
      this.setDoorXPosition(
        model,
        leftEdgeDoor,
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        sizeZ,
        slit
      );
    } else {
      this.setDoorZPosition(
        model,
        leftEdgeDoor,
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        sizeZ,
        slit
      );
    }

    // Apply scale
    const scaleY = sizeY / originalSize.y;

    let scaleZ;

    if (drawerClicked) {
      scaleZ = sizeZ / originalSize.z;
    } else {
      // scaleZ =
      //   sizeZ >= moduleData.limit.width.min &&
      //   sizeZ < moduleData.limit.width.max
      //     ? sizeZ / 2 / originalSize.z
      //     : sizeZ >= moduleData.limit.width.max &&
      //         sizeZ / 2 >= moduleData.limit.width.min &&
      //         sizeZ / 2 <= moduleData.limit.width.max
      //       ? sizeZ / 2 / originalSize.z
      //       : moduleData.limit.width.max / originalSize.z;

      scaleZ =
        sizeZ <= moduleData.limit.width.max &&
        leftEdgeDoor?.mesh?.userData?.type !== InsideType.DOOR &&
        rightEdgeDoor?.mesh?.userData?.type !== InsideType.DOOR
          ? sizeZ / originalSize.z
          : sizeZ / 2 / originalSize.z;
    }

    model.scale.y = scaleY;
    model.scale.z = scaleZ;

    // Set userData
    model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
    model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
    model.userData.topEdgeUuid = xuongTruoc.uuid;
    model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;

    model.userData.leftEdgeDoorUuid = leftEdgeDoor.mesh.uuid;
    model.userData.rightEdgeDoorUuid = rightEdgeDoor.mesh.uuid;

    model.userData.braceUuid = undefined;
    model.userData.beveled = moduleData?.beveled ?? true;
    model.userData.typeHau = typeHau;

    const check = this.handleCheckLeftRight(
      clonedModule,
      model,
      leftEdge.mesh,
      rightEdge.mesh,
      typeHau
    );

    model.userData.handlePosition = {
      left: check.isLeft,
      right: check.isRight,
      center: check.isCenter,
      // current: check.isRight ? 'right' : check.isLeft ? 'left' : null,
      // current: check.isRight ? 'right' : check.isLeft ? 'left' : 'center',
      current: check.isRight
        ? 'right'
        : check.isLeft
          ? 'left'
          : check.isCenter
            ? 'center'
            : 'drawer',
    };

    addModelToCabinet();

    ///test vat 45
    // setTimeout(() => {
    //   this.cut45Door(clonedModule, model);
    // }, 50);
    ///test vat 45

    //Scale cửa đặc biệt
    const materialType = moduleData.material_type;

    // if (!materialType?.includes('tron')) {
    setTimeout(async () => {
      await this.cutSpecialDoor(clonedModule, model, materialType);
    }, 200);
    // }
  }

  scale45DoorWithTopEdge(
    drawerClicked,
    model,
    edges,
    sizes,
    moduleData,
    clonedModule,
    typeHau,
    originalSize,
    gaps,
    addModelToCabinet,
    addBraceToCabinet
  ) {
    const doorDirection = moduleData.door_direction;
    const isCurveModule =
      clonedModule.cabinetType.toLowerCase().includes('curve') &&
      clonedModule.formularController.currentOptions.caoKhungHoc;
    const isBenchModule =
      clonedModule.cabinetType.toLowerCase().includes('bench') ||
      clonedModule.cabinetType.toLowerCase().includes('tv');

    let checkSubZ = false;
    if (drawerClicked) {
      if (
        isCurveModule ||
        drawerClicked.mesh.userData.type === InsideType.ACCESSORY_FLIP
      ) {
        checkSubZ = true;
      }
    }

    // //Cửa cho tủ góc
    // if (clonedModule.cabinetType.toLowerCase().includes('corner')) {
    //   this.scale45DoorForCornerModule(
    //     moduleData,
    //     model,
    //     clonedModule,
    //     addModelToCabinet,
    //     addBraceToCabinet
    //   );

    //   return;
    // }
    // //Cửa cho tủ góc

    const {
      bottomEdge,
      rightEdge,
      leftEdge,
      backEdge,
      topEdge,
      rightEdgeDoor,
      leftEdgeDoor,
    } = edges;

    if (
      rightEdgeDoor.mesh.userData?.type === InsideType.DOOR &&
      leftEdgeDoor.mesh.userData?.type === InsideType.DOOR
    ) {
      // Nếu cả hai bên đều có type === InsideType.DOOR thì return
      // message.error("Không thể thêm cửa ở vị trí này!!!");
      openNotification({
        key: 'cant-add-door-existing-door',
        icon: <Icon.ICWarning />,
        iconColor: '#F97D16',
        title: i18n.t('Unable to add the product.'),
        description: 'Vị trí đã có sản phẩm hoặc kích thước không phù hợp.',
        placement: 'bottomRight',
        closable: true,
        duration: 2,
      });
      return;
    }

    const { sizeBottom, sizeRight, sizeLeft, sizeTop } = sizes;
    const { slit, countertopGap } = gaps;

    const orientation = this.getModuleOrientation(clonedModule.group);
    const leftPos = clonedModule.group.worldToLocal(
      leftEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
    );
    const rightPos = clonedModule.group.worldToLocal(
      rightEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
    );
    const topPos = clonedModule.group.worldToLocal(
      topEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
    );
    const bottomPos = clonedModule.group.worldToLocal(
      bottomEdge?.mesh.getWorldPosition(new THREE.Vector3()).clone()
    );

    let drawerPos;
    if (drawerClicked) {
      drawerPos = clonedModule.group.worldToLocal(
        drawerClicked?.mesh.getWorldPosition(new THREE.Vector3()).clone()
      );
    }

    //Size
    let sizeY =
      topPos.y -
      bottomPos.y -
      topDoorGapBase +
      (topEdge.mesh.name.toUpperCase().includes('NOC') &&
      clonedModule.cabinetType.toLowerCase().includes('lower') &&
      isCurveModule === false &&
      isBenchModule === false
        ? sizeTop.y + topDoorGapBase
        : 0) +
      (topEdge.mesh.name.toUpperCase().includes('NOC') &&
      clonedModule.cabinetType.toLowerCase().includes('upper') &&
      clonedModule.moduleType.toLowerCase().includes('hood')
        ? sizeTop.y
        : 0);

    //Cửa cho tủ trên (trừ máy hút mùi và tủ TV treo)
    if (
      clonedModule.cabinetType.toLowerCase().includes('upper') &&
      !clonedModule.moduleType.toLowerCase().includes('hood') &&
      !clonedModule.moduleType.toLowerCase().includes('tv')
    ) {
      sizeY = topPos.y - bottomPos.y + sizeTop.y;
    }
    //Cửa cho tủ trên (trừ máy hút mùi và tủ TV treo)

    //Cửa hộc kéo
    if (drawerClicked) {
      if (drawerClicked.mesh.userData.type === InsideType.U_DRAWER) {
        this.scale45DoorForUDrawer(
          drawerClicked,
          model,
          edges,
          sizes,
          moduleData,
          clonedModule,
          originalSize,
          gaps,
          addModelToCabinet,
          addBraceToCabinet
        );

        return;
      }

      if (
        topEdge.mesh.userData?.type === InsideType.DRAWER ||
        topEdge.mesh.userData?.type === InsideType.ACCESSORY_DISH_LOWER
      ) {
        sizeY -= drawerClicked.mesh.userData.bottomGap + this.braceIndent;
      }

      if (
        bottomEdge.mesh.userData?.type === InsideType.DRAWER ||
        bottomEdge.mesh.userData?.type === InsideType.ACCESSORY_DISH_LOWER
      ) {
        sizeY =
          topPos.y -
          (drawerPos.y -
            drawerClicked.mesh.userData.bottomGap -
            this.braceIndent) -
          topDoorGapBase +
          (topEdge.mesh.name.toUpperCase().includes('NOC') &&
          clonedModule.cabinetType.toLowerCase().includes('lower') &&
          isCurveModule === false &&
          isBenchModule === false
            ? sizeTop.y + topDoorGapBase
            : 0);

        if (
          topEdge.mesh.userData?.type === InsideType.DRAWER ||
          topEdge.mesh.userData?.type === InsideType.ACCESSORY_DISH_LOWER
        ) {
          sizeY =
            topPos.y -
            topEdge.mesh.userData.bottomGap -
            this.braceHeight +
            this.braceIndent -
            (drawerPos.y -
              drawerClicked.mesh.userData.bottomGap -
              this.braceIndent);
        }
      }

      if (drawerClicked.mesh.userData.type === InsideType.ACCESSORY_FLIP) {
        sizeY = drawerClicked.mesh.userData.sizeDefault.y;
      }

      //Lưu thông tin hộc kéo
      model.userData.drawerUuid = drawerClicked.mesh.uuid;
    }
    //Cửa hộc kéo

    // const sizeZ = this.setDoorZSize(
    //   orientation,
    //   leftEdge,
    //   leftPos,
    //   sizeLeft,
    //   rightEdge,
    //   rightPos,
    //   sizeRight,
    //   slit,
    //   checkSubZ
    // );
    let sizeZ;
    if (typeHau === TypeConnerModule.SUB) {
      sizeZ = this.setDoorXSize(
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        rightEdge,
        rightPos,
        sizeRight,
        slit,
        checkSubZ
      );
    } else {
      sizeZ = this.setDoorZSize(
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        rightEdge,
        rightPos,
        sizeRight,
        slit,
        checkSubZ
      );
    }

    //Position
    // model.position.x =
    //   Number(clonedModule.formularController.currentDimensions.depth) / 2 -
    //   (isCurveModule ? thicknessBase : 0) -
    //   (isBenchModule ? thicknessBase : 0) -
    //   (drawerClicked?.mesh?.userData?.type === InsideType.ACCESSORY_FLIP
    //     ? thicknessBase
    //     : 0);

    if (!typeHau) {
      model.position.x =
        Number(clonedModule.formularController.currentDimensions.depth) / 2 -
        (isCurveModule ? thicknessBase : 0) -
        (isBenchModule ? thicknessBase : 0) -
        (drawerClicked?.mesh?.userData?.type === InsideType.ACCESSORY_FLIP
          ? thicknessBase
          : 0);
    } else if (typeHau === TypeConnerModule.SUB) {
      model.position.z =
        clonedModule.formularController.currentOptions.rongK2 / 2 -
        Number(clonedModule.formularController.currentDimensions.depth);

      model.rotation.y += Math.PI / 2;
    } else {
      model.position.x =
        Number(clonedModule.formularController.currentDimensions.depth) -
        clonedModule.formularController.currentOptions.rongK1 / 2;
    }

    model.position.y = bottomPos.y;

    //Cửa hộc keos
    if (drawerClicked) {
      if (
        bottomEdge.mesh.userData?.type === InsideType.DRAWER ||
        bottomEdge.mesh.userData?.type === InsideType.ACCESSORY_DISH_LOWER
      ) {
        model.position.y =
          drawerPos.y -
          drawerClicked.mesh.userData.bottomGap -
          this.braceIndent;
      }

      if (drawerClicked.mesh.userData.type === InsideType.ACCESSORY_FLIP) {
        model.position.y = drawerPos.y;
      }
    }
    //Cửa hộc keos

    //Cửa cho tủ máy hút mùi trên
    if (
      clonedModule.cabinetType.toLowerCase().includes('upper') &&
      clonedModule.moduleType.toLowerCase().includes('hood')
    ) {
      model.position.y = bottomPos.y + topDoorGapBase;
    }
    //Cửa cho tủ máy hút mùi trên

    //Cửa cho tủ đụng trần
    if (clonedModule.cabinetType.toLowerCase().includes('ceiling')) {
      model.position.y = bottomPos.y + sizeBottom.y + topDoorGapBase;
    }
    //Cửa cho tủ đụng trần

    // this.setDoorZPosition(
    //   model,
    //   leftEdgeDoor,
    //   orientation,
    //   leftEdge,
    //   leftPos,
    //   sizeLeft,
    //   sizeZ,
    //   slit,
    //   checkSubZ
    // );
    if (typeHau === TypeConnerModule.SUB) {
      this.setDoorXPosition(
        model,
        leftEdgeDoor,
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        sizeZ,
        slit,
        checkSubZ
      );
    } else {
      this.setDoorZPosition(
        model,
        leftEdgeDoor,
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        sizeZ,
        slit,
        checkSubZ
      );
    }

    //Scale
    const scaleY = sizeY / originalSize.y;
    const horizontalType =
      sizeY > HorizontalType.LIM_HEIGHT
        ? HorizontalType.TWO_D
        : HorizontalType.ONE_D;

    let scaleZ;

    if (drawerClicked) {
      scaleZ = sizeZ / originalSize.z;
    } else {
      // scaleZ =
      //   sizeZ >= moduleData.limit.width.min &&
      //   sizeZ < moduleData.limit.width.max
      //     ? isCurveModule
      //       ? sizeZ / originalSize.z
      //       : sizeZ / 2 / originalSize.z
      //     : sizeZ >= moduleData.limit.width.max &&
      //         sizeZ / 2 >= moduleData.limit.width.min &&
      //         sizeZ / 2 <= moduleData.limit.width.max
      //       ? sizeZ / 2 / originalSize.z
      //       : moduleData.limit.width.max / originalSize.z;

      scaleZ =
        sizeZ <= moduleData.limit.width.max &&
        leftEdgeDoor?.mesh?.userData?.type !== InsideType.DOOR &&
        rightEdgeDoor?.mesh?.userData?.type !== InsideType.DOOR
          ? sizeZ / originalSize.z
          : sizeZ / 2 / originalSize.z;
    }

    model.scale.y = scaleY;
    model.scale.z = scaleZ;

    model.userData.leftEdgeUuid = leftEdge.mesh.uuid;
    model.userData.rightEdgeUuid = rightEdge.mesh.uuid;
    model.userData.topEdgeUuid = topEdge.mesh.uuid;
    model.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;
    model.userData.leftEdgeDoorUuid = leftEdgeDoor.mesh.uuid;
    model.userData.rightEdgeDoorUuid = rightEdgeDoor.mesh.uuid;

    model.userData.beveled = moduleData?.beveled ?? true;
    model.userData.typeHau = typeHau;
    model.userData.doorDirection = doorDirection;
    model.userData.horizontalType = horizontalType;

    const check = this.handleCheckLeftRight(
      clonedModule,
      model,
      leftEdge.mesh,
      rightEdge.mesh,
      typeHau
    );

    model.userData.handlePosition = {
      left: check.isLeft,
      right: check.isRight,
      center: check.isCenter,
      // current: check.isRight ? 'right' : check.isLeft ? 'left' : null,
      // current: check.isRight ? 'right' : check.isLeft ? 'left' : 'center',
      current: check.isRight
        ? 'right'
        : check.isLeft
          ? 'left'
          : check.isCenter
            ? 'center'
            : 'drawer',
    };

    ///test vat 45
    // setTimeout(() => {
    //   this.cut45Door(clonedModule, model);
    // }, 50);
    ///test vat 45

    //Scale cửa đặc biệt
    const materialType = moduleData.material_type;

    // if (!materialType?.includes('tron')) {
    setTimeout(async () => {
      await this.cutSpecialDoor(
        clonedModule,
        model,
        materialType,
        doorDirection,
        horizontalType
      );
    }, 200);
    // }

    //Thêm tay nâng mặc định cho cửa ngang
    if (doorDirection === DoorDirection.HORIZONTAL) {
      const liftSupportModel = this.loadLiftSupportModel(
        model.userData.defaultLiftSupport,
        clonedModule,
        model
      );
    }

    if (
      leftEdgeDoor.mesh.userData?.type === InsideType.DOOR ||
      rightEdgeDoor.mesh.userData?.type === InsideType.DOOR
    ) {
      model.userData.existsBrace = true;

      addModelToCabinet();
      return;
    }

    //Là tủ đụng trần hoặc tủ dưới mà kh đụng tới tấm NÓC thì mới thêm bạ
    if (
      (clonedModule.cabinetType.toLowerCase().includes('lower') &&
        (!topEdge.mesh.name.toUpperCase().includes('NOC') ||
          (!clonedModule.cabinetType.toLowerCase().includes('wardrobe') &&
            !clonedModule.cabinetType.toLowerCase().includes('decorative') &&
            !clonedModule.cabinetType.toLowerCase().includes('shoe')))) ||
      clonedModule.cabinetType.toLowerCase().includes('ceiling') ||
      (clonedModule.cabinetType.toLowerCase().includes('upper') &&
        clonedModule.cabinetType.toLowerCase().includes('tv')) ||
      (clonedModule.cabinetType.toLowerCase().includes('lower') &&
        (isCurveModule === true || isBenchModule === true))
    ) {
      // if (
      //   clonedModule.cabinetType.toLowerCase().includes('kitchen') &&
      //   clonedModule.formularController.currentDimensions.depth > 0.35 &&
      //   clonedModule.cabinetType.toLowerCase().includes('ceiling')
      // ) {
      //   addModelToCabinet();
      //   return;
      // }

      if (drawerClicked?.mesh?.userData?.type === InsideType.ACCESSORY_FLIP) {
        addModelToCabinet();
        return;
      }

      this.createAndAddBrace(
        drawerClicked,
        model,
        edges,
        sizes,
        clonedModule,
        originalSize,
        addBraceToCabinet,
        false, //isUDrawerDoor
        isCurveModule,
        isBenchModule
      )
        .then(braceUuid => {
          model.userData.braceUuid = braceUuid;
        })
        .catch(error => {
          console.error('Error creating brace:', error);
        });
    }

    addModelToCabinet();
  }

  //TINH THEO TRUC Z
  setDoorZPosition(
    model,
    leftEdgeDoor,
    orientation,
    leftEdge,
    leftPos,
    sizeLeft,
    sizeZ,
    slit,
    checkSubZ
  ) {
    const leftEdgeDoorConvert = leftEdgeDoor?.mesh ?? leftEdgeDoor;
    const leftEdgeConvert = leftEdge?.mesh ?? leftEdge;

    let checkDoorPos = 'left';
    if (
      model.userData.handlePosition?.right === true ||
      (model.userData.handlePosition?.left === true &&
        model.userData.handlePosition?.right === true)
    ) {
      checkDoorPos = 'left';
    } else if (model.userData.handlePosition?.left === true) {
      checkDoorPos = 'right';
    }

    if (
      checkDoorPos === 'right' ||
      leftEdgeDoorConvert?.userData?.type === InsideType.DOOR
    ) {
      // Đã có cửa
      if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
        model.position.z =
          leftPos.z -
          (leftEdgeConvert?.userData?.type !== undefined
            ? sizeLeft.z / 2 + slit
            : checkSubZ
              ? sizeLeft.z
              : 0) -
          sizeZ / 2;
      } else if (
        orientation === 'ROTATED_90' ||
        orientation === 'ROTATED_270'
      ) {
        model.position.z =
          leftPos.z -
          (leftEdgeConvert?.userData?.type !== undefined
            ? sizeLeft.x / 2 + slit
            : checkSubZ
              ? sizeLeft.x
              : 0) -
          sizeZ / 2;
      }
    } else {
      //Chưa có cửa
      if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
        model.position.z =
          leftPos.z -
          (leftEdgeConvert?.userData?.type !== undefined
            ? sizeLeft.z / 2 + slit
            : checkSubZ
              ? sizeLeft.z
              : 0);
      } else if (
        orientation === 'ROTATED_90' ||
        orientation === 'ROTATED_270'
      ) {
        model.position.z =
          leftPos.z -
          (leftEdgeConvert?.userData?.type !== undefined
            ? sizeLeft.x / 2 + slit
            : checkSubZ
              ? sizeLeft.x
              : 0);
      }
    }
  }

  setDoorZSize(
    orientation,
    leftEdge,
    leftPos,
    sizeLeft,
    rightEdge,
    rightPos,
    sizeRight,
    slit,
    isCurveModule
  ) {
    let sizeZ;
    const leftEdgeConvert = leftEdge?.mesh ?? leftEdge;
    const rightEdgeConvert = rightEdge?.mesh ?? rightEdge;

    const subSizeZ = isCurveModule ? thicknessBase : 0;

    if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
      sizeZ =
        leftPos.z -
        rightPos.z +
        sizeRight.z +
        (leftEdgeConvert?.userData?.type !== undefined
          ? -sizeLeft.z / 2 - slit
          : 0 - subSizeZ) +
        (rightEdgeConvert?.userData?.type !== undefined
          ? -sizeRight.z / 2 - slit
          : 0 - subSizeZ);
    } else if (orientation === 'ROTATED_90' || orientation === 'ROTATED_270') {
      sizeZ =
        leftPos.z -
        rightPos.z +
        sizeRight.x +
        (leftEdgeConvert?.userData?.type !== undefined
          ? -sizeLeft.x / 2 - slit
          : 0 - subSizeZ) +
        (rightEdgeConvert?.userData?.type !== undefined
          ? -sizeRight.x / 2 - slit
          : 0 - subSizeZ);
    }

    // return Number(sizeZ.toFixed(3));
    return Math.floor(sizeZ * 1000) / 1000;
  }
  //TINH THEO TRUC Z

  //TINH THEO TRUC X : (SUB)
  setDoorXPosition(
    model,
    leftEdgeDoor,
    orientation,
    leftEdge,
    leftPos,
    sizeLeft,
    sizeZ,
    slit,
    checkSubZ
  ) {
    const leftEdgeDoorConvert = leftEdgeDoor?.mesh ?? leftEdgeDoor;
    const leftEdgeConvert = leftEdge?.mesh ?? leftEdge;

    let checkDoorPos = 'left';
    if (
      model.userData.handlePosition?.right === true ||
      (model.userData.handlePosition?.left === true &&
        model.userData.handlePosition?.right === true)
    ) {
      checkDoorPos = 'left';
    } else if (model.userData.handlePosition?.left === true) {
      checkDoorPos = 'right';
    }

    if (
      checkDoorPos === 'right' ||
      leftEdgeDoorConvert?.userData?.type === InsideType.DOOR
    ) {
      // Đã có cửa
      switch (orientation) {
        case 'NORMAL':
          model.position.x =
            leftPos.x -
            (leftEdgeConvert?.userData?.type !== undefined
              ? sizeLeft.x / 2 + slit
              : checkSubZ
                ? sizeLeft.x
                : 0) -
            sizeZ / 2 +
            sizeLeft.x;
          break;

        case 'ROTATED_90':
          model.position.x =
            leftPos.x -
            (leftEdgeConvert?.userData?.type !== undefined
              ? sizeLeft.z / 2 + slit
              : checkSubZ
                ? sizeLeft.z
                : 0) -
            sizeZ / 2 +
            sizeLeft.z;
          break;

        case 'ROTATED_180':
          model.position.x =
            leftPos.x -
            (leftEdgeConvert?.userData?.type !== undefined
              ? sizeLeft.x / 2 + slit
              : checkSubZ
                ? sizeLeft.x
                : 0) -
            sizeZ / 2 +
            sizeLeft.x;
          break;

        case 'ROTATED_270':
          model.position.x =
            leftPos.x -
            (leftEdgeConvert?.userData?.type !== undefined
              ? sizeLeft.z / 2 + slit
              : checkSubZ
                ? sizeLeft.z
                : 0) -
            sizeZ / 2 +
            sizeLeft.z;
          break;
      }
    } else {
      // Chưa có cửa
      switch (orientation) {
        case 'NORMAL':
          model.position.x =
            leftPos.x -
            (leftEdgeConvert?.userData?.type !== undefined
              ? sizeLeft.x / 2 + slit
              : checkSubZ
                ? sizeLeft.x
                : 0) +
            sizeLeft.x;
          break;

        case 'ROTATED_90':
          model.position.x =
            leftPos.x -
            (leftEdgeConvert?.userData?.type !== undefined
              ? sizeLeft.z / 2 + slit
              : checkSubZ
                ? sizeLeft.z
                : 0) +
            sizeLeft.z;
          break;

        case 'ROTATED_180':
          model.position.x =
            leftPos.x -
            (leftEdgeConvert?.userData?.type !== undefined
              ? sizeLeft.x / 2 + slit
              : checkSubZ
                ? sizeLeft.x
                : 0) +
            sizeLeft.x;
          break;

        case 'ROTATED_270':
          model.position.x =
            leftPos.x -
            (leftEdgeConvert?.userData?.type !== undefined
              ? sizeLeft.z / 2 + slit
              : checkSubZ
                ? sizeLeft.z
                : 0) +
            sizeLeft.z;
          break;
      }
    }
  }

  setDoorXSize(
    orientation,
    leftEdge,
    leftPos,
    sizeLeft,
    rightEdge,
    rightPos,
    sizeRight,
    slit,
    isCurveModule
  ) {
    let sizeZ;
    const leftEdgeConvert = leftEdge?.mesh ?? leftEdge;
    const rightEdgeConvert = rightEdge?.mesh ?? rightEdge;

    const subSizeZ = isCurveModule ? thicknessBase : 0;

    if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
      sizeZ =
        leftPos.x -
        rightPos.x +
        sizeRight.x +
        (leftEdgeConvert?.userData?.type !== undefined
          ? -sizeLeft.x / 2 - slit
          : 0 - subSizeZ) +
        (rightEdgeConvert?.userData?.type !== undefined
          ? -sizeRight.x / 2 - slit
          : 0 - subSizeZ);
    } else if (orientation === 'ROTATED_90' || orientation === 'ROTATED_270') {
      sizeZ =
        leftPos.x -
        rightPos.x +
        sizeRight.z +
        (leftEdgeConvert?.userData?.type !== undefined
          ? -sizeLeft.z / 2 - slit
          : 0 - subSizeZ) +
        (rightEdgeConvert?.userData?.type !== undefined
          ? -sizeRight.z / 2 - slit
          : 0 - subSizeZ);
    }

    // return Number(sizeZ.toFixed(3));
    return Math.floor(sizeZ * 1000) / 1000;
  }
  //TINH THEO TRUC X

  handleCheckLeftRight(clonedModule, model, leftEdge, rightEdge, typeHau) {
    const modelSize = this.getSize(model);

    const leftPos = clonedModule.group.worldToLocal(
      leftEdge.getWorldPosition(new THREE.Vector3()).clone()
    );
    const rightPos = clonedModule.group.worldToLocal(
      rightEdge.getWorldPosition(new THREE.Vector3()).clone()
    );

    // const isLeft =
    //   Math.abs(model.position.z - modelSize.z - rightPos.z) <=
    //   2 * thicknessBase;
    // const isRight = Math.abs(model.position.z - leftPos.z) <= 2 * thicknessBase;

    let isLeft = false;
    let isRight = false;
    let isCenter = false;

    //Cua ngang
    if (model.userData.doorDirection === DoorDirection.HORIZONTAL) {
      let isCenter = true;

      return { isLeft, isRight, isCenter };
    }

    if (typeHau === TypeConnerModule.SUB) {
      isLeft =
        Math.abs(
          model.position.x - Math.max(modelSize.x, modelSize.z) - rightPos.x
        ) <=
        2 * thicknessBase;
      isRight = Math.abs(model.position.x - leftPos.x) <= 2 * thicknessBase;
    } else {
      isLeft =
        Math.abs(
          model.position.z - Math.max(modelSize.x, modelSize.z) - rightPos.z
        ) <=
        2 * thicknessBase;
      isRight = Math.abs(model.position.z - leftPos.z) <= 2 * thicknessBase;
    }

    return { isLeft, isRight, isCenter };
  }

  createAndAddBrace(
    drawerClicked,
    model,
    edges,
    sizes,
    clonedModule,
    originalSize,
    addBraceToCabinet,
    isUDrawerDoor,
    isCurveDoor,
    isBenchDoor,
    layer
  ) {
    return new Promise((resolve, reject) => {
      const loader = new GLTFLoader();
      loader.load(
        '/assets/glb/BRACE.glb',
        gltf => {
          try {
            const { bottomEdge, rightEdge, leftEdge, topEdge } = edges;
            const { sizeRight, sizeLeft } = sizes;

            // Hàm helper để lấy position
            const getPosition = edge => {
              if (!edge) return null;

              if (edge?.mesh) {
                return clonedModule.group.worldToLocal(
                  edge.mesh.getWorldPosition(new THREE.Vector3()).clone()
                );
              } else {
                return clonedModule.group.worldToLocal(
                  edge.getWorldPosition(new THREE.Vector3()).clone()
                );
              }
            };

            // Kiểm tra các position cần thiết
            const orientation = this.getModuleOrientation(clonedModule.group);
            const bottomPos = getPosition(bottomEdge);
            const topPos = getPosition(topEdge);
            const rightPos = getPosition(rightEdge);
            const leftPos = getPosition(leftEdge);

            if (!bottomPos || !topPos || !rightPos || !leftPos) {
              console.warn('Cannot get positions for brace creation');
              reject(new Error('Cannot get positions for brace creation'));
              return;
            }

            const typeHau = model?.userData?.typeHau;

            const mesh = gltf.scene.children[0];
            const modelClone = model.clone();
            const originalSizeMesh = this.getSize(mesh);

            const modelPos = clonedModule.group.worldToLocal(
              modelClone.getWorldPosition(new THREE.Vector3()).clone()
            );

            mesh.userData = { ...modelClone.userData };

            mesh.name = 'BRACE';
            mesh.userData.name = 'BRACE';
            mesh.userData.type = InsideType.BRACE;
            mesh.userData.doorUuid = model.uuid;

            // mesh.position.x =
            //   Number(clonedModule.formularController.currentDimensions.depth) /
            //     2 -
            //   this.braceThickness -
            //   (isCurveDoor ? thicknessBase : 0) -
            //   (isBenchDoor ? thicknessBase : 0);

            if (!typeHau) {
              mesh.position.x =
                Number(
                  clonedModule.formularController.currentDimensions.depth
                ) /
                  2 -
                (isCurveDoor ? thicknessBase : 0) -
                (isBenchDoor ? thicknessBase : 0) -
                this.braceThickness;
            } else if (typeHau === TypeConnerModule.SUB) {
              mesh.position.z =
                clonedModule.formularController.currentOptions.rongK2 / 2 -
                Number(
                  clonedModule.formularController.currentDimensions.depth
                ) +
                this.braceThickness;

              mesh.rotation.y += Math.PI / 2;
            } else {
              mesh.position.x =
                Number(
                  clonedModule.formularController.currentDimensions.depth
                ) -
                clonedModule.formularController.currentOptions.rongK1 / 2 -
                this.braceThickness;
            }

            // Position Y
            if (clonedModule.moduleType?.includes('ceiling')) {
              // Tủ đụng trần bạ cửa nắm phía dưới
              const bottomBBox = new THREE.Box3().setFromObject(
                bottomEdge?.mesh ?? bottomEdge
              );
              const bottomSize = new THREE.Vector3();
              bottomBBox.getSize(bottomSize);

              mesh.position.y = bottomPos.y + bottomSize.y;
            } else {
              // Các trường hợp khác bạ nằm trên
              mesh.position.y =
                topPos.y -
                this.braceHeight -
                (topEdge?.userData?.type === InsideType.DRAWER ||
                topEdge?.userData?.type === InsideType.ACCESSORY_DISH_LOWER
                  ? topEdge?.userData?.bottomGap || 0
                  : 0) -
                (topEdge?.mesh?.userData?.type === InsideType.DRAWER ||
                topEdge?.mesh?.userData?.type ===
                  InsideType.ACCESSORY_DISH_LOWER
                  ? topEdge?.mesh?.userData?.bottomGap || 0
                  : 0);
            }

            // Position Z và Scale Z
            let sizeBraceZ;

            // if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
            //   mesh.position.z = leftPos.z - sizeLeft.z;
            //   sizeBraceZ = leftPos.z - rightPos.z - sizeLeft.z;
            // } else if (
            //   orientation === 'ROTATED_90' ||
            //   orientation === 'ROTATED_270'
            // ) {
            //   mesh.position.z = leftPos.z - sizeLeft.x;
            //   sizeBraceZ = leftPos.z - rightPos.z - sizeLeft.x;
            // }

            if (typeHau === TypeConnerModule.SUB) {
              // if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
              //   mesh.position.x = leftPos.x + sizeLeft.x;
              //   sizeBraceZ = leftPos.x - rightPos.x - sizeLeft.x;
              // } else if (
              //   orientation === 'ROTATED_90' ||
              //   orientation === 'ROTATED_270'
              // ) {
              //   mesh.position.x = leftPos.x + sizeLeft.z;
              //   sizeBraceZ = leftPos.x - rightPos.x - sizeLeft.z;
              // }

              mesh.position.x = leftPos.x;
              sizeBraceZ =
                leftPos.x - rightPos.x - Math.min(sizeLeft.x, sizeLeft.z);
            } else {
              // if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
              //   mesh.position.z = leftPos.z - sizeLeft.z;
              //   sizeBraceZ = leftPos.z - rightPos.z - sizeLeft.z;
              // } else if (
              //   orientation === 'ROTATED_90' ||
              //   orientation === 'ROTATED_270'
              // ) {
              //   mesh.position.z = leftPos.z - sizeLeft.x;
              //   sizeBraceZ = leftPos.z - rightPos.z - sizeLeft.x;
              // }

              mesh.position.z = leftPos.z - Math.min(sizeLeft.x, sizeLeft.z);
              sizeBraceZ =
                leftPos.z - rightPos.z - Math.min(sizeLeft.x, sizeLeft.z);
            }

            // Scale
            const scaleBraceY = this.braceHeight / originalSizeMesh.y;
            const scaleBraceZ = sizeBraceZ / originalSizeMesh.z;
            mesh.scale.y = scaleBraceY;
            mesh.scale.z = scaleBraceZ;

            if (isUDrawerDoor === true) {
              const uDrawerBBox = new THREE.Box3().setFromObject(model);
              const uDrawerSize = new THREE.Vector3();
              uDrawerBBox.getSize(uDrawerSize);

              mesh.scale.y = scaleBraceY;
              mesh.scale.z =
                Math.max(uDrawerSize.x, uDrawerSize.z) / originalSize.z;

              mesh.position.x =
                model.position.x - Math.min(uDrawerSize.x, uDrawerSize.z);
              mesh.position.y = model.position.y + uDrawerSize.y - 0.01;
              mesh.position.z = model.position.z;
            }

            addBraceToCabinet(mesh);

            resolve(mesh.uuid);
          } catch (error) {
            console.error('Error in brace creation:', error);
            reject(error);
          }
        },
        undefined,
        error => {
          console.error('Error loading BRACE.glb:', error);
          reject(error);
        }
      );
    });
  }

  createAndAddBraceForCornerModule(model, clonedModule, addBraceToCabinet) {
    return new Promise((resolve, reject) => {
      const loader = new GLTFLoader();
      loader.load(
        '/assets/glb/BRACE.glb',
        gltf => {
          try {
            // Xoá hết các bạ đã tồn tại đối với tủ góc
            const toRemove = [];
            clonedModule.group.traverse(mesh => {
              if (mesh.userData?.type === InsideType.BRACE) {
                toRemove.push(mesh);
              }
            });
            toRemove.forEach(mesh => {
              mesh.parent?.remove(mesh);
            });

            clonedModule.listInside = clonedModule.listInside.filter(
              item => item.type !== InsideType.BRACE
            );

            // Thêm lại bạ mới
            const orientation = this.getModuleOrientation(clonedModule.group);
            const lstBrace = ['BA-NGANG-1', 'BA-NGANG-2'];
            const foundMeshes = lstBrace
              .map(edgeName => {
                let found = null;

                clonedModule.group.traverse(mesh => {
                  if (
                    mesh.isMesh &&
                    mesh.name.includes(edgeName) &&
                    mesh.scale.x > 0
                  ) {
                    found = mesh;
                  }
                });

                return found;
              })
              .filter(mesh => mesh !== null);

            const doorClone = model.clone();

            // Duyệt qua từng mesh tìm được
            foundMeshes.forEach(item => {
              const targetMesh = item.clone();

              // Clone model để tạo brace mới cho mỗi mesh
              const braceModel = gltf.scene.children[0].clone();

              // ✓ Copy userData an toàn hơn
              braceModel.userData = { ...doorClone.userData };

              braceModel.name = 'BRACE';
              braceModel.userData.name = 'BRACE';
              braceModel.userData.type = InsideType.BRACE;
              braceModel.userData.doorUuid = model.uuid;

              // Lấy size 3D của target mesh
              const boundingBox = new THREE.Box3().setFromObject(targetMesh);
              const targetSize = new THREE.Vector3();
              boundingBox.getSize(targetSize);

              // Lấy size 3D của md
              const mdBox = new THREE.Box3().setFromObject(clonedModule.group);
              const mdSize = new THREE.Vector3();
              mdBox.getSize(mdSize);

              // Reset scale của door model
              braceModel.scale.set(1, 1, 1);

              // Lấy size hiện tại của door model
              const doorBoundingBox = new THREE.Box3().setFromObject(
                braceModel
              );
              const doorSize = new THREE.Vector3();
              doorBoundingBox.getSize(doorSize);

              // Set position của door model
              braceModel.position.copy(targetMesh.position);

              if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
                braceModel.position.setX(targetMesh.position.x - mdSize.x / 2);
                braceModel.position.setZ(targetMesh.position.z + mdSize.z / 2);

                if (targetMesh.name.includes('BA-NGANG-1')) {
                  braceModel.position.setX(
                    targetMesh.position.x - mdSize.x / 2 + targetSize.x
                  );
                }
              } else if (
                orientation === 'ROTATED_90' ||
                orientation === 'ROTATED_270'
              ) {
                braceModel.position.setX(targetMesh.position.x - mdSize.z / 2);
                braceModel.position.setZ(targetMesh.position.z + mdSize.x / 2);

                if (targetMesh.name.includes('BA-NGANG-1')) {
                  braceModel.position.setX(
                    targetMesh.position.x - mdSize.z / 2 + targetSize.x
                  );
                }
              }

              if (item.name.includes('BA-NGANG-1')) {
                braceModel.rotation.y = Math.PI / 2;

                braceModel.scale.x = targetSize.z / doorSize.x;
                braceModel.scale.y = targetSize.y / doorSize.y;
                braceModel.scale.z = targetSize.x / doorSize.z;
              } else {
                braceModel.scale.x = targetSize.x / doorSize.x;
                braceModel.scale.y = targetSize.y / doorSize.y;
                braceModel.scale.z = targetSize.z / doorSize.z;
              }

              targetMesh.visible = true;

              braceModel.userData.isInside = true;

              addBraceToCabinet(braceModel);
            });

            // resolve(mesh.uuid);
            resolve();
          } catch (error) {
            console.error('Error in brace creation:', error);
            reject(error);
          }
        },
        undefined,
        error => {
          console.error('Error loading BRACE.glb:', error);
          reject(error);
        }
      );
    });
  }

  getPointDoorRecomment = (intersection, currentCabinet, module, door) => {
    let leftEdge, rightEdge, topEdge, bottomEdge;
    let meshPoints = currentCabinet.getActiveMeshPoints(module);
    const orientation = module
      ? this.getModuleOrientation(module.group)
      : this.getModuleOrientation(currentCabinet.cloneModule.group);

    const typeHau = door.userData.typeHau;
    if (typeHau) {
      const ignoreXanBa = false;
      const checkXanBa = false;

      if (orientation === 'NORMAL') {
        if (typeHau === TypeConnerModule.MAIN) {
          meshPoints = EdgeDetector.findMeshPoints(
            module.group,
            ignoreXanBa,
            checkXanBa
          );
        } else if (typeHau === TypeConnerModule.SUB) {
          meshPoints = EdgeDetector.findMeshPointsR90(
            module.group,
            ignoreXanBa,
            checkXanBa
          );
        }
      }
      if (orientation === 'ROTATED_270') {
        if (typeHau === TypeConnerModule.MAIN) {
          meshPoints = EdgeDetector.findMeshPointsRotation(
            module.group,
            ignoreXanBa,
            checkXanBa
          );
        } else if (typeHau === TypeConnerModule.SUB) {
          meshPoints = EdgeDetector.findMeshPoints(
            module.group,
            ignoreXanBa,
            checkXanBa
          );
        }
      }
    }

    if (
      ((orientation === 'NORMAL' || orientation === 'ROTATED_180') &&
        typeHau === TypeConnerModule.MAIN) ||
      (orientation === 'ROTATED_270' && typeHau === TypeConnerModule.SUB) ||
      ((orientation === 'NORMAL' || orientation === 'ROTATED_180') && !typeHau)
    ) {
      ({ rightEdge, leftEdge } = EdgeDetector.findClosestZEdges(
        undefined,
        meshPoints,
        intersection,
        [InsideType.COL_PANEL]
      ));
      ({ bottomEdge, topEdge } = EdgeDetector.findClosestYEdges(
        undefined,
        meshPoints,
        intersection,
        [InsideType.COL_PANEL]
      ));
    } else if (
      orientation === 'ROTATED_270' &&
      (typeHau === TypeConnerModule.MAIN || !typeHau)
    ) {
      ({ rightEdge, leftEdge } = EdgeDetector.findClosestZEdgesR270(
        undefined,
        meshPoints,
        intersection,
        [InsideType.COL_PANEL]
      ));
      ({ bottomEdge, topEdge } = EdgeDetector.findClosestYEdgesR270(
        undefined,
        meshPoints,
        intersection,
        [InsideType.COL_PANEL]
      ));
    } else if (
      orientation === 'ROTATED_90' ||
      typeHau === TypeConnerModule.SUB
    ) {
      ({ rightEdge, leftEdge } = EdgeDetector.findClosestZEdgesR90(
        undefined,
        meshPoints,
        intersection,
        [InsideType.COL_PANEL]
      ));
      ({ bottomEdge, topEdge } = EdgeDetector.findClosestYEdgesR90(
        undefined,
        meshPoints,
        intersection,
        [InsideType.COL_PANEL]
      ));
    }

    if (rightEdge && leftEdge && bottomEdge && topEdge) {
      const sizeTop = this.getSize(topEdge.mesh);
      const sizeRight = this.getSize(rightEdge.mesh);
      const sizeBottom = this.getSize(bottomEdge.mesh);
      const sizeLeft = this.getSize(leftEdge.mesh);

      return {
        edges: {
          rightEdge,
          leftEdge,
          bottomEdge,
          topEdge,
        },
        sizes: {
          sizeRight,
          sizeLeft,
          sizeBottom,
          sizeTop,
        },
      };
    }
  };

  applyDoorChangesToAll = async (
    md,
    currentCabinet,
    ids,
    currentModuleType
  ) => {
    if (!currentCabinet?.modules || !md || !ids) {
      console.error('Missing required parameters');
      return;
    }

    const idSet = new Set(ids);

    try {
      let totalReplaced = 0;

      // Lặp qua tất cả modules
      for (const [moduleKey, module] of currentCabinet.modules) {
        if (
          !module?.listInside ||
          !Array.isArray(module.listInside) ||
          !ModuleTypeHelper.isSameKey(module.moduleType, currentModuleType)
        ) {
          continue;
        }

        const idMapping = new Map();

        // Tìm TẤT CẢ các index của door có id matching
        const doorIndices = [];
        // module.listInside.forEach((inside, index) => {
        //   if (inside.type === InsideType.DOOR && inside?._id &&
        //     idSet.has(inside._id)) {
        //     doorIndices.push(index);
        //   }
        // });
        module.listInside.forEach((inside, index) => {
          if (
            inside.type === InsideType.DOOR &&
            inside?._id &&
            inside.gltf.userData.beveled === md.beveled &&
            inside.gltf.userData.doorDirection === md.door_direction
          ) {
            doorIndices.push(index);
          }
        });

        if (doorIndices.length === 0) {
          continue;
        }

        // Reset texture
        module.textures.set('DOOR', {});

        // Xử lý từ cuối lên đầu để tránh vấn đề index
        for (let i = doorIndices.length - 1; i >= 0; i--) {
          const doorInsideIndex = doorIndices[i];
          const oldDoorInside = module.listInside[doorInsideIndex];

          const oldId = oldDoorInside.gltf.uuid;

          const cloneInsideData = { ...oldDoorInside };
          const cloneUserData = { ...oldDoorInside.gltf.userData };

          // Lưu lại thông tin vị trí và size của inside cũ
          const position = oldDoorInside.gltf.position.clone(); // Clone position
          const size = GeometryUtils.getSizeRotation(
            module.group,
            oldDoorInside.gltf
          );
          const rotation = oldDoorInside.gltf.rotation.clone(); // Clone rotation

          // Xóa model cũ khỏi scene
          if (oldDoorInside.gltf) {
            //Nếu cửa mới hoặc cũ là cửa nhôm kính thì xoá luôn tay nắm
            if (
              oldDoorInside.gltf.userData.materialType?.includes('nhom_kinh') ||
              md.material_type?.includes('nhom_kinh')
            ) {
              this.removeInside(oldDoorInside.gltf, module);
            } else {
              this.removeInside(oldDoorInside.gltf, module, true);
            }
          }

          // Load model mới từ md
          const newDoorInside = await this.loadDoorModel({
            moduleData: md,
            module: module,
            position: position,
            size: size,
            rotation: rotation,
            cloneUserData: cloneUserData,
            cloneInsideData: cloneInsideData,
            horizontalType: oldDoorInside.gltf.userData?.horizontalType,
          });

          // Thêm inside mới vào vị trí đã xóa
          if (newDoorInside) {
            module.listInside.splice(doorInsideIndex, 0, newDoorInside);
            const newId = newDoorInside.gltf.uuid;
            idMapping.set(oldId, newId);
            totalReplaced++;

            //Nếu cửa cũ là cửa nhôm kính thì thêm lại tay nắm mặc định cho cửa mới
            if (
              oldDoorInside.gltf.userData.materialType?.includes('nhom_kinh') &&
              md.default_handle
            ) {
              const addHandleToCabinet = handle => {
                const data = {
                  _id: handle.uuid,
                  glb: md.default_handle.model,
                  type: handle.userData.type,
                  name: handle.userData.name,
                  thumbnail_url: handle.userData.thumbnail_url,
                  price: handle.userData.price,
                  gltfUuid: handle.uuid,
                  gltf: handle,
                };

                handle.userData.isInside = true;
                handle.userData.model = md.default_handle.model;
                module.listInside.push(data);
                module.group.add(handle);
              };

              //Thêm tay nắm mặc định
              const handleModel = this.loadHandleModel(
                md.default_handle,
                module,
                newDoorInside.gltf,
                addHandleToCabinet
              );

              newDoorInside.gltf.userData.handleUuid = handleModel.uuid;
            }
          }
        }

        if (idMapping.size > 0) {
          module.listInside.forEach(item => {
            if (item?.gltf?.userData) {
              const userData = item.gltf.userData;

              // Lặp qua tất cả các thuộc tính trong userData
              Object.keys(userData).forEach(key => {
                const value = userData[key];

                // Kiểm tra nếu giá trị này match với oldId
                if (idMapping.has(value)) {
                  // Thay thế bằng newId
                  userData[key] = idMapping.get(value);
                }
              });
            }
          });
        }

        //Update lại vị trí tay nắm cho hộc kéo
        // if (!module.moduleType?.includes('corner')) {
        module.group.traverse(object => {
          if (
            object.userData?.drawerUuid &&
            object.userData.type === InsideType.DOOR &&
            object.userData.isInside === true
          ) {
            this.updateHandlePosition(object, module);
          }
        });
        // }

        await this.scene.textureController.reloadTexture({ module });
      }

      if (totalReplaced === 0) {
        const doorType = md.has_handle
          ? i18n.t('CÓ TAY NẮM')
          : i18n.t('KHÔNG CÓ TAY NẮM');
        const doorDirection =
          md.door_direction === DoorDirection.HORIZONTAL
            ? 'CỬA NGANG'
            : 'CỬA DỌC';

        openMessageError({
          content: i18n.t(
            'Can\'t find a "{{doorDirection}}  {{doorType}}" type to replace',
            {
              doorDirection,
              doorType,
            }
          ),
        });
      }

      // Cập nhật scene một lần sau khi xong tất cả
      if (this.sceneManager) {
        this.sceneManager.render();
      }
    } catch (error) {
      console.error('Error in applyDoorChangesToAll:', error);
      throw error;
    }
  };

  // Hàm helper để load door model (bạn có thể cần điều chỉnh theo implementation hiện tại)
  loadDoorModel = async ({
    moduleData,
    module,
    position,
    size,
    rotation,
    cloneUserData,
    cloneInsideData,
    horizontalType, // type lấy từ cửa cũ
  }) => {
    try {
      // Load GLTF model từ moduleData
      const gltfLoader = new GLTFLoader();
      const gltf = await new Promise((resolve, reject) => {
        gltfLoader.load(
          moduleData.model || moduleData.url,
          loadedGltf => resolve(loadedGltf),
          undefined,
          error => reject(error)
        );
      });

      const doorDirection = moduleData.door_direction;

      // Tạo inside object mới
      const newInside = {
        _id: moduleData.id || moduleData._id,
        type: InsideType.DOOR,
        name: moduleData.name,
        gltf: gltf.scene,
        position: position,
        size: size,
        rotation: rotation,
        moduleData: moduleData, // Lưu lại thông tin module data
      };

      cloneInsideData._id = moduleData.id || moduleData._id;
      cloneInsideData.id = moduleData.id || moduleData._id;
      cloneInsideData.name = moduleData.name;
      cloneInsideData.glb = moduleData.model;
      cloneInsideData.thumbnail_url = moduleData.icon;
      cloneInsideData.gltf = gltf.scene;
      cloneInsideData.gltfUuid = gltf.scene.uuid;
      gltf.scene.userData = {
        ...cloneUserData,
        materialType: moduleData.material_type, // Cập nhật material type theo cửa mới
        useAcrylic: moduleData.use_acrylic, // Cập nhật useAcrylic theo cửa mới
      };

      this.setInfoModelChild(gltf.scene);

      // Set position, rotation, scale cho gltf
      if (gltf.scene) {
        gltf.scene.position.set(position.x, position.y, position.z);
        gltf.scene.rotation.set(rotation.x, rotation.y, rotation.z);

        // Scale theo size nếu cần
        if (size.x && size.y && size.z) {
          const bbox = new THREE.Box3().setFromObject(gltf.scene);
          const currentSize = bbox.getSize(new THREE.Vector3());

          const scaleY = size.y / currentSize.y;
          if (rotation.y === 0) {
            const scaleX = size.x / currentSize.x;
            const scaleZ = size.z / currentSize.z;
            gltf.scene.scale.set(scaleX, scaleY, scaleZ);
          } else if (rotation.y === Math.PI / 2) {
            const scaleZ = size.x / currentSize.x;
            const scaleX = size.z / currentSize.z;
            gltf.scene.scale.set(scaleX, scaleY, scaleZ);
          }
        }

        // Thêm vào scene
        // module.group.add(gltf.scene);

        //Scale cửa đặc biệt
        const materialType = moduleData.material_type;
        let specialDoor;

        switch (materialType) {
          case 'tron':
            specialDoor = this.createNormalDoorFrame(
              module,
              gltf.scene,
              doorDirection,
              horizontalType
            );
            break;

          case 'acrylic':
            specialDoor = this.createAcrylicDoorFrame(
              module,
              gltf.scene,
              doorDirection,
              horizontalType
            );
            break;

          case 'pano':
            specialDoor = this.createPanoDoorFrame(
              module,
              gltf.scene,
              doorDirection,
              horizontalType
            );
            break;

          case 'kinh':
            specialDoor = this.createGlassDoorFrame(
              module,
              gltf.scene,
              doorDirection,
              horizontalType
            );
            break;

          case 'vom':
            specialDoor = this.createArchDoorFrame(
              module,
              gltf.scene,
              doorDirection,
              horizontalType
            );
            break;

          case 'nhom_kinh':
            specialDoor = this.createAluminumGlassDoorFrame(
              module,
              gltf.scene,
              doorDirection,
              horizontalType
            );
            break;

          case 'phay_ngang':
            specialDoor = this.createHorizontalBarDoorFrame(
              module,
              gltf.scene,
              doorDirection,
              horizontalType
            );
            break;

          case 'phay_doc':
            specialDoor = this.createVerticalBarDoorFrame(
              module,
              gltf.scene,
              doorDirection,
              horizontalType
            );
            break;

          case 'la_sach':
            specialDoor = this.createLouverDoorFrame(
              module,
              gltf.scene,
              doorDirection,
              horizontalType
            );
            break;

          case 'duc_lo':
            specialDoor = await this.createHorizontalHoleDoorFrame(
              module,
              gltf.scene,
              doorDirection,
              horizontalType
            );
            break;

          default:
            specialDoor = gltf.scene;
            break;
        }

        cloneInsideData.materialType = moduleData.material_type;
        cloneInsideData.useAcrylic = moduleData.use_acrylic;
        cloneInsideData.gltf = specialDoor;
        cloneInsideData.gltfUuid = specialDoor.uuid;

        specialDoor.position.set(position.x, position.y, position.z);
        specialDoor.rotation.set(rotation.x, rotation.y, rotation.z);
        module.group.add(specialDoor);
      }

      return cloneInsideData;
    } catch (error) {
      console.error('Error loading door model:', error);
      return null;
    }
  };

  /** Xử lý cửa */

  async scaleInsideAfterModuleScale(
    clonedModule,
    oldDimensions,
    newDimensions,
    oldOptions,
    newOptions
  ) {
    if (!clonedModule?.listInside || clonedModule.listInside.length === 0) {
      return;
    }

    // //Nếu là module khuyết cột thì xoá hết toàn bộ cửa và inside hiện có
    // if (
    //   clonedModule.moduleType?.toLowerCase()?.includes('column') &&
    //   clonedModule.listInside.length > 0
    // ) {
    //   message.warning(
    //     'Kích thước tủ khuyết cột bị thay đổi nên sẽ xoá hết các inside hiện có!!!'
    //   );

    //   clonedModule.listInside.forEach(inside => {
    //     this.removeInside(inside.gltf, clonedModule);
    //   });
    // }

    const scaleRatios = {
      x: newDimensions.depth / oldDimensions.depth,
      y: newDimensions.height / oldDimensions.height,
      z: newDimensions.width / oldDimensions.width,
    };
    const scaleRatiosOptions = {
      x: newOptions.rongK1 / oldOptions.rongK1,
      z: newOptions.rongK2 / oldOptions.rongK2,
    };

    if (clonedModule.moduleType?.toLowerCase()?.includes('corner')) {
      if (
        !nearlyEqual(scaleRatios.z, 1, 0.000001) ||
        !nearlyEqual(scaleRatios.x, 1, 0.000001) ||
        !nearlyEqual(scaleRatiosOptions.x, 1, 0.000001) ||
        !nearlyEqual(scaleRatiosOptions.z, 1, 0.000001)
      ) {
        const firstPassItems = clonedModule.listInside.filter(inside => {
          const type = inside?.gltf?.userData?.type;
          return (
            inside?.gltf &&
            (type === InsideType.VPANEL || type === InsideType.VPANEL2)
          );
        });

        await Promise.all(
          firstPassItems.map(
            inside =>
              new Promise(resolve => {
                this.scaleIndividualInsideCorner(
                  inside.gltf,
                  scaleRatios,
                  clonedModule,
                  newDimensions,
                  oldDimensions
                );
                resolve();
              })
          )
        );

        // BƯỚC 2: Scale các type còn lại trừ tay nắm
        const secondPassItems = clonedModule.listInside.filter(inside => {
          const type = inside?.gltf?.userData?.type;
          return (
            inside?.gltf &&
            type !== InsideType.VPANEL &&
            type !== InsideType.VPANEL2 &&
            type !== InsideType.HANDLE &&
            type !== InsideType.LOCK &&
            type !== InsideType.BRACE
          );
        });

        console.log(
          `Scaling ${secondPassItems.length} items in second pass (other types)`
        );

        await Promise.all(
          secondPassItems.map(
            inside =>
              new Promise(resolve => {
                this.scaleIndividualInsideCorner(
                  inside.gltf,
                  scaleRatios,
                  clonedModule,
                  newDimensions,
                  oldDimensions
                );
                resolve();
              })
          )
        );
        return; // Không cần scale theo trục Y nữa
      }
    }

    if (!nearlyEqual(scaleRatios.z, 1, 0.000001)) {
      // BƯỚC 1: Scale type 1 và 11 với Promise.all để đợi hoàn thành
      const firstPassItems = clonedModule.listInside.filter(inside => {
        const type = inside?.gltf?.userData?.type;
        return (
          inside?.gltf &&
          (type === InsideType.VPANEL || type === InsideType.VPANEL2)
        );
      });

      await Promise.all(
        firstPassItems.map(
          inside =>
            new Promise(resolve => {
              this.scaleIndividualInside(
                inside.gltf,
                scaleRatios,
                clonedModule,
                newDimensions,
                oldDimensions
              );
              resolve();
            })
        )
      );

      // BƯỚC 2: Scale các type còn lại trừ tay nắm
      const secondPassItems = clonedModule.listInside.filter(inside => {
        const type = inside?.gltf?.userData?.type;
        return (
          inside?.gltf &&
          type !== InsideType.VPANEL &&
          type !== InsideType.VPANEL2 &&
          type !== InsideType.HANDLE &&
          type !== InsideType.LOCK &&
          type !== InsideType.BRACE
        );
      });

      console.log(
        `Scaling ${secondPassItems.length} items in second pass (other types)`
      );

      await Promise.all(
        secondPassItems.map(
          inside =>
            new Promise(resolve => {
              this.scaleIndividualInside(
                inside.gltf,
                scaleRatios,
                clonedModule,
                newDimensions,
                oldDimensions
              );
              resolve();
            })
        )
      );
    }

    if (!nearlyEqual(scaleRatios.y, 1, 0.000001)) {
      //Tủ góc
      if (clonedModule.cabinetType.toLowerCase().includes('corner')) {
        //Scale lại cửa trừ tay nắm tủ góc
        const doorCornerItems = clonedModule.listInside.filter(inside => {
          const type = inside?.gltf?.userData?.type;
          return (
            inside?.gltf &&
            type === InsideType.DOOR &&
            type !== InsideType.HANDLE &&
            type !== InsideType.LOCK
          );
        });

        await Promise.all(
          doorCornerItems.map(
            inside =>
              new Promise(resolve => {
                this.scaleIndividualInsideY(
                  inside.gltf,
                  scaleRatios,
                  clonedModule,
                  true //isScaleY
                );
                resolve();
              })
          )
        );

        // Cập nhật display
        this.updateCabinetDisplay(clonedModule);
        // return;
      }

      scaleRatios.y =
        (newDimensions.height -
          (clonedModule.formularController.currentOptions.caoChan || 0)) /
        (oldDimensions.height -
          (clonedModule.formularController.currentOptions.caoChan || 0));
      // BƯỚC 1: Scale type 1 và 11 với Promise.all để đợi hoàn thành
      const firstPassItems = clonedModule.listInside.filter(inside => {
        const type = inside?.gltf?.userData?.type;
        return (
          inside?.gltf &&
          (type === InsideType.HPANEL ||
            type === InsideType.HPANEL_LEFT ||
            type === InsideType.HPANEL_RIGHT)
        );
      });

      await Promise.all(
        firstPassItems.map(
          inside =>
            new Promise(resolve => {
              this.scaleIndividualInsideY(
                inside.gltf,
                scaleRatios,
                clonedModule,
                undefined,
                oldDimensions,
                newDimensions
              );
              resolve();
            })
        )
      );

      // BƯỚC 2: Scale các type còn lại trừ tay nắm
      const secondPassItems = clonedModule.listInside.filter(inside => {
        const type = inside?.gltf?.userData?.type;
        return (
          inside?.gltf &&
          type !== InsideType.HPANEL &&
          type !== InsideType.HPANEL_LEFT &&
          type !== InsideType.HPANEL_RIGHT &&
          type !== InsideType.HANDLE &&
          type !== InsideType.LOCK &&
          type !== InsideType.BRACE
        );
      });

      console.log(
        `Scaling ${secondPassItems.length} items in second pass (other types)`
      );
      let accessoryFlipScaled = false;
      await Promise.all(
        secondPassItems.map(
          inside =>
            new Promise(resolve => {
              if (
                inside.gltf.userData?.type === InsideType.HPANEL_SHOE ||
                inside.gltf.userData?.type === InsideType.ACCESSORY_FLIP
              ) {
                if (accessoryFlipScaled) return; // bỏ qua những cái sau
                accessoryFlipScaled = true;
              }
              this.scaleIndividualInsideY(
                inside.gltf,
                scaleRatios,
                clonedModule,
                undefined,
                oldDimensions,
                newDimensions
              );
              resolve();
            })
        )
      );
    }

    if (!nearlyEqual(scaleRatios.x, 1, 0.000001)) {
      //Tủ góc
      if (clonedModule.cabinetType.toLowerCase().includes('corner')) {
        //Scale lại cửa trừ  tay nắm tủ góc
        const doorCornerItems = clonedModule.listInside.filter(inside => {
          const type = inside?.gltf?.userData?.type;
          return (
            inside?.gltf &&
            type === InsideType.DOOR &&
            type !== InsideType.HANDLE &&
            type !== InsideType.LOCK
          );
        });

        await Promise.all(
          doorCornerItems.map(
            inside =>
              new Promise(resolve => {
                this.scaleIndividualInside(
                  inside.gltf,
                  scaleRatios,
                  clonedModule,
                  newDimensions,
                  oldDimensions
                );
                resolve();
              })
          )
        );

        // Cập nhật display
        this.updateCabinetDisplay(clonedModule);

        message.warning(
          'Thay đổi kích thước tủ góc nên cửa hiện tại bị xoá đi.'
        );
        return;
      }

      // if (newDimensions.depth < 0.333) {
      //   let checkHpanel = false;
      //   clonedModule.listInside.forEach(inside => {
      //     if (inside.type === InsideType.HPANEL) {
      //       checkHpanel = true;
      //     }
      //   });
      //   if (checkHpanel) {
      //     clonedModule.listInside.forEach(inside => {
      //       clonedModule.group.remove(inside.gltf);
      //       clonedModule.listInside = clonedModule.listInside.filter(
      //         item => item.gltf.uuid !== inside.gltf.uuid
      //       );
      //     });
      //   }
      // }

      let deltaX = 0;
      deltaX = newDimensions.depth - oldDimensions.depth;

      scaleRatios.x =
        (newDimensions.depth -
          (clonedModule.formularController.currentOptions.DDTHau || 0)) /
        (oldDimensions.depth -
          (clonedModule.formularController.currentOptions.DDTHau || 0));

      const firstPassItems = clonedModule.listInside.filter(inside => {
        const type = inside?.gltf?.userData?.type;
        return inside?.gltf && type === InsideType.VPANEL;
      });

      await Promise.all(
        firstPassItems.map(
          inside =>
            new Promise(resolve => {
              let deltaXDrawer = 0;
              if (inside.gltf.userData?.type === InsideType.DRAWER) {
                deltaXDrawer = 0.02;
                scaleRatios.x =
                  (newDimensions.depth -
                    (clonedModule.formularController.currentOptions.DDTHau ||
                      0) -
                    deltaXDrawer) /
                  (oldDimensions.depth -
                    (clonedModule.formularController.currentOptions.DDTHau ||
                      0) -
                    deltaXDrawer);
              }

              this.scaleIndividualInsideX(
                inside.gltf,
                scaleRatios,
                clonedModule,
                null,
                deltaX,
                newDimensions,
                oldDimensions
              );
              resolve();
            })
        )
      );

      let accessoryFlipScaled = false;
      await Promise.all(
        clonedModule.listInside.map(
          inside =>
            new Promise(resolve => {
              if (
                inside.type === InsideType.VPANEL ||
                inside.type === InsideType.DOOR
              )
                return resolve(); // bỏ qua những cái VPANEL đã scale ở trên
              let deltaXDrawer = 0;
              if (inside.gltf.userData?.type === InsideType.DRAWER) {
                deltaXDrawer = 0.02;
                scaleRatios.x =
                  (newDimensions.depth -
                    (clonedModule.formularController.currentOptions.DDTHau ||
                      0) -
                    deltaXDrawer) /
                  (oldDimensions.depth -
                    (clonedModule.formularController.currentOptions.DDTHau ||
                      0) -
                    deltaXDrawer);
              }

              if (inside.gltf.userData?.type === InsideType.HPANEL_SHOE) {
                if (accessoryFlipScaled) return; // bỏ qua những cái sau
                accessoryFlipScaled = true;
              }

              this.scaleIndividualInsideX(
                inside.gltf,
                scaleRatios,
                clonedModule,
                null,
                deltaX,
                newDimensions,
                oldDimensions
              );
              resolve();
            })
        )
      );
      await Promise.all(
        clonedModule.listInside.map(
          inside =>
            new Promise(resolve => {
              if (inside.type !== InsideType.DOOR) return resolve(); // bỏ qua những cái VPANEL đã scale ở trên
              let deltaXDrawer = 0;
              if (inside.gltf.userData?.type === InsideType.DRAWER) {
                deltaXDrawer = 0.02;
                scaleRatios.x =
                  (newDimensions.depth -
                    (clonedModule.formularController.currentOptions.DDTHau ||
                      0) -
                    deltaXDrawer) /
                  (oldDimensions.depth -
                    (clonedModule.formularController.currentOptions.DDTHau ||
                      0) -
                    deltaXDrawer);
              }

              if (inside.gltf.userData?.type === InsideType.HPANEL_SHOE) {
                if (accessoryFlipScaled) return; // bỏ qua những cái sau
                accessoryFlipScaled = true;
              }

              this.scaleIndividualInsideX(
                inside.gltf,
                scaleRatios,
                clonedModule,
                null,
                deltaX,
                newDimensions,
                oldDimensions
              );
              resolve();
            })
        )
      );
    }

    //Trường hợp thay đổi option kh ảnh hưởng tới kích thước w/h/d như: bct, bcp,...
    if (
      nearlyEqual(scaleRatios.x, 1, 0.000001) &&
      nearlyEqual(scaleRatios.y, 1, 0.000001) &&
      nearlyEqual(scaleRatios.z, 1, 0.000001)
    ) {
      if (!clonedModule.cabinetType.toLowerCase().includes('corner')) {
        await Promise.all(
          clonedModule.listInside.map(
            inside =>
              new Promise(resolve => {
                this.scaleIndividualInside(
                  inside.gltf,
                  scaleRatios,
                  clonedModule,
                  newDimensions,
                  oldDimensions
                );

                resolve();
              })
          )
        );
      }
    }

    // Cập nhật display
    this.updateCabinetDisplay(clonedModule);
  }

  async scaleInsideAfterScaleChanTu(clonedModule, myScene) {
    if (!clonedModule?.listInside || clonedModule.listInside.length === 0) {
      return;
    }

    const firstPassItems = clonedModule.listInside.filter(inside => {
      const type = inside?.gltf?.userData?.type;
      return inside?.gltf && type === InsideType.HPANEL;
    });

    await Promise.all(
      firstPassItems.map(
        inside =>
          new Promise(resolve => {
            this.scaleIndividualInsideYCaoChan(
              inside.gltf,
              myScene,
              clonedModule
            );
            resolve();
          })
      )
    );

    // BƯỚC 2: Scale các type còn lại trừ tay nắm
    const secondPassItems = clonedModule.listInside.filter(inside => {
      const type = inside?.gltf?.userData?.type;
      return inside?.gltf && type !== InsideType.HPANEL;
    });

    let accessoryFlipScaled = false;
    await Promise.all(
      secondPassItems.map(
        inside =>
          new Promise(resolve => {
            if (
              inside.gltf.userData?.type === InsideType.HPANEL_SHOE ||
              inside.gltf.userData?.type === InsideType.ACCESSORY_FLIP
            ) {
              if (accessoryFlipScaled) return; // bỏ qua những cái sau
              accessoryFlipScaled = true;
            }
            this.scaleIndividualInsideYCaoChan(
              inside.gltf,
              myScene,
              clonedModule
            );
            resolve();
          })
      )
    );

    // Cập nhật display
    this.updateCabinetDisplay(clonedModule);
  }

  scaleIndividualInside(
    insideGltf,
    scaleRatios,
    clonedModule,
    newDimensions,
    oldDimensions
  ) {
    const insideType = insideGltf.userData?.type;

    //Nếu là module khuyết cột thì xoá hết toàn bộ cửa và inside hiện có
    // if (clonedModule.moduleType?.toLowerCase()?.includes('column')) {
    //   this.removeInside(insideGltf, clonedModule);
    //   message.warning(
    //     'Kích thước tủ khuyết cột bị thay đổi nên sẽ xoá hết các inside hiện có!!!'
    //   );
    // }

    switch (insideType) {
      case InsideType.HPANEL: // Xà ngang
        this.scaleXaNgang(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.ACCESSORY_FLIP: // Xà ngang
        this.scaleAccessoryFlip(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.HPANEL_SHOE: // Xà ngang
        this.scaleXanXeo(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.HPANEL_RIGHT: // Xà ngang
        this.scaleXaNgangRight(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.HPANEL_LEFT: // Xà ngang
        this.scaleXaNgangLeft(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.VPANEL: // Xà dọc
        this.scaleXaDocZ(
          insideGltf,
          scaleRatios,
          clonedModule,
          newDimensions,
          oldDimensions
        );
        this.adjustBackPanelForHPanel(clonedModule, this.scene);
        break;

      case InsideType.DRAWER: // học tủ
        this.scaleKe(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.U_DRAWER: // học tủ
        this.scaleUDrawer(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.VPANEL2: // Group phức tạp
        this.scaleXaDocZ(
          insideGltf,
          scaleRatios,
          clonedModule,
          newDimensions,
          oldDimensions
        );
        this.adjustBackPanelForHPanel(clonedModule, this.scene);
        break;

      case InsideType.ACCESSORY_GENERAL: // Phụ kiện không scale
        this.repositionAccessoryGeneral(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.CLOTHES_HANGER: // Phụ kiện không scale
        this.scaleXaNgang(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.LED_RECESSED: // Led âm
        this.scaleXaNgang(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.LED_CORNER: // Led góc
        this.scaleXaNgang(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.DOOR: // Scale cửa tủ
        this.rescaleDoor(clonedModule);
        break;

      case InsideType.MIRROR_DOOR:
        this.rescaleDoor(clonedModule);
        break;

      case InsideType.ACCESSORY_SINK:
        this.repositionAccessorySink(insideGltf, scaleRatios, clonedModule);
        break;
      case InsideType.ACCESSORY_COOKER:
        this.repositionAccessorySink(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.ACCESSORY_WASHER:
        this.repositionAccessoryWash(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.ACCESSORY_FRIDGE:
        this.repositionAccessoryFridge(insideGltf, scaleRatios, clonedModule);
        break;

      default:
        // Scale mặc định
        this.scaleDefault(insideGltf, scaleRatios);
        break;
    }
  }

  scaleIndividualInsideCorner(
    insideGltf,
    scaleRatios,
    clonedModule,
    newDimensions,
    oldDimensions
  ) {
    const insideType = insideGltf.userData?.type;
    const orientation = this.getModuleOrientation(clonedModule.group);
    //Nếu là module khuyết cột thì xoá hết toàn bộ cửa và inside hiện có
    // if (clonedModule.moduleType?.toLowerCase()?.includes('column')) {
    //   this.removeInside(insideGltf, clonedModule);
    //   message.warning(
    //     'Kích thước tủ khuyết cột bị thay đổi nên sẽ xoá hết các inside hiện có!!!'
    //   );
    // }

    switch (insideType) {
      case InsideType.HPANEL: // Xà ngang
        const edgesHpanel = this.findEdgeMeshes(insideGltf, clonedModule);

        if (edgesHpanel.leftEdge && edgesHpanel.rightEdge) {
          const { leftEdge, rightEdge, topEdge, bottomEdge } = edgesHpanel;

          if (!leftEdge || !rightEdge) {
            console.warn('Missing left or right edge for xà ngang');
            return;
          }

          let sizeRight;
          let sizeLeft;
          let sizeBottom;
          let sizeTop;
          let sizeBack;

          if (topEdge) {
            sizeTop = this.getSize(topEdge);
          }

          if (bottomEdge) {
            sizeBottom = this.getSize(bottomEdge);
          }

          if (rightEdge) {
            sizeRight = this.getSize(rightEdge);
          }

          if (leftEdge) {
            sizeLeft = this.getSize(leftEdge);
          }

          const isMain = insideGltf.userData.typeHau === TypeConnerModule.MAIN;
          const leftWorldPos = new THREE.Vector3();
          const rightWorldPos = new THREE.Vector3();
          leftEdge.getWorldPosition(leftWorldPos);
          rightEdge.getWorldPosition(rightWorldPos);
          const originalScale = insideGltf.scale.clone();
          const originalSize = this.getSize(insideGltf);

          const cloneChild = insideGltf.clone();
          cloneChild.scale.set(1, 1, 1);
          cloneChild.rotation.set(0, 0, 0);
          const cloneSize = this.getSize(cloneChild);

          const leftPos = this.getLocalPosOf(clonedModule.group, leftEdge);
          const rightPos = this.getLocalPosOf(clonedModule.group, rightEdge);

          if (orientation === 'NORMAL') {
            if (isMain) {
              //Scale Z
              const scaleZ =
                Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;

              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.z;

              const scaleX = sizeRight.x / cloneSize.x;
              insideGltf.scale.x = scaleX;
              insideGltf.position.x = leftPos.x;
              insideGltf.position.z = leftPos.z - sizeLeft.z;
            } else {
              // scale Z

              let sizeL = 0;
              if (leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.x;
                sizeL = sizeLeft.x;
              }
              if (leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.x;
                sizeL = sizeLeft.x * 2;
              }
              if (!leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = 0;
              }
              if (!leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = sizeLeft.x;
              }
              const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeL;

              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.x;
              const scaleX = sizeLeft.z / cloneSize.x;
              insideGltf.scale.x = scaleX;
              insideGltf.position.z = leftPos.z;
            }
          }
          if (orientation === 'ROTATED_270') {
            if (!isMain) {
              //Scale Z

              let sizeL;
              if (leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.z;
                sizeL = sizeLeft.z;
              }
              if (leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.z;
                sizeL = sizeLeft.z * 2;
              }
              if (!leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = 0;
              }
              if (!leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = sizeLeft.z;
              }

              const scaleZ = Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeL;
              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.z;
              const scaleX = sizeLeft.x / cloneSize.x;
              insideGltf.scale.x = scaleX;
              insideGltf.position.z = leftPos.z;
            } else {
              // scale Z

              const scaleZ =
                Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;

              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.x;

              insideGltf.position.x = leftPos.x;
              insideGltf.position.z = leftPos.z - sizeLeft.x;
              const scaleX = sizeRight.z / cloneSize.x;
              insideGltf.scale.x = scaleX;
            }
          }
        }
        break;

      case InsideType.DRAWER: // Xà ngang
        const edgesDrawer = this.findEdgeMeshes(insideGltf, clonedModule);

        if (edgesDrawer.leftEdge && edgesDrawer.rightEdge) {
          const { leftEdge, rightEdge, topEdge, bottomEdge } = edgesDrawer;

          if (!leftEdge || !rightEdge) {
            console.warn('Missing left or right edge for xà ngang');
            return;
          }

          let sizeRight;
          let sizeLeft;
          let sizeBottom;
          let sizeTop;
          let sizeBack;

          if (topEdge) {
            sizeTop = this.getSize(topEdge);
          }

          if (bottomEdge) {
            sizeBottom = this.getSize(bottomEdge);
          }

          if (rightEdge) {
            sizeRight = this.getSize(rightEdge);
          }

          if (leftEdge) {
            sizeLeft = this.getSize(leftEdge);
          }

          const isMain = insideGltf.userData.typeHau === TypeConnerModule.MAIN;
          const leftWorldPos = new THREE.Vector3();
          const rightWorldPos = new THREE.Vector3();
          leftEdge.getWorldPosition(leftWorldPos);
          rightEdge.getWorldPosition(rightWorldPos);
          const originalScale = insideGltf.scale.clone();
          const insideClone = insideGltf.clone();
          insideClone.rotation.set(0, 0, 0);
          const originalSize = this.getSize(insideClone);

          const cloneChild = insideGltf.clone();
          cloneChild.scale.set(1, 1, 1);
          cloneChild.rotation.set(0, 0, 0);
          const cloneSize = this.getSize(cloneChild);

          const leftPos = this.getLocalPosOf(clonedModule.group, leftEdge);
          const rightPos = this.getLocalPosOf(clonedModule.group, rightEdge);

          if (orientation === 'NORMAL') {
            if (isMain) {
              //Scale Z
              const scaleZ =
                Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;

              const scaleX = sizeRight.x - 0.02;
              const rotationClone = insideGltf.rotation.clone();
              insideGltf.rotation.set(0, 0, 0);
              this.scaleCabinetShelfWidth(insideGltf, originalSize, scaleZ);
              this.scaleCabinetShelfDepth(insideGltf, originalSize, scaleX);
              insideGltf.rotation.copy(rotationClone);
              insideGltf.position.x = leftPos.x + 0.02;
              insideGltf.position.z = leftPos.z - sizeLeft.z;
            } else {
              // scale Z

              let sizeL;
              if (leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.x;
                sizeL = sizeLeft.x;
              }
              if (leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.x;
                sizeL = sizeLeft.x * 2;
              }
              if (!leftEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = sizeLeft.x * 2;
              }
              const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeL;

              const scaleX = sizeLeft.z - 0.02;
              const rotationClone = insideGltf.rotation.clone();
              insideGltf.rotation.set(0, 0, 0);
              this.scaleCabinetShelfWidth(insideGltf, originalSize, scaleZ);
              this.scaleCabinetShelfDepth(insideGltf, originalSize, scaleX);
              insideGltf.rotation.copy(rotationClone);
              insideGltf.position.z = leftPos.z - 0.02;
            }
          }
          if (orientation === 'ROTATED_270') {
            if (!isMain) {
              //Scale Z

              let sizeL;
              if (leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.z;
                sizeL = sizeLeft.z;
              }
              if (leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.z;
                sizeL = sizeLeft.z * 2;
              }
              if (!leftEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = sizeLeft.z;
              }

              const scaleZ = Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeL;
              const scaleX = sizeLeft.x - 0.02;
              const rotationClone = insideGltf.rotation.clone();
              insideGltf.rotation.set(0, 0, 0);
              this.scaleCabinetShelfWidth(insideGltf, originalSize, scaleZ);
              this.scaleCabinetShelfDepth(insideGltf, originalSize, scaleX);
              insideGltf.rotation.copy(rotationClone);
              insideGltf.position.z = leftPos.z - 0.02;
            } else {
              // scale Z

              const scaleZ =
                Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;

              insideGltf.position.x = leftPos.x + 0.02;
              insideGltf.position.z = leftPos.z - sizeLeft.x;
              const scaleX = sizeRight.z - 0.02;
              const rotationClone = insideGltf.rotation.clone();
              insideGltf.rotation.set(0, 0, 0);
              this.scaleCabinetShelfWidth(insideGltf, originalSize, scaleZ);
              this.scaleCabinetShelfDepth(insideGltf, originalSize, scaleX);
              insideGltf.rotation.copy(rotationClone);
            }
          }
        }
        break;

      case InsideType.LED_CORNER: // Xà ngang
        const edgesLedCorner = this.findEdgeMeshes(insideGltf, clonedModule);

        if (edgesLedCorner.leftEdge && edgesLedCorner.rightEdge) {
          const { leftEdge, rightEdge, topEdge, bottomEdge } = edgesLedCorner;

          if (!leftEdge || !rightEdge) {
            console.warn('Missing left or right edge for xà ngang');
            return;
          }

          let sizeRight;
          let sizeLeft;
          let sizeBottom;
          let sizeTop;
          let sizeBack;

          if (topEdge) {
            sizeTop = this.getSize(topEdge);
          }

          if (bottomEdge) {
            sizeBottom = this.getSize(bottomEdge);
          }

          if (rightEdge) {
            sizeRight = this.getSize(rightEdge);
          }

          if (leftEdge) {
            sizeLeft = this.getSize(leftEdge);
          }

          const isMain = insideGltf.userData.typeHau === TypeConnerModule.MAIN;
          const leftWorldPos = new THREE.Vector3();
          const rightWorldPos = new THREE.Vector3();
          leftEdge.getWorldPosition(leftWorldPos);
          rightEdge.getWorldPosition(rightWorldPos);
          const originalScale = insideGltf.scale.clone();
          const originalSize = this.getSize(insideGltf);

          const cloneChild = insideGltf.clone();
          cloneChild.scale.set(1, 1, 1);
          cloneChild.rotation.set(0, 0, 0);
          const cloneSize = this.getSize(cloneChild);

          const leftPos = this.getLocalPosOf(clonedModule.group, leftEdge);
          const rightPos = this.getLocalPosOf(clonedModule.group, rightEdge);

          if (orientation === 'NORMAL') {
            if (isMain) {
              //Scale Z
              const scaleZ =
                Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;

              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.z;

              insideGltf.position.x = leftPos.x;
              insideGltf.position.z = leftPos.z - sizeLeft.z;
            } else {
              // scale Z

              let sizeL = 0;
              if (leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.x;
                sizeL = sizeLeft.x;
              }
              if (leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.x;
                sizeL = sizeLeft.x * 2;
              }
              if (!leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = 0;
              }
              if (!leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = sizeLeft.x;
              }
              const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeL;

              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.x;
              insideGltf.position.z = leftPos.z;
            }
          }
          if (orientation === 'ROTATED_270') {
            if (!isMain) {
              //Scale Z

              let sizeL;
              if (leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.z;
                sizeL = sizeLeft.z;
              }
              if (leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.z;
                sizeL = sizeLeft.z * 2;
              }
              if (!leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = 0;
              }
              if (!leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = sizeLeft.z;
              }

              const scaleZ = Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeL;
              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.z;
              insideGltf.position.z = leftPos.z;
            } else {
              // scale Z

              const scaleZ =
                Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;

              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.x;

              insideGltf.position.x = leftPos.x;
              insideGltf.position.z = leftPos.z - sizeLeft.x;
            }
          }
        }
        break;

      case InsideType.LED_RECESSED: // Xà ngang
        const edgesLedRecessed = this.findEdgeMeshes(insideGltf, clonedModule);

        if (edgesLedRecessed.leftEdge && edgesLedRecessed.rightEdge) {
          const { leftEdge, rightEdge, topEdge, bottomEdge } = edgesLedRecessed;

          if (!leftEdge || !rightEdge) {
            console.warn('Missing left or right edge for xà ngang');
            return;
          }

          let sizeRight;
          let sizeLeft;
          let sizeBottom;
          let sizeTop;
          let sizeBack;

          if (topEdge) {
            sizeTop = this.getSize(topEdge);
          }

          if (bottomEdge) {
            sizeBottom = this.getSize(bottomEdge);
          }

          if (rightEdge) {
            sizeRight = this.getSize(rightEdge);
          }

          if (leftEdge) {
            sizeLeft = this.getSize(leftEdge);
          }

          const isMain = insideGltf.userData.typeHau === TypeConnerModule.MAIN;
          const leftWorldPos = new THREE.Vector3();
          const rightWorldPos = new THREE.Vector3();
          leftEdge.getWorldPosition(leftWorldPos);
          rightEdge.getWorldPosition(rightWorldPos);
          const originalScale = insideGltf.scale.clone();
          const originalSize = this.getSize(insideGltf);

          const cloneChild = insideGltf.clone();
          cloneChild.scale.set(1, 1, 1);
          cloneChild.rotation.set(0, 0, 0);
          const cloneSize = this.getSize(cloneChild);

          const leftPos = this.getLocalPosOf(clonedModule.group, leftEdge);
          const rightPos = this.getLocalPosOf(clonedModule.group, rightEdge);

          if (orientation === 'NORMAL') {
            if (isMain) {
              //Scale Z
              const scaleZ =
                Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;

              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.z;

              insideGltf.position.x = leftPos.x + sizeRight.x / 2;
              insideGltf.position.z = leftPos.z - sizeLeft.z;
            } else {
              // scale Z

              let sizeL = 0;
              if (leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.x;
                sizeL = sizeLeft.x;
              }
              if (leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.x;
                sizeL = sizeLeft.x * 2;
              }
              if (!leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = 0;
              }
              if (!leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = sizeLeft.x;
              }
              const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeL;

              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.x;
              insideGltf.position.z = leftPos.z - sizeLeft.z / 2;
            }
          }
          if (orientation === 'ROTATED_270') {
            if (!isMain) {
              //Scale Z

              let sizeL;
              if (leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.z;
                sizeL = sizeLeft.z;
              }
              if (leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.z;
                sizeL = sizeLeft.z * 2;
              }
              if (!leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = 0;
              }
              if (!leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = sizeLeft.z;
              }

              const scaleZ = Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeL;
              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.z;
              insideGltf.position.z = leftPos.z - sizeLeft.x / 2;
            } else {
              // scale Z

              const scaleZ =
                Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;

              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.x;

              insideGltf.position.x = leftPos.x + sizeRight.z / 2;
              insideGltf.position.z = leftPos.z - sizeLeft.x;
            }
          }
        }
        break;

      case InsideType.CLOTHES_HANGER: // Xà ngang
        const edgesClothesHanger = this.findEdgeMeshes(
          insideGltf,
          clonedModule
        );

        if (edgesClothesHanger.leftEdge && edgesClothesHanger.rightEdge) {
          const { leftEdge, rightEdge, topEdge, bottomEdge } =
            edgesClothesHanger;

          if (!leftEdge || !rightEdge) {
            console.warn('Missing left or right edge for xà ngang');
            return;
          }

          let sizeRight;
          let sizeLeft;
          let sizeBottom;
          let sizeTop;
          let sizeBack;

          if (topEdge) {
            sizeTop = this.getSize(topEdge);
          }

          if (bottomEdge) {
            sizeBottom = this.getSize(bottomEdge);
          }

          if (rightEdge) {
            sizeRight = this.getSize(rightEdge);
          }

          if (leftEdge) {
            sizeLeft = this.getSize(leftEdge);
          }

          const isMain = insideGltf.userData.typeHau === TypeConnerModule.MAIN;
          const leftWorldPos = new THREE.Vector3();
          const rightWorldPos = new THREE.Vector3();
          leftEdge.getWorldPosition(leftWorldPos);
          rightEdge.getWorldPosition(rightWorldPos);
          const originalScale = insideGltf.scale.clone();
          const originalSize = this.getSize(insideGltf);

          const cloneChild = insideGltf.clone();
          cloneChild.scale.set(1, 1, 1);
          cloneChild.rotation.set(0, 0, 0);
          const cloneSize = this.getSize(cloneChild);

          const leftPos = this.getLocalPosOf(clonedModule.group, leftEdge);
          const rightPos = this.getLocalPosOf(clonedModule.group, rightEdge);

          if (orientation === 'NORMAL') {
            if (isMain) {
              //Scale Z
              const scaleZ =
                Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;

              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.z;

              insideGltf.position.x = leftPos.x + sizeRight.x / 2;
              insideGltf.position.z = leftPos.z - sizeLeft.z;
            } else {
              // scale Z

              let sizeL = 0;
              insideGltf.position.x = leftPos.x;
              if (leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.x;
                sizeL = sizeLeft.x;
              }
              if (leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.x;
                sizeL = sizeLeft.x * 2;
              }
              if (!leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = 0;
              }
              const scaleZ = Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeL;

              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.x;
              insideGltf.position.z = leftPos.z - sizeLeft.z / 2;
            }
          }
          if (orientation === 'ROTATED_270') {
            if (!isMain) {
              //Scale Z

              let sizeL = 0;
              insideGltf.position.x = leftPos.x;
              if (leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.z;
                sizeL = sizeLeft.z;
              }
              if (leftEdge?.userData.type && !rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x - sizeLeft.z;
                sizeL = sizeLeft.z * 2;
              }
              if (!leftEdge?.userData.type && rightEdge?.userData.type) {
                insideGltf.position.x = leftPos.x;
                sizeL = 0;
              }

              const scaleZ = Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeL;
              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.z;
              insideGltf.position.z = leftPos.z - sizeLeft.x / 2;
            } else {
              // scale Z

              const scaleZ =
                Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;

              insideGltf.scale.z = (scaleZ * originalScale.z) / originalSize.x;

              insideGltf.position.x = leftPos.x + sizeRight.z / 2;
              insideGltf.position.z = leftPos.z - sizeLeft.x;
            }
          }
        }
        break;

      case InsideType.HPANEL_SHOE: // Xà ngang
        this.scaleXanXeo(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.VPANEL: // Xà dọc
      case InsideType.VPANEL2:
        const edges = this.findEdgeMeshes(insideGltf, clonedModule);

        if (edges.leftEdge && edges.rightEdge) {
          const { leftEdge, rightEdge, topEdge, bottomEdge } = edges;

          if (!leftEdge || !rightEdge) {
            console.warn('Missing left or right edge for xà ngang');
            return;
          }

          let sizeRight;
          let sizeLeft;
          let sizeBottom;
          let sizeTop;
          let sizeBack;

          if (topEdge) {
            sizeTop = this.getSize(topEdge);
          }

          if (bottomEdge) {
            sizeBottom = this.getSize(bottomEdge);
          }

          if (rightEdge) {
            sizeRight = this.getSize(rightEdge);
          }

          if (leftEdge) {
            sizeLeft = this.getSize(leftEdge);
          }

          const isMain = insideGltf.userData.typeHau === TypeConnerModule.MAIN;
          const leftWorldPos = new THREE.Vector3();
          const rightWorldPos = new THREE.Vector3();
          leftEdge.getWorldPosition(leftWorldPos);
          rightEdge.getWorldPosition(rightWorldPos);

          const cloneChild = insideGltf.clone();
          cloneChild.scale.set(1, 1, 1);
          cloneChild.rotation.set(0, 0, 0);
          const cloneSize = this.getSize(cloneChild);

          if (orientation === 'NORMAL') {
            if (isMain) {
              //Scale Z
              const scaleZ =
                Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;
              const sizeOld = insideGltf.userData.sizeGLR;

              const ratio = scaleZ / sizeOld;
              insideGltf.position.z = insideGltf.position.z * ratio;

              const leftPos = clonedModule.group.worldToLocal(
                leftEdge?.getWorldPosition(new THREE.Vector3()).clone()
              );

              insideGltf.position.x = leftPos.x;
              const scaleX = sizeRight.x / cloneSize.x;
              insideGltf.scale.x = scaleX;
              insideGltf.userData.sizeGLR = scaleZ;
            } else {
              // scale Z

              const scaleZ =
                Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;

              const sizeOld = insideGltf.userData.sizeGLR;

              const ratio = scaleZ / sizeOld;
              insideGltf.position.z = insideGltf.position.z * ratio;
              const leftPos = clonedModule.group.worldToLocal(
                leftEdge?.getWorldPosition(new THREE.Vector3()).clone()
              );

              insideGltf.position.z = leftPos.z;
              const scaleX = sizeRight.z / cloneSize.x;
              insideGltf.scale.x = scaleX;
              insideGltf.userData.sizeGLR = scaleZ;
            }
          }
          if (orientation === 'ROTATED_270') {
            if (!isMain) {
              //Scale Z
              const scaleZ =
                Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;
              const sizeOld = insideGltf.userData.sizeGLR;

              const ratio = scaleZ / sizeOld;
              insideGltf.position.z = insideGltf.position.z * ratio;

              insideGltf.position.z = insideGltf.position.z * ratio;
              const leftPos = clonedModule.group.worldToLocal(
                leftEdge?.getWorldPosition(new THREE.Vector3()).clone()
              );

              insideGltf.position.z = leftPos.z;
              const scaleX = sizeRight.x / cloneSize.x;
              insideGltf.scale.x = scaleX;
              insideGltf.userData.sizeGLR = scaleZ;
            } else {
              // scale Z

              const scaleZ =
                Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;

              const sizeOld = insideGltf.userData.sizeGLR;

              const ratio = scaleZ / sizeOld;
              insideGltf.position.z = insideGltf.position.z * ratio;
              const leftPos = clonedModule.group.worldToLocal(
                leftEdge?.getWorldPosition(new THREE.Vector3()).clone()
              );

              insideGltf.position.x = leftPos.x;
              const scaleX = sizeRight.z / cloneSize.x;
              insideGltf.scale.x = scaleX;
              insideGltf.userData.sizeGLR = scaleZ;
            }
          }
        }

        this.adjustBackPanelForHPanel(clonedModule, this.scene);
        break;

      case InsideType.DOOR: // Scale cửa tủ
        this.rescaleDoor(clonedModule);
        break;

      case InsideType.MIRROR_DOOR:
        this.rescaleDoor(clonedModule);
        break;

      case InsideType.ACCESSORY_COOKER: // Xà ngang
      case InsideType.ACCESSORY_SINK: // Xà ngang
        const edgesSink = this.findEdgeMeshes(insideGltf, clonedModule);

        if (edgesSink.leftEdge && edgesSink.rightEdge) {
          const { leftEdge, rightEdge, topEdge, bottomEdge } = edgesSink;

          if (!leftEdge || !rightEdge) {
            console.warn('Missing left or right edge for xà ngang');
            return;
          }

          let sizeRight;
          let sizeLeft;
          let sizeBottom;
          let sizeTop;
          let sizeBack;

          if (topEdge) {
            sizeTop = this.getSize(topEdge);
          }

          if (bottomEdge) {
            sizeBottom = this.getSize(bottomEdge);
          }

          if (rightEdge) {
            sizeRight = this.getSize(rightEdge);
          }

          if (leftEdge) {
            sizeLeft = this.getSize(leftEdge);
          }

          const isMain = insideGltf.userData.typeHau === TypeConnerModule.MAIN;
          const leftWorldPos = new THREE.Vector3();
          const rightWorldPos = new THREE.Vector3();
          leftEdge.getWorldPosition(leftWorldPos);
          rightEdge.getWorldPosition(rightWorldPos);
          const originalScale = insideGltf.scale.clone();
          const originalSize = this.getSize(insideGltf);

          const cloneChild = insideGltf.clone();
          cloneChild.scale.set(1, 1, 1);
          cloneChild.rotation.set(0, 0, 0);
          const cloneSize = this.getSize(cloneChild);

          const leftPos = this.getLocalPosOf(clonedModule.group, leftEdge);
          const rightPos = this.getLocalPosOf(clonedModule.group, rightEdge);

          if (orientation === 'NORMAL') {
            if (isMain) {
              const scaleZ =
                Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;
              const scaleX = sizeLeft.x - originalSize.x;
              insideGltf.position.x = leftPos.x + scaleX / 2;
              insideGltf.position.z =
                leftPos.z - sizeLeft.z - (scaleZ - originalSize.z) / 2;
            } else {
              const scaleZ =
                Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;
              const scaleX = sizeLeft.z - originalSize.z;
              insideGltf.position.x = leftPos.x - (scaleZ - originalSize.x) / 2;
              insideGltf.position.z = leftPos.z - scaleX / 2;
            }
          }
          if (orientation === 'ROTATED_270') {
            if (!isMain) {
              //Scale
              const scaleZ =
                Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;
              const scaleX = sizeLeft.x - originalSize.x;
              insideGltf.position.z = leftPos.z - scaleX / 2;
              insideGltf.position.x = leftPos.x - (scaleZ - originalSize.z) / 2;
            } else {
              // scale Z

              const scaleZ =
                Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;
              const scaleX = sizeLeft.z - originalSize.z;
              insideGltf.position.x = leftPos.x + scaleX / 2;
              insideGltf.position.z =
                leftPos.z - sizeLeft.x - (scaleZ - originalSize.x) / 2;
            }
          }
        }
        break;

      case InsideType.ACCESSORY_GENERAL: // Xà ngang
        const edgesAccGeneral = this.findEdgeMeshes(insideGltf, clonedModule);

        if (edgesAccGeneral.leftEdge && edgesAccGeneral.rightEdge) {
          const { leftEdge, rightEdge, topEdge, bottomEdge } = edgesAccGeneral;

          if (!leftEdge || !rightEdge) {
            console.warn('Missing left or right edge for xà ngang');
            return;
          }

          let sizeRight;
          let sizeLeft;
          let sizeBottom;
          let sizeTop;
          let sizeBack;

          if (topEdge) {
            sizeTop = this.getSize(topEdge);
          }

          if (bottomEdge) {
            sizeBottom = this.getSize(bottomEdge);
          }

          if (rightEdge) {
            sizeRight = this.getSize(rightEdge);
          }

          if (leftEdge) {
            sizeLeft = this.getSize(leftEdge);
          }

          const isMain = insideGltf.userData.typeHau === TypeConnerModule.MAIN;
          const leftWorldPos = new THREE.Vector3();
          const rightWorldPos = new THREE.Vector3();
          leftEdge.getWorldPosition(leftWorldPos);
          rightEdge.getWorldPosition(rightWorldPos);
          const originalScale = insideGltf.scale.clone();
          const originalSize = this.getSize(insideGltf);

          const cloneChild = insideGltf.clone();
          cloneChild.scale.set(1, 1, 1);
          cloneChild.rotation.set(0, 0, 0);
          const cloneSize = this.getSize(cloneChild);

          const leftPos = this.getLocalPosOf(clonedModule.group, leftEdge);
          const rightPos = this.getLocalPosOf(clonedModule.group, rightEdge);

          if (orientation === 'NORMAL') {
            if (isMain) {
              const scaleZ =
                Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;
              const scaleX = sizeLeft.x - originalSize.x;
              insideGltf.position.x = leftPos.x + scaleX;
              insideGltf.position.z =
                leftPos.z - sizeLeft.z - (scaleZ - originalSize.z) / 2;
            } else {
              const scaleZ =
                Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;
              const scaleX = sizeLeft.z - originalSize.z;
              insideGltf.position.x = leftPos.x - (scaleZ - originalSize.x) / 2;
              insideGltf.position.z = leftPos.z - scaleX;
            }
          }
          if (orientation === 'ROTATED_270') {
            if (!isMain) {
              //Scale
              const scaleZ =
                Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;
              const scaleX = sizeLeft.x - originalSize.x;
              insideGltf.position.z = leftPos.z - scaleX;
              insideGltf.position.x = leftPos.x - (scaleZ - originalSize.z) / 2;
            } else {
              // scale Z

              const scaleZ =
                Math.abs(rightWorldPos.x - leftWorldPos.x) - sizeLeft.x;
              const scaleX = sizeLeft.z - originalSize.z;
              insideGltf.position.x = leftPos.x + scaleX;
              insideGltf.position.z =
                leftPos.z - sizeLeft.x - (scaleZ - originalSize.x) / 2;
            }
          }
        }
        break;

      default:
        // Scale mặc định
        this.scaleDefault(insideGltf, scaleRatios);
        break;
    }
  }

  scaleIndividualInsideYCaoChan(insideGltf, myScene, clonedModule) {
    const insideType = insideGltf.userData?.type;

    const topEdgeMesh = myScene.findGLTFByUuid(
      insideGltf.userData?.topEdgeUuid
    );
    const bottomEdgeMesh = myScene.findGLTFByUuid(
      insideGltf.userData?.bottomEdgeUuid
    );

    const bottomEdgeDrawerMesh = myScene.findGLTFByUuid(
      insideGltf.userData?.bottomDrawerUuid
    );

    switch (insideType) {
      case InsideType.HPANEL: // Xà ngang
        if (topEdgeMesh && bottomEdgeMesh) {
          const topWorldPos = topEdgeMesh.getWorldPosition(new THREE.Vector3());
          const bottomWorldPos = bottomEdgeMesh.getWorldPosition(
            new THREE.Vector3()
          );
          const topLocalPos = clonedModule.group.worldToLocal(
            topWorldPos.clone()
          );
          const bottomLocalPos = clonedModule.group.worldToLocal(
            bottomWorldPos.clone()
          );

          const size = this.getSize(insideGltf);
          const sizeBottom = this.getSize(bottomEdgeMesh);

          const currentHeight =
            topWorldPos.y - bottomWorldPos.y - sizeBottom.y - size.y;
          const deltaModelAndBottom =
            (currentHeight * insideGltf.userData?.verticalEdgeRatio) /
            (1 + insideGltf.userData?.verticalEdgeRatio);
          insideGltf.position.y = topLocalPos.y - deltaModelAndBottom - size.y;
        }
        break;

      case InsideType.HPANEL_SHOE: // Xà ngang
        if (topEdgeMesh && bottomEdgeMesh) {
          this.resetHpanelShoeScale(insideGltf, null, clonedModule);
        }
        break;

      case InsideType.VPANEL: // Xà dọc
        if (topEdgeMesh && bottomEdgeMesh) {
          const topWorldPos = topEdgeMesh.getWorldPosition(new THREE.Vector3());
          const bottomWorldPos = bottomEdgeMesh.getWorldPosition(
            new THREE.Vector3()
          );
          const topLocalPos = clonedModule.group.worldToLocal(
            topWorldPos.clone()
          );
          const bottomLocalPos = clonedModule.group.worldToLocal(
            bottomWorldPos.clone()
          );

          insideGltf.scale.y = 1;

          const size = this.getSize(insideGltf);
          const sizeBottom = this.getSize(bottomEdgeMesh);

          const currentHeight = topWorldPos.y - bottomWorldPos.y - sizeBottom.y;
          insideGltf.scale.y = currentHeight / size.y;
          insideGltf.position.y = bottomLocalPos.y + sizeBottom.y;
        }
        break;

      case InsideType.DRAWER: // Xà dọc
        if (topEdgeMesh && bottomEdgeMesh) {
          const topWorldPos = topEdgeMesh.getWorldPosition(new THREE.Vector3());
          const bottomWorldPos = bottomEdgeMesh.getWorldPosition(
            new THREE.Vector3()
          );

          const topLocalPos = clonedModule.group.worldToLocal(
            topWorldPos.clone()
          );
          const bottomLocalPos = clonedModule.group.worldToLocal(
            bottomWorldPos.clone()
          );

          insideGltf.scale.y = 1;

          const size = this.getSize(insideGltf);
          const sizeBottom = this.getSize(bottomEdgeMesh);

          let sizeY;

          const currentHeight = topWorldPos.y - bottomWorldPos.y - sizeBottom.y;
          if (insideGltf.userData.indexDuplicate !== 0) {
            const bottomDrawerWorldPos = bottomEdgeDrawerMesh.getWorldPosition(
              new THREE.Vector3()
            );
            const bottomDrawerLocalPos = clonedModule.group.worldToLocal(
              bottomDrawerWorldPos.clone()
            );
            const sizeBottomDrawer = this.getSize(bottomEdgeDrawerMesh);

            sizeY =
              topLocalPos.y -
              bottomDrawerLocalPos.y -
              sizeBottomDrawer.y -
              insideGltf.userData.bottomGap -
              2 * insideGltf.userData.topGap -
              0.1;

            insideGltf.position.y =
              bottomDrawerLocalPos.y +
              sizeBottomDrawer.y +
              insideGltf.userData.bottomGap +
              0.05 +
              insideGltf.userData.topGap;
          } else {
            if (topLocalPos.y - bottomLocalPos.y - sizeBottom.y > 0.3) {
              sizeY =
                (topLocalPos.y -
                  bottomLocalPos.y -
                  sizeBottom.y -
                  2 * insideGltf.userData.bottomGap -
                  2 * insideGltf.userData.topGap -
                  0.1) /
                2;
            } else {
              sizeY =
                topLocalPos.y -
                bottomLocalPos.y -
                sizeBottom.y -
                insideGltf.userData.bottomGap -
                insideGltf.userData.topGap;
            }

            insideGltf.position.y =
              bottomLocalPos.y + sizeBottom.y + insideGltf.userData.bottomGap;
          }
          InsideCabinetHandler.scaleHeight(insideGltf, sizeY - 0.008);
        }
        break;

      case InsideType.DOOR: // Scale cửa tủ
        this.rescaleDoor(clonedModule);
        break;

      default:
        // Scale mặc định
        // this.scaleDefault(insideGltf);
        break;
    }
  }

  scaleIndividualInsideY(
    insideGltf,
    scaleRatios,
    clonedModule,
    isScaleY,
    oldDimensions,
    newDimensions
  ) {
    const insideType = insideGltf.userData?.type;

    switch (insideType) {
      case InsideType.HPANEL: // Xà ngang
        insideGltf.position.y *= scaleRatios.y;
        break;

      case InsideType.HPANEL_RIGHT: // Xà ngang
        insideGltf.position.y *= scaleRatios.y;
        break;

      case InsideType.HPANEL_LEFT: // Xà ngang
        insideGltf.position.y *= scaleRatios.y;
        break;

      case InsideType.HPANEL_SHOE:
        this.resetHpanelShoeScale(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.ACCESSORY_FLIP:
        this.resetAccessoryFlip(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.ACCESSORY_WASHER:
      case InsideType.ACCESSORY_HOOD:
        break;

      case InsideType.VPANEL: // Xà dọc
        this.scaleXaDocY(insideGltf, scaleRatios, clonedModule);
        this.adjustBackPanelForHPanel(clonedModule, this.scene);
        break;

      case InsideType.DRAWER: // học tủ
        this.scaleKe(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.U_DRAWER: // học tủ
        this.scaleUDrawer(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.VPANEL2: // Group phức tạp
        // this.scaleComplexGroup(insideGltf, scaleRatios, clonedModule);
        this.scaleXaDocY2Height(
          insideGltf,
          scaleRatios,
          clonedModule,
          oldDimensions,
          newDimensions
        );
        break;

      case InsideType.ACCESSORY: // Phụ kiện không scale
        this.scaleAccessory(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.CLOTHES_HANGER: // Phụ kiện không scale
        this.scaleAccessory(insideGltf, scaleRatios, clonedModule);
        // this.scaleDefault(insideGltf, scaleRatios);
        break;

      case InsideType.LED_RECESSED:
        this.scaleAccessory(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.LED_CORNER:
        this.scaleAccessory(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.DOOR: // Scale cửa tủ
        this.rescaleDoor(clonedModule, isScaleY);
        break;

      case InsideType.ACCESSORY_SINK: // Scale cửa tủ
        this.repositionAccessorySink(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.ACCESSORY_FAUCET: // Scale cửa tủ
        this.repositionAccessoryFaucet(insideGltf, scaleRatios, clonedModule);
        break;
      case InsideType.ACCESSORY_COOKER: // Scale cửa tủ
        this.repositionAccessorySink(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.ACCESSORY_FRIDGE: // Scale cửa tủ
        this.repositionAccessoryFridge(insideGltf, scaleRatios, clonedModule);
        break;

      default:
        // Scale mặc định
        this.scaleDefault(insideGltf, scaleRatios);
        break;
    }
  }

  scaleIndividualInsideX(
    insideGltf,
    scaleRatios,
    clonedModule,
    isScaleY,
    deltaX,
    newDimensions,
    oldDimensions
  ) {
    const insideType = insideGltf.userData?.type;

    switch (insideType) {
      case InsideType.HPANEL:
      case InsideType.HPANEL_RIGHT:
      case InsideType.HPANEL_LEFT:
      case InsideType.ACCESSORY_WASHER:
      // case InsideType.ACCESSORY_FRIDGE:
      case InsideType.ACCESSORY_HOOD:
      case InsideType.VPANEL:
        const sizeOld = this.getSizeRotation(clonedModule.group, insideGltf);
        const deltaDepth = newDimensions.depth - oldDimensions.depth;
        const sizeXNew = sizeOld.x + deltaDepth;
        const cloneMesh = insideGltf.clone();
        cloneMesh.scale.set(1, 1, 1);
        cloneMesh.rotation.set(0, 0, 0);
        const sizeClone = this.getSize(cloneMesh);
        insideGltf.scale.x = sizeXNew / sizeClone.x;
        const deltaXPos = oldDimensions.depth / 2 + insideGltf.position.x;
        const newPosX = deltaXPos - newDimensions.depth / 2;
        insideGltf.position.x = newPosX;
        break;

      case InsideType.HPANEL_SHOE:
        this.resetHpanelShoeScale(insideGltf, scaleRatios, clonedModule);
        this.rescaleDoor(clonedModule);
        break;

      case InsideType.CLOTHES_HANGER:
      case InsideType.LED_RECESSED:
      case InsideType.LED_CORNER:
      case InsideType.ACCESSORY_GENERAL:
      case InsideType.ACCESSORY_DISH_UPPER:
      case InsideType.ACCESSORY_DISH_LOWER:
        const deltaXAccessory = newDimensions.depth - oldDimensions.depth;
        insideGltf.position.x += deltaXAccessory / 2;
        break;

      case InsideType.DRAWER: // học tủ
        insideGltf.position.x *= scaleRatios.x;
        const originalSize = this.getSizeRotation(
          clonedModule.group,
          insideGltf
        );
        const sizeX = originalSize.x + deltaX;
        this.scaleCabinetShelfDepth(insideGltf, originalSize, sizeX);
        break;

      case InsideType.U_DRAWER: // học tủ
        this.scaleUDrawer(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.ACCESSORY_FLIP: // học tủ
        if (clonedModule.formularController.currentDimensions.depth < 0.33) {
          openNotification({
            key: 'cant-add-door-existing-door',
            icon: <Icon.ICWarning />,
            iconColor: '#F97D16',
            title: 'Không thể thay đổi chiều sâu',
            description:
              'Module này tồn tại inside có chiều sâu tủ tối thiểu là 350.',
            placement: 'bottomRight',
            closable: true,
            duration: 4,
          });
        }
        break;

      case InsideType.VPANEL2: // Group phức tạp
        // this.scaleComplexGroup(insideGltf, scaleRatios, clonedModule);
        this.scaleXaDocY2Depth(
          insideGltf,
          scaleRatios,
          clonedModule,
          oldDimensions,
          newDimensions
        );
        break;

      case InsideType.DOOR: // Scale cửa tủ
        this.rescaleDoor(clonedModule, isScaleY);
        break;

      case InsideType.ACCESSORY_SINK: // Scale cửa tủ
        this.repositionAccessorySink(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.ACCESSORY_FAUCET: // Scale cửa tủ
        this.repositionAccessoryFaucet(insideGltf, scaleRatios, clonedModule);
        break;
      case InsideType.ACCESSORY_COOKER: // Scale cửa tủ
        this.repositionAccessorySink(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.ACCESSORY_FRIDGE: // Scale cửa tủ
        this.repositionAccessoryFridge(insideGltf, scaleRatios, clonedModule);
        break;

      case InsideType.BRACE: // Scale cửa tủ
        break;

      default:
        // Scale mặc định
        this.scaleDefault(insideGltf, scaleRatios);
        break;
    }
  }

  findBackEdge(clonedModule) {
    // Logic tìm back edge - có thể tìm theo tên hoặc vị trí
    let backEdge = null;

    clonedModule.group.traverse(child => {
      if (
        child.name &&
        (child.name.includes('XUONG-SAU') || child.name.includes('BACK'))
      ) {
        backEdge = child;
      }
    });

    return backEdge;
  }

  recalculateXaNgangDepth(insideGltf, clonedModule, scaleRatios) {
    // Tìm back edge để tính depth
    const backEdge = this.findBackEdge(clonedModule);

    if (backEdge) {
      const sizeBack = this.getSize(backEdge);
      const backWorldPos = backEdge.getWorldPosition(new THREE.Vector3());
      const backLocalPos = clonedModule.group.worldToLocal(
        backWorldPos.clone()
      );

      // Tính depth mới
      const currentDepth =
        clonedModule.formularController?.currentDimensions?.depth || 0.6;
      const newDepth = currentDepth - (backLocalPos.x + sizeBack.x);

      // Cập nhật position X
      insideGltf.position.x = backLocalPos.x + sizeBack.x;

      // Scale theo depth nếu cần
      const originalSize =
        insideGltf.userData.sizeDefault || this.getSize(insideGltf);
      const newScaleX = newDepth / originalSize.x;

      if (insideGltf.isGroup) {
        insideGltf.children.forEach(child => {
          if (child.isMesh) {
            child.scale.x = newScaleX;
          }
        });
      } else {
        insideGltf.scale.x = newScaleX;
      }
    }
  }

  recalculateXaNgangDimensions(insideGltf, edges, scaleRatios, clonedModule) {
    const { leftEdge, rightEdge } = edges;

    if (!leftEdge || !rightEdge) {
      console.warn('Missing left or right edge for xà ngang');
      return;
    }

    const orientation = this.getModuleOrientation(clonedModule.group);

    // Lấy kích thước của các edges
    const sizeLeft = this.getSize(leftEdge);
    const sizeRight = this.getSize(rightEdge);

    // Lấy world positions của các edges
    const leftWorldPos = leftEdge.getWorldPosition(new THREE.Vector3());
    const rightWorldPos = rightEdge.getWorldPosition(new THREE.Vector3());

    // Chuyển đổi về local coordinates của module
    const leftLocalPos = clonedModule.group.worldToLocal(leftWorldPos.clone());
    const rightLocalPos = clonedModule.group.worldToLocal(
      rightWorldPos.clone()
    );

    let sizeL = 0;
    if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
      sizeL = sizeLeft.z;
    } else if (orientation === 'ROTATED_90' || orientation === 'ROTATED_270') {
      sizeL = sizeLeft.x;
    }
    // Tính kích thước mới theo chiều rộng (Z)
    const newWidth = Math.abs(leftLocalPos.z - rightLocalPos.z) - sizeL;

    // Lấy kích thước gốc của inside
    const originalSize =
      insideGltf.userData.sizeDefault || this.getSize(insideGltf);

    const cloneMesh = insideGltf.clone();
    cloneMesh.scale.set(1, 1, 1);
    const sizeClone = this.getSize(cloneMesh);

    // Tính scale ratio mới cho chiều rộng
    const newScaleZ = newWidth / sizeClone.z;

    // Cập nhật position - căn theo left edge
    insideGltf.position.z = leftLocalPos.z - sizeL;

    if (
      insideGltf.userData.type === InsideType.CLOTHES_HANGER ||
      insideGltf.userData.type === InsideType.LED_CORNER ||
      insideGltf.userData.type === InsideType.LED_RECESSED
    ) {
      insideGltf.scale.z = newScaleZ;
    } else {
      // Cập nhật scale
      if (insideGltf.isGroup) {
        insideGltf.children.forEach(child => {
          if (child.isMesh) {
            child.scale.z = newScaleZ;
          }
        });
      } else {
        insideGltf.scale.z = newScaleZ;
      }
    }
  }

  recalculateAccessoryFlipDimensions(
    insideGltf,
    edges,
    scaleRatios,
    clonedModule
  ) {
    const { leftEdge, rightEdge } = edges;

    if (!leftEdge || !rightEdge) {
      console.warn('Missing left or right edge for xà ngang');
      return;
    }

    const orientation = this.getModuleOrientation(clonedModule.group);

    // Lấy kích thước của các edges
    const sizeLeft = this.getSize(leftEdge);
    const sizeRight = this.getSize(rightEdge);

    // Lấy world positions của các edges
    const leftWorldPos = leftEdge.getWorldPosition(new THREE.Vector3());
    const rightWorldPos = rightEdge.getWorldPosition(new THREE.Vector3());

    // Chuyển đổi về local coordinates của module
    const leftLocalPos = clonedModule.group.worldToLocal(leftWorldPos.clone());
    const rightLocalPos = clonedModule.group.worldToLocal(
      rightWorldPos.clone()
    );

    let sizeL = 0;
    if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
      sizeL = sizeLeft.z;
    } else if (orientation === 'ROTATED_90' || orientation === 'ROTATED_270') {
      sizeL = sizeLeft.x;
    }
    // Tính kích thước mới theo chiều rộng (Z)
    const newWidth = Math.abs(leftLocalPos.z - rightLocalPos.z) - sizeL;

    // Lấy kích thước gốc của inside
    const originalSize =
      insideGltf.userData.sizeDefault || this.getSize(insideGltf);

    const cloneMesh = insideGltf.clone();
    cloneMesh.scale.set(1, 1, 1);
    const sizeClone = this.getSize(cloneMesh);

    // Tính scale ratio mới cho chiều rộng
    const newScaleZ = newWidth / sizeClone.z;

    // Cập nhật position - căn theo left edge
    insideGltf.position.z = leftLocalPos.z - sizeL;

    // Cập nhật scale
    insideGltf.scale.z = newScaleZ;

    // Tính toán depth (X) nếu cần
    // this.recalculateXaNgangDepth(insideGltf, clonedModule, scaleRatios);

    console.log(
      `Recalculated xà ngang: width=${newWidth.toFixed(
        3
      )}, scaleZ=${newScaleZ.toFixed(3)}`
    );
  }

  recalculateXanXeoDimensions(insideGltf, edges, scaleRatios, clonedModule) {
    const { leftEdge, rightEdge } = edges;

    if (!leftEdge || !rightEdge) {
      console.warn('Missing left or right edge for xà ngang');
      return;
    }

    const orientation = this.getModuleOrientation(clonedModule.group);

    // Lấy kích thước của các edges
    const sizeLeft = this.getSize(leftEdge);
    const sizeRight = this.getSize(rightEdge);

    // Lấy world positions của các edges
    const leftWorldPos = leftEdge.getWorldPosition(new THREE.Vector3());
    const rightWorldPos = rightEdge.getWorldPosition(new THREE.Vector3());

    // Chuyển đổi về local coordinates của module
    const leftLocalPos = clonedModule.group.worldToLocal(leftWorldPos.clone());
    const rightLocalPos = clonedModule.group.worldToLocal(
      rightWorldPos.clone()
    );

    let sizeL = 0;
    if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
      sizeL = sizeLeft.z;
    } else if (orientation === 'ROTATED_90' || orientation === 'ROTATED_270') {
      sizeL = sizeLeft.x;
    }
    // Tính kích thước mới theo chiều rộng (Z)
    const newWidth = Math.abs(leftLocalPos.z - rightLocalPos.z) - sizeL;

    // Lấy kích thước gốc của inside
    const originalSize =
      insideGltf.userData.sizeDefault || this.getSize(insideGltf);

    // Cập nhật scale
    if (insideGltf.isGroup) {
      insideGltf.children.forEach(child => {
        if (child.isMesh) {
          const cloneMesh = child.clone();
          cloneMesh.scale.set(1, 1, 1);
          const sizeClone = this.getSize(cloneMesh);

          // Tính scale ratio mới cho chiều rộng
          const newScaleZ = newWidth / sizeClone.z;
          child.scale.z = newScaleZ;
        }
      });
    } else {
      insideGltf.scale.z = newScaleZ;
    }

    insideGltf.position.z = leftLocalPos.z - sizeL;

    const cloneData = insideGltf.clone();
    const size11 = this.getSize(cloneData);
    cloneData.rotation.set(0, 0, 0);
    const size112 = this.getSize(cloneData);
    const sizeData = [];

    cloneData.traverse(child => {
      if (!child.isMesh) return;
      // Lấy rotation (rad)
      const rotation = {
        x: child.rotation.x,
        y: child.rotation.y,
        z: child.rotation.z,
      };

      const position = {
        x: child.position.x,
        y: child.position.y,
        z: child.position.z,
      };

      child.rotation.set(0, 0, 0);

      const size = this.getSize(child);
      const size2 = this.getSizeRotation(clonedModule.group, child);

      sizeData.push({
        name: child.name || 'unnamed',
        size: {
          x: size.x,
          y: size.y,
          z: size.z,
        },
        rotation,
        position,
      });
    });

    this.disposeObject3D(cloneData);
    insideGltf.userData.sizeData = sizeData;
  }

  recalculateXaNgangDimensionsRight(
    insideGltf,
    edges,
    scaleRatios,
    clonedModule
  ) {
    const { leftEdge } = edges;

    if (!leftEdge) {
      console.warn('Missing left or right edge for xà ngang');
      return;
    }

    const orientation = this.getModuleOrientation(clonedModule.group);

    // Lấy kích thước của các edges
    const sizeLeft = this.getSize(leftEdge);

    // Lấy world positions của các edges
    const leftWorldPos = leftEdge.getWorldPosition(new THREE.Vector3());

    // Chuyển đổi về local coordinates của module
    const leftLocalPos = clonedModule.group.worldToLocal(leftWorldPos.clone());
    const tamHau = this.getMeshByName(clonedModule.group, 'HAU');
    const sizeHau = this.getSize(tamHau);

    let sizeL = 0;
    let sizeH = 0;
    if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
      sizeL = sizeLeft.z;
      sizeH = sizeHau.z;
    } else if (orientation === 'ROTATED_90' || orientation === 'ROTATED_270') {
      sizeL = sizeLeft.x;
      sizeH = sizeHau.x;
    }
    // Tính kích thước mới theo chiều rộng (Z)
    const newWidth = sizeH - sizeL;

    // Lấy kích thước gốc của inside
    const originalSize =
      insideGltf.userData.sizeDefault || this.getSize(insideGltf);

    // Tính scale ratio mới cho chiều rộng
    const newScaleZ = newWidth / originalSize.z;

    // Cập nhật position - căn theo left edge
    insideGltf.position.z = leftLocalPos.z - sizeL;

    // Cập nhật scale
    if (insideGltf.isGroup) {
      insideGltf.children.forEach(child => {
        if (child.isMesh) {
          child.scale.z = newScaleZ;
        }
      });
    } else {
      insideGltf.scale.z = newScaleZ;
    }

    // Tính toán depth (X) nếu cần
    // this.recalculateXaNgangDepth(insideGltf, clonedModule, scaleRatios);

    console.log(
      `Recalculated xà ngang: width=${newWidth.toFixed(
        3
      )}, scaleZ=${newScaleZ.toFixed(3)}`
    );
  }

  recalculateXaNgangDimensionsLeft(
    insideGltf,
    edges,
    scaleRatios,
    clonedModule
  ) {
    const { rightEdge } = edges;

    if (!rightEdge) {
      console.warn('Missing left or right edge for xà ngang');
      return;
    }

    const orientation = this.getModuleOrientation(clonedModule.group);

    // Lấy kích thước của các edges
    const sizeRight = this.getSize(rightEdge);

    // Lấy world positions của các edges

    // Chuyển đổi về local coordinates của module
    const tamHau = this.getMeshByName(clonedModule.group, 'HAU');
    const backWorldPos = tamHau.getWorldPosition(new THREE.Vector3());
    const backLocalPos = clonedModule.group.worldToLocal(backWorldPos.clone());
    const sizeHau = this.getSize(tamHau);

    let sizeR = 0;
    let sizeH = 0;
    if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
      sizeR = sizeRight.z;
      sizeH = sizeHau.z;
    } else if (orientation === 'ROTATED_90' || orientation === 'ROTATED_270') {
      sizeR = sizeRight.x;
      sizeH = sizeHau.x;
    }
    // Tính kích thước mới theo chiều rộng (Z)
    const newWidth = sizeH - sizeR;

    // Lấy kích thước gốc của inside
    const originalSize =
      insideGltf.userData.sizeDefault || this.getSize(insideGltf);

    // Tính scale ratio mới cho chiều rộng
    const newScaleZ = newWidth / originalSize.z;

    // Cập nhật position - căn theo left edge
    insideGltf.position.z = backLocalPos.z;

    // Cập nhật scale
    if (insideGltf.isGroup) {
      insideGltf.children.forEach(child => {
        if (child.isMesh) {
          child.scale.z = newScaleZ;
        }
      });
    } else {
      insideGltf.scale.z = newScaleZ;
    }

    // Tính toán depth (X) nếu cần
    // this.recalculateXaNgangDepth(insideGltf, clonedModule, scaleRatios);

    console.log(
      `Recalculated xà ngang: width=${newWidth.toFixed(
        3
      )}, scaleZ=${newScaleZ.toFixed(3)}`
    );
  }

  recalculateXaDocDimensionsY(insideGltf, edges, scaleRatios, clonedModule) {
    const { bottomEdge, topEdge } = edges;

    if (!bottomEdge || !topEdge) {
      console.warn('Missing left or right edge for xà ngang');
      return;
    }

    const orientation = this.getModuleOrientation(clonedModule.group);

    // Lấy kích thước của các edges
    const sizeBottom = this.getSize(bottomEdge);
    const sizeTop = this.getSize(topEdge);

    // Lấy world positions của các edges
    const bottomWorldPos = bottomEdge.getWorldPosition(new THREE.Vector3());
    const topWorldPos = topEdge.getWorldPosition(new THREE.Vector3());

    // Chuyển đổi về local coordinates của module
    const bottomLocalPos = clonedModule.group.worldToLocal(
      bottomWorldPos.clone()
    );
    const topLocalPos = clonedModule.group.worldToLocal(topWorldPos.clone());

    const sizeY = sizeBottom.y;
    // Tính kích thước mới theo chiều rộng (Z)
    const newHeight = Math.abs(topLocalPos.y - bottomLocalPos.y) - sizeY;

    // Lấy kích thước gốc của inside
    const originalSize =
      insideGltf.userData.sizeDefault || this.getSize(insideGltf);

    // Tính scale ratio mới cho chiều rộng
    const newScaleY = newHeight / originalSize.y;

    // Cập nhật position - căn theo left edge
    insideGltf.position.y = bottomLocalPos.y + sizeY;

    // Cập nhật scale
    if (insideGltf.isGroup) {
      insideGltf.children.forEach(child => {
        if (child.isMesh) {
          child.scale.y = newScaleY;
        }
      });
    } else {
      insideGltf.scale.y = newScaleY;
    }

    // Tính toán depth (X) nếu cần
    // this.recalculateXaNgangDepth(insideGltf, clonedModule, scaleRatios);
  }

  recalculateXaDocDimensionsZ(
    insideGltf,
    edges,
    scaleRatios,
    clonedModule,
    newDimensions,
    oldDimensions
  ) {
    const { leftEdge, rightEdge } = edges;

    if (!leftEdge || !rightEdge) {
      console.warn('Missing left or right edge for xà ngang');
      return;
    }

    const sizeModule = oldDimensions.width;

    const deltaPositionZ = insideGltf.position.z;

    // const newPositionZ = (deltaPositionZ * (newDimensions.width- 0.017)) / (oldDimensions.width - 0.017);
    // insideGltf.position.z = newPositionZ;
    const newPositionZ =
      ((sizeModule / 2 - deltaPositionZ) / (oldDimensions.width - 0.017)) *
      (newDimensions.width - oldDimensions.width);
    const x1 =
      (sizeModule / 2 - deltaPositionZ) / (oldDimensions.width - 0.017);
    const x2 = newDimensions.width - oldDimensions.width;
    insideGltf.position.z =
      deltaPositionZ - x1 * x2 - sizeModule / 2 + newDimensions.width / 2;
    // insideGltf.position.z = deltaPositionZ - (x1 * x2) + sizeModule/2;
  }

  scaleXaNgang(insideGltf, scaleRatios, clonedModule) {
    // Tìm các edge meshes
    const edges = this.findEdgeMeshes(insideGltf, clonedModule);

    if (edges.leftEdge && edges.rightEdge) {
      // Tính lại dựa trên edges thực tế
      this.recalculateXaNgangDimensions(
        insideGltf,
        edges,
        scaleRatios,
        clonedModule
      );
    } else {
      // Fallback: scale theo tỷ lệ đơn giản
      console.warn('Using fallback scaling for xà ngang');
      if (insideGltf.isGroup) {
        insideGltf.children.forEach(child => {
          if (child.isMesh) {
            child.scale.z *= scaleRatios.z;
            child.scale.x *= scaleRatios.x;
          }
        });
      } else {
        insideGltf.scale.z *= scaleRatios.z;
        insideGltf.scale.x *= scaleRatios.x;
      }

      // Scale position theo tỷ lệ
      insideGltf.position.x *= scaleRatios.x;
      insideGltf.position.y *= scaleRatios.y;
    }
  }

  scaleAccessoryFlip(insideGltf, scaleRatios, clonedModule) {
    // Tìm các edge meshes
    const edges = this.findEdgeMeshes(insideGltf, clonedModule);

    if (edges.leftEdge && edges.rightEdge) {
      // Tính lại dựa trên edges thực tế
      this.recalculateAccessoryFlipDimensions(
        insideGltf,
        edges,
        scaleRatios,
        clonedModule
      );
    } else {
      // Fallback: scale theo tỷ lệ đơn giản
      console.warn('Using fallback scaling for xà ngang');
      if (insideGltf.isGroup) {
        insideGltf.children.forEach(child => {
          if (child.isMesh) {
            child.scale.z *= scaleRatios.z;
            child.scale.x *= scaleRatios.x;
          }
        });
      } else {
        insideGltf.scale.z *= scaleRatios.z;
        insideGltf.scale.x *= scaleRatios.x;
      }

      // Scale position theo tỷ lệ
      insideGltf.position.x *= scaleRatios.x;
      insideGltf.position.y *= scaleRatios.y;
    }
  }

  /**
   * Scale xà ngang (type 0)
   */
  scaleXanXeo(insideGltf, scaleRatios, clonedModule) {
    // Tìm các edge meshes
    const edges = this.findEdgeMeshes(insideGltf, clonedModule);

    if (edges.leftEdge && edges.rightEdge) {
      // Tính lại dựa trên edges thực tế
      this.recalculateXanXeoDimensions(
        insideGltf,
        edges,
        scaleRatios,
        clonedModule
      );
    } else {
      // Fallback: scale theo tỷ lệ đơn giản
      console.warn('Using fallback scaling for xà ngang');
      if (insideGltf.isGroup) {
        insideGltf.children.forEach(child => {
          if (child.isMesh) {
            child.scale.z *= scaleRatios.z;
            child.scale.x *= scaleRatios.x;
          }
        });
      } else {
        insideGltf.scale.z *= scaleRatios.z;
        insideGltf.scale.x *= scaleRatios.x;
      }

      // Scale position theo tỷ lệ
      insideGltf.position.x *= scaleRatios.x;
      insideGltf.position.y *= scaleRatios.y;
    }
  }

  resetXanXeoDimensions(insideGltf, edges, clonedModule, listId) {
    let moduleData;

    const addModelToCabinet = group => {
      const data = {
        _id: moduleData._id,
        name: moduleData.name,
        glb: moduleData.glb,
        type: moduleData?.type,
        materialType: moduleData?.material_type,
        useAcrylic: moduleData?.use_acrylic,
        dependent: moduleData?.dependent,
        doorDistance: moduleData?.doorDistance,
        thumbnail_url: moduleData?.thumbnail_url ?? moduleData?.icon,
        limit: moduleData?.limit,
        beveled: moduleData?.beveled ?? false,
        gltfUuid: group?.uuid,
        gltf: group,
        group: group,
      };

      group.userData.isInside = true;
      group.userData.model = moduleData.glb;
      group.userData.limit = moduleData?.limit;
      clonedModule.listInside.push(data);
      clonedModule.group.add(group);

      return data;
    };

    const { leftEdge, rightEdge, bottomEdge, topEdge } = edges;

    if (!leftEdge || !rightEdge || !bottomEdge || !topEdge) {
      console.warn('Missing left or right edge for xà ngang');
      return;
    }

    const orientation = this.getModuleOrientation(clonedModule.group);

    // Lấy kích thước của các edges
    const sizeLeft = this.getSize(leftEdge);
    const sizeRight = this.getSize(rightEdge);
    const sizeBottom = this.getSize(bottomEdge);
    const sizeTop = this.getSize(topEdge);

    // Lấy world positions của các edges
    const leftWorldPos = leftEdge.getWorldPosition(new THREE.Vector3());
    const rightWorldPos = rightEdge.getWorldPosition(new THREE.Vector3());
    const bottomWorldPos = bottomEdge.getWorldPosition(new THREE.Vector3());
    const topWorldPos = topEdge.getWorldPosition(new THREE.Vector3());

    // Chuyển đổi về local coordinates của module
    const leftLocalPos = clonedModule.group.worldToLocal(leftWorldPos.clone());
    const rightLocalPos = clonedModule.group.worldToLocal(
      rightWorldPos.clone()
    );
    const bottomLocalPos = clonedModule.group.worldToLocal(
      bottomWorldPos.clone()
    );
    const topLocalPos = clonedModule.group.worldToLocal(topWorldPos.clone());

    let sizeL = 0;
    let sizeX = 0;
    let sizeZ = 0;
    let positionZ = 0;
    if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
      sizeL = sizeLeft.z;
      sizeX = sizeLeft.x;
      sizeZ = Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;
      positionZ = leftLocalPos.z - sizeLeft.z;
    } else if (orientation === 'ROTATED_90' || orientation === 'ROTATED_270') {
      positionZ = leftLocalPos.z - sizeLeft.x;
      sizeL = sizeLeft.x;
      sizeX = sizeLeft.z;
      sizeZ = Math.abs(rightWorldPos.x - leftWorldPos.x - sizeLeft.x);
    }
    // Tính kích thước mới theo chiều rộng (Z)
    const newWidth = Math.abs(leftLocalPos.z - rightLocalPos.z) - sizeL;

    // Lấy kích thước gốc của inside
    const originalSize =
      insideGltf.userData.sizeDefault || this.getSize(insideGltf);

    clonedModule.listInside.forEach(inside => {
      if (
        inside.gltf.uuid !== insideGltf.uuid &&
        listId.includes(inside.gltf.uuid)
      ) {
        clonedModule.group.remove(inside.gltf);
        clonedModule.listInside = clonedModule.listInside.filter(
          item => item.gltf.uuid !== inside.gltf.uuid
        );
      }

      if (inside.gltf.uuid === insideGltf.uuid) {
        moduleData = inside;
      }
    });

    insideGltf.rotation.set(0, 0, 0);

    let cloneMesh;
    insideGltf.traverse(child => {
      if (child.isMesh) {
        const cloneChild = child.clone();
        cloneChild.scale.set(1, 1, 1);
        cloneChild.position.set(0, 0, 0);
        cloneChild.rotation.set(0, 0, 0);
        cloneMesh = cloneChild;
      }
    });

    const cloneGroup = new THREE.Group();
    const clone1 = cloneMesh.clone();
    clone1.name = 'HPANEL-SHOE-MAIN';
    const clone2 = cloneMesh.clone();
    clone2.name = 'HPANEL-SHOE-SUB';

    const originalSizeClone = this.getSize(cloneMesh);

    const sizeClone1 = (2 * (sizeX - 0.04)) / Math.sqrt(2) - 0.047;
    clone1.scale.x = sizeClone1 / originalSizeClone.x;
    clone1.scale.z = sizeZ / originalSizeClone.z;

    const sizeClone2 = 0.03;
    clone2.scale.x = sizeClone2 / originalSizeClone.x;
    clone2.scale.z = sizeZ / originalSizeClone.z;

    clone2.position.y = clone1.position.y + originalSizeClone.y;
    clone2.position.x = clone1.position.x + sizeClone1;
    clone2.rotation.z += THREE.MathUtils.degToRad(90);

    cloneGroup.add(clone1);
    cloneGroup.add(clone2);
    const oldInsideGltfUuid = insideGltf.uuid;
    const oldUserData = insideGltf.userData;
    clonedModule.group.remove(insideGltf);
    insideGltf = cloneGroup;
    clonedModule.group.add(insideGltf);
    insideGltf.userData = oldUserData;
    clonedModule.listInside.forEach(inside => {
      if (inside.gltf.uuid === oldInsideGltfUuid) {
        inside.gltf = insideGltf;
        inside.gltfUuid = insideGltf.uuid;
      }
    });
    insideGltf.userData.type = InsideType.HPANEL_SHOE;
    this.setInfoModelChild(insideGltf);

    insideGltf.position.x = Math.min(leftLocalPos.x, rightLocalPos.x);
    insideGltf.position.z = positionZ;
    insideGltf.position.y = sizeLeft.y / 2;
    insideGltf.rotation.z += THREE.MathUtils.degToRad(-45);

    const cloneData = insideGltf.clone();
    const size11 = this.getSize(cloneData);
    cloneData.rotation.set(0, 0, 0);
    const size112 = this.getSize(cloneData);
    const sizeData = [];

    cloneData.traverse(child => {
      if (!child.isMesh) return;
      // Lấy rotation (rad)
      const rotation = {
        x: child.rotation.x,
        y: child.rotation.y,
        z: child.rotation.z,
      };

      const position = {
        x: child.position.x,
        y: child.position.y,
        z: child.position.z,
      };

      child.rotation.set(0, 0, 0);

      const size = this.getSize(child);
      const size2 = this.getSizeRotation(clonedModule.group, child);

      sizeData.push({
        name: child.name || 'unnamed',
        size: {
          x: size.x,
          y: size.y,
          z: size.z,
        },
        rotation,
        position,
      });
    });

    this.disposeObject3D(cloneData);
    insideGltf.userData.sizeData = sizeData;

    const sizeModel = this.getSize(insideGltf);

    if (bottomEdge && topEdge) {
      const bottomWorldPos = new THREE.Vector3();
      const topWorldPos = new THREE.Vector3();
      bottomEdge.getWorldPosition(bottomWorldPos);
      topEdge.getWorldPosition(topWorldPos);

      const box = new THREE.Box3().setFromObject(insideGltf);
      const posModel = insideGltf.getWorldPosition(new THREE.Vector3());
      const min = box.min.clone();
      const deltaY = posModel.y - min.y;

      insideGltf.position.y = bottomLocalPos.y + sizeBottom.y + deltaY + 0.001;

      const clone3 = insideGltf.clone();
      this.setInfoModelChild(clone3);

      const hpanelSub = this.getMeshByName(clone3, 'HPANEL-SHOE-SUB');
      const box2 = new THREE.Box3().setFromObject(hpanelSub);
      const max = box2.max.clone();
      const posModel2 = clone3.getWorldPosition(new THREE.Vector3());
      const deltaY2 = posModel2.y - max.y;

      addModelToCabinet(clone3);

      clone3.position.y = topLocalPos.y - (0.255 - deltaY2);

      const sizeY = clone3.position.y - insideGltf.position.y - sizeModel.y;

      const numMesh = Math.floor(sizeY / 0.255);

      if (numMesh >= 1) {
        const gap = (sizeY - numMesh * sizeModel.y) / (numMesh + 1);
        for (let i = 1; i < numMesh + 1; i++) {
          const clone4 = insideGltf.clone();
          this.setInfoModelChild(clone4);
          clone4.position.y = insideGltf.position.y + i * sizeModel.y + i * gap;
          addModelToCabinet(clone4);
        }
      }
    }

    console.log(`Reset xà ngang: width=${sizeZ.toFixed(2)}`);
  }

  resetAccessoryFlipDimensions(insideGltf, edges, scaleRatios, clonedModule) {
    let moduleData;

    const addModelToCabinet = group => {
      const data = {
        _id: moduleData._id,
        name: moduleData.name,
        glb: moduleData.glb,
        type: moduleData?.type,
        materialType: moduleData?.material_type,
        useAcrylic: moduleData?.use_acrylic,
        dependent: moduleData?.dependent,
        doorDistance: moduleData?.doorDistance,
        thumbnail_url: moduleData?.thumbnail_url ?? moduleData?.icon,
        limit: moduleData?.limit,
        beveled: moduleData?.beveled ?? false,
        gltfUuid: group?.uuid,
        gltf: group,
        group: group,
      };

      group.userData.isInside = true;
      group.userData.model = moduleData.glb;
      group.userData.limit = moduleData?.limit;
      clonedModule.listInside.push(data);
      clonedModule.group.add(group);

      return data;
    };

    const { leftEdge, rightEdge, bottomEdge, topEdge } = edges;

    if (!leftEdge || !rightEdge || !bottomEdge || !topEdge) {
      console.warn('Missing left or right edge for xà ngang');
      return;
    }

    const orientation = this.getModuleOrientation(clonedModule.group);

    // Lấy kích thước của các edges
    const sizeLeft = this.getSize(leftEdge);
    const sizeRight = this.getSize(rightEdge);
    const sizeBottom = this.getSize(bottomEdge);
    const sizeTop = this.getSize(topEdge);

    // Lấy world positions của các edges
    const leftWorldPos = leftEdge.getWorldPosition(new THREE.Vector3());
    const rightWorldPos = rightEdge.getWorldPosition(new THREE.Vector3());
    const bottomWorldPos = bottomEdge.getWorldPosition(new THREE.Vector3());
    const topWorldPos = topEdge.getWorldPosition(new THREE.Vector3());

    // Chuyển đổi về local coordinates của module
    const leftLocalPos = clonedModule.group.worldToLocal(leftWorldPos.clone());
    const rightLocalPos = clonedModule.group.worldToLocal(
      rightWorldPos.clone()
    );
    const bottomLocalPos = clonedModule.group.worldToLocal(
      bottomWorldPos.clone()
    );
    const topLocalPos = clonedModule.group.worldToLocal(topWorldPos.clone());

    let sizeL = 0;
    let sizeX = 0;
    let sizeZ = 0;
    let positionZ = 0;
    if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
      sizeL = sizeLeft.z;
      sizeX = sizeLeft.x;
      sizeZ = Math.abs(rightWorldPos.z - leftWorldPos.z) - sizeLeft.z;
      positionZ = leftLocalPos.z - sizeLeft.z;
    } else if (orientation === 'ROTATED_90' || orientation === 'ROTATED_270') {
      positionZ = leftLocalPos.z - sizeLeft.x;
      sizeL = sizeLeft.x;
      sizeX = sizeLeft.z;
      sizeZ = Math.abs(rightWorldPos.x - leftWorldPos.x - sizeLeft.x);
    }
    // Tính kích thước mới theo chiều rộng (Z)
    const newWidth = Math.abs(leftLocalPos.z - rightLocalPos.z) - sizeL;

    // Lấy kích thước gốc của inside
    const originalSize = this.getSizeRotation(clonedModule.group, insideGltf);

    clonedModule.listInside.forEach(inside => {
      if (inside.gltf.uuid !== insideGltf.uuid) {
        clonedModule.group.remove(inside.gltf);
        clonedModule.listInside = clonedModule.listInside.filter(
          item => item.gltf.uuid !== inside.gltf.uuid
        );
      }

      if (inside.gltf.uuid === insideGltf.uuid) {
        moduleData = inside;
      }
    });

    const deltaGap = (0.315 - originalSize.y) / 2;
    insideGltf.position.y = topLocalPos.y - 0.33 + deltaGap;

    const sizeY = topWorldPos.y - bottomWorldPos.y - sizeBottom.y;

    const numMesh = Math.floor(sizeY / 0.33);

    if (numMesh > 1) {
      for (let i = 1; i < numMesh; i++) {
        const clone4 = insideGltf.clone();
        this.setInfoModelChild(clone4);
        clone4.position.y = topWorldPos.y - 0.33 * (i + 1) + deltaGap;
        addModelToCabinet(clone4);
      }
    }

    // if (bottomEdge && topEdge) {
    //   const bottomWorldPos = new THREE.Vector3();
    //   const topWorldPos = new THREE.Vector3();
    //   bottomEdge.getWorldPosition(bottomWorldPos);
    //   topEdge.getWorldPosition(topWorldPos);

    //   const box = new THREE.Box3().setFromObject(insideGltf);
    //   const posModel = insideGltf.getWorldPosition(new THREE.Vector3());
    //   const min = box.min.clone();
    //   const deltaY = posModel.y - min.y;

    //   insideGltf.position.y = bottomWorldPos.y + sizeBottom.y + deltaY + 0.001;

    //   const clone3 = insideGltf.clone();
    //   this.setInfoModelChild(clone3);

    //   const hpanelSub = this.getMeshByName(clone3, 'HPANEL-SHOE-SUB');
    //   const box2 = new THREE.Box3().setFromObject(hpanelSub);
    //   const max = box2.max.clone();
    //   const posModel2 = clone3.getWorldPosition(new THREE.Vector3());
    //   const deltaY2 = posModel2.y - max.y;

    //   addModelToCabinet(clone3);

    //   clone3.position.y = topWorldPos.y - (0.255 - deltaY2);

    //   const sizeY = clone3.position.y - insideGltf.position.y - sizeModel.y;

    //   const numMesh = Math.floor(sizeY / 0.255);

    //   if (numMesh >= 1) {
    //     const gap = (sizeY - numMesh * sizeModel.y) / (numMesh + 1);
    //     for (let i = 1; i < numMesh + 1; i++) {
    //       const clone4 = insideGltf.clone();
    //       this.setInfoModelChild(clone4);
    //       clone4.position.y = insideGltf.position.y + i * sizeModel.y + i * gap;
    //       addModelToCabinet(clone4);
    //     }
    //   }
    // }
  }

  groupHpanelShoeByEdge(clonedModule) {
    const resultMap = new Map();

    if (!clonedModule?.listInside) return [];

    clonedModule.listInside.forEach(inside => {
      if (inside.type !== InsideType.HPANEL_SHOE) return;

      const leftUuid = inside.gltf?.userData?.leftEdgeUuid;
      const rightUuid = inside.gltf?.userData?.rightEdgeUuid;

      if (!leftUuid || !rightUuid) return;

      // Tạo key duy nhất cho cặp edge
      const key = `${leftUuid}_${rightUuid}`;

      if (!resultMap.has(key)) {
        resultMap.set(key, []);
      }

      resultMap.get(key).push(inside);
    });

    // Convert Map → Array
    return Array.from(resultMap.values());
  }

  resetHpanelShoeScale(insideGltf, scaleRatios, clonedModule) {
    // Tìm các edge meshes
    const edges = this.findEdgeMeshes(insideGltf, clonedModule);

    if (
      edges.leftEdge &&
      edges.rightEdge &&
      edges.topEdge &&
      edges.bottomEdge
    ) {
      // Tính lại dựa trên edges thực tế
      const groupedList = this.groupHpanelShoeByEdge(clonedModule);
      groupedList.forEach(group => {
        const listId = group.map(item => item.gltf.uuid).join(', ');
        const edgesInside = this.findEdgeMeshes(group[0].gltf, clonedModule);
        this.resetXanXeoDimensions(
          group[0].gltf,
          edgesInside,
          clonedModule,
          listId
        );
      });
    } else {
      // Fallback: scale theo tỷ lệ đơn giản
      console.warn('Using fallback scaling for xà ngang');
      if (insideGltf.isGroup) {
        insideGltf.children.forEach(child => {
          if (child.isMesh) {
            child.scale.z *= scaleRatios.z;
            child.scale.x *= scaleRatios.x;
          }
        });
      } else {
        insideGltf.scale.z *= scaleRatios.z;
        insideGltf.scale.x *= scaleRatios.x;
      }

      // Scale position theo tỷ lệ
      insideGltf.position.x *= scaleRatios.x;
      insideGltf.position.y *= scaleRatios.y;
    }
  }

  resetAccessoryFlip(insideGltf, scaleRatios, clonedModule) {
    // Tìm các edge meshes
    const edges = this.findEdgeMeshes(insideGltf, clonedModule);

    if (
      edges.leftEdge &&
      edges.rightEdge &&
      edges.topEdge &&
      edges.bottomEdge
    ) {
      // Tính lại dựa trên edges thực tế
      this.resetAccessoryFlipDimensions(
        insideGltf,
        edges,
        scaleRatios,
        clonedModule
      );
    } else {
      // Fallback: scale theo tỷ lệ đơn giản
      console.warn('Using fallback scaling for xà ngang');
      if (insideGltf.isGroup) {
        insideGltf.children.forEach(child => {
          if (child.isMesh) {
            child.scale.z *= scaleRatios.z;
            child.scale.x *= scaleRatios.x;
          }
        });
      } else {
        insideGltf.scale.z *= scaleRatios.z;
        insideGltf.scale.x *= scaleRatios.x;
      }

      // Scale position theo tỷ lệ
      insideGltf.position.x *= scaleRatios.x;
      insideGltf.position.y *= scaleRatios.y;
    }
  }

  scaleXaNgangRight(insideGltf, scaleRatios, clonedModule) {
    // Tìm các edge meshes
    const edges = this.findEdgeMeshes(insideGltf, clonedModule);

    if (edges.leftEdge) {
      // Tính lại dựa trên edges thực tế
      this.recalculateXaNgangDimensionsRight(
        insideGltf,
        edges,
        scaleRatios,
        clonedModule
      );
    } else {
      // Fallback: scale theo tỷ lệ đơn giản
      console.warn('Using fallback scaling for xà ngang');
      if (insideGltf.isGroup) {
        insideGltf.children.forEach(child => {
          if (child.isMesh) {
            child.scale.z *= scaleRatios.z;
            child.scale.x *= scaleRatios.x;
          }
        });
      } else {
        insideGltf.scale.z *= scaleRatios.z;
        insideGltf.scale.x *= scaleRatios.x;
      }

      // Scale position theo tỷ lệ
      insideGltf.position.x *= scaleRatios.x;
      insideGltf.position.y *= scaleRatios.y;
    }
  }

  scaleXaNgangLeft(insideGltf, scaleRatios, clonedModule) {
    // Tìm các edge meshes
    const edges = this.findEdgeMeshes(insideGltf, clonedModule);

    if (edges.rightEdge) {
      // Tính lại dựa trên edges thực tế
      this.recalculateXaNgangDimensionsLeft(
        insideGltf,
        edges,
        scaleRatios,
        clonedModule
      );
    } else {
      // Fallback: scale theo tỷ lệ đơn giản
      console.warn('Using fallback scaling for xà ngang');
      if (insideGltf.isGroup) {
        insideGltf.children.forEach(child => {
          if (child.isMesh) {
            child.scale.z *= scaleRatios.z;
            child.scale.x *= scaleRatios.x;
          }
        });
      } else {
        insideGltf.scale.z *= scaleRatios.z;
        insideGltf.scale.x *= scaleRatios.x;
      }

      // Scale position theo tỷ lệ
      insideGltf.position.x *= scaleRatios.x;
      insideGltf.position.y *= scaleRatios.y;
    }
  }

  /**
   * Scale xà dọc (type 1)
   */
  // scaleXaDoc(insideGltf, scaleRatios, clonedModule) {
  //   // Xà dọc chủ yếu scale theo chiều cao (y)
  //   // if (insideGltf.isGroup) {
  //   //   insideGltf.children.forEach(child => {
  //   //     if (child.isMesh) {
  //   //       child.scale.y *= scaleRatios.y;
  //   //     }
  //   //   });
  //   // } else {
  //   //   insideGltf.scale.y *= scaleRatios.y;
  //   // }

  //   // Cập nhật position
  //   this.repositionXaDoc(insideGltf, scaleRatios, clonedModule);
  // }

  scaleXaDocZ(
    insideGltf,
    scaleRatios,
    clonedModule,
    newDimensions,
    oldDimensions
  ) {
    const edges = this.findEdgeMeshes(insideGltf, clonedModule);

    if (edges.leftEdge && edges.rightEdge) {
      // Tính lại dựa trên edges thực tế
      this.recalculateXaDocDimensionsZ(
        insideGltf,
        edges,
        scaleRatios,
        clonedModule,
        newDimensions,
        oldDimensions
      );
    } else {
      // Fallback: scale theo tỷ lệ đơn giản
      console.warn('Using fallback scaling for xà ngang');
      if (insideGltf.isGroup) {
        insideGltf.children.forEach(child => {
          if (child.isMesh) {
            child.scale.z *= scaleRatios.z;
            child.scale.x *= scaleRatios.x;
          }
        });
      } else {
        insideGltf.scale.z *= scaleRatios.z;
        insideGltf.scale.x *= scaleRatios.x;
      }

      // Scale position theo tỷ lệ
      insideGltf.position.x *= scaleRatios.x;
      insideGltf.position.y *= scaleRatios.y;
    }
  }

  scaleXaDocY(insideGltf, scaleRatios, clonedModule) {
    const edges = this.findEdgeMeshes(insideGltf, clonedModule);

    if (edges.bottomEdge && edges.topEdge) {
      // Tính lại dựa trên edges thực tế
      this.recalculateXaDocDimensionsY(
        insideGltf,
        edges,
        scaleRatios,
        clonedModule
      );
    } else {
      // Fallback: scale theo tỷ lệ đơn giản
      console.warn('Using fallback scaling for xà ngang');
      if (insideGltf.isGroup) {
        insideGltf.children.forEach(child => {
          if (child.isMesh) {
            child.scale.z *= scaleRatios.z;
            child.scale.x *= scaleRatios.x;
          }
        });
      } else {
        insideGltf.scale.z *= scaleRatios.z;
        insideGltf.scale.x *= scaleRatios.x;
      }

      // Scale position theo tỷ lệ
      insideGltf.position.x *= scaleRatios.x;
      insideGltf.position.y *= scaleRatios.y;
    }
  }

  scaleXaDocY2Height(
    insideGltf,
    scaleRatios,
    clonedModule,
    oldDimensions,
    newDimensions
  ) {
    const edges = this.findEdgeMeshes(insideGltf, clonedModule);

    if (edges.bottomEdge && edges.topEdge) {
      insideGltf.traverse(child => {
        if (child.isMesh) {
          if (child.position.y === 0) {
            const sizeMesh = this.getSize(child);
            const cloneMesh = child.clone();
            cloneMesh.scale.set(1, 1, 1);
            const sizeClone = this.getSize(cloneMesh);
            const newHeight =
              newDimensions.height - oldDimensions.height + sizeMesh.y;
            const newScaleY = newHeight / sizeClone.y;
            child.scale.y = newScaleY;
          } else {
            child.position.y =
              child.position.y + (newDimensions.height - oldDimensions.height);
          }
        }
      });
    }
  }

  scaleXaDocY2Depth(
    insideGltf,
    scaleRatios,
    clonedModule,
    oldDimensions,
    newDimensions
  ) {
    const edges = this.findEdgeMeshes(insideGltf, clonedModule);

    const deltaDepth = newDimensions.depth - oldDimensions.depth;

    if (edges.bottomEdge && edges.topEdge) {
      insideGltf.traverse(child => {
        if (child.isMesh) {
          const sizeMesh = this.getSizeRotation(clonedModule.group, child);
          const cloneMesh = child.clone();
          cloneMesh.scale.set(1, 1, 1);
          const sizeClone = this.getSize(cloneMesh);
          const deltaX = oldDimensions.depth - sizeMesh.x;
          const newDepth = newDimensions.depth - deltaX;
          const newScaleX = newDepth / sizeClone.x;
          child.scale.x = newScaleX;
        }
      });
      insideGltf.position.x = insideGltf.position.x - deltaDepth / 2;
    }
  }

  /**
   * Scale đợt kệ (type 3)
   */
  scaleKe(insideGltf, scaleRatios, clonedModule) {
    // Tìm các edge meshes
    const edges = this.findEdgeMeshes(insideGltf, clonedModule);

    if (edges.leftEdge && edges.rightEdge) {
      const { leftEdge, rightEdge } = edges;

      // Lấy kích thước của các edges
      const sizeLeft = this.getSize(leftEdge);
      const sizeRight = this.getSize(rightEdge);

      // Lấy world positions của các edges
      const leftWorldPos = leftEdge.getWorldPosition(new THREE.Vector3());
      const rightWorldPos = rightEdge.getWorldPosition(new THREE.Vector3());

      // Chuyển đổi về local coordinates của module
      const leftLocalPos = clonedModule.group.worldToLocal(
        leftWorldPos.clone()
      );
      const rightLocalPos = clonedModule.group.worldToLocal(
        rightWorldPos.clone()
      );

      // Tính kích thước mới theo chiều rộng (Z)
      let newWidth = 0;

      // Lấy kích thước gốc của inside
      const originalSize = this.getSizeRotation(clonedModule.group, insideGltf);

      // Cập nhật position - căn theo left edge
      const orientation = this.getModuleOrientation(clonedModule.group);
      if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
        newWidth = Math.abs(leftLocalPos.z - rightLocalPos.z) - sizeLeft.z;
        insideGltf.position.z = leftLocalPos.z - sizeLeft.z;
      } else if (
        orientation === 'ROTATED_90' ||
        orientation === 'ROTATED_270'
      ) {
        newWidth = Math.abs(leftLocalPos.z - rightLocalPos.z) - sizeLeft.x;
        insideGltf.position.z = leftLocalPos.z - sizeLeft.x;
      }
      this.scaleCabinetShelfWidth(insideGltf, originalSize, newWidth);
    } else {
      // Fallback: scale theo tỷ lệ đơn giản
      console.warn('Using fallback scaling for xà ngang');
    }
  }

  scaleDrawerY(insideGltf, scaleRatios, clonedModule) {
    // Tìm các edge meshes
    const edges = this.findEdgeMeshes(insideGltf, clonedModule);

    if (edges.topEdge && edges.bottomEdge) {
      const { topEdge, bottomEdge } = edges;

      // Lấy kích thước của các edges
      const sizeTop = this.getSize(topEdge);
      const sizeBottom = this.getSize(bottomEdge);

      // Lấy world positions của các edges
      const topWorldPos = topEdge.getWorldPosition(new THREE.Vector3());
      const bottomWorldPos = bottomEdge.getWorldPosition(new THREE.Vector3());

      // Chuyển đổi về local coordinates của module
      const topLocalPos = clonedModule.group.worldToLocal(topWorldPos.clone());
      const bottomLocalPos = clonedModule.group.worldToLocal(
        bottomWorldPos.clone()
      );

      // Tính kích thước mới theo chiều rộng (Z)
      let newWidth = 0;

      // Lấy kích thước gốc của inside
      const originalSize = this.getSizeRotation(clonedModule.group, insideGltf);

      const sizeY = Math.abs(topLocalPos.y - bottomLocalPos.y) - sizeBottom.y;

      const sizeDrawer =
        originalSize.y +
        insideGltf.userData.bottomGap +
        insideGltf.userData.topGap;
      if (sizeY < sizeDrawer) {
        const newSizeDrawer =
          sizeY - insideGltf.userData.bottomGap - insideGltf.userData.topGap;
        InsideCabinetHandler.scaleHeight(insideGltf, newSizeDrawer - 0.008);
      }
    } else {
      // Fallback: scale theo tỷ lệ đơn giản
      console.warn('Using fallback scaling for xà ngang');
    }
  }

  scaleUDrawer(insideGltf, scaleRatios, clonedModule) {
    // Tìm các edge meshes
    const edges = this.findEdgeMeshes(insideGltf, clonedModule);

    if (edges.leftEdge && edges.rightEdge) {
      const { leftEdge, rightEdge } = edges;

      // Lấy kích thước của các edges
      const sizeLeft = this.getSize(leftEdge);
      const sizeRight = this.getSize(rightEdge);

      // Lấy world positions của các edges
      const leftWorldPos = leftEdge.getWorldPosition(new THREE.Vector3());
      const rightWorldPos = rightEdge.getWorldPosition(new THREE.Vector3());

      // Chuyển đổi về local coordinates của module
      const leftLocalPos = clonedModule.group.worldToLocal(
        leftWorldPos.clone()
      );
      const rightLocalPos = clonedModule.group.worldToLocal(
        rightWorldPos.clone()
      );

      // Tính kích thước mới theo chiều rộng (Z)
      let newWidth = 0;

      // Lấy kích thước gốc của inside
      const originalSize = this.getSizeRotation(clonedModule.group, insideGltf);
      const originalSize2 = this.getSize(insideGltf);

      // Cập nhật position - căn theo left edge
      const orientation = this.getModuleOrientation(clonedModule.group);
      if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
        newWidth = Math.abs(leftLocalPos.z - rightLocalPos.z) - sizeLeft.z;
        insideGltf.position.z = leftLocalPos.z - sizeLeft.z;
      } else if (
        orientation === 'ROTATED_90' ||
        orientation === 'ROTATED_270'
      ) {
        newWidth = Math.abs(leftLocalPos.z - rightLocalPos.z) - sizeLeft.x;
        insideGltf.position.z = leftLocalPos.z - sizeLeft.x;
      }
      InsideCabinetHandler.scaleWidth(insideGltf, newWidth, originalSize);
      InsideCabinetHandler.resetScaleUdrawerChild(
        clonedModule,
        insideGltf,
        orientation,
        this.scaleCabinetShelfWidth
      );
    } else {
      // Fallback: scale theo tỷ lệ đơn giản
      console.warn('Using fallback scaling for xà ngang');
    }
  }

  /**
   * Scale group phức tạp (type 11)
   */
  scaleComplexGroup(insideGltf, scaleRatios, clonedModule) {
    if (insideGltf.isGroup) {
      insideGltf.children.forEach(child => {
        this.scaleIndividualInside(child, scaleRatios, clonedModule);
      });
    }
  }

  /**
   * Scale phụ kiện (type 7) - thường giữ nguyên hoặc chỉ di chuyển
   */
  repositionAccessorySink(insideGltf, scaleRatios, clonedModule) {
    const biaTrai = this.getMeshByName(clonedModule.group, 'BIA-TRAI');
    const biaPhai = this.getMeshByName(clonedModule.group, 'BIA-PHAI');
    const xuongTruoc1 = this.getMeshByName(clonedModule.group, 'XUONG-TRUOC-1');
    const tamHau = this.getMeshByName(clonedModule.group, 'HAU');
    const biaTraiPos = clonedModule.group.worldToLocal(
      biaTrai?.getWorldPosition(new THREE.Vector3()).clone()
    );
    const biaPhaiPos = clonedModule.group.worldToLocal(
      biaPhai?.getWorldPosition(new THREE.Vector3()).clone()
    );
    const xuongTruoc1Pos = clonedModule.group.worldToLocal(
      xuongTruoc1?.getWorldPosition(new THREE.Vector3()).clone()
    );
    const tamHauPos = clonedModule.group.worldToLocal(
      tamHau?.getWorldPosition(new THREE.Vector3()).clone()
    );
    const size = this.getSizeRotation(clonedModule.group, insideGltf);
    const sizeBiaTrai = this.getSizeRotation(clonedModule.group, biaTrai);
    const sizeXuongTruoc1 = this.getSizeRotation(
      clonedModule.group,
      xuongTruoc1
    );
    const sizeHau = this.getSizeRotation(clonedModule.group, tamHau);
    if (biaTrai && biaPhai && xuongTruoc1 && tamHau) {
      const sizeZ = Math.abs(biaPhaiPos.z - (biaTraiPos.z - sizeBiaTrai.z));
      const gapLeft = (sizeZ - size.z) / 2;
      insideGltf.position.z = biaTraiPos.z - sizeBiaTrai.z - gapLeft;
      const gapBottom =
        xuongTruoc1Pos.x + sizeXuongTruoc1.x - tamHauPos.x - size.x;
      const posX = gapBottom / 2;
      insideGltf.position.x = tamHauPos.x + posX + sizeHau.x;
      insideGltf.position.y =
        xuongTruoc1Pos.y + sizeXuongTruoc1.y - size.y + 0.022;
    }
  }

  repositionAccessoryFaucet(insideGltf, scaleRatios, clonedModule) {
    const posVoiMesh = this.getMeshByName(clonedModule.group, 'POS-VOI');
    if (posVoiMesh) {
      const posVoi = clonedModule.group.worldToLocal(
        posVoiMesh.getWorldPosition(new THREE.Vector3()).clone()
      );
      insideGltf.position.y = posVoi.y;
      insideGltf.position.z = posVoi.z;
      insideGltf.position.x = posVoi.x;
    }
  }

  repositionAccessoryFridge(insideGltf, scaleRatios, clonedModule) {
    const biaTrai = this.getMeshByName(clonedModule.group, 'G-BIA-1');
    const biaPhai = this.getMeshByName(clonedModule.group, 'G-BIA-2');
    const biaTraiPos = clonedModule.group.worldToLocal(
      biaTrai?.getWorldPosition(new THREE.Vector3()).clone()
    );
    const biaPhaiPos = clonedModule.group.worldToLocal(
      biaPhai?.getWorldPosition(new THREE.Vector3()).clone()
    );

    const size = this.getSizeRotation(clonedModule.group, insideGltf);
    const sizeBiaTrai = this.getSizeRotation(clonedModule.group, biaTrai);
    if (biaTrai && biaPhai) {
      insideGltf.position.x = biaTraiPos.x + thicknessBase;
      const gapZ = (biaTraiPos.z - biaPhaiPos.z - sizeBiaTrai.z - size.z) / 2;
      insideGltf.position.z = biaTraiPos.z - sizeBiaTrai.z - gapZ;
    }
  }

  repositionAccessoryWash(insideGltf, scaleRatios, clonedModule) {
    const biaTrai = this.getMeshByName(clonedModule.group, 'G-BIA-1');
    const biaPhai = this.getMeshByName(clonedModule.group, 'G-BIA-2');
    const biaTraiPos = clonedModule.group.worldToLocal(
      biaTrai?.getWorldPosition(new THREE.Vector3()).clone()
    );
    const biaPhaiPos = clonedModule.group.worldToLocal(
      biaPhai?.getWorldPosition(new THREE.Vector3()).clone()
    );

    const size = this.getSizeRotation(clonedModule.group, insideGltf);
    const sizeBiaTrai = this.getSizeRotation(clonedModule.group, biaTrai);
    if (biaTrai && biaPhai) {
      insideGltf.position.x = biaTraiPos.x + thicknessBase;
      const gapZ = (biaTraiPos.z - biaPhaiPos.z - sizeBiaTrai.z - size.z) / 2;
      insideGltf.position.z = biaTraiPos.z - sizeBiaTrai.z - gapZ;
    }
  }

  /**
   * Scale lại kích thước của máy rửa chén hoặc tủ lạnh
   * @param {number} targetWidth - Chiều rộng mục tiêu (đơn vị: mét)
   * @param {number} targetHeight - Chiều cao mục tiêu (đơn vị: mét)
   * @param {number} targetDepth - Chiều sâu mục tiêu (đơn vị: mét)
   */
  scaleWashAndFridge(targetWidth, targetHeight, targetDepth, module) {
    try {
      const targetObject = module?.listInside?.find(
        inside =>
          inside.type === InsideType.ACCESSORY_WASHER ||
          InsideType.ACCESSORY_FRIDGE
      );

      if (!targetObject) {
        console.warn('Không tìm thấy đối tượng thiết bị để scale');
        return;
      }

      const scaleY = targetHeight / targetObject.gltf.userData.sizeDefault.y;
      const scaleZ = targetWidth / targetObject.gltf.userData.sizeDefault.z;
      const scaleX = targetDepth / targetObject.gltf.userData.sizeDefault.x;

      targetObject.gltf.scale.z = scaleZ;
      targetObject.gltf.scale.y = scaleY;
      targetObject.gltf.scale.x = scaleX;

      // Cập nhật matrix
      targetObject.gltf.updateMatrixWorld(true);

      return targetObject;
    } catch (error) {
      console.error('Lỗi khi scale thiết bị:', error);
      throw error;
    }
  }

  scaleAccessory(insideGltf, scaleRatios, clonedModule) {
    // Phụ kiện thường không scale, chỉ cập nhật position
    this.repositionAccessory(insideGltf, scaleRatios, clonedModule);
  }

  /**
   * Rescale cửa
   */
  rescaleDoor(cloneModule, isScaleY, nextStep) {
    // if (
    //   cloneModule.cabinetType.toLowerCase().includes('corner') &&
    //   nextStep !== true
    // ) {
    //   this.rescaleDoorCornerModule(cloneModule, isScaleY);
    // } else {
    if (this.cabinetType.includes(TypeCabinet.KITCHEN)) {
      let beveled = this.beveled;

      //Khi không phải đang editting thì lấy giá trị beveled trực tiếp từ inside
      if (this.editting === false && cloneModule.listInside?.length > 0) {
        cloneModule.listInside.forEach(inside => {
          if (inside.type === InsideType.DOOR) {
            beveled = inside.beveled;

            return;
          }
        });
      }

      cloneModule.group.traverse(object => {
        if (
          object.userData.type === InsideType.DOOR &&
          object.userData.isInside === true
        ) {
          if (beveled === true) {
            this.rescale45Door(object, cloneModule);
          } else {
            this.rescaleBasicDoor(object, cloneModule);
          }
        }
      });

      if (beveled === true) {
        //Rescale bạ cửa 45
        cloneModule.group.traverse(object => {
          if (object.isMesh && object.userData.type === InsideType.BRACE) {
            this.rescaleBrace(object, cloneModule);
          }
        });

        //Xoá tất cả tay nắm
        this.removeAllHandles(cloneModule);
      } else {
        //Update lại vị trí tay nắm
        cloneModule.group.traverse(object => {
          if (
            object.userData.type === InsideType.DOOR &&
            object.userData.isInside === true
          ) {
            this.updateHandlePosition(object, cloneModule);
          }
        });

        //Xoá tất cả bạ cửa
        this.removeAllBraces(cloneModule);
      }
    } else {
      cloneModule.group.traverse(object => {
        if (
          object.userData.type === InsideType.DOOR &&
          object.userData.isInside === true
        ) {
          if (object.userData.beveled === true) {
            this.rescale45Door(object, cloneModule);
          } else {
            this.rescaleBasicDoor(object, cloneModule);
          }
        }
      });

      //Rescale bạ cửa 45
      cloneModule.group.traverse(object => {
        if (object.isMesh && object.userData.type === InsideType.BRACE) {
          this.rescaleBrace(object, cloneModule);
        }
      });

      //Update lại vị trí tay nắm
      cloneModule.group.traverse(object => {
        if (
          object.userData.type === InsideType.DOOR &&
          object.userData.isInside === true
        ) {
          this.updateHandlePosition(object, cloneModule);
        }
      });
    }
    // }
  }

  rescaleDoorCornerModule(cornerModule, isScaleY) {
    if (isScaleY) {
      // Bước 1: Thu thập tất cả door templates
      const doorTemplates = [];
      cornerModule.group.traverse(object => {
        if (object.userData.type === InsideType.DOOR_TEMPLATE) {
          doorTemplates.push(object);
        }
      });

      // Bước 2: Thu thập tất cả doors cần scale
      const doorsToScale = [];
      cornerModule.group.traverse(object => {
        if (object.userData.type === InsideType.DOOR) {
          doorsToScale.push(object);
        }
      });

      // Bước 3: Scale từng door theo template tương ứng
      doorsToScale.forEach(door => {
        // Tìm template tương ứng
        const matchedTemplate = doorTemplates.find(
          template => template.userData.name === door.userData.cornerDoor
        );

        if (matchedTemplate) {
          // Lấy kích thước của template
          const templateBBox = new THREE.Box3().setFromObject(matchedTemplate);
          const templateSize = new THREE.Vector3();
          templateBBox.getSize(templateSize);

          // Lấy kích thước gốc của door
          const doorClone = door.clone();
          doorClone.scale.set(1, 1, 1);

          const doorBBox = new THREE.Box3().setFromObject(doorClone);
          const doorSize = new THREE.Vector3();
          doorBBox.getSize(doorSize);

          if (door.userData.beveled === true) {
            if (
              door.userData.beveled === true &&
              cornerModule.moduleType.toLowerCase().includes('ceiling')
            ) {
              templateSize.y -= topDoorGapBase + thicknessBase;
            } else if (
              cornerModule.moduleType.toLowerCase().includes('kitchen') &&
              cornerModule.moduleType.toLowerCase().includes('lower')
            ) {
              templateSize.y -= topDoorGapBase - minDoorGap;
            }
          }

          // Tính scale ratio
          const scaleX = templateSize.x / doorSize.z;
          const scaleY = templateSize.y / doorSize.y;
          const scaleZ = templateSize.z / doorSize.x;

          // Áp dụng scale
          door.scale.setY(scaleY);

          console.log(
            `Scaled door ${door.userData.name} to match template ${matchedTemplate.userData.name}`
          );
        } else {
          console.warn(
            `No matching template found for door: ${door.userData.cornerDoor}`
          );
        }
      });
    } else {
      const doorsToRemove = [];

      cornerModule.group.traverse(object => {
        if (
          object.userData.type === InsideType.DOOR ||
          object.userData.type === InsideType.BRACE
        ) {
          doorsToRemove.push(object);
        }
      });

      // Xóa sau khi traverse xong
      doorsToRemove.forEach(door => {
        this.removeInside(door, cornerModule);
      });
    }
  }

  removeAllBraces(cloneModule) {
    const bracesToRemove = [];
    const uuidsToRemove = [];

    cloneModule.group.traverse(object => {
      if (object.isMesh && object.userData.type === InsideType.BRACE) {
        bracesToRemove.push(object);
        uuidsToRemove.push(object.uuid);
      }
    });

    // Xóa tất cả braces khỏi scene
    bracesToRemove.forEach(brace => {
      if (brace.parent) {
        brace.parent.remove(brace);

        // Cleanup memory
        brace.geometry?.dispose();
        if (brace.material) {
          if (Array.isArray(brace.material)) {
            brace.material.forEach(mat => mat.dispose());
          } else {
            brace.material.dispose();
          }
        }
      }
    });

    // Xóa tất cả items khỏi listInside trong 1 lần
    cloneModule.listInside = cloneModule.listInside.filter(
      item => !uuidsToRemove.includes(item.gltfUuid)
    );
  }

  removeAllHandles(cloneModule) {
    const handlesToRemove = [];
    const uuidsToRemove = [];

    cloneModule.group.traverse(object => {
      if (
        object.userData.type === InsideType.DOOR &&
        object.userData.isInside === true
      ) {
        object.userData.handleUuid = null;
      }

      if (object.isMesh && object.userData.type === InsideType.HANDLE) {
        handlesToRemove.push(object);
        uuidsToRemove.push(object.uuid);
      }
    });

    // Xóa tất cả braces khỏi scene
    handlesToRemove.forEach(handle => {
      if (handle.parent) {
        handle.parent.remove(handle);

        // Cleanup memory
        handle.geometry?.dispose();
        if (handle.material) {
          if (Array.isArray(handle.material)) {
            handle.material.forEach(mat => mat.dispose());
          } else {
            handle.material.dispose();
          }
        }
      }
    });

    // Xóa tất cả items khỏi listInside trong 1 lần
    cloneModule.listInside = cloneModule.listInside.filter(
      item => !uuidsToRemove.includes(item.gltfUuid)
    );
  }

  // CUA KINH
  createGlassDoorFrameGeometric(
    width,
    height,
    frameThickness = 0.06,
    depth = 0.02
  ) {
    const innerWidth = width - frameThickness * 2;
    const innerHeight = height - frameThickness * 2;

    // Tạo shape bên ngoài (outer rectangle)
    const outerShape = new THREE.Shape();
    outerShape.moveTo(-width / 2, 0);
    outerShape.lineTo(width / 2, 0);
    outerShape.lineTo(width / 2, height);
    outerShape.lineTo(-width / 2, height);
    outerShape.lineTo(-width / 2, 0);

    // Tạo hole bên trong (inner rectangle)
    const innerHole = new THREE.Path();
    innerHole.moveTo(-innerWidth / 2, frameThickness);
    innerHole.lineTo(innerWidth / 2, frameThickness);
    innerHole.lineTo(innerWidth / 2, frameThickness + innerHeight);
    innerHole.lineTo(-innerWidth / 2, frameThickness + innerHeight);
    innerHole.lineTo(-innerWidth / 2, frameThickness);

    outerShape.holes.push(innerHole);

    const extrudeSettings = {
      steps: 1,
      depth: depth,
      bevelEnabled: false,
    };

    const frameGeometry = new THREE.ExtrudeGeometry(
      outerShape,
      extrudeSettings
    );
    frameGeometry.rotateY(Math.PI / 2);
    frameGeometry.translate(0, 0, -width / 2);

    return frameGeometry;
  }

  createGlassDoorFrame(
    clonedModule,
    door,
    doorDirection,
    horizontalType,
    doorConfig = {}
  ) {
    door.updateMatrixWorld(true);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      door.scale.y = door.scale.y / 2;
    }
    //CUA NGANG

    let doorSize;
    const size = this.getSize(door);

    doorSize = {
      x: Math.max(size.x, size.z),
      y: size.y,
      z: Math.min(size.x, size.z),
    };

    if (!doorSize) return;

    const config = {
      frameThickness: 0.06,
      frameDepth: 0.017,
      glassOpacity: 0.3,
      glassColor: 0x8e9393,
      ...doorConfig,
    };

    // Tạo geometry cho khung
    const frameGeometry = this.createGlassDoorFrameGeometric(
      doorSize.x,
      doorSize.y,
      config.frameThickness,
      config.frameDepth
    );

    // Material cho khung
    const frameMaterial =
      door.material ||
      (door.children && door.children[0]?.material) ||
      new THREE.MeshStandardMaterial({ color: 0xffffff });

    const glassDoor = new THREE.Group();

    // Tạo mesh cửa kính
    const frameMesh = new THREE.Mesh(frameGeometry, frameMaterial.clone());
    frameMesh.name = 'DOOR-GLASS-FRAME';

    // Tính toán kích thước tấm kính
    const innerWidth = doorSize.x - config.frameThickness * 2;
    const innerHeight = doorSize.y - config.frameThickness * 2;

    // Tạo tấm kính
    const glassGeometry = new THREE.BoxGeometry(0.005, innerHeight, innerWidth);
    const glassMaterial = new THREE.MeshStandardMaterial({
      color: config.glassColor,
      transparent: true,
      opacity: config.glassOpacity,
      metalness: 0,
      roughness: 0.05,
      side: THREE.DoubleSide,
      depthWrite: false, // tránh z-fighting với transparent
    });

    const glassMesh = new THREE.Mesh(glassGeometry, glassMaterial);
    glassMesh.name = 'GLASS-PANEL';

    glassMesh.userData.notNesting = true; //không nesting chi tiết này
    glassMesh.position.set(
      config.frameDepth / 2,
      doorSize.y / 2,
      -doorSize.x / 2
    );

    // Add kính vào khung
    glassDoor.add(frameMesh);
    glassDoor.add(glassMesh);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      const frameMeshClone = frameMesh.clone();
      const glassMeshClone = glassMesh.clone();

      frameMeshClone.position.y += doorSize.y;
      glassMeshClone.position.y += doorSize.y;

      glassDoor.add(frameMeshClone);
      glassDoor.add(glassMeshClone);
    }
    //CUA NGANG

    // Áp dụng transform từ model gốc
    glassDoor.position.copy(
      clonedModule.group.worldToLocal(
        door.getWorldPosition(new THREE.Vector3()).clone()
      )
    );
    glassDoor.rotation.copy(door.rotation.clone());

    glassDoor.name = 'DOOR';
    glassDoor.parent = door.parent;
    glassDoor.visible = door.visible;
    glassDoor.userData = {
      ...door.userData,
    };

    this.setInfoModelChild(glassDoor);

    return glassDoor; // Trả về 1 mesh, kính là child của nó
  }
  // CUA KINH

  //CUA VOM
  /**
   * Tính các thông số hình học của cung vòm (arc) đi qua:
   *  - 2 điểm chân vòm (springline) ở hai bên: (±halfW, topY)
   *  - 1 điểm đỉnh vòm ở giữa: (0, apexY = height)
   *
   * @param {number} width  chiều rộng vùng vòm (thường là innerWidth)
   * @param {number} height chiều cao vùng vòm (thường là innerHeight)
   * @param {number} archRatio tỉ lệ độ "cao" của vòm so với chiều rộng
   *                            (0.2 = vòm thoải/dẹt, 0.5 = vòm tròn/cao)
   */
  createArchShapePoints(width, height, archRatio = 0.35) {
    const halfW = width / 2;
    const archHeight = width * archRatio; // độ cao phần cong, tính từ chân vòm lên đỉnh
    const topY = height - archHeight; // y tại chân vòm (springline)
    const apexY = height; // y tại đỉnh vòm

    // Tâm cung tròn nằm trên trục giữa (x = 0), tính từ 2 điểm đã biết:
    // (halfW, topY) và (0, apexY)
    const cy =
      (apexY * apexY - halfW * halfW - topY * topY) / (2 * (apexY - topY));
    const radius = apexY - cy;

    const angleRight = Math.atan2(topY - cy, halfW); // góc tại chân vòm bên phải
    const angleLeft = Math.PI - angleRight; // góc tại chân vòm bên trái (đối xứng)

    return { halfW, topY, apexY, cy, radius, angleRight, angleLeft };
  }

  /**
   * Vẽ 1 THREE.Path/Shape hình "chữ nhật có đỉnh vòm" (2 cạnh bên + đáy thẳng, đỉnh cong)
   * dùng chung cho cả lỗ khoét (hole) và tấm kính (panel).
   *
   * @param {THREE.Path|THREE.Shape} pathOrShape - đối tượng path để vẽ lên (moveTo/lineTo/absarc)
   * @param {number} halfW nửa chiều rộng
   * @param {number} baseY  y tại đáy (chân cửa)
   * @param {object} arch  kết quả từ createArchShapePoints
   */
  drawArchOutline(pathOrShape, halfW, baseY, arch) {
    const { topY, cy, radius, angleRight, angleLeft } = arch;

    pathOrShape.moveTo(-halfW, baseY);
    pathOrShape.lineTo(halfW, baseY);
    pathOrShape.lineTo(halfW, baseY + topY); // cạnh phải, thẳng lên tới chân vòm
    pathOrShape.absarc(0, baseY + cy, radius, angleRight, angleLeft, false); // cung vòm
    pathOrShape.lineTo(-halfW, baseY); // cạnh trái xuống đáy, đóng path
  }

  /**
   * Tạo geometry khung cửa vòm: khung ngoài chữ nhật, khoét lỗ hình vòm bên trong.
   */
  createArchDoorFrameGeometric(
    width,
    height,
    frameThickness = 0.06,
    depth = 0.02,
    archRatio = 0.35
  ) {
    const innerWidth = width - frameThickness * 2;
    const innerHeight = height - frameThickness * 2;

    // === Khung ngoài: hình chữ nhật (giữ nguyên như code gốc) ===
    const outerShape = new THREE.Shape();
    outerShape.moveTo(-width / 2, 0);
    outerShape.lineTo(width / 2, 0);
    outerShape.lineTo(width / 2, height);
    outerShape.lineTo(-width / 2, height);
    outerShape.lineTo(-width / 2, 0);

    // === Lỗ khoét bên trong: hình vòm ===
    const arch = this.createArchShapePoints(innerWidth, innerHeight, archRatio);
    const innerHole = new THREE.Path();
    this.drawArchOutline(innerHole, innerWidth / 2, frameThickness, arch);
    outerShape.holes.push(innerHole);

    const extrudeSettings = { steps: 1, depth, bevelEnabled: false };
    const frameGeometry = new THREE.ExtrudeGeometry(
      outerShape,
      extrudeSettings
    );
    frameGeometry.rotateY(Math.PI / 2);
    frameGeometry.translate(0, 0, -width / 2);

    return { frameGeometry, arch, innerWidth, innerHeight };
  }

  /**
   * Tạo geometry tấm kính hình vòm (thay cho BoxGeometry hình chữ nhật của code gốc).
   *
   * QUAN TRỌNG: phải dùng CHUNG hệ tọa độ với createArchDoorFrameGeometric
   * (cùng `width` toàn phần khi translate, cùng `baseY = frameThickness`)
   * thì kính mới khớp chính xác với lỗ khoét trên khung, không bị lệch.
   */
  createArchGlassPanelGeometry(width, frameThickness, glassThickness, arch) {
    const innerWidth = width - frameThickness * 2;

    const glassShape = new THREE.Shape();
    // baseY = frameThickness -> giống hệt baseY dùng khi vẽ innerHole của khung
    this.drawArchOutline(glassShape, innerWidth / 2, frameThickness, arch);

    const glassGeometry = new THREE.ExtrudeGeometry(glassShape, {
      steps: 1,
      depth: glassThickness,
      bevelEnabled: false,
    });
    // Xoay + dịch giống HỆT như frameGeometry (dùng width toàn phần, không dùng innerWidth)
    glassGeometry.rotateY(Math.PI / 2);
    glassGeometry.translate(0, 0, -width / 2);

    return glassGeometry;
  }

  createArchDoorFrame(
    clonedModule,
    door,
    doorDirection,
    horizontalType,
    doorConfig = {}
  ) {
    door.updateMatrixWorld(true);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      door.scale.y = door.scale.y / 2;
    }
    //CUA NGANG

    const size = this.getSize(door);
    const doorSize = {
      x: Math.max(size.x, size.z),
      y: size.y,
      z: Math.min(size.x, size.z),
    };
    if (!doorSize) return;

    const config = {
      frameThickness: 0.06,
      frameDepth: 0.017,
      archRatio: 0.35, // độ cong của vòm, chỉnh theo ý muốn
      glassOpacity: 0.3,
      glassColor: 0x8e9393,
      glassThickness: 0.005,
      ...doorConfig,
    };

    // === Khung ngoài + lỗ vòm ===
    const { frameGeometry, arch } = this.createArchDoorFrameGeometric(
      doorSize.x,
      doorSize.y,
      config.frameThickness,
      config.frameDepth,
      config.archRatio
    );

    const frameMaterial =
      door.material ||
      (door.children && door.children[0]?.material) ||
      new THREE.MeshStandardMaterial({ color: 0xffffff });

    const archDoor = new THREE.Group();

    // === Khung ngoài ===
    const frameMesh = new THREE.Mesh(frameGeometry, frameMaterial.clone());
    frameMesh.name = 'DOOR-ARCH-FRAME';
    archDoor.add(frameMesh);

    // === Tấm kính hình vòm ===
    const glassGeometry = this.createArchGlassPanelGeometry(
      doorSize.x,
      config.frameThickness,
      config.glassThickness,
      arch
    );

    const glassMaterial = new THREE.MeshStandardMaterial({
      color: config.glassColor,
      transparent: true,
      opacity: config.glassOpacity,
      metalness: 0,
      roughness: 0.05,
      side: THREE.DoubleSide,
      depthWrite: false, // tránh z-fighting với transparent
    });

    const glassMesh = new THREE.Mesh(glassGeometry, glassMaterial);
    glassMesh.name = 'GLASS-PANEL';
    glassMesh.userData.notNesting = true; // không nesting chi tiết này

    // glassGeometry đã tự extrude + rotateY + translate giống hệt frameGeometry
    // nên không cần set thêm position/rotation cho glassMesh
    archDoor.add(glassMesh);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      const frameMeshClone = frameMesh.clone();
      const glassMeshClone = glassMesh.clone();

      frameMeshClone.position.y += doorSize.y;
      glassMeshClone.position.y += doorSize.y;

      archDoor.add(frameMeshClone);
      archDoor.add(glassMeshClone);
    }
    //CUA NGANG

    archDoor.position.copy(
      clonedModule.group.worldToLocal(
        door.getWorldPosition(new THREE.Vector3()).clone()
      )
    );
    archDoor.rotation.copy(door.rotation.clone());

    archDoor.name = 'DOOR';
    archDoor.parent = door.parent;
    archDoor.visible = door.visible;
    archDoor.userData = { ...door.userData };

    this.setInfoModelChild(archDoor);

    return archDoor;
  }

  createArchDoorFrameNotGlass(
    clonedModule,
    door,
    doorDirection,
    horizontalType,
    doorConfig = {}
  ) {
    door.updateMatrixWorld(true);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      door.scale.y = door.scale.y / 2;
    }
    //CUA NGANG

    const size = this.getSize(door);
    const doorSize = {
      x: Math.max(size.x, size.z),
      y: size.y,
      z: Math.min(size.x, size.z),
    };
    if (!doorSize) return;

    const config = {
      frameThickness: 0.06,
      frameDepth: 0.017,
      archRatio: 0.35, // độ cong của vòm, chỉnh theo ý muốn
      ...doorConfig,
    };

    // === Khung ngoài + lỗ vòm ===
    const { frameGeometry } = this.createArchDoorFrameGeometric(
      doorSize.x,
      doorSize.y,
      config.frameThickness,
      config.frameDepth,
      config.archRatio
    );

    const frameMaterial =
      door.material ||
      (door.children && door.children[0]?.material) ||
      new THREE.MeshStandardMaterial({ color: 0xffffff });

    const archDoor = new THREE.Group();

    // === Chỉ khung ngoài, không có kính ===
    const frameMesh = new THREE.Mesh(frameGeometry, frameMaterial.clone());
    frameMesh.name = 'DOOR-ARCH-FRAME';
    archDoor.add(frameMesh);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      const frameMeshClone = frameMesh.clone();
      frameMeshClone.position.y += doorSize.y;
      archDoor.add(frameMeshClone);
    }
    //CUA NGANG

    archDoor.position.copy(
      clonedModule.group.worldToLocal(
        door.getWorldPosition(new THREE.Vector3()).clone()
      )
    );
    archDoor.rotation.copy(door.rotation.clone());

    archDoor.name = 'DOOR';
    archDoor.parent = door.parent;
    archDoor.visible = door.visible;
    archDoor.userData = { ...door.userData };

    this.setInfoModelChild(archDoor);

    return archDoor;
  }
  //CUA VOM

  //CUA ACRYLIC
  createAcrylicDoorFrameGeometric(width, height, depth) {
    // === PHẦN 1: BASE ĐẶC (kéo dài depth - grooveDepth) ===
    const baseShape = new THREE.Shape();
    baseShape.moveTo(-width / 2, 0);
    baseShape.lineTo(width / 2, 0);
    baseShape.lineTo(width / 2, height);
    baseShape.lineTo(-width / 2, height);
    baseShape.lineTo(-width / 2, 0);

    const baseGeometry = new THREE.ExtrudeGeometry(baseShape, {
      steps: 1,
      depth: depth,
      bevelEnabled: false,
    });

    // Rotate và translate tất cả
    baseGeometry.rotateY(Math.PI / 2);
    baseGeometry.translate(0, 0, -width / 2);

    return baseGeometry;
  }

  createAcrylicDoorFrame(
    clonedModule,
    door,
    doorDirection,
    horizontalType,
    doorConfig = {}
  ) {
    door.updateMatrixWorld(true);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      door.scale.y = door.scale.y / 2;
    }
    //CUA NGANG

    let doorSize;
    const size = this.getSize(door);

    doorSize = {
      x: Math.max(size.x, size.z),
      y: size.y,
      z: Math.min(size.x, size.z),
    };

    if (!doorSize) return;

    const baseGeometry = this.createAcrylicDoorFrameGeometric(
      doorSize.x, //w
      doorSize.y, //h
      doorSize.z //d
    );

    const frameMaterial =
      door.material ||
      (door.children && door.children[0]?.material) ||
      new THREE.MeshStandardMaterial({ color: 0xffffff });

    //Acrylic material
    const makeAcrylic = mat => {
      const m = mat.clone();
      // m.metalness = 0; // 0 = không kim loại, 1 = kim loại hoàn toàn
      m.roughness = 0.3; // 0 = bóng gương, 1 = mờ hoàn toàn
      return m;
    };
    //Acrylic material

    // Tạo group
    const acrylicDoor = new THREE.Group();

    const baseMesh = new THREE.Mesh(baseGeometry, makeAcrylic(frameMaterial));
    baseMesh.name = 'DOOR-ACRYLIC';
    acrylicDoor.add(baseMesh);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      const baseMeshClone = baseMesh.clone();

      baseMeshClone.position.y += doorSize.y;

      panoDoor.add(baseMeshClone);
    }
    //CUA NGANG

    acrylicDoor.position.copy(
      clonedModule.group.worldToLocal(
        door.getWorldPosition(new THREE.Vector3()).clone()
      )
    );
    acrylicDoor.rotation.copy(door.rotation.clone());

    acrylicDoor.name = 'DOOR';
    acrylicDoor.parent = door.parent;
    acrylicDoor.visible = door.visible;
    acrylicDoor.userData = {
      ...door.userData,
    };

    this.setInfoModelChild(acrylicDoor);

    return acrylicDoor;
  }
  //CUA ACRYLIC

  //CUA TRON
  createNormalDoorFrameGeometric(width, height, depth) {
    // === PHẦN 1: BASE ĐẶC (kéo dài depth - grooveDepth) ===
    const baseShape = new THREE.Shape();
    baseShape.moveTo(-width / 2, 0);
    baseShape.lineTo(width / 2, 0);
    baseShape.lineTo(width / 2, height);
    baseShape.lineTo(-width / 2, height);
    baseShape.lineTo(-width / 2, 0);

    const baseGeometry = new THREE.ExtrudeGeometry(baseShape, {
      steps: 1,
      depth: depth,
      bevelEnabled: false,
    });

    // Rotate và translate tất cả
    baseGeometry.rotateY(Math.PI / 2);
    baseGeometry.translate(0, 0, -width / 2);

    return baseGeometry;
  }

  createNormalDoorFrame(
    clonedModule,
    door,
    doorDirection,
    horizontalType,
    doorConfig = {}
  ) {
    door.updateMatrixWorld(true);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      door.scale.y = door.scale.y / 2;
    }
    //CUA NGANG

    let doorSize;
    const size = this.getSize(door);

    doorSize = {
      x: Math.max(size.x, size.z),
      y: size.y,
      z: Math.min(size.x, size.z),
    };

    if (!doorSize) return;

    const baseGeometry = this.createNormalDoorFrameGeometric(
      doorSize.x, //w
      doorSize.y, //h
      doorSize.z //d
    );

    const frameMaterial =
      door.material ||
      (door.children && door.children[0]?.material) ||
      new THREE.MeshStandardMaterial({ color: 0xffffff });

    // //Acrylic material
    // const makeAcrylic = mat => {
    //   const m = mat.clone();
    //   // m.metalness = 0; // 0 = không kim loại, 1 = kim loại hoàn toàn
    //   m.roughness = 0.3; // 0 = bóng gương, 1 = mờ hoàn toàn
    //   return m;
    // };
    // //Acrylic material

    // Tạo group
    const normalDoor = new THREE.Group();

    // const baseMesh = new THREE.Mesh(baseGeometry, makeAcrylic(frameMaterial));
    const baseMesh = new THREE.Mesh(baseGeometry, frameMaterial);
    baseMesh.name = 'DOOR-NORMAL';
    normalDoor.add(baseMesh);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      const baseMeshClone = baseMesh.clone();

      baseMeshClone.position.y += doorSize.y;

      normalDoor.add(baseMeshClone);
    }
    //CUA NGANG

    normalDoor.position.copy(
      clonedModule.group.worldToLocal(
        door.getWorldPosition(new THREE.Vector3()).clone()
      )
    );
    normalDoor.rotation.copy(door.rotation.clone());

    normalDoor.name = 'DOOR';
    normalDoor.parent = door.parent;
    normalDoor.visible = door.visible;
    normalDoor.userData = {
      ...door.userData,
    };

    this.setInfoModelChild(normalDoor);

    return normalDoor;
  }
  //CUA TRON

  //CUA PANO
  createPanoDoorFrameGeometric(
    width,
    height,
    frameThickness,
    depth,
    grooveDepth,
    grooveWidth
  ) {
    const innerWidth = width - frameThickness * 2;
    const innerHeight = height - frameThickness * 2;

    const topY = height - frameThickness;
    const bottomY = frameThickness;
    const leftX = -innerWidth / 2;
    const rightX = innerWidth / 2;

    // === PHẦN 1: BASE ĐẶC (kéo dài depth - grooveDepth) ===
    const baseShape = new THREE.Shape();
    baseShape.moveTo(-width / 2, 0);
    baseShape.lineTo(width / 2, 0);
    baseShape.lineTo(width / 2, height);
    baseShape.lineTo(-width / 2, height);
    baseShape.lineTo(-width / 2, 0);

    const baseGeometry = new THREE.ExtrudeGeometry(baseShape, {
      steps: 1,
      depth: depth - grooveDepth,
      bevelEnabled: false,
    });

    // === PHẦN 2: Khung ngoài có rãnh (chỉ kéo dài grooveDepth ở mặt sau) ===
    const mainShape = new THREE.Shape();
    mainShape.moveTo(-width / 2, 0);
    mainShape.lineTo(width / 2, 0);
    mainShape.lineTo(width / 2, height);
    mainShape.lineTo(-width / 2, height);
    mainShape.lineTo(-width / 2, 0);

    // Đục bỏ phần viền xanh ngoài
    const outerHole = new THREE.Path();
    outerHole.moveTo(leftX, bottomY);
    outerHole.lineTo(rightX, bottomY);
    outerHole.lineTo(rightX, topY);
    outerHole.lineTo(leftX, topY);
    outerHole.lineTo(leftX, bottomY);
    mainShape.holes.push(outerHole);

    const mainGeometry = new THREE.ExtrudeGeometry(mainShape, {
      steps: 1,
      depth: depth,
      bevelEnabled: false,
    });

    // === PHẦN 3: Phần bên trong
    const innerShape = new THREE.Shape();
    innerShape.moveTo(leftX + grooveWidth, bottomY + grooveWidth);
    innerShape.lineTo(rightX - grooveWidth, bottomY + grooveWidth);
    innerShape.lineTo(rightX - grooveWidth, topY - grooveWidth);
    innerShape.lineTo(leftX + grooveWidth, topY - grooveWidth);
    innerShape.lineTo(leftX + grooveWidth, bottomY + grooveWidth);

    const innerGeometry = new THREE.ExtrudeGeometry(innerShape, {
      steps: 1,
      depth: depth,
      bevelEnabled: false,
    });

    // Rotate và translate tất cả
    baseGeometry.rotateY(Math.PI / 2);
    baseGeometry.translate(0, 0, -width / 2);

    mainGeometry.rotateY(Math.PI / 2);
    mainGeometry.translate(0, 0, -width / 2);

    innerGeometry.rotateY(Math.PI / 2);
    innerGeometry.translate(0, 0, -width / 2);

    return {
      baseGeometry,
      mainGeometry,
      innerGeometry,
    };
  }

  createPanoDoorFrame(
    clonedModule,
    door,
    doorDirection,
    horizontalType,
    doorConfig = {}
  ) {
    door.updateMatrixWorld(true);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      door.scale.y = door.scale.y / 2;
    }
    //CUA NGANG

    let doorSize;
    const size = this.getSize(door);

    doorSize = {
      x: Math.max(size.x, size.z),
      y: size.y,
      z: Math.min(size.x, size.z),
    };

    if (!doorSize) return;

    const config = {
      frameThickness: 0.06,
      frameDepth: 0.017,
      grooveDepth: 0.006,
      grooveWidth: 0.02,
      ...doorConfig,
    };

    const { baseGeometry, mainGeometry, innerGeometry } =
      this.createPanoDoorFrameGeometric(
        doorSize.x,
        doorSize.y,
        config.frameThickness,
        config.frameDepth,
        config.grooveDepth,
        config.grooveWidth
      );

    const frameMaterial =
      door.material ||
      (door.children && door.children[0]?.material) ||
      new THREE.MeshStandardMaterial({ color: 0xffffff });

    // Tạo group
    const panoDoor = new THREE.Group();

    // Thêm base đặc
    const baseMesh = new THREE.Mesh(baseGeometry, frameMaterial);
    baseMesh.name = 'DOOR-PANO-BASE';
    baseMesh.userData.notNesting = true; //không nesting chi tiết này
    baseMesh.userData.requireBorder = false;
    panoDoor.add(baseMesh);

    // Thêm phần khung ngoài có rãnh
    const outerMesh = new THREE.Mesh(mainGeometry, frameMaterial);
    outerMesh.name = 'DOOR-PANO-OUTER';
    panoDoor.add(outerMesh);

    // Thêm phần khung trong
    const innerMesh = new THREE.Mesh(innerGeometry, frameMaterial);
    innerMesh.name = 'DOOR-PANO-INNER';
    innerMesh.userData.notNesting = true; //không nesting chi tiết này
    panoDoor.add(innerMesh);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      const baseMeshClone = baseMesh.clone();
      const outerMeshClone = outerMesh.clone();
      const innerMeshClone = innerMesh.clone();

      baseMeshClone.position.y += doorSize.y;
      outerMeshClone.position.y += doorSize.y;
      innerMeshClone.position.y += doorSize.y;

      panoDoor.add(baseMeshClone);
      panoDoor.add(outerMeshClone);
      panoDoor.add(innerMeshClone);
    }
    //CUA NGANG

    panoDoor.position.copy(
      clonedModule.group.worldToLocal(
        door.getWorldPosition(new THREE.Vector3()).clone()
      )
    );
    panoDoor.rotation.copy(door.rotation.clone());

    panoDoor.name = 'DOOR';
    panoDoor.parent = door.parent;
    panoDoor.visible = door.visible;
    panoDoor.userData = {
      ...door.userData,
    };

    this.setInfoModelChild(panoDoor);

    return panoDoor;
  }
  //CUA PANO

  // CUA NHOM KINH
  createAluminumDoorFrameGeometric(
    width,
    height,
    frameThickness = 0.06,
    depth = 0.02
  ) {
    const innerWidth = width - frameThickness * 2;
    const innerHeight = height - frameThickness * 2;

    // Tạo shape bên ngoài (outer rectangle)
    const outerShape = new THREE.Shape();
    outerShape.moveTo(-width / 2, 0);
    outerShape.lineTo(width / 2, 0);
    outerShape.lineTo(width / 2, height);
    outerShape.lineTo(-width / 2, height);
    outerShape.lineTo(-width / 2, 0);

    // Tạo hole bên trong (inner rectangle)
    const innerHole = new THREE.Path();
    innerHole.moveTo(-innerWidth / 2, frameThickness);
    innerHole.lineTo(innerWidth / 2, frameThickness);
    innerHole.lineTo(innerWidth / 2, frameThickness + innerHeight);
    innerHole.lineTo(-innerWidth / 2, frameThickness + innerHeight);
    innerHole.lineTo(-innerWidth / 2, frameThickness);

    outerShape.holes.push(innerHole);

    const extrudeSettings = {
      steps: 1,
      depth: depth,
      bevelEnabled: false,
    };

    const frameGeometry = new THREE.ExtrudeGeometry(
      outerShape,
      extrudeSettings
    );
    frameGeometry.rotateY(Math.PI / 2);
    frameGeometry.translate(0, 0, -width / 2);

    return frameGeometry;
  }

  createAluminumGlassDoorFrame(
    clonedModule,
    door,
    doorDirection,
    horizontalType,
    doorConfig = {}
  ) {
    door.updateMatrixWorld(true);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      door.scale.y = door.scale.y / 2;
    }
    //CUA NGANG

    let doorSize;
    const size = this.getSize(door);

    doorSize = {
      x: Math.max(size.x, size.z),
      y: size.y,
      z: Math.min(size.x, size.z),
    };

    if (!doorSize) return;

    const config = {
      frameThickness: 0.02,
      frameDepth: aluminumFrameDepth,
      glassOpacity: 0.3,
      glassColor: 0x8e9393,
      ...doorConfig,
    };

    // Tạo geometry cho khung
    const frameGeometry = this.createAluminumDoorFrameGeometric(
      doorSize.x,
      doorSize.y,
      config.frameThickness,
      config.frameDepth
    );

    // Material cho khung
    const frameMaterial =
      door.material ||
      (door.children && door.children[0]?.material) ||
      new THREE.MeshStandardMaterial({ color: 0xffffff });

    const glassDoor = new THREE.Group();

    // Tạo mesh cửa kính
    const frameMesh = new THREE.Mesh(frameGeometry, frameMaterial.clone());
    frameMesh.name = 'DOOR-GLASS-FRAME';

    // Tính toán kích thước tấm kính
    const innerWidth = doorSize.x - config.frameThickness * 2;
    const innerHeight = doorSize.y - config.frameThickness * 2;

    // Tạo tấm kính
    const glassGeometry = new THREE.BoxGeometry(0.005, innerHeight, innerWidth);
    const glassMaterial = new THREE.MeshStandardMaterial({
      color: config.glassColor,
      transparent: true,
      opacity: config.glassOpacity,
      metalness: 0,
      roughness: 0.05,
      side: THREE.DoubleSide,
      depthWrite: false, // tránh z-fighting với transparent
    });

    const glassMesh = new THREE.Mesh(glassGeometry, glassMaterial);
    glassMesh.name = 'GLASS-PANEL';
    glassMesh.userData.notNesting = true; //không nesting chi tiết này
    glassMesh.position.set(
      config.frameDepth / 2,
      doorSize.y / 2,
      -doorSize.x / 2
    );

    //Tay nắm nhôm kính
    let handleAlumiumGeometry, handleAlumiumMesh;

    if (!door.userData.handlePosition) {
      return;
    }

    if (door.userData?.drawerUuid) {
      handleAlumiumGeometry = new THREE.BoxGeometry(
        aluminumHandleSize,
        doorSize.x,
        aluminumHandleSize
      );
      handleAlumiumMesh = new THREE.Mesh(handleAlumiumGeometry, frameMaterial);

      handleAlumiumMesh.rotation.x = -Math.PI / 2;
      handleAlumiumMesh.position.setZ(-doorSize.x / 2);
      handleAlumiumMesh.position.setY(doorSize.y - aluminumHandleSize / 2);
    } else if (doorDirection === DoorDirection.HORIZONTAL) {
      handleAlumiumGeometry = new THREE.BoxGeometry(
        aluminumHandleSize,
        doorSize.x,
        aluminumHandleSize
      );
      handleAlumiumMesh = new THREE.Mesh(handleAlumiumGeometry, frameMaterial);

      handleAlumiumMesh.rotation.x = -Math.PI / 2;
      handleAlumiumMesh.position.setZ(-doorSize.x / 2);
      handleAlumiumMesh.position.setY(aluminumHandleSize / 2);
    } else {
      handleAlumiumGeometry = new THREE.BoxGeometry(
        aluminumHandleSize,
        doorSize.y,
        aluminumHandleSize
      );
      handleAlumiumMesh = new THREE.Mesh(handleAlumiumGeometry, frameMaterial);

      handleAlumiumMesh.position.setY(doorSize.y / 2);

      if (
        door.userData.handlePosition.left &&
        door.userData.handlePosition.current === 'left'
      ) {
        handleAlumiumMesh.position.z = -aluminumHandleSize / 2;
      } else if (
        door.userData.handlePosition.right &&
        door.userData.handlePosition.current === 'right'
      ) {
        handleAlumiumMesh.position.z = -doorSize.x + aluminumHandleSize / 2;
      }
    }

    handleAlumiumMesh.name = 'TAY-NAM-NHOM-KINH';
    handleAlumiumMesh.position.setX(
      aluminumFrameDepth + aluminumHandleSize / 2
    );
    //Tay nắm nhôm kính

    // Add kính vào khung
    glassDoor.add(frameMesh);
    glassDoor.add(glassMesh);
    glassDoor.add(handleAlumiumMesh);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      const frameMeshClone = frameMesh.clone();
      const glassMeshClone = glassMesh.clone();

      frameMeshClone.position.y += doorSize.y;
      glassMeshClone.position.y += doorSize.y;

      glassDoor.add(frameMeshClone);
      glassDoor.add(glassMeshClone);
    }
    //CUA NGANG

    // Áp dụng transform từ model gốc
    glassDoor.position.copy(
      clonedModule.group.worldToLocal(
        door.getWorldPosition(new THREE.Vector3()).clone()
      )
    );
    glassDoor.rotation.copy(door.rotation.clone());

    glassDoor.name = 'DOOR';
    glassDoor.parent = door.parent;
    glassDoor.visible = door.visible;
    glassDoor.userData = {
      ...door.userData,
    };

    this.setInfoModelChild(glassDoor);

    return glassDoor; // Trả về 1 mesh, kính là child của nó
  }
  // CUA NHÔM KINH

  //CUA THANH NGANG
  createHorizontalBarDoorGeometric(
    width,
    height,
    frameThickness,
    depth,
    grooveDepth,
    grooveWidth
  ) {
    const innerWidth = width - frameThickness * 2;
    const innerHeight = height - frameThickness * 2;

    const topY = height - frameThickness;
    const bottomY = frameThickness;
    const leftX = -innerWidth / 2;
    const rightX = innerWidth / 2;

    // === PHẦN 1: BASE ĐẶC (kéo dài depth - grooveDepth) ===
    const baseShape = new THREE.Shape();
    baseShape.moveTo(-width / 2, 0);
    baseShape.lineTo(width / 2, 0);
    baseShape.lineTo(width / 2, height);
    baseShape.lineTo(-width / 2, height);
    baseShape.lineTo(-width / 2, 0);

    const baseGeometry = new THREE.ExtrudeGeometry(baseShape, {
      steps: 1,
      depth: depth - grooveDepth,
      bevelEnabled: false,
    });

    // === PHẦN 2: Khung ngoài có rãnh (chỉ kéo dài grooveDepth ở mặt sau) ===
    const mainShape = new THREE.Shape();
    mainShape.moveTo(-width / 2, 0);
    mainShape.lineTo(width / 2, 0);
    mainShape.lineTo(width / 2, height);
    mainShape.lineTo(-width / 2, height);
    mainShape.lineTo(-width / 2, 0);

    // Đục bỏ phần viền xanh ngoài
    const outerHole = new THREE.Path();
    outerHole.moveTo(leftX, bottomY);
    outerHole.lineTo(rightX, bottomY);
    outerHole.lineTo(rightX, topY);
    outerHole.lineTo(leftX, topY);
    outerHole.lineTo(leftX, bottomY);
    mainShape.holes.push(outerHole);

    const mainGeometry = new THREE.ExtrudeGeometry(mainShape, {
      steps: 1,
      depth: depth,
      bevelEnabled: false,
    });

    // === PHẦN 3: Tạo các thanh ngang rời biệt ===
    const minBarHeight = 0.05; // Chiều cao tối thiểu mỗi thanh 50mm
    const barSpacing = 0.006; // Khoảng cách giữa các thanh và 2 cạnh bìa 60mm

    // Tính toán vùng có thể đặt thanh
    const availableHeight = innerHeight - grooveWidth * 2;

    // Tính số lượng thanh tối đa (với chiều cao tối thiểu)
    const maxBars = Math.floor(
      (availableHeight + barSpacing) / (minBarHeight + barSpacing)
    );

    // Tính chiều cao thực tế của mỗi thanh để phân bố đều
    const actualBarHeight =
      (availableHeight - (maxBars - 1) * barSpacing) / maxBars;

    // Tạo từng thanh ngang riêng biệt
    const barGeometries = [];

    for (let i = 0; i < maxBars; i++) {
      const yBottom =
        bottomY + grooveWidth + i * (actualBarHeight + barSpacing);
      const yTop = yBottom + actualBarHeight;

      // Tạo shape cho từng thanh
      const barShape = new THREE.Shape();
      barShape.moveTo(leftX + grooveWidth, yBottom);
      barShape.lineTo(rightX - grooveWidth, yBottom);
      barShape.lineTo(rightX - grooveWidth, yTop);
      barShape.lineTo(leftX + grooveWidth, yTop);
      barShape.lineTo(leftX + grooveWidth, yBottom);

      const barGeometry = new THREE.ExtrudeGeometry(barShape, {
        steps: 1,
        depth: depth,
        bevelEnabled: false,
      });

      barGeometries.push(barGeometry);
    }

    // Rotate và translate tất cả
    baseGeometry.rotateY(Math.PI / 2);
    baseGeometry.translate(0, 0, -width / 2);

    mainGeometry.rotateY(Math.PI / 2);
    mainGeometry.translate(0, 0, -width / 2);

    barGeometries.forEach(geometry => {
      geometry.rotateY(Math.PI / 2);
      geometry.translate(0, 0, -width / 2);
    });

    return {
      baseGeometry,
      mainGeometry,
      barGeometries, // Trả về mảng các thanh riêng biệt
    };
  }

  createHorizontalBarDoorFrame(
    clonedModule,
    door,
    doorDirection,
    horizontalType,
    doorConfig = {}
  ) {
    door.updateMatrixWorld(true);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      door.scale.y = door.scale.y / 2;
    }
    //CUA NGANG

    let doorSize;
    const size = this.getSize(door);

    doorSize = {
      x: Math.max(size.x, size.z),
      y: size.y,
      z: Math.min(size.x, size.z),
    };

    if (!doorSize) return;

    const config = {
      frameThickness: 0.06,
      frameDepth: 0.017,
      grooveDepth: 0.006,
      grooveWidth: 0.006,
      ...doorConfig,
    };

    const { baseGeometry, mainGeometry, barGeometries } =
      this.createHorizontalBarDoorGeometric(
        doorSize.x,
        doorSize.y,
        config.frameThickness,
        config.frameDepth,
        config.grooveDepth,
        config.grooveWidth
      );

    const frameMaterial =
      door.material ||
      (door.children && door.children[0]?.material) ||
      new THREE.MeshStandardMaterial({ color: 0x8b4513 });

    // Tạo group
    const doorMesh = new THREE.Group();

    // Thêm base đặc
    const baseMesh = new THREE.Mesh(baseGeometry, frameMaterial.clone());
    baseMesh.name = 'DOOR-HORIZONTAL-BAR-BASE';
    baseMesh.userData.notNesting = true; //không nesting chi tiết này
    baseMesh.userData.requireBorder = false;
    doorMesh.add(baseMesh);

    // Thêm phần khung ngoài
    const mainMesh = new THREE.Mesh(mainGeometry, frameMaterial.clone());
    mainMesh.name = 'DOOR-HORIZONTAL-BAR-FRAME';
    doorMesh.add(mainMesh);

    // Thêm từng thanh ngang riêng biệt
    const barGroup = new THREE.Group();

    barGeometries.forEach((geometry, index) => {
      const barMesh = new THREE.Mesh(geometry, frameMaterial.clone());
      barMesh.name = `DOOR-HORIZONTAL-BAR-${index}`;
      barMesh.userData.notNesting = true; //không nesting chi tiết này

      barGroup.add(barMesh);
    });
    doorMesh.add(barGroup);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      const baseMeshClone = baseMesh.clone();
      const mainMeshClone = mainMesh.clone();
      const barGroupClone = barGroup.clone();

      baseMeshClone.position.y += doorSize.y;
      mainMeshClone.position.y += doorSize.y;
      barGroupClone.position.y += doorSize.y;

      doorMesh.add(baseMeshClone);
      doorMesh.add(mainMeshClone);
      doorMesh.add(barGroupClone);
    }
    //CUA NGANG

    doorMesh.position.copy(
      clonedModule.group.worldToLocal(
        door.getWorldPosition(new THREE.Vector3()).clone()
      )
    );
    doorMesh.rotation.copy(door.rotation.clone());

    doorMesh.name = 'DOOR';
    doorMesh.parent = door.parent;
    doorMesh.visible = door.visible;
    doorMesh.userData = {
      ...door.userData,
    };

    this.setInfoModelChild(doorMesh);

    return doorMesh;
  }
  //CUA THANH NGANG

  //CUA THANH DOC
  createVerticalBarDoorGeometric(
    width,
    height,
    frameThickness,
    depth,
    grooveDepth,
    grooveWidth
  ) {
    const innerWidth = width - frameThickness * 2;
    const innerHeight = height - frameThickness * 2;

    const topY = height - frameThickness;
    const bottomY = frameThickness;
    const leftX = -innerWidth / 2;
    const rightX = innerWidth / 2;

    // === PHẦN 1: BASE ĐẶC (kéo dài depth - grooveDepth) ===
    const baseShape = new THREE.Shape();
    baseShape.moveTo(-width / 2, 0);
    baseShape.lineTo(width / 2, 0);
    baseShape.lineTo(width / 2, height);
    baseShape.lineTo(-width / 2, height);
    baseShape.lineTo(-width / 2, 0);

    const baseGeometry = new THREE.ExtrudeGeometry(baseShape, {
      steps: 1,
      depth: depth - grooveDepth,
      bevelEnabled: false,
    });

    // === PHẦN 2: Khung ngoài có rãnh (chỉ kéo dài grooveDepth ở mặt sau) ===
    const mainShape = new THREE.Shape();
    mainShape.moveTo(-width / 2, 0);
    mainShape.lineTo(width / 2, 0);
    mainShape.lineTo(width / 2, height);
    mainShape.lineTo(-width / 2, height);
    mainShape.lineTo(-width / 2, 0);

    // Đục bỏ phần viền xanh ngoài
    const outerHole = new THREE.Path();
    outerHole.moveTo(leftX, bottomY);
    outerHole.lineTo(rightX, bottomY);
    outerHole.lineTo(rightX, topY);
    outerHole.lineTo(leftX, topY);
    outerHole.lineTo(leftX, bottomY);
    mainShape.holes.push(outerHole);

    const mainGeometry = new THREE.ExtrudeGeometry(mainShape, {
      steps: 1,
      depth: depth,
      bevelEnabled: false,
    });

    // === PHẦN 3: Tạo các thanh ngang rời biệt ===
    const minBarWidth = 0.025; // Chiều rộng tối thiểu mỗi thanh 25mm
    const barSpacing = 0.006; // Khoảng cách giữa các thanh và 2 cạnh bìa 60mm

    // Tính toán vùng có thể đặt thanh
    const availableWidth = innerWidth - grooveWidth * 2;

    // Tính số lượng thanh tối đa (với chiều rộng tối thiểu)
    const maxBars = Math.floor(
      (availableWidth + barSpacing) / (minBarWidth + barSpacing)
    );

    // Tính chiều rộng thực tế của mỗi thanh để phân bố đều
    const actualBarWidth =
      (availableWidth - (maxBars - 1) * barSpacing) / maxBars;

    // Tạo từng thanh ngang riêng biệt
    const barGeometries = [];

    for (let i = 0; i < maxBars; i++) {
      const xLeft = leftX + grooveWidth + i * (actualBarWidth + barSpacing);
      const xRight = xLeft + actualBarWidth;

      // Tạo shape cho từng thanh
      const barShape = new THREE.Shape();
      barShape.moveTo(xLeft, bottomY + grooveWidth);
      barShape.lineTo(xRight, bottomY + grooveWidth);
      barShape.lineTo(xRight, topY - grooveWidth);
      barShape.lineTo(xLeft, topY - grooveWidth);
      barShape.lineTo(xLeft, bottomY + grooveWidth);
      const barGeometry = new THREE.ExtrudeGeometry(barShape, {
        steps: 1,
        depth: depth,
        bevelEnabled: false,
      });

      barGeometries.push(barGeometry);
    }

    // Rotate và translate tất cả
    baseGeometry.rotateY(Math.PI / 2);
    baseGeometry.translate(0, 0, -width / 2);

    mainGeometry.rotateY(Math.PI / 2);
    mainGeometry.translate(0, 0, -width / 2);

    barGeometries.forEach(geometry => {
      geometry.rotateY(Math.PI / 2);
      geometry.translate(0, 0, -width / 2);
    });

    return {
      baseGeometry,
      mainGeometry,
      barGeometries, // Trả về mảng các thanh riêng biệt
    };
  }

  createVerticalBarDoorFrame(
    clonedModule,
    door,
    doorDirection,
    horizontalType,
    doorConfig = {}
  ) {
    door.updateMatrixWorld(true);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      door.scale.y = door.scale.y / 2;
    }
    //CUA NGANG

    let doorSize;
    const size = this.getSize(door);

    doorSize = {
      x: Math.max(size.x, size.z),
      y: size.y,
      z: Math.min(size.x, size.z),
    };

    if (!doorSize) return;

    const config = {
      frameThickness: 0.06,
      frameDepth: 0.017,
      grooveDepth: 0.006,
      grooveWidth: 0.006,
      ...doorConfig,
    };

    const { baseGeometry, mainGeometry, barGeometries } =
      this.createVerticalBarDoorGeometric(
        doorSize.x,
        doorSize.y,
        config.frameThickness,
        config.frameDepth,
        config.grooveDepth,
        config.grooveWidth
      );

    const frameMaterial =
      door.material ||
      (door.children && door.children[0]?.material) ||
      new THREE.MeshStandardMaterial({ color: 0x8b4513 });

    // Tạo group
    const doorMesh = new THREE.Group();

    // Thêm base đặc
    const baseMesh = new THREE.Mesh(baseGeometry, frameMaterial.clone());
    baseMesh.name = 'DOOR-VERTICAL-BAR-BASE';
    baseMesh.userData.notNesting = true; //không nesting chi tiết này
    baseMesh.userData.requireBorder = false;
    doorMesh.add(baseMesh);

    // Thêm phần khung ngoài
    const mainMesh = new THREE.Mesh(mainGeometry, frameMaterial.clone());
    mainMesh.name = 'DOOR-VERTICAL-BAR-FRAME';
    doorMesh.add(mainMesh);

    // Thêm từng thanh ngang riêng biệt
    const barGroup = new THREE.Group();
    barGeometries.forEach((geometry, index) => {
      const barMesh = new THREE.Mesh(geometry, frameMaterial.clone());
      barMesh.name = `DOOR-VERTICAL-BAR-${index}`;
      barMesh.userData.notNesting = true; //không nesting chi tiết này
      barGroup.add(barMesh);
    });
    doorMesh.add(barGroup);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      const baseMeshClone = baseMesh.clone();
      const mainMeshClone = mainMesh.clone();
      const barGroupClone = barGroup.clone();

      baseMeshClone.position.y += doorSize.y;
      mainMeshClone.position.y += doorSize.y;
      barGroupClone.position.y += doorSize.y;

      doorMesh.add(baseMeshClone);
      doorMesh.add(mainMeshClone);
      doorMesh.add(barGroupClone);
    }
    //CUA NGANG

    doorMesh.position.copy(
      clonedModule.group.worldToLocal(
        door.getWorldPosition(new THREE.Vector3()).clone()
      )
    );
    doorMesh.rotation.copy(door.rotation.clone());

    doorMesh.name = 'DOOR';
    doorMesh.parent = door.parent;
    doorMesh.visible = door.visible;
    doorMesh.userData = {
      ...door.userData,
    };

    this.setInfoModelChild(doorMesh);

    return doorMesh;
  }
  //CUA THANH DOC

  //CUA DUC LO NGANG
  async createHorizontalHoleDoorGeometric(
    width,
    height,
    frameThickness,
    depth
  ) {
    // Tính toán kích thước và số lượng lỗ
    const holeWidth = width - 0.12; // width - 120mm
    const holeHeight = 0.022; // 22mm
    const spacing = 0.05; // 50mm khoảng cách giữa các lỗ
    const edgeMargin = 0.06; // 60mm từ bìa trên và dưới

    // Tính số lượng lỗ dựa vào chiều cao, tối thiểu 1 lỗ
    const availableHeight = height - edgeMargin * 2 - holeHeight;
    const numHoles = Math.max(
      1,
      Math.floor(availableHeight / (holeHeight + spacing)) + 1
    );

    // Tính lại spacing thực tế để phân bố đều
    const actualSpacing =
      numHoles > 1
        ? (height - edgeMargin * 2 - numHoles * holeHeight) / (numHoles - 1)
        : 0;

    // Tạo danh sách các lỗ
    const holeConfigs = [];
    for (let i = 0; i < numHoles; i++) {
      const centerY =
        numHoles === 1
          ? height / 2 // đặt lỗ giữa nếu chỉ có 1
          : edgeMargin + holeHeight / 2 + i * (holeHeight + actualSpacing);
      holeConfigs.push({
        width: holeWidth,
        height: holeHeight,
        centerY: centerY,
      });
    }

    // === Khung chính với lỗ đục bo cong ===
    const mainShape = new THREE.Shape();
    mainShape.moveTo(-width / 2, 0);
    mainShape.lineTo(width / 2, 0);
    mainShape.lineTo(width / 2, height);
    mainShape.lineTo(-width / 2, height);
    mainShape.lineTo(-width / 2, 0);

    // Đục các lỗ chữ nhật với 2 đầu bo cong 180 độ
    holeConfigs.forEach(hole => {
      const holePath = new THREE.Path();

      const radius = hole.height / 2; // Bán kính bo cong = 1/2 chiều cao lỗ
      const leftEdge = -hole.width / 2 + radius; // Cạnh trái (sau khi trừ bán kính)
      const rightEdge = hole.width / 2 - radius; // Cạnh phải (sau khi trừ bán kính)

      // Bắt đầu từ giữa cạnh dưới
      holePath.moveTo(leftEdge, hole.centerY - hole.height / 2);

      // Cạnh dưới (phần thẳng)
      holePath.lineTo(rightEdge, hole.centerY - hole.height / 2);

      // Bo cong phải (180 độ) - từ dưới lên trên
      holePath.absarc(
        rightEdge,
        hole.centerY,
        radius,
        -Math.PI / 2, // Góc bắt đầu (dưới)
        Math.PI / 2, // Góc kết thúc (trên)
        false
      );

      // Cạnh trên (phần thẳng)
      holePath.lineTo(leftEdge, hole.centerY + hole.height / 2);

      // Bo cong trái (180 độ) - từ trên xuống dưới
      holePath.absarc(
        leftEdge,
        hole.centerY,
        radius,
        Math.PI / 2, // Góc bắt đầu (trên)
        -Math.PI / 2, // Góc kết thúc (dưới)
        false
      );

      mainShape.holes.push(holePath);
    });

    const mainGeometry = new THREE.ExtrudeGeometry(mainShape, {
      steps: 1,
      depth: depth,
      bevelEnabled: false,
    });

    // Rotate và translate
    mainGeometry.rotateY(Math.PI / 2);
    mainGeometry.translate(0, 0, -width / 2);

    return {
      mainGeometry,
    };
  }

  async createHorizontalHoleDoorFrame(
    clonedModule,
    door,
    doorDirection,
    horizontalType,
    doorConfig = {}
  ) {
    door.updateMatrixWorld(true);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      door.scale.y = door.scale.y / 2;
    }
    //CUA NGANG

    let doorSize;
    const size = this.getSize(door);

    doorSize = {
      x: Math.max(size.x, size.z),
      y: size.y,
      z: Math.min(size.x, size.z),
    };

    if (!doorSize) return;

    const config = {
      frameThickness: 0.06,
      frameDepth: 0.017,
      ...doorConfig,
    };

    const { mainGeometry } = await this.createHorizontalHoleDoorGeometric(
      doorSize.x,
      doorSize.y,
      config.frameThickness,
      config.frameDepth
    );

    const frameMaterial =
      door.material ||
      (door.children && door.children[0]?.material) ||
      new THREE.MeshStandardMaterial({ color: 0x8b4513 });

    // Tạo group
    const horizontalHoleDoor = new THREE.Group();

    // Thêm khung chính
    const mainMesh = new THREE.Mesh(mainGeometry, frameMaterial.clone());
    mainMesh.name = 'DOOR-HORIZONTAL-HOLE-FRAME';
    horizontalHoleDoor.add(mainMesh);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      const mainMeshClone = mainMesh.clone();

      mainMeshClone.position.y += doorSize.y;

      horizontalHoleDoor.add(mainMeshClone);
    }
    //CUA NGANG

    horizontalHoleDoor.position.copy(
      clonedModule.group.worldToLocal(
        door.getWorldPosition(new THREE.Vector3()).clone()
      )
    );
    horizontalHoleDoor.rotation.copy(door.rotation.clone());

    horizontalHoleDoor.name = 'DOOR';
    horizontalHoleDoor.parent = door.parent;
    horizontalHoleDoor.visible = door.visible;
    horizontalHoleDoor.userData = {
      ...door.userData,
    };

    this.setInfoModelChild(horizontalHoleDoor);

    return horizontalHoleDoor;
  }
  //CUA DUC LO NGANG

  //CUA LA SACH
  createLouverDoorGeometric(
    width,
    height,
    frameThickness = 0.06,
    depth = 0.02
  ) {
    const innerWidth = width - frameThickness * 2;
    const innerHeight = height - frameThickness * 2;

    // Outer shape
    const outerShape = new THREE.Shape();
    outerShape.moveTo(-width / 2, 0);
    outerShape.lineTo(width / 2, 0);
    outerShape.lineTo(width / 2, height);
    outerShape.lineTo(-width / 2, height);
    outerShape.closePath();

    // Inner hole
    const innerHole = new THREE.Path();
    innerHole.moveTo(-innerWidth / 2, frameThickness);
    innerHole.lineTo(innerWidth / 2, frameThickness);
    innerHole.lineTo(innerWidth / 2, frameThickness + innerHeight);
    innerHole.lineTo(-innerWidth / 2, frameThickness + innerHeight);
    innerHole.closePath();

    outerShape.holes.push(innerHole);

    const extrudeSettings = {
      steps: 1,
      depth: depth,
      bevelEnabled: false,
    };

    const geometry = new THREE.ExtrudeGeometry(outerShape, extrudeSettings);

    // X là chiều sâu cửa
    geometry.rotateY(Math.PI / 2);
    geometry.translate(0, 0, -width / 2);

    return geometry;
  }

  createLouverSlats(
    innerWidth,
    innerHeight,
    frameThickness,
    frameDepth,
    doorSize
  ) {
    const slats = [];

    const slotHeight = 0.017;
    const spacing = 0.015;
    const topBottomMargin = 0;

    const availableHeight = innerHeight - topBottomMargin * 2;
    const numSlats = Math.floor(availableHeight / (slotHeight + spacing));

    const totalSlatsHeight = numSlats * slotHeight + (numSlats - 1) * spacing;

    const actualTopMargin =
      frameThickness + (innerHeight - totalSlatsHeight) / 2;

    const slatDepth = frameDepth;
    const tilt = 0.015; // độ nghiêng

    for (let i = 0; i < numSlats; i++) {
      const yCenter =
        actualTopMargin +
        slotHeight / 2 +
        i * (slotHeight + spacing) +
        spacing / 2;

      // ===== VERTICES =====
      const vertices = new Float32Array([
        // ---- FRONT (x = 0) – PHẲNG ----
        0,
        yCenter - slotHeight / 2,
        -innerWidth / 2, // 0
        0,
        yCenter - slotHeight / 2,
        innerWidth / 2, // 1
        0,
        yCenter + slotHeight / 2,
        innerWidth / 2, // 2
        0,
        yCenter + slotHeight / 2,
        -innerWidth / 2, // 3

        // ---- BACK (x = slatDepth) – PHẲNG & SONG SONG ----
        slatDepth,
        yCenter - slotHeight / 2 - tilt,
        -innerWidth / 2, // 4
        slatDepth,
        yCenter - slotHeight / 2 - tilt,
        innerWidth / 2, // 5
        slatDepth,
        yCenter + slotHeight / 2 - tilt,
        innerWidth / 2, // 6
        slatDepth,
        yCenter + slotHeight / 2 - tilt,
        -innerWidth / 2, // 7
      ]);

      const indices = [
        // Front (YZ plane)
        0, 1, 2, 0, 2, 3,

        // Back (YZ plane)
        4, 6, 5, 4, 7, 6,

        // Bottom
        0, 4, 5, 0, 5, 1,

        // Top
        3, 2, 6, 3, 6, 7,

        // Left
        0, 3, 7, 0, 7, 4,

        // Right
        1, 5, 6, 1, 6, 2,
      ];

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
      geometry.setIndex(indices);
      geometry.computeVertexNormals();

      geometry.translate(0, 0, -doorSize.x / 2);
      slats.push(geometry);
    }

    return slats;
  }

  createLouverDoorFrame(
    clonedModule,
    door,
    doorDirection,
    horizontalType,
    doorConfig = {}
  ) {
    door.updateMatrixWorld(true);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      door.scale.y = door.scale.y / 2;
    }
    //CUA NGANG

    const size = this.getSize(door);
    const doorSize = {
      x: Math.max(size.x, size.z),
      y: size.y,
      z: Math.min(size.x, size.z),
    };

    const config = {
      frameThickness: 0.06,
      frameDepth: 0.017,
      ...doorConfig,
    };

    // Frame geometry
    const frameGeometry = this.createLouverDoorGeometric(
      doorSize.x,
      doorSize.y,
      config.frameThickness,
      config.frameDepth
    );

    const frameMaterial =
      door.material ||
      (door.children && door.children[0]?.material) ||
      new THREE.MeshStandardMaterial({ color: 0x8b4513 });

    // Tạo group
    const louverDoor = new THREE.Group();

    const louverFrameMesh = new THREE.Mesh(
      frameGeometry,
      frameMaterial.clone()
    );
    louverFrameMesh.name = 'DOOR-LOUVER-FRAME';

    // Inner size
    const innerWidth = doorSize.x - config.frameThickness * 2;
    const innerHeight = doorSize.y - config.frameThickness * 2;

    // Louver slats
    const slatGeometries = this.createLouverSlats(
      innerWidth,
      innerHeight,
      config.frameThickness,
      config.frameDepth,
      doorSize
    );

    slatGeometries.forEach((geometry, index) => {
      const slatMesh = new THREE.Mesh(geometry, frameMaterial.clone());
      slatMesh.name = `DOOR-LOUVER-SLAT-${index}`;
      slatMesh.userData.notNesting = true; //không nesting chi tiết này
      louverFrameMesh.add(slatMesh);

      // // Tạo đường viền cho thanh louver
      // const THRESH = 60; // Threshold nhỏ để hiện tất cả các cạnh (kể cả cạnh xéo)
      // const edges = new THREE.EdgesGeometry(geometry, THRESH);
      // const edgeMaterial = new THREE.LineBasicMaterial({ color: 0x000000 });
      // const edgeLines = new THREE.LineSegments(edges, edgeMaterial);

      // edgeLines.name = `LOUVER-SLAT-EDGES-${index}`;
      // edgeLines.userData.requireBorder = false;
      // edgeLines.visibleForRaycast = false;

      // louverFrameMesh.add(edgeLines);
    });

    louverDoor.add(louverFrameMesh);

    //CUA NGANG
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      horizontalType === '2canh'
    ) {
      const louverFrameMeshClone = louverFrameMesh.clone();

      louverFrameMeshClone.position.y += doorSize.y;

      louverDoor.add(louverFrameMeshClone);
    }
    //CUA NGANG

    // Position like original door
    louverDoor.position.copy(
      clonedModule.group.worldToLocal(
        door.getWorldPosition(new THREE.Vector3())
      )
    );
    louverDoor.rotation.copy(door.rotation.clone());

    louverDoor.name = 'DOOR';
    louverDoor.parent = door.parent;
    louverDoor.visible = door.visible;
    louverDoor.userData = { ...door.userData };

    this.setInfoModelChild(louverDoor);

    return louverDoor;
  }
  //CUA LA SACH

  // Hàm alternative - sử dụng BoxGeometry xoay 45 độ để cắt
  createCutModelAlternative(clonedModule, model, cutSize, cutPos) {
    let modelGeometry;
    if (model.geometry) {
      modelGeometry = model.geometry;
    } else if (model.children && model.children.length > 0) {
      modelGeometry = model.children[0].geometry;
    } else {
      throw new Error('Không tìm thấy geometry trong model');
    }

    const bbox = new THREE.Box3().setFromObject(model);
    const size = bbox.getSize(new THREE.Vector3());

    // this.scene.scene.add(bbox);

    // Model cho CSG
    const modelForCSG = new THREE.Mesh(modelGeometry);

    modelForCSG.scale.copy(model.scale);
    modelForCSG.position.set(0, 0, 0);
    modelForCSG.updateMatrixWorld(true);

    // Cut box xoay 45 độ
    // const cutGeometry = new THREE.BoxGeometry(cutSize, cutSize, size.z * 2);
    const cutGeometry = new THREE.BoxGeometry(cutSize, cutSize, 2);
    const cutForCSG = new THREE.Mesh(cutGeometry);

    switch (cutPos) {
      case CutPosition.TOP:
        cutForCSG.position.set(0, size.y, -size.z / 2);
        break;

      case CutPosition.BOTTOM:
        cutForCSG.position.set(0, 0, -size.z / 2);
        break;

      default:
        cutForCSG.position.set(0, size.y, -size.z / 2);
        break;
    }

    cutForCSG.rotation.z = Math.PI / 4;
    cutForCSG.updateMatrixWorld(true);

    try {
      const resultMesh = CSG.subtract(modelForCSG, cutForCSG);

      if (
        resultMesh &&
        resultMesh.geometry &&
        resultMesh.geometry.attributes.position.count > 0
      ) {
        resultMesh.position.copy(
          clonedModule.group.worldToLocal(
            model.getWorldPosition(new THREE.Vector3()).clone()
          )
        );
        resultMesh.rotation.copy(model.rotation);
        resultMesh.name = model.name;
        resultMesh.parent = model.parent;
        resultMesh.userData = { ...model.userData };

        return resultMesh;
      }

      return model;
    } catch (error) {
      console.error('Alternative CSG failed:', error);
      return model;
    }
  }
  // Hàm alternative - sử dụng BoxGeometry xoay 45 độ để cắt

  cut45Door(clonedModule, door) {
    // door.updateMatrixWorld(true);
    // const newMesh = this.createCutModelAlternative(
    //   clonedModule,
    //   door,
    //   0.012 * Math.sqrt(2),
    //   CutPosition.TOP
    // );
    // // this.scene.scene.add(newMesh);
    // door?.parent?.add(newMesh);
    // door?.parent?.remove(door);
  }

  // async cutSpecialDoor(clonedModule, door, materialType) {
  async cutSpecialDoor(
    clonedModule,
    door,
    materialType,
    doorDirection,
    horizontalType
  ) {
    let specialDoor;

    if (!materialType) {
      return;
    }

    switch (materialType) {
      case 'tron':
        specialDoor = this.createNormalDoorFrame(
          clonedModule,
          door,
          doorDirection,
          horizontalType
        );
        break;

      case 'acrylic':
        specialDoor = this.createAcrylicDoorFrame(
          clonedModule,
          door,
          doorDirection,
          horizontalType
        );
        break;

      case 'pano':
        specialDoor = this.createPanoDoorFrame(
          clonedModule,
          door,
          doorDirection,
          horizontalType
        );
        break;

      case 'kinh':
        specialDoor = this.createGlassDoorFrame(
          clonedModule,
          door,
          doorDirection,
          horizontalType
        );
        break;

      case 'vom':
        specialDoor = this.createArchDoorFrame(
          clonedModule,
          door,
          doorDirection,
          horizontalType
        );
        break;

      case 'nhom_kinh':
        specialDoor = this.createAluminumGlassDoorFrame(
          clonedModule,
          door,
          doorDirection,
          horizontalType
        );
        break;

      case 'phay_ngang':
        specialDoor = this.createHorizontalBarDoorFrame(
          clonedModule,
          door,
          doorDirection,
          horizontalType
        );
        break;

      case 'phay_doc':
        specialDoor = this.createVerticalBarDoorFrame(
          clonedModule,
          door,
          doorDirection,
          horizontalType
        );
        break;

      case 'la_sach':
        specialDoor = this.createLouverDoorFrame(
          clonedModule,
          door,
          doorDirection,
          horizontalType
        );
        break;

      case 'duc_lo':
        specialDoor = await this.createHorizontalHoleDoorFrame(
          clonedModule,
          door,
          doorDirection,
          horizontalType
        );
        break;

      default:
        break;
    }

    if (!specialDoor) {
      message.error('Tạo cửa đặc biệt thất bại!!!');
      return;
    }

    if (door.visible === false) {
      specialDoor.traverse(object => {
        object.visible = false;
        object.raycast = () => {};
      });
    }

    //Update listInside
    const foundInside = clonedModule.listInside.find(inside => {
      return inside.gltf?.uuid === door.uuid;
    });
    if (foundInside) {
      foundInside.gltf = specialDoor;
      foundInside.gltfUuid = specialDoor.uuid;

      clonedModule.group.add(specialDoor);
      clonedModule.group.remove(door);
    }

    //Set lại các mối quan hệ cũ
    const idMapping = new Map();
    const oldId = door.uuid;
    const newId = specialDoor.uuid;

    idMapping.set(oldId, newId);

    if (idMapping.size > 0) {
      clonedModule.listInside.forEach(item => {
        if (item?.gltf?.userData) {
          const userData = item.gltf.userData;

          // Lặp qua tất cả các thuộc tính trong userData
          Object.keys(userData).forEach(key => {
            const value = userData[key];

            // Kiểm tra nếu giá trị này match với oldId
            if (idMapping.has(value)) {
              // Thay thế bằng newId
              userData[key] = idMapping.get(value);
            }
          });
        }
      });
    }

    //Cập nhật uuid cho các cánh clone đang mở
    this.scene.scene.traverse(obj => {
      if (obj.userData.originalDoorUuid === door.uuid) {
        obj.userData.originalDoorUuid = specialDoor.uuid;
      }
    });

    setTimeout(() => {
      this.scene?.handleDrawBorder();
      this.scene.textureController?.reloadTexture({ module: clonedModule });
    }, 0);

    return specialDoor;
  }

  async createHorizontalDoor(clonedModule, door, horizontalType) {
    // if (horizontalType === '2canh') {
    //   const size = this.getSize(door);
    //   // const size = this.getSizeRotation(clonedModule.group, door);

    //   const cloneDoorTop = door.clone();
    //   cloneDoorTop.position.y += size.y;
    //   // cloneDoorTop.position.x += size.z / 2;

    //   clonedModule.group.add(cloneDoorTop);
    //   // door.add(cloneDoorTop);
    // }
    // return;

    // let specialDoor = new THREE.Group();

    // // Tính chiều cao thực tế đang hiển thị của door (đã bao gồm scale hiện tại)
    // const box = new THREE.Box3().setFromObject(door);
    // const height = box.max.y - box.min.y;

    // specialDoor.add(door);

    // // Clone thứ hai - nửa trên
    // if (horizontalType === '2canh') {
    //   const cloneDoorTop = door.clone();
    //   cloneDoorTop.position.y += height;

    //   specialDoor.add(cloneDoorTop);
    // }

    // specialDoor.userData = door.userData;

    // clonedModule.group.add(specialDoor);
    // clonedModule.group.remove(door);
    // return;

    ///////////////////

    let specialDoor = new THREE.Group();

    // Tính chiều cao thực tế đang hiển thị của door (đã bao gồm scale hiện tại)
    const box = new THREE.Box3().setFromObject(door);
    const height = box.max.y - box.min.y;

    // Clone thứ nhất - nửa dưới
    const cloneDoorBottom = door.clone();
    cloneDoorBottom.position.y += 0;

    specialDoor.add(cloneDoorBottom);

    // Clone thứ hai - nửa trên
    if (horizontalType === '2canh') {
      const cloneDoorTop = door.clone();
      cloneDoorTop.position.y += height;

      specialDoor.add(cloneDoorTop);
    }

    specialDoor.userData = door.userData;

    if (!specialDoor) {
      message.error('Tạo cửa đặc biệt thất bại!!!');
      return;
    }

    if (door.visible === false) {
      specialDoor.traverse(object => {
        object.visible = false;
        object.raycast = () => {};
      });
    }

    //Update listInside
    const foundInside = clonedModule.listInside.find(inside => {
      return inside.gltf.uuid === door.uuid;
    });
    if (foundInside) {
      foundInside.gltf = specialDoor;
      foundInside.gltfUuid = specialDoor.uuid;

      clonedModule.group.add(specialDoor);
      clonedModule.group.remove(door);
    }

    //Set lại các mối quan hệ cũ
    const idMapping = new Map();
    const oldId = door.uuid;
    const newId = specialDoor.uuid;

    idMapping.set(oldId, newId);

    if (idMapping.size > 0) {
      clonedModule.listInside.forEach(item => {
        if (item?.gltf?.userData) {
          const userData = item.gltf.userData;

          // Lặp qua tất cả các thuộc tính trong userData
          Object.keys(userData).forEach(key => {
            const value = userData[key];

            // Kiểm tra nếu giá trị này match với oldId
            if (idMapping.has(value)) {
              // Thay thế bằng newId
              userData[key] = idMapping.get(value);
            }
          });
        }
      });
    }

    //Cập nhật uuid cho các cánh clone đang mở
    this.scene.scene.traverse(obj => {
      if (obj.userData.originalDoorUuid === door.uuid) {
        obj.userData.originalDoorUuid = specialDoor.uuid;
      }
    });

    setTimeout(() => {
      this.scene?.handleDrawBorder();
      this.scene.textureController?.reloadTexture({ module: clonedModule });
    }, 100);
  }

  rescale45Door(door, clonedModule) {
    // //Cửa cho tủ góc
    // if (clonedModule.cabinetType.toLowerCase().includes('corner')) {
    //   const doorBoundingBox = new THREE.Box3().setFromObject(door);
    //   const doorSize = new THREE.Vector3();
    //   doorBoundingBox.getSize(doorSize);

    //   let originalSize;
    //   const doorClone = door.clone();
    //   doorClone.scale.set(1, 1, 1);
    //   originalSize = this.getSize(doorClone);

    //   if (clonedModule.cabinetType.toLowerCase().includes('lower')) {
    //     doorSize.y -= 0.025;
    //   } else if (clonedModule.cabinetType.toLowerCase().includes('ceiling')) {
    //     doorSize.y -= topDoorGapBase + thicknessBase;

    //     door.position.y += topDoorGapBase + thicknessBase;

    //     //Kiểm tra tủ góc đã có bạ hay chưa
    //     let existsBrace = false;
    //     clonedModule.group.traverse(object => {
    //       if (object.userData.type === InsideType.BRACE) {
    //         existsBrace = true;
    //         return;
    //       }
    //     });

    //     if (!existsBrace) {
    //       const addBraceToCabinet = brace => {
    //         const data = {
    //           _id: brace.userData?.id, // Sử dụng UUID của brace làm _id tạm thời
    //           glb: door.userData?.model ?? '/assets/glb/BRACE.glb',
    //           type: brace.userData.type,
    //           gltfUuid: brace.uuid,
    //           gltf: brace,
    //         };

    //         brace.userData.isInside = true;
    //         brace.userData.model =
    //           door.userData?.model ?? '/assets/glb/BRACE.glb';
    //         clonedModule.listInside.push(data);
    //         clonedModule.group.add(brace);
    //       };

    //       this.createAndAddBraceForCornerModule(
    //         door,
    //         clonedModule,
    //         addBraceToCabinet
    //       )
    //         .then(braceUuid => {
    //           // door.userData.braceUuid = braceUuid;
    //         })
    //         .catch(error => {
    //           console.error('Error creating brace:', error);
    //         });
    //     }
    //   }

    //   door.scale.y = doorSize.y / originalSize.y;

    //   return;
    // }

    const isCurveModule = clonedModule.cabinetType
      .toLowerCase()
      .includes('curve');
    const isBenchModule =
      clonedModule.cabinetType.toLowerCase().includes('bench') ||
      clonedModule.cabinetType.toLowerCase().includes('tv');

    // Tìm các edge meshes
    const edges = this.findEdgeMeshes(door, clonedModule);
    const {
      leftEdge,
      rightEdge,
      topEdge,
      bottomEdge,
      leftEdgeDoor,
      rightEdgeDoor,
    } = edges;

    if (!leftEdge || !rightEdge || !topEdge || !bottomEdge) {
      //Rescale cho cửa của hộc kéo trong tủ
      if (
        door.userData.drawerUuid &&
        door.userData.frameUuid &&
        leftEdge &&
        rightEdge
      ) {
        const doorClone = door.clone();
        doorClone.scale.set(1, 1, 1);
        const doorOriginalSize = this.getSize(doorClone);

        let frameModel;
        if (door.userData.frameUuid) {
          frameModel = this.findMeshByUuid(
            clonedModule.group,
            door.userData.frameUuid
          );
        }

        const { leftEdgePos, leftEdgeSize, scaleZ, scaleY, sizeZ, sizeY } =
          this.calculateUDrawerDoorTransform(
            doorOriginalSize,
            frameModel.userData.infoChildDrawer.numDrawer,
            leftEdge,
            rightEdge,
            clonedModule,
            true //is45Door
          );

        door.position.y =
          leftEdgePos.y + (sizeY + topDoorGapBase) * door.userData.idxDrawer;
        door.position.x =
          leftEdgePos.x +
          Math.max(leftEdgeSize.x, leftEdgeSize.z) -
          Math.min(leftEdgeSize.x, leftEdgeSize.z);
        door.position.z =
          leftEdgePos.z - Math.min(leftEdgeSize.x, leftEdgeSize.z);

        door.scale.y = scaleY;
        door.scale.z = scaleZ;
      }

      console.warn('Missing required edges for door scaling');
      return;
    }

    const orientation = this.getModuleOrientation(clonedModule?.group);

    // Lấy kích thước của các edges
    const sizeLeft = this.getSize(leftEdge);
    const sizeRight = this.getSize(rightEdge);
    const sizeTop = this.getSize(topEdge);
    const sizeBottom = this.getSize(bottomEdge);

    // Lấy local position của các edges
    const leftPos = clonedModule.group.worldToLocal(
      leftEdge.getWorldPosition(new THREE.Vector3()).clone()
    );
    const rightPos = clonedModule.group.worldToLocal(
      rightEdge.getWorldPosition(new THREE.Vector3()).clone()
    );
    const topPos = clonedModule.group.worldToLocal(
      topEdge.getWorldPosition(new THREE.Vector3()).clone()
    );
    const bottomPos = clonedModule.group.worldToLocal(
      bottomEdge.getWorldPosition(new THREE.Vector3()).clone()
    );

    const customPoint = door.userData?.customPoint;
    const typeHau = door.userData?.typeHau;

    door.scale.set(1, 1, 1); // Reset scale để tính toán chính xác

    let originalSize;
    const doorClone = door.clone();
    originalSize = this.getSize(doorClone);

    let drawerModel, drawerPos;
    if (door.userData.drawerUuid) {
      drawerModel = this.findMeshByUuid(
        clonedModule.group,
        door.userData.drawerUuid
      );

      drawerPos = clonedModule.group.worldToLocal(
        drawerModel.getWorldPosition(new THREE.Vector3()).clone()
      );
    }

    //Size
    let sizeY =
      topPos.y -
      bottomPos.y +
      (topEdge.userData?.type !== undefined ? 0 : sizeTop.y) -
      topDoorGapBase -
      (topEdge.userData?.type !== undefined
        ? 0
        : topEdge.name.toUpperCase().includes('NOC')
          ? thicknessBase
          : countertopGap) +
      (topEdge.name.toUpperCase().includes('NOC') &&
      clonedModule.cabinetType.toLowerCase().includes('lower') &&
      (clonedModule.cabinetType.toLowerCase().includes('wardrobe') ||
        clonedModule.cabinetType.toLowerCase().includes('decorative') ||
        clonedModule.cabinetType.toLowerCase().includes('shoe')) &&
      isCurveModule === false &&
      isBenchModule === false
        ? topDoorGapBase + sizeTop.y
        : 0) +
      (topEdge.name.toUpperCase().includes('NOC') &&
      clonedModule.cabinetType.toLowerCase().includes('upper') &&
      clonedModule.moduleType.toLowerCase().includes('hood')
        ? sizeTop.y
        : 0) +
      (customPoint?.bottom === PointSnapPos.TOP
        ? -sizeBottom.y
        : customPoint?.bottom === PointSnapPos.CENTER
          ? -sizeBottom.y / 2
          : 0);

    //Cửa cho tủ trên (trừ máy hút mùi và tủ TV treo)
    if (
      clonedModule.cabinetType.toLowerCase().includes('upper') &&
      !clonedModule.moduleType.toLowerCase().includes('hood') &&
      !clonedModule.moduleType.toLowerCase().includes('tv')
    ) {
      sizeY = topPos.y - bottomPos.y + sizeTop.y;
    }
    //Cửa cho tủ trên (trừ máy hút mùi và tủ TV treo)

    //Cửa hộc kéo
    if (door.userData.drawerUuid) {
      if (
        topEdge.userData.type === InsideType.DRAWER ||
        topEdge.userData.type === InsideType.ACCESSORY_DISH_LOWER
      ) {
        sizeY = sizeY - drawerModel.userData.bottomGap - this.braceIndent;
      }

      if (
        bottomEdge.userData.type === InsideType.DRAWER ||
        bottomEdge.userData.type === InsideType.ACCESSORY_DISH_LOWER
      ) {
        if (clonedModule.cabinetType.toLowerCase().includes('kitchen')) {
          sizeY =
            topPos.y +
            sizeTop.y +
            (topEdge.userData?.type !== undefined ? -sizeTop.y / 2 - slit : 0) -
            (drawerPos.y - drawerModel.userData.bottomGap - this.braceIndent) -
            topDoorGapBase -
            (topEdge.userData?.type !== undefined ||
            topEdge.name.toUpperCase().includes('NOC')
              ? 0
              : countertopGap) +
            (topEdge.name.toUpperCase().includes('NOC') &&
            clonedModule.cabinetType.toLowerCase().includes('lower') &&
            isCurveModule === false &&
            isBenchModule === false
              ? topDoorGapBase
              : 0);
        } else {
          sizeY =
            topPos.y -
            (drawerPos.y - drawerModel.userData.bottomGap - this.braceIndent) -
            topDoorGapBase +
            (topEdge.name.toUpperCase().includes('NOC') &&
            clonedModule.cabinetType.toLowerCase().includes('lower') &&
            isCurveModule === false &&
            isBenchModule === false
              ? sizeTop.y + topDoorGapBase
              : 0);
        }

        if (
          topEdge.userData.type === InsideType.DRAWER ||
          topEdge.userData.type === InsideType.ACCESSORY_DISH_LOWER
        ) {
          if (clonedModule.cabinetType.toLowerCase().includes('kitchen')) {
            sizeY -=
              sizeTop.y / 2 +
              topEdge.userData.bottomGap +
              this.braceHeight -
              this.braceIndent -
              topDoorGapBase;
          } else {
            sizeY =
              topPos.y -
              topEdge.userData.bottomGap -
              this.braceHeight +
              this.braceIndent -
              (drawerPos.y - drawerModel.userData.bottomGap - this.braceIndent);
          }
        }
      }
    }
    //Cửa hộc kéo

    // const sizeZ = this.setDoorZSize(
    //   orientation,
    //   leftEdge,
    //   leftPos,
    //   sizeLeft,
    //   rightEdge,
    //   rightPos,
    //   sizeRight,
    //   slit,
    //   isCurveModule
    // );
    let sizeZ;
    if (typeHau === TypeConnerModule.SUB) {
      sizeZ = this.setDoorXSize(
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        rightEdge,
        rightPos,
        sizeRight,
        slit,
        isCurveModule
      );
    } else {
      sizeZ = this.setDoorZSize(
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        rightEdge,
        rightPos,
        sizeRight,
        slit,
        isCurveModule
      );
    }

    //Position
    // door.position.x =
    //   Number(clonedModule.formularController.currentDimensions.depth) / 2 -
    //   (isCurveModule ? thicknessBase : 0) -
    //   (isBenchModule ? thicknessBase : 0);

    if (!typeHau) {
      door.position.x =
        Number(clonedModule.formularController.currentDimensions.depth) / 2 -
        (isCurveModule ? thicknessBase : 0) -
        (isBenchModule ? thicknessBase : 0);
    } else if (typeHau === TypeConnerModule.SUB) {
      door.position.z =
        clonedModule.formularController.currentOptions.rongK2 / 2 -
        Number(clonedModule.formularController.currentDimensions.depth);
    } else {
      door.position.x =
        Number(clonedModule.formularController.currentDimensions.depth) -
        clonedModule.formularController.currentOptions.rongK1 / 2;
    }

    door.position.y =
      bottomPos.y +
      (customPoint?.bottom === PointSnapPos.TOP
        ? sizeBottom.y
        : customPoint?.bottom === PointSnapPos.CENTER
          ? sizeBottom.y / 2
          : 0);

    //Cửa hộc keos
    if (door.userData.drawerUuid) {
      if (
        bottomEdge.userData?.type === InsideType.DRAWER ||
        bottomEdge.userData?.type === InsideType.ACCESSORY_DISH_LOWER
      ) {
        door.position.y =
          drawerPos.y - drawerModel.userData.bottomGap - this.braceIndent;
      }
    }
    //Cửa hộc keos

    //Cửa cho tủ máy hút mùi trên
    if (
      clonedModule.cabinetType.toLowerCase().includes('upper') &&
      clonedModule.moduleType.toLowerCase().includes('hood')
    ) {
      door.position.y = bottomPos.y + topDoorGapBase;
    }
    //Cửa cho tủ máy hút mùi trên

    //Cửa cho tủ đụng trần
    if (clonedModule.cabinetType.toLowerCase().includes('ceiling')) {
      //TRƯỜNG HỢP CỬA CÓ BẠ
      door.position.y = bottomPos.y + sizeBottom.y + topDoorGapBase;

      //TRƯỜNG HỢP CỬA KHÔNG BẠ
      if (door.userData.withBrace === CeilingDoor.WITHOUT_BRACE) {
        sizeY += thicknessBase + topDoorGapBase;
        door.position.y = bottomPos.y;
      }
    }
    //Cửa cho tủ đụng trần

    // this.setDoorZPosition(
    //   door,
    //   leftEdgeDoor,
    //   orientation,
    //   leftEdge,
    //   leftPos,
    //   sizeLeft,
    //   sizeZ,
    //   slit,
    //   isCurveModule
    // );
    if (typeHau === TypeConnerModule.SUB) {
      this.setDoorXPosition(
        door,
        leftEdgeDoor,
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        sizeZ,
        slit,
        isCurveModule
      );
    } else {
      this.setDoorZPosition(
        door,
        leftEdgeDoor,
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        sizeZ,
        slit,
        isCurveModule
      );
    }

    //Scale
    const scaleY = sizeY / originalSize.y;

    let scaleZ;

    if (door.userData.drawerUuid) {
      scaleZ = sizeZ / Math.max(originalSize.x, originalSize.z);
    } else {
      const checkFullWidth =
        (sizeZ <= door.userData.limit.width.max &&
          door.userData?.handlePosition?.left &&
          door.userData?.handlePosition?.right) ||
        door.userData?.handlePosition?.center;

      //Giới hạn chiều rộng cửa để tránh vượt quá giới hạn max
      const clampedSizeZ =
        sizeZ < door.userData.limit.width.max * 2
          ? sizeZ
          : door.userData.limit.width.max * 2;
      scaleZ = checkFullWidth
        ? clampedSizeZ / Math.max(originalSize.x, originalSize.z)
        : clampedSizeZ / 2 / Math.max(originalSize.x, originalSize.z);

      if (checkFullWidth) {
        door.userData.handlePosition = {
          ...door.userData.handlePosition,
          left: true,
          right: true,
        };
      }
    }

    door.scale.y = scaleY;
    door.scale.z = scaleZ;

    ///test vat 45
    // setTimeout(() => {
    //   this.cut45Door(clonedModule, door);
    // }, 50);
    ///test vat 45

    //Scale lại cửa đặc biệt
    const materialType = door.userData?.materialType;
    const doorDirection = door.userData?.doorDirection;
    const horizontalType = door.userData?.horizontalType;

    //Thêm tay nâng mặc định cho cửa ngang
    if (doorDirection === DoorDirection.HORIZONTAL && !clonedModule.isSaveClone) {
      const liftSupportModel = this.loadLiftSupportModel(
        door.userData.defaultLiftSupport,
        clonedModule,
        door
      );
    }

    // if (!materialType?.includes('tron')) {
    setTimeout(async () => {
      await this.cutSpecialDoor(
        clonedModule,
        door,
        materialType,
        doorDirection,
        horizontalType
      );
    }, 200);
    // }

    //Cửa cho tủ trên hoặc trường hợp cửa đụng tới tấm nóc
    if (
      (clonedModule.cabinetType.toLowerCase().includes('lower') &&
        (!topEdge.name.toUpperCase().includes('NOC') ||
          (!clonedModule.cabinetType.toLowerCase().includes('wardrobe') &&
            !clonedModule.cabinetType.toLowerCase().includes('decorative') &&
            !clonedModule.cabinetType.toLowerCase().includes('shoe')))) ||
      clonedModule.cabinetType.toLowerCase().includes('ceiling') ||
      (clonedModule.cabinetType.toLowerCase().includes('upper') &&
        clonedModule.cabinetType.toLowerCase().includes('tv')) ||
      (clonedModule.cabinetType.toLowerCase().includes('lower') &&
        (isCurveModule === true || isBenchModule === true))
    ) {
      //Thêm bạ cửa 45
      if (
        topEdge.userData?.type !== undefined ||
        topEdge.name.toUpperCase().includes('NOC')
      ) {
        const addBraceToCabinet = brace => {
          const data = {
            _id: brace.userData?.id,
            // glb: door.userData.model,
            glb: '/assets/glb/BRACE.glb',
            type: brace.userData.type,
            gltfUuid: brace.uuid,
            gltf: brace,
          };
          brace.userData.isInside = true;
          // brace.userData.model = door.userData.model;
          brace.userData.model = '/assets/glb/BRACE.glb';
          clonedModule.listInside.push(data);
          clonedModule.group.add(brace);
          //   this.findPoint();
        };

        if (
          leftEdge.userData?.type === InsideType.DOOR ||
          rightEdge.userData?.type === InsideType.DOOR ||
          door.userData.existsBrace === true ||
          door.userData.braceUuid
        ) {
          return;
        }

        this.createAndAddBrace(
          null, // khong truyen drawer
          door,
          edges,
          { sizeBottom, sizeRight, sizeLeft, sizeTop },
          clonedModule,
          originalSize,
          addBraceToCabinet
        )
          .then(braceUuid => {
            door.userData.braceUuid = braceUuid;
          })
          .catch(error => {
            console.error('Error creating brace:', error);
          });
      }
    }
  }

  rescaleBasicDoor(door, clonedModule) {
    // if (clonedModule.cabinetType.toLowerCase().includes('corner')) {
    //   const doorBoundingBox = new THREE.Box3().setFromObject(door);
    //   const doorSize = new THREE.Vector3();
    //   doorBoundingBox.getSize(doorSize);

    //   let originalSize;
    //   const doorClone = door.clone();
    //   doorClone.scale.set(1, 1, 1);
    //   originalSize = this.getSize(doorClone);

    //   if (clonedModule.cabinetType.toLowerCase().includes('lower')) {
    //     doorSize.y += 0.025;
    //   } else if (clonedModule.cabinetType.toLowerCase().includes('ceiling')) {
    //     doorSize.y += topDoorGapBase + thicknessBase;

    //     door.position.y -= topDoorGapBase + thicknessBase;
    //   }

    //   door.scale.y = doorSize.y / originalSize.y;

    //   if (!door.userData.handleUuid) {
    //     //Thêm lại tay nắm khi chuyển loại cửa
    //     const addHandleToCabinet = handle => {
    //       const data = {
    //         _id: handle.userData?.id,
    //         glb: door.userData.default_handle.model,
    //         type: handle.userData.type,
    //         name: handle.userData.name,
    //         gltfUuid: handle.uuid,
    //         gltf: handle,
    //       };
    //       handle.userData.isInside = true;
    //       handle.userData.model = door.userData.default_handle.model;
    //       clonedModule.listInside.push(data);
    //       clonedModule.group.add(handle);
    //     };

    //     //Thêm tay nắm mặc định
    //     const handleModel = this.loadHandleModel(
    //       door.userData.default_handle,
    //       clonedModule,
    //       door,
    //       addHandleToCabinet
    //     );

    //     door.userData.handleUuid = handleModel.uuid;
    //   }

    //   // if (!door.userData.lockUuid) {
    //   //   //Thêm lại tay nắm khi chuyển loại cửa
    //   //   const addModelToCabinet = model => {
    //   //     const data = {
    //   //       _id: model.userData?.id,
    //   //       glb: door.userData.default_lock.model,
    //   //       type: model.userData.type,
    //   //       name: model.userData.name,
    //   //       gltfUuid: model.uuid,
    //   //       gltf: model,
    //   //     };
    //   //     model.userData.isInside = true;
    //   //     model.userData.model = door.userData.default_lock.model;
    //   //     clonedModule.listInside.push(data);
    //   //     clonedModule.group.add(model);
    //   //   };

    //   //   //Thêm tay nắm mặc định
    //   //   const lockModel = this.loadLockModel(
    //   //     door.userData.default_lock,
    //   //     clonedModule,
    //   //     door,
    //   //     addModelToCabinet
    //   //   );

    //   //   door.userData.lockUuid = lockModel.uuid;
    //   // }

    //   return;
    // }

    const isCurveModule = clonedModule.cabinetType
      .toLowerCase()
      .includes('curve');
    const isBenchModule =
      clonedModule.cabinetType.toLowerCase().includes('bench') ||
      clonedModule.cabinetType.toLowerCase().includes('tv');

    // Tìm các edge meshes
    const edges = this.findEdgeMeshes(door, clonedModule);
    const {
      leftEdge,
      rightEdge,
      topEdge,
      bottomEdge,
      leftEdgeDoor,
      rightEdgeDoor,
    } = edges;

    if (!leftEdge || !rightEdge || !topEdge || !bottomEdge) {
      //Rescale cho cửa của hộc kéo trong tủ
      if (
        door.userData.drawerUuid &&
        door.userData.frameUuid &&
        leftEdge &&
        rightEdge
      ) {
        const doorClone = door.clone();
        doorClone.scale.set(1, 1, 1);
        const doorOriginalSize = this.getSize(doorClone);

        let frameModel;
        if (door.userData.frameUuid) {
          frameModel = this.findMeshByUuid(
            clonedModule.group,
            door.userData.frameUuid
          );
        }

        const { leftEdgePos, leftEdgeSize, scaleZ, scaleY, sizeZ, sizeY } =
          this.calculateUDrawerDoorTransform(
            doorOriginalSize,
            frameModel.userData.infoChildDrawer.numDrawer,
            leftEdge,
            rightEdge,
            clonedModule,
            false //is45Door
          );

        door.position.y = leftEdgePos.y + sizeY * door.userData.idxDrawer;
        door.position.x =
          leftEdgePos.x +
          Math.max(leftEdgeSize.x, leftEdgeSize.z) -
          Math.min(leftEdgeSize.x, leftEdgeSize.z);
        door.position.z =
          leftEdgePos.z - Math.min(leftEdgeSize.x, leftEdgeSize.z);

        door.scale.y = scaleY;
        door.scale.z = scaleZ;
      }

      console.warn('Missing required edges for door scaling');
      return;
    }

    const orientation = this.getModuleOrientation(clonedModule?.group);

    // Lấy kích thước của các edges
    const sizeLeft = this.getSize(leftEdge);
    const sizeRight = this.getSize(rightEdge);
    const sizeTop = this.getSize(topEdge);
    const sizeBottom = this.getSize(bottomEdge);

    // Lấy world positions của các edges
    const leftPos = clonedModule.group.worldToLocal(
      leftEdge.getWorldPosition(new THREE.Vector3()).clone()
    );
    const rightPos = clonedModule.group.worldToLocal(
      rightEdge.getWorldPosition(new THREE.Vector3()).clone()
    );
    const topPos = clonedModule.group.worldToLocal(
      topEdge.getWorldPosition(new THREE.Vector3()).clone()
    );
    const bottomPos = clonedModule.group.worldToLocal(
      bottomEdge.getWorldPosition(new THREE.Vector3()).clone()
    );

    const customPoint = door.userData?.customPoint;
    const typeHau = door.userData?.typeHau;

    door.scale.set(1, 1, 1); // Reset scale để tính toán chính xác

    let originalSize;
    const doorClone = door.clone();
    originalSize = this.getSize(doorClone);

    let drawerModel, drawerPos, qtyDrawer;
    if (door.userData.drawerUuid) {
      drawerModel = this.findMeshByUuid(
        clonedModule.group,
        door.userData.drawerUuid
      );

      qtyDrawer = drawerModel.userData.drawerCount ?? 1;

      // //Trường hợp kéo từng hộc kéo thì đếm số lượng
      // if (!qtyDrawer) {
      //   let count = 0;
      //   clonedModule.group.traverse(object => {
      //     if (
      //       object.userData.isInside === true &&
      //       (object.userData.type === InsideType.DRAWER ||
      //         object.userData.type === InsideType.ACCESSORY_DISH_LOWER) &&
      //       nearlyEqual(object.position.x, drawerModel.position.x, 0.01) &&
      //       nearlyEqual(object.position.z, drawerModel.position.z, 0.01)
      //     ) {
      //       count++;
      //     }
      //   });

      //   qtyDrawer = count;
      // }

      drawerPos = clonedModule.group.worldToLocal(
        drawerModel.getWorldPosition(new THREE.Vector3()).clone()
      );
    }

    let sizeY =
      topPos.y +
      (topEdge.userData?.type !== undefined
        ? sizeTop.y / 2 - slit
        : sizeTop.y) -
      (bottomPos.y +
        (bottomEdge.userData?.type !== undefined ? sizeBottom.y / 2 : 0)) -
      // (bottomEdge.userData?.type !== undefined
      //   ? sizeBottom.y / 2
      //   : customPoint
      //     ? sizeBottom.y / 2
      //     : 0)) -
      (topEdge.userData?.type !== undefined ||
      topEdge.name.toUpperCase().includes('NOC')
        ? 0
        : countertopGap + minDoorGap) -
      (isCurveModule && topEdge.name.toUpperCase().includes('NOC')
        ? sizeTop.y
        : 0) -
      (isBenchModule && topEdge.name.toUpperCase().includes('NOC')
        ? sizeTop.y
        : 0) +
      (customPoint?.top === PointSnapPos.TOP
        ? topEdge.userData?.type !== undefined
          ? sizeTop.y / 2
          : 0
        : customPoint?.top === PointSnapPos.CENTER
          ? topEdge.userData?.type !== undefined
            ? 0
            : -sizeTop.y / 2
          : customPoint?.top === PointSnapPos.BOTTOM
            ? topEdge.userData?.type !== undefined
              ? -sizeTop.y / 2
              : -sizeTop.y
            : 0) +
      (customPoint?.bottom === PointSnapPos.TOP
        ? bottomEdge.userData?.type !== undefined
          ? -sizeBottom.y / 2
          : -sizeBottom.y
        : customPoint?.bottom === PointSnapPos.CENTER
          ? bottomEdge.userData?.type !== undefined
            ? 0
            : -sizeBottom.y / 2
          : customPoint?.bottom === PointSnapPos.BOTTOM
            ? bottomEdge.userData?.type !== undefined
              ? sizeBottom.y / 2
              : 0
            : 0);

    //Cửa hộc kéo
    let topFrameEdge, bottomFrameEdge, drawerHeight;
    let topFrameEdgeSize, bottomFrameEdgeSize;

    if (door.userData.drawerUuid) {
      //Hộc kéo thường
      topFrameEdge = this.findMeshByUuid(
        clonedModule.group,
        drawerModel.userData.topEdgeUuid
      );
      topFrameEdgeSize = this.getSize(topFrameEdge);

      bottomFrameEdge = this.findMeshByUuid(
        clonedModule.group,
        drawerModel.userData.bottomEdgeUuid
      );
      bottomFrameEdgeSize = this.getSize(bottomFrameEdge);

      let totalHeight =
        topFrameEdge.position.y -
        bottomFrameEdge.position.y +
        topFrameEdgeSize.y -
        (topFrameEdge.userData?.type !== undefined
          ? topFrameEdgeSize.y / 2
          : 0) -
        (bottomFrameEdge.userData?.type !== undefined
          ? bottomFrameEdgeSize.y / 2
          : 0) -
        //Tu bep duoi
        (clonedModule.moduleType.includes('kitchen') &&
        clonedModule.moduleType.includes('lower') &&
        topEdge.userData?.type !== InsideType.HPANEL
          ? countertopGap + minDoorGap
          : 0) -
        //Tu TV hoac đôn ngồi
        ((clonedModule.moduleType.includes('tv') &&
          topFrameEdge.name?.toUpperCase()?.includes('NOC')) ||
        clonedModule.moduleType.includes('bench')
          ? topFrameEdgeSize.y
          : 0);

      drawerHeight = (totalHeight - 0.001 * (qtyDrawer - 1)) / qtyDrawer;

      sizeY = drawerHeight;
    }
    //Cửa hộc kéo

    let checkSubZ = false;

    if (
      isCurveModule ||
      drawerModel?.userData?.type === InsideType.ACCESSORY_FLIP
    ) {
      checkSubZ = true;
    }

    // const sizeZ = this.setDoorZSize(
    //   orientation,
    //   leftEdge,
    //   leftPos,
    //   sizeLeft,
    //   rightEdge,
    //   rightPos,
    //   sizeRight,
    //   slit,
    //   checkSubZ
    // );
    let sizeZ;
    if (typeHau === TypeConnerModule.SUB) {
      sizeZ = this.setDoorXSize(
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        rightEdge,
        rightPos,
        sizeRight,
        slit,
        checkSubZ
      );
    } else {
      sizeZ = this.setDoorZSize(
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        rightEdge,
        rightPos,
        sizeRight,
        slit,
        checkSubZ
      );
    }

    //Position
    // door.position.x =
    //   Number(clonedModule.formularController.currentDimensions.depth) / 2 -
    //   (isCurveModule ? thicknessBase : 0) -
    //   (isBenchModule ? thicknessBase : 0) -
    //   (drawerModel?.userData?.type === InsideType.ACCESSORY_FLIP
    //     ? thicknessBase
    //     : 0);

    if (!typeHau) {
      door.position.x =
        Number(clonedModule.formularController.currentDimensions.depth) / 2 -
        (isCurveModule ? thicknessBase : 0) -
        (isBenchModule ? thicknessBase : 0) -
        (drawerModel?.userData?.type === InsideType.ACCESSORY_FLIP
          ? thicknessBase
          : 0);
    } else if (typeHau === TypeConnerModule.SUB) {
      door.position.z =
        clonedModule.formularController.currentOptions.rongK2 / 2 -
        Number(clonedModule.formularController.currentDimensions.depth);
    } else {
      door.position.x =
        Number(clonedModule.formularController.currentDimensions.depth) -
        clonedModule.formularController.currentOptions.rongK1 / 2;
    }

    // door.position.y =
    //   bottomPos.y +
    //   (bottomEdge.userData?.type !== undefined
    //     ? sizeBottom.y / 2 + slit
    //     : customPoint
    //       ? sizeBottom.y / 2
    //       : 0) +
    //   (customPoint?.bottom === PointSnapPos.TOP
    //     ? sizeBottom.y / 2
    //     : customPoint?.bottom === PointSnapPos.BOTTOM
    //       ? -sizeBottom.y / 2
    //       : 0);

    door.position.y =
      bottomPos.y +
      (bottomEdge.userData?.type !== undefined ? sizeBottom.y / 2 + slit : 0) +
      (customPoint?.bottom === PointSnapPos.TOP
        ? bottomEdge.userData?.type !== undefined
          ? sizeBottom.y / 2
          : sizeBottom.y
        : customPoint?.bottom === PointSnapPos.CENTER
          ? bottomEdge.userData?.type !== undefined
            ? 0
            : sizeBottom.y / 2
          : customPoint?.bottom === PointSnapPos.BOTTOM
            ? bottomEdge.userData?.type !== undefined
              ? -sizeBottom.y / 2
              : 0
            : 0);

    //Cửa hộc keos
    if (drawerModel) {
      door.position.y =
        drawerModel.userData.indexDuplicate * (drawerHeight + 0.001) +
        (bottomFrameEdge.userData?.type === undefined
          ? bottomFrameEdge.position.y
          : bottomFrameEdge.position.y + bottomFrameEdgeSize.y / 2);
    }
    //Cửa hộc keos

    //Cửa xắn lật tủ giày
    if (drawerModel?.userData?.type === InsideType.ACCESSORY_FLIP) {
      door.position.y = drawerPos.y;
    }
    //Cửa xắn lật tủ giày

    // this.setDoorZPosition(
    //   door,
    //   leftEdgeDoor,
    //   orientation,
    //   leftEdge,
    //   leftPos,
    //   sizeLeft,
    //   sizeZ,
    //   slit,
    //   checkSubZ
    // );
    if (typeHau === TypeConnerModule.SUB) {
      this.setDoorXPosition(
        door,
        leftEdgeDoor,
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        sizeZ,
        slit,
        checkSubZ
      );
    } else {
      this.setDoorZPosition(
        door,
        leftEdgeDoor,
        orientation,
        leftEdge,
        leftPos,
        sizeLeft,
        sizeZ,
        slit,
        checkSubZ
      );
    }

    //Scale
    const scaleY = sizeY / originalSize.y;

    let scaleZ;

    if (door.userData.drawerUuid) {
      scaleZ = sizeZ / Math.max(originalSize.x, originalSize.z);
    } else {
      const checkFullWidth =
        (sizeZ <= door.userData.limit.width.max &&
          door.userData?.handlePosition?.left &&
          door.userData?.handlePosition?.right) ||
        door.userData?.handlePosition?.center;

      //Giới hạn chiều rộng cửa để tránh vượt quá giới hạn max
      const clampedSizeZ =
        sizeZ < door.userData.limit.width.max * 2
          ? sizeZ
          : door.userData.limit.width.max * 2;
      scaleZ = checkFullWidth
        ? clampedSizeZ / Math.max(originalSize.x, originalSize.z)
        : clampedSizeZ / 2 / Math.max(originalSize.x, originalSize.z);

      if (checkFullWidth) {
        door.userData.handlePosition = {
          ...door.userData.handlePosition,
          left: sizeZ <= door.userData.limit.width.max,
          right: sizeZ <= door.userData.limit.width.max,
        };
      }
    }

    door.scale.y = scaleY;
    door.scale.z = scaleZ;

    //reset brace
    door.userData.braceUuid = undefined;
    door.userData.existsBrace = false;

    if (!door.userData.handleUuid && !clonedModule.isSaveClone) {
      //Thêm lại tay nắm khi chuyển loại cửa
      const addHandleToCabinet = handle => {
        const data = {
          _id: handle.uuid,
          glb: door.userData.default_handle.model,
          type: handle.userData.type,
          name: handle.userData.name,
          thumbnail_url: handle.userData.thumbnail_url,
          price: handle.userData.price,
          gltfUuid: handle.uuid,
          gltf: handle,
        };
        handle.userData.isInside = true;
        handle.userData.model = door.userData.default_handle.model;
        clonedModule.listInside.push(data);
        clonedModule.group.add(handle);
      };

      //Thêm tay nắm mặc định
      const handleModel = this.loadHandleModel(
        door.userData.default_handle,
        clonedModule,
        door,
        addHandleToCabinet
      );

      door.userData.handleUuid = handleModel.uuid;
    }

    //Scale lại cửa đặc biệt
    const materialType = door.userData?.materialType;
    const doorDirection = door.userData?.doorDirection;
    const horizontalType = door.userData?.horizontalType;

    //Thêm tay nâng mặc định cho cửa ngang
    if (
      doorDirection === DoorDirection.HORIZONTAL &&
      !clonedModule.isSaveClone
    ) {
      const liftSupportModel = this.loadLiftSupportModel(
        door.userData.defaultLiftSupport,
        clonedModule,
        door
      );
    }

    // if (!materialType?.includes('tron')) {
    setTimeout(async () => {
      return await this.cutSpecialDoor(
        clonedModule,
        door,
        materialType,
        doorDirection,
        horizontalType
      );
    }, 200);
    // }
  }

  /**
   * Rescale bạ cửa 45
   */
  rescaleBrace(brace, clonedModule) {
    let ceilingDoor;
    if (
      clonedModule.moduleType.toLowerCase().includes('kitchen') &&
      clonedModule.moduleType.toLowerCase().includes('ceiling')
    ) {
      //Kiểm tra loại cửa đụng trần có / không bạ
      if (clonedModule.listInside?.length > 0) {
        clonedModule.listInside.forEach(inside => {
          if (inside.type === InsideType.DOOR) {
            ceilingDoor = inside.withBrace;

            return;
          }
        });
      }
    }

    // Tìm các edge meshes
    const edges = this.findEdgeMeshes(brace, clonedModule);
    const { leftEdge, rightEdge, topEdge, bottomEdge } = edges;

    if (!leftEdge || !rightEdge || !topEdge || !bottomEdge) {
      //Rescale brace cho hộc kéo trong tủ
      if (brace.userData.frameUuid) {
        let drawerModel, drawerPos;

        if (brace.userData.drawerUuid) {
          drawerModel = this.findMeshByUuid(
            clonedModule.group,
            brace.userData.drawerUuid
          );

          drawerPos = clonedModule.group.worldToLocal(
            drawerModel.getWorldPosition(new THREE.Vector3()).clone()
          );
        }

        const uDrawerBBox = new THREE.Box3().setFromObject(drawerModel);
        const uDrawerSize = new THREE.Vector3();
        uDrawerBBox.getSize(uDrawerSize);

        const braceClone = brace.clone();
        braceClone.scale.set(1, 1, 1);
        const barceOriginalSize = this.getSize(braceClone);

        brace.scale.z =
          Math.max(uDrawerSize.x, uDrawerSize.z) / barceOriginalSize.z;

        // brace.position.x =
        //   drawerModel.position.x - Math.min(uDrawerSize.x, uDrawerSize.z);
        // brace.position.y = drawerModel.position.y + uDrawerSize.y - 0.01;
        brace.position.z = drawerModel.position.z;
      }

      console.warn('Missing required edges for door scaling');
      return;
    }

    // Hàm helper để lấy position
    const getPosition = edge => {
      if (edge?.mesh) {
        return clonedModule.group.worldToLocal(
          edge.mesh.getWorldPosition(new THREE.Vector3()).clone()
        );
      } else {
        return clonedModule.group.worldToLocal(
          edge.getWorldPosition(new THREE.Vector3()).clone()
        );
      }
    };
    const typeHau = brace.userData?.typeHau;
    const isCurveModule = clonedModule.cabinetType
      .toLowerCase()
      .includes('curve');
    const isBenchModule =
      clonedModule.cabinetType.toLowerCase().includes('bench') ||
      clonedModule.cabinetType.toLowerCase().includes('tv');

    // Kiểm tra các position cần thiết
    const orientation = this.getModuleOrientation(clonedModule.group);
    const bottomPos = getPosition(bottomEdge);
    const topPos = getPosition(topEdge);
    const rightPos = getPosition(rightEdge);
    const leftPos = getPosition(leftEdge);

    // Lấy kích thước của các edges
    const sizeLeft = this.getSize(leftEdge);

    let originalSize;
    const braceClone = brace.clone();
    braceClone.scale.set(1, 1, 1);
    originalSize = this.getSize(braceClone);

    // brace.position.x =
    //   Number(clonedModule.formularController.currentDimensions.depth) / 2 -
    //   this.braceThickness -
    //   (isCurveModule ? thicknessBase : 0) -
    //   (isBenchModule ? thicknessBase : 0);

    if (!typeHau) {
      brace.position.x =
        Number(clonedModule.formularController.currentDimensions.depth) / 2 -
        (isCurveModule ? thicknessBase : 0) -
        (isBenchModule ? thicknessBase : 0) -
        this.braceThickness;
    } else if (typeHau === TypeConnerModule.SUB) {
      brace.position.z =
        clonedModule.formularController.currentOptions.rongK2 / 2 -
        Number(clonedModule.formularController.currentDimensions.depth) +
        this.braceThickness;
    } else {
      brace.position.x =
        Number(clonedModule.formularController.currentDimensions.depth) -
        clonedModule.formularController.currentOptions.rongK1 / 2 -
        this.braceThickness;
    }

    if (clonedModule.moduleType?.includes('ceiling')) {
      //Tủ đụng trần bạ cửa nắm phía dưới
      const bottomBBox = new THREE.Box3().setFromObject(
        bottomEdge?.mesh ?? bottomEdge
      );
      const bottomSize = new THREE.Vector3();
      bottomBBox.getSize(bottomSize);

      brace.position.y = bottomPos.y + bottomSize.y;

      //TRƯỜNG HỢP CỬA ĐỤNG TRẦN KHÔNG BẠ
      if (ceilingDoor === CeilingDoor.WITHOUT_BRACE) {
        brace.position.y = bottomPos.y;
      }
    } else {
      brace.position.y =
        topPos.y -
        this.braceHeight -
        (topEdge?.userData?.type === InsideType.DRAWER ||
        topEdge?.userData?.type === InsideType.ACCESSORY_DISH_LOWER
          ? topEdge?.userData?.bottomGap
          : 0);
    }

    let sizeBraceZ;
    // if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
    //   brace.position.z = leftPos.z - sizeLeft.z;
    //   sizeBraceZ = leftPos.z - rightPos.z - sizeLeft.z;
    // } else if (orientation === 'ROTATED_90' || orientation === 'ROTATED_270') {
    //   brace.position.z = leftPos.z - sizeLeft.x;
    //   sizeBraceZ = leftPos.z - rightPos.z - sizeLeft.x;
    // }

    if (typeHau === TypeConnerModule.SUB) {
      brace.position.x = leftPos.x;
      sizeBraceZ = leftPos.x - rightPos.x - Math.min(sizeLeft.x, sizeLeft.z);
    } else {
      brace.position.z = leftPos.z - Math.min(sizeLeft.x, sizeLeft.z);
      sizeBraceZ = leftPos.z - rightPos.z - Math.min(sizeLeft.x, sizeLeft.z);
    }

    // const scaleBraceX = thicknessBase / originalSize.x;
    // const scaleBraceY = this.braceHeight / originalSize.y;
    // const scaleBraceZ = sizeBraceZ / originalSize.z;

    const scaleBraceX =
      thicknessBase / Math.min(originalSize.x, originalSize.z);
    const scaleBraceY = this.braceHeight / originalSize.y;
    const scaleBraceZ = sizeBraceZ / Math.max(originalSize.x, originalSize.z);

    brace.scale.x = scaleBraceX;
    brace.scale.y = scaleBraceY;
    brace.scale.z = scaleBraceZ;

    if (ceilingDoor === CeilingDoor.WITHOUT_BRACE) {
      brace.scale.set(0, 0, 0);
    }
  }

  /**
   * Update lại vị trí của tay nắm khi rescale cửa
   */
  updateHandlePosition(door, cloneModule) {
    const orientation = this.getModuleOrientation(cloneModule.group);
    const handleModel = this.findMeshByUuid(
      cloneModule.group,
      door.userData.handleUuid
    );

    if (!handleModel) {
      this.updateLockPosition(door, cloneModule);
      return;
    }

    const doorSize = this.getSize(door);
    const handleSize = this.getSize(handleModel);

    const doorPos = cloneModule.group.worldToLocal(
      door.getWorldPosition(new THREE.Vector3()).clone()
    );

    handleModel.position.x = door.position.x + thicknessBase;
    handleModel.position.z = door.position.z + thicknessBase;

    //Vị trí cho cả tay nắm của hộc kéo
    if (door.userData.drawerUuid) {
      //Vị trí theo độ cao
      if (door.userData.materialType.includes('tron')) {
        handleModel.position.y = doorPos.y + doorSize.y / 2;
      } else {
        handleModel.position.y = doorPos.y + doorSize.y - handlePadding;
      }

      //Vị trí theo phương ngang
      if (door.userData.typeHau === TypeConnerModule.SUB) {
        handleModel.position.z = door.position.z - thicknessBase;

        // handleModel.rotation.x = Math.PI / 2;

        handleModel.position.x =
          doorPos.x - Math.max(doorSize.x, doorSize.z) / 2;
      } else {
        handleModel.rotation.x = Math.PI / 2;

        handleModel.position.z =
          doorPos.z - Math.max(doorSize.x, doorSize.z) / 2;
      }
    } else if (door.userData.doorDirection === DoorDirection.HORIZONTAL) {
      //Vị trí theo độ cao
      handleModel.position.y = doorPos.y + handlePadding;

      //Vị trí theo phương ngang
      if (door.userData.typeHau === TypeConnerModule.SUB) {
        handleModel.position.z = door.position.z - thicknessBase;

        handleModel.position.x =
          doorPos.x - Math.max(doorSize.x, doorSize.z) / 2;
      } else {
        handleModel.rotation.x = Math.PI / 2;

        handleModel.position.z =
          doorPos.z - Math.max(doorSize.x, doorSize.z) / 2;
      }
    } else {
      //Vị trí theo độ cao
      if (door.position.y > handleYRcm - handleSize.y / 2 - handlePadding) {
        handleModel.position.y =
          door.position.y + handlePadding + handleSize.y / 2;
      } else if (
        door.position.y + doorSize.y <
        handleYRcm + handleSize.y / 2 + handlePadding
      ) {
        handleModel.position.y =
          door.position.y + doorSize.y - handlePadding - handleSize.y / 2;
      } else {
        handleModel.position.y = handleYRcm;
      }

      //Set lại độ cao của tay nắm nếu nó không thuộc tủ dưới
      if (!cloneModule?.moduleType?.includes('lower')) {
        handleModel.position.y =
          door.position.y + handlePadding + handleSize.y / 2;
      }

      //Đối với tủ TV thì vị trí nằm giữa theo chiều cao
      if (cloneModule?.moduleType?.includes('tv')) {
        handleModel.position.y = door.position.y + doorSize.y / 2;
      }

      if (door.userData.typeHau === TypeConnerModule.SUB) {
        // if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
        //   if (door.userData.handlePosition.current === 'left') {
        //     handleModel.position.x = door.position.x - handlePadding;
        //   } else if (door.userData.handlePosition.current === 'right') {
        //     handleModel.position.x =
        //       door.position.x - (doorSize.x - handlePadding);
        //   }
        // } else if (
        //   orientation === 'ROTATED_90' ||
        //   orientation === 'ROTATED_270'
        // ) {
        //   if (door.userData.handlePosition.current === 'left') {
        //     handleModel.position.x = door.position.x - handlePadding;
        //   } else if (door.userData.handlePosition.current === 'right') {
        //     handleModel.position.x =
        //       door.position.x - (doorSize.z - handlePadding);
        //   }
        // }

        // Viết gọn
        handleModel.position.z = door.position.z - thicknessBase;

        if (door.userData.handlePosition.current === 'left') {
          handleModel.position.x = door.position.x - handlePadding;
        } else if (door.userData.handlePosition.current === 'right') {
          handleModel.position.x =
            door.position.x -
            (Math.max(doorSize.x, doorSize.z) - handlePadding);
        }
      } else {
        // if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
        //   if (door.userData.handlePosition.current === 'left') {
        //     handleModel.position.z = door.position.z - handlePadding;
        //   } else if (door.userData.handlePosition.current === 'right') {
        //     handleModel.position.z =
        //       door.position.z - (doorSize.z - handlePadding);
        //   }
        // } else if (
        //   orientation === 'ROTATED_90' ||
        //   orientation === 'ROTATED_270'
        // ) {
        //   if (door.userData.handlePosition.current === 'left') {
        //     handleModel.position.z = door.position.z - handlePadding;
        //   } else if (door.userData.handlePosition.current === 'right') {
        //     handleModel.position.z =
        //       door.position.z - (doorSize.x - handlePadding);
        //   }
        // }

        // Viết gọn
        if (door.userData.handlePosition.current === 'left') {
          handleModel.position.z = door.position.z - handlePadding;
        } else if (door.userData.handlePosition.current === 'right') {
          handleModel.position.z =
            door.position.z -
            (Math.max(doorSize.x, doorSize.z) - handlePadding);
        }
      }
    }

    this.updateLockPosition(door, cloneModule);
  }

  /**
   * Update lại vị trí của ổ khóa khi rescale cửa
   */
  updateLockPosition(door, cloneModule) {
    const orientation = this.getModuleOrientation(cloneModule.group);
    const handleModel = this.findMeshByUuid(
      cloneModule.group,
      door.userData.handleUuid
    );
    const lockModel = this.findMeshByUuid(
      cloneModule.group,
      door.userData.lockUuid
    );

    if (!lockModel) return;

    const doorSize = this.getSize(door);
    const handleSize = handleModel
      ? this.getSize(handleModel)
      : handleSizeDefault;

    const doorPos = cloneModule.group.worldToLocal(
      door.getWorldPosition(new THREE.Vector3()).clone()
    );

    lockModel.position.x = door.position.x + thicknessBase;

    //Vị trí cho cả tay nắm của hộc kéo
    if (door.userData.drawerUuid) {
      //Vị trí theo độ cao
      if (door.userData.materialType.includes('tron')) {
        lockModel.position.y = doorPos.y + doorSize.y - lockPadding;
      } else {
        lockModel.position.y = doorPos.y + doorSize.y - lockPadding;
      }

      //Vị trí theo phương ngang
      if (door.userData.typeHau === TypeConnerModule.SUB) {
        lockModel.position.z = door.position.z - thicknessBase;

        lockModel.position.x = doorPos.x - Math.max(doorSize.x, doorSize.z) / 2;
      } else {
        lockModel.position.z = doorPos.z - Math.max(doorSize.x, doorSize.z) / 2;
      }
    } else if (door.userData.doorDirection === DoorDirection.HORIZONTAL) {
      //Vị trí theo độ cao
      if (door.userData.materialType.includes('tron')) {
        lockModel.position.y = doorPos.y + lockPadding;
      } else {
        lockModel.position.y = doorPos.y + lockPadding;
      }

      //Vị trí theo phương ngang
      if (door.userData.typeHau === TypeConnerModule.SUB) {
        lockModel.position.z = door.position.z - thicknessBase;

        lockModel.position.x = doorPos.x - Math.max(doorSize.x, doorSize.z) / 2;
      } else {
        lockModel.position.z = doorPos.z - Math.max(doorSize.x, doorSize.z) / 2;
      }
    } else {
      //Vị trí theo độ cao
      if (
        door.position.y >
        handleYRcm - handleSize.y - handlePadding - lockPadding
      ) {
        lockModel.position.y =
          door.position.y + handlePadding + lockPadding + handleSize.y;
      } else if (
        door.position.y + doorSize.y <
        handleYRcm + handleSize.y + handlePadding + lockPadding
      ) {
        lockModel.position.y =
          door.position.y +
          doorSize.y -
          handlePadding -
          lockPadding -
          handleSize.y;
      } else {
        lockModel.position.y = handleYRcm - handleSize.y / 2 - lockPadding;
      }

      //Set lại độ cao của tay nắm nếu nó không thuộc tủ dưới
      if (!cloneModule?.moduleType?.includes('lower')) {
        lockModel.position.y =
          door.position.y + handlePadding + lockPadding + handleSize.y;
      }

      //Đối với tủ TV thì vị trí nằm giữa theo chiều cao
      if (cloneModule?.moduleType?.includes('tv')) {
        lockModel.position.y = door.position.y + doorSize.y / 2;
      }

      //Vị trí theo phương ngang
      // if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
      //   if (door.userData.handlePosition.current === 'left') {
      //     lockModel.position.z = door.position.z - handlePadding;
      //   } else if (door.userData.handlePosition.current === 'right') {
      //     lockModel.position.z = door.position.z - (doorSize.z - handlePadding);
      //   }
      // } else if (
      //   orientation === 'ROTATED_90' ||
      //   orientation === 'ROTATED_270'
      // ) {
      //   if (door.userData.handlePosition.current === 'left') {
      //     lockModel.position.z = door.position.z - handlePadding;
      //   } else if (door.userData.handlePosition.current === 'right') {
      //     lockModel.position.z = door.position.z - (doorSize.x - handlePadding);
      //   }
      // }

      //Vị trí theo phương ngang
      if (door.userData.typeHau === TypeConnerModule.SUB) {
        lockModel.position.z = door.position.z - thicknessBase;

        if (door.userData.handlePosition.current === 'left') {
          lockModel.position.x = door.position.x - handlePadding;
        } else if (door.userData.handlePosition.current === 'right') {
          lockModel.position.x =
            door.position.x -
            (Math.max(doorSize.x, doorSize.z) - handlePadding);
        }
      } else {
        if (door.userData.handlePosition.current === 'left') {
          lockModel.position.z = door.position.z - handlePadding;
        } else if (door.userData.handlePosition.current === 'right') {
          lockModel.position.z =
            door.position.z -
            (Math.max(doorSize.x, doorSize.z) - handlePadding);
        }
      }
    }
  }

  /**
   * Scale mặc định
   */
  scaleDefault(insideGltf, scaleRatios) {
    insideGltf.scale.x *= scaleRatios.x;
    insideGltf.scale.y *= scaleRatios.y;
    insideGltf.scale.z *= scaleRatios.z;
  }

  /**
   * Cập nhật lại position cho xà ngang
   */
  repositionXaNgang(insideGltf, scaleRatios, clonedModule) {
    // Giữ nguyên tỷ lệ position so với module
    insideGltf.position.y *= scaleRatios.y;
    insideGltf.position.x *= scaleRatios.x;
    // Z thường giữ nguyên vì xà ngang căn theo edges
  }

  /**
   * Cập nhật lại position cho xà dọc
   */
  repositionXaDoc(insideGltf, scaleRatios, clonedModule) {
    insideGltf.position.x *= scaleRatios.x;
    insideGltf.position.z *= scaleRatios.z;
    // Y thường giữ nguyên vì xà dọc căn theo bottom edge
  }

  /**
   * Cập nhật lại position cho kệ
   */
  repositionKe(insideGltf, scaleRatios, clonedModule) {
    insideGltf.position.x *= scaleRatios.x;
    insideGltf.position.y *= scaleRatios.y;
    // Z giữ tương đối theo left edge
  }

  /**
   * Cập nhật lại position cho phụ kiện
   */
  repositionAccessory(insideGltf, scaleRatios, clonedModule) {
    // Phụ kiện scale position theo tỷ lệ để giữ vị trí tương đối
    insideGltf.position.x *= scaleRatios.x;
    insideGltf.position.y *= scaleRatios.y;
    insideGltf.position.z *= scaleRatios.z;
  }

  repositionAccessoryGeneral(insideGltf, scaleRatios, clonedModule) {
    let leftEdge, rightEdge;
    let sizeLeft, sizeRight;
    let leftPos, rightPos;
    if (insideGltf?.userData?.leftEdgeUuid) {
      leftEdge = this.findMeshByUuid(
        clonedModule.group,
        insideGltf.userData.leftEdgeUuid
      );
      sizeLeft = this.getSizeRotation(clonedModule.group, leftEdge);
      leftPos = clonedModule.group.worldToLocal(
        leftEdge?.getWorldPosition(new THREE.Vector3()).clone()
      );
    }

    if (insideGltf?.userData?.rightEdgeUuid) {
      rightEdge = this.findMeshByUuid(
        clonedModule.group,
        insideGltf.userData.rightEdgeUuid
      );
      sizeRight = this.getSizeRotation(clonedModule.group, rightEdge);
      rightPos = clonedModule.group.worldToLocal(
        rightEdge?.getWorldPosition(new THREE.Vector3()).clone()
      );
    }
    if (leftEdge && rightEdge) {
      leftPos = clonedModule.group.worldToLocal(
        leftEdge?.getWorldPosition(new THREE.Vector3()).clone()
      );
      rightPos = clonedModule.group.worldToLocal(
        rightEdge?.getWorldPosition(new THREE.Vector3()).clone()
      );
      const newSize = this.getSizeRotation(clonedModule.group, insideGltf);
      const sizeZ = Math.abs(rightPos.z - (leftPos.z - sizeLeft.z));
      const gapLeft = (sizeZ - newSize.z) / 2;
      insideGltf.position.z = leftPos.z - sizeLeft.z - gapLeft;
    }
  }

  /**
   * Cập nhật lại position cho cửa
   */
  repositionDoor(insideGltf, scaleRatios, clonedModule) {
    // insideGltf.position.x *= scaleRatios.x;
    insideGltf.position.y *= scaleRatios.y;
    insideGltf.position.z *= scaleRatios.z;
    // Z giữ tương đối theo left edge
  }

  /**
   * Cập nhật display sau khi scale
   */
  updateCabinetDisplay(clonedModule) {
    // Tìm lại các points và edges
    if (typeof clonedModule.findPoint === 'function') {
      clonedModule.findPoint();
    }

    // Cập nhật edge lines nếu có
    if (typeof this.drawEdgeLine === 'function') {
      this.drawEdgeLine();
    }
  }

  // /**
  //  * Xoá những inside phụ thuộc khi xoá inside
  //  */
  // checkUuidExists(data, targetUuid) {
  //   if (!targetUuid) return false;

  //   const uuidFields = [
  //     'leftEdgeUuid',
  //     'rightEdgeUuid',
  //     'topEdgeUuid',
  //     'bottomEdgeUuid',
  //     'backEdgeUuid',
  //     'braceUuid',
  //     'doorUuid',
  //     'handleUuid',
  //     'lockUuid',
  //     'liftSupportUuid',
  //     'drawerUuid',
  //     'parentUDrawerUuid',
  //     'originalUuid',
  //     'originalDoorUuid',
  //   ];

  //   return uuidFields.some(field => data[field] === targetUuid);
  // }

  /**
   * Xoá những inside phụ thuộc khi xoá inside
   */
  checkUuidExists(data, targetUuid) {
    if (!targetUuid || !data) return false;

    const type = data?.type;

    let uuidFields = [
      'leftEdgeUuid',
      'rightEdgeUuid',
      'topEdgeUuid',
      'bottomEdgeUuid',
      'backEdgeUuid',
      'braceUuid',
      'doorUuid',
      'handleUuid',
      'lockUuid',
      'liftSupportUuid',
      'drawerUuid',
      'parentUDrawerUuid',
      'originalUuid',
      'originalDoorUuid',
    ];

    // loại bỏ theo type
    if (
      [
        InsideType.HPANEL,
        InsideType.HPANEL_LEFT,
        InsideType.HPANEL_RIGHT,
      ].includes(type)
    ) {
      uuidFields = uuidFields.filter(
        field => !['topEdgeUuid', 'bottomEdgeUuid'].includes(field)
      );
    }

    if (
      [InsideType.VPANEL, InsideType.VPANEL, InsideType.VCURVE].includes(type)
    ) {
      uuidFields = uuidFields.filter(
        field => !['leftEdgeUuid', 'rightEdgeUuid'].includes(field)
      );
    }

    return uuidFields.some(field => data[field] === targetUuid);
  }

  checkIsParent(data, checkChildUuid) {
    if (!checkChildUuid) return false;

    const uuidFields = [
      'braceUuid',
      'doorUuid',
      'handleUuid',
      'lockUuid',
      'liftSupportUuid',
      'mirrorUuid',
    ];

    return uuidFields.some(field => data[field] === checkChildUuid);
  }

  removeInside(inside, cloneModule, isApplyDoorAll = false) {
    if (!inside) {
      return;
    }

    const objectsToRemove = [];
    const uuidsToRemove = [];

    cloneModule.group.traverse(object => {
      if ((object.isMesh || object.isGroup) && object.uuid === inside.uuid) {
        objectsToRemove.push(object);
        uuidsToRemove.push(object.uuid);
      }
    });

    // Remove objects from scene and cleanup memory
    objectsToRemove.forEach(object => {
      if (object.parent) {
        object.parent.remove(object);
        this.disposeObject(object);
      }
    });

    // Remove from listInside in one operation (more efficient)
    if (cloneModule?.listInside && uuidsToRemove.length > 0) {
      cloneModule.listInside = cloneModule.listInside.filter(
        item => !uuidsToRemove.includes(item.gltfUuid)
      );
    }

    if (
      inside.userData?.type === InsideType.LIFT_SUPPORT ||
      inside.userData?.type === InsideType.HANDLE ||
      inside.userData?.type === InsideType.LOCK
    ) {
      return; // Tay nắm hoặc ổ khóa không xóa phụ thuộc
    }

    if (isApplyDoorAll) return; // Khi áp dụng cửa tất cả không xóa phụ thuộc

    // Gọi hàm xóa các phụ thuộc (đệ quy)
    this.removeInsideDependencies(inside, cloneModule);
    this.adjustBackPanelForHPanel(cloneModule, this.scene);
  }

  removeInsideDependencies(inside, cloneModule) {
    if (
      inside.userData?.type === InsideType.LIFT_SUPPORT ||
      inside.userData?.type === InsideType.HANDLE ||
      inside.userData?.type === InsideType.LOCK
    ) {
      return; // Tay nắm hoặc ổ khóa không xóa phụ thuộc
    }

    const objectsToRemove = [];
    const uuidsToRemove = [];

    cloneModule.group.traverse(object => {
      if (object?.userData?.type !== undefined) {
        const exists = this.checkUuidExists(object.userData, inside.uuid);
        const isParent = this.checkIsParent(inside.userData, object.uuid);

        if (exists || isParent) {
          objectsToRemove.push(object);
          uuidsToRemove.push(object.uuid);
        }
      }
    });

    // Remove objects from scene and cleanup memory
    objectsToRemove.forEach(object => {
      if (object.parent) {
        object.parent.remove(object);
        this.disposeObject(object);
      }
    });

    // Batch remove from listInside (more efficient)
    if (cloneModule?.listInside && uuidsToRemove.length > 0) {
      cloneModule.listInside = cloneModule.listInside.filter(
        item => !uuidsToRemove.includes(item.gltfUuid)
      );
    }

    // ĐỆ QUY: Xóa các phụ thuộc của các object vừa xóa
    objectsToRemove.forEach(removedObject => {
      // Gọi đệ quy để xóa các phụ thuộc của object này
      this.removeInsideDependencies(removedObject, cloneModule);
    });
  }

  disposeObject(object) {
    if (object.geometry) {
      object.geometry.dispose();
    }
    if (object.material) {
      if (Array.isArray(object.material)) {
        object.material.forEach(mat => mat.dispose());
      } else {
        object.material.dispose();
      }
    }
  }

  saveDesign(clonedModule) {
    this.referenceManager.convertAllUuidsToIdentifiers(clonedModule);
    const payload = {
      schemaVersion: 1,
      cabinet: {
        doorType: clonedModule.cabinet?.doorType,
        beveled: clonedModule.cabinet?.beveled,
        dimensions: clonedModule?.formularController?.currentDimensions || null,
      },
      modules: [],
    };

    (clonedModule.listInside || []).forEach(inside => {
      if (!inside?.gltf) return;

      // 1) Lưu vị trí + xoay local
      const localTRS = this.getLocalTRS(inside.gltf);

      // 2) Lưu kích thước “thực tế” hiện tại (local)
      const size = this.getSize(inside.gltf); // trả về {x,y,z}

      // Clean userData - bỏ các UUID fields
      const cleanedUserData = this.referenceManager.cleanUserDataForSave(
        inside.gltf.userData
      );

      payload.modules.push({
        id: inside._id,
        model: inside.glb,
        name: inside.name,
        type: inside.type,
        price: inside.price,
        dependent: inside?.dependent || null,
        doorDistance: inside?.doorDistance || null,
        thumbnail_url: inside?.thumbnail_url || null,
        // handlePosition: inside?.handlePosition || null,
        limit: inside?.limit || null,
        beveled: inside?.gltf?.userData?.beveled ?? null,
        materialType: inside?.gltf?.userData?.materialType ?? null,
        useAcrylic: inside?.gltf?.userData?.useAcrylic ?? null,
        localTRS,
        savedSize: { x: size.x, y: size.y, z: size.z }, // << lưu size, không lưu scale
        userData: cleanedUserData,
        group: inside.group,
      });
    });

    return payload;
  }

  saveInsideDesignOfModule(clonedModule) {
    this.referenceManager.convertAllUuidsToIdentifiers(clonedModule);

    const payload = {
      schemaVersion: 1,
      cabinet: {
        doorType: clonedModule.cabinet?.doorType,
        beveled: clonedModule.cabinet?.beveled,
        dimensions: clonedModule?.formularController?.currentDimensions || null,
      },
      modules: [],
    };

    (clonedModule.listInside || []).forEach(inside => {
      if (!inside?.gltf) return;

      // 1) Lưu vị trí + xoay local
      const localTRS = this.getLocalTRS(inside.gltf);

      // 2) Lưu kích thước “thực tế” hiện tại (local)
      const size = this.getSizeRotation(clonedModule.group, inside.gltf); // trả về {x,y,z}

      // Clean userData - bỏ các UUID fields
      const cleanedUserData = this.referenceManager.cleanUserDataForSave(
        inside.gltf.userData
      );

      payload.modules.push({
        id: inside._id,
        model: inside.glb,
        type: inside.type,
        dependent: inside?.dependent || null,
        doorDistance: inside?.doorDistance || null,
        thumbnail_url: inside?.thumbnail_url || null,
        // handlePosition: inside?.handlePosition || null,
        groupId: inside?.groupId || null,
        limit: inside?.gltf?.userData?.limit || null,
        default_handle: inside?.gltf?.userData?.default_handle || null,
        default_lock: inside?.gltf?.userData?.default_lock || null,
        default_glass_type: inside?.gltf?.userData?.default_glass_type || null,
        beveled: inside?.gltf?.userData?.beveled ?? null,
        materialType: inside?.gltf?.userData?.materialType ?? null,
        useAcrylic: inside?.gltf?.userData?.useAcrylic ?? null,
        localTRS,
        savedSize: { x: size.x, y: size.y, z: size.z }, // << lưu size, không lưu scale
        userData: cleanedUserData,
        // group: inside?.group,
        name: inside?.name,
        price: inside?.price,
        material: inside?.material,
      });
    });

    return payload;
  }

  async ensureAccessories(productType) {
    if (!productType)
      return {
        byId: new Map(),
        byName: new Map(),
        byType: new Map(),
        insides: [], // Thêm insides mặc định
      };

    if (!this.accessoryCache) this.accessoryCache = new Map();

    if (this.accessoryCache.has(productType)) {
      return this.accessoryCache.get(productType);
    }

    // const resp = await callApi({
    //   endpoint: `modules/v3/inside-accessories?product_type=${productType}`,
    //   method: 'get',
    // });

    const resp = await callApi({
      endpoint: `modules/v3/inside-accessories`,
      method: 'get',
      params: {
        product_type: productType,
        country_code: process.env.REACT_APP_COUNTRY_CODE,
      },
    });

    const list = resp?.data?.result?.data?.accessories ?? [];
    const insides = resp?.data?.result?.data?.insides ?? []; // Lấy insides

    const byId = new Map();
    const byName = new Map();
    const byType = new Map();

    for (const a of list) {
      if (a?.id != null) byId.set(String(a.id), a);
      if (a?.name) byName.set(a.name, a);
      if (a?.type != null) {
        const arr = byType.get(a.type) || [];
        arr.push(a);
        byType.set(a.type, arr);
      }
    }

    const pack = { byId, byName, byType, insides }; // Thêm insides vào pack
    this.accessoryCache.set(productType, pack);
    return pack;
  }

  pickAccessoryFor(m, accMap) {
    if (!accMap) return null;
    const byId = accMap.byId?.get(String(m.id));
    if (byId) return byId;

    const byName = m.name ? accMap.byName?.get(m.name) : null;
    if (byName) return byName;

    const byTypeArr = accMap.byType?.get(m.type);
    return byTypeArr && byTypeArr.length ? byTypeArr[0] : null;
  }

  async ensureDoor(productType) {
    if (!productType)
      return {
        byId: new Map(),
        byMaterialType: new Map(),
      };

    if (!this.doorCache) this.doorCache = new Map();

    if (this.doorCache.has(productType)) {
      return this.doorCache.get(productType);
    }

    const response = await callApi({
      endpoint: `modules/v3/door-and-handles`,
      method: 'get',
      params: { product_type: productType, door_type: 'HINGED' },
    });

    const merged = [
      ...response?.data?.result?.data?.door_with_handle,
      ...response?.data?.result?.data?.door_without_handle,
    ];
    const list = merged ?? [];

    const byId = new Map();
    const byMaterialType = new Map();

    for (const a of list) {
      if (a?.id != null) byId.set(String(a.id), a);
      if (a?.name) byMaterialType.set(a.material_type, a);
    }

    const pack = { byId, byMaterialType };
    this.doorCache.set(productType, pack);
    return pack;
  }

  pickDoorFor(m, doorMap) {
    if (!doorMap) return null;
    const byId = doorMap.byId?.get(String(m.id));
    if (byId) return byId;

    const byMaterialType = m.materialType
      ? doorMap.byMaterialType?.get(m.materialType)
      : null;
    if (byMaterialType) return byMaterialType;
  }

  async loadInsideDesignOfModule(
    payload,
    moduleInstance,
    currentCabinet,
    productType
  ) {
    if (!payload || !Array.isArray(payload.modules)) return;

    const pt =
      (moduleInstance?.moduleData?.product_type_id ||
        moduleInstance?.moduleData?.product_type) ??
      null;

    const accMap = await this.ensureAccessories(pt);
    const doorMap = await this.ensureDoor(pt);
    const insides = accMap.insides || [];

    const insidesById = new Map();
    const insidesByName = new Map();

    for (const inside of insides) {
      if (inside?.id) insidesById.set(String(inside.id), inside);
      if (inside?.name) insidesByName.set(inside.name, inside);
    }

    // Load tất cả modules
    const tasks = payload.modules.map(async m => {
      const acc = this.pickAccessoryFor(m, accMap);
      const door = this.pickDoorFor(m, doorMap);

      const insideModel =
        insidesById.get(String(m.id)) || insidesByName.get(m.name) || null;

      const insideData = {
        id: m.id,
        model: insideModel?.model ?? acc?.model ?? m.model,
        price: acc?.price ?? m.price,
        type: m.type,
        name: m.name,
        localTRS: m.localTRS,
        savedSize: m.savedSize,
        limit: m.userData.limit,
        doorDistance: m.doorDistance,
        groupId: m.groupId,
        thumbnail_url: m.thumbnail_url,
        dependent: m.dependent,
        beveled: m.userData.beveled,
        materialType: door?.material_type ?? m.userData.materialType,
        useAcrylic: door?.use_acrylic ?? m.userData.useAcrylic,
        userData: m.userData, // Có identifier fields
        group: acc?.group || m?.group || null,
      };

      if (!insideData.model) return;

      await this.loadRealModel(
        insideData,
        null,
        moduleInstance,
        currentCabinet
      );

      // Convert Identifier -> UUID và rebuild dependencies
      this.referenceManager.convertAllIdentifiersToUuids(moduleInstance);
    });

    await Promise.all(tasks);
    this.adjustBackPanelForHPanel(moduleInstance, this.scene);
  }

  //
  //  Tính khoảng cách giữa mesh đến 4 mặt gần nhất và vẽ line
  //

  // Tính khoảng cách từ mesh đến cạnh edge
  calculateDistanceToEdge = ({ mesh, edge, orientation }) => {
    if (!mesh || !edge) return;
    const boxMesh = new THREE.Box3().setFromObject(mesh);
    const boxEdge = new THREE.Box3().setFromObject(edge);
    if (boxMesh.intersectsBox(boxEdge)) return 0;
    const edgeCenter = boxEdge.getCenter(new THREE.Vector3());
    const edgeSize = boxEdge.getSize(new THREE.Vector3());
    const meshCenter = boxMesh.getCenter(new THREE.Vector3());
    const meshSize = boxMesh.getSize(new THREE.Vector3());

    const dx = Math.max(
      0,
      Math.max(boxMesh.min.x - boxEdge.max.x, boxEdge.min.x - boxMesh.max.x)
    );
    const dy = Math.max(
      0,
      Math.max(boxMesh.min.y - boxEdge.max.y, boxEdge.min.y - boxMesh.max.y)
    );
    const dz = Math.max(
      0,
      Math.max(boxMesh.min.z - boxEdge.max.z, boxEdge.min.z - boxMesh.max.z)
    );

    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const faceData = [
      {
        normal: new THREE.Vector3(0, 0, 1),
        offset: meshSize.z / 2,
      },
      {
        normal: new THREE.Vector3(0, 0, -1),
        offset: meshSize.z / 2,
      },
      {
        normal: new THREE.Vector3(0, 1, 0),
        offset: meshSize.y / 2,
      },
      {
        normal: new THREE.Vector3(0, -1, 0),
        offset: meshSize.y / 2,
      },
      {
        normal: new THREE.Vector3(1, 0, 0),
        offset: meshSize.x / 2,
      },
      {
        normal: new THREE.Vector3(-1, 0, 0),
        offset: meshSize.x / 2,
      },
    ];

    const face = faceData.find(item =>
      nearlyEqualVector(item.normal, orientation)
    );
    const planePoint = meshCenter
      .clone()
      .add(face.normal.clone().multiplyScalar(face.offset));
    const targetPoint = planePoint
      .clone()
      .add(face.normal.clone().multiplyScalar(distance));

    if (lessOrEqual(distance, 0)) {
      return {
        distance: 0,
        endpoints: [planePoint, targetPoint],
        module: edge,
      };
    }
    return {
      distance,
      endpoints: [planePoint, targetPoint],
      module: edge,
    };
  };

  /**
   * Tâm mặt của bounding box theo một hướng (vector đơn vị theo trục)
   */
  getFaceCenterByVector(box, vector) {
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const half =
      (Math.abs(vector.x) * size.x +
        Math.abs(vector.y) * size.y +
        Math.abs(vector.z) * size.z) /
      2;

    return center.addScaledVector(vector, half);
  }

  /**
   * Bounding box world của một mesh point (đã tính sẵn trong findMeshPoints)
   */
  getMeshPointBox(meshPoint) {
    return new THREE.Box3(
      new THREE.Vector3(meshPoint.mX.min, meshPoint.mY.min, meshPoint.mZ.min),
      new THREE.Vector3(meshPoint.mX.max, meshPoint.mY.max, meshPoint.mZ.max)
    );
  }

  /**
   * Khoảng cách từ mesh tới một edge theo hướng chỉ định, tính theo cách của
   * PositionController: bắn ray từ tâm bounding box qua tâm mặt tương ứng rồi
   * lấy giao điểm với bounding box của edge.
   * Trả về cùng shape với calculateDistanceToEdge để dùng thay thế trực tiếp.
   */
  rayDistanceToEdge({ mesh, edge, orientation }) {
    if (!mesh || !edge) return;

    const box = new THREE.Box3().setFromObject(mesh);
    const center = box.getCenter(new THREE.Vector3());
    const faceCenter = this.getFaceCenterByVector(box, orientation);

    const hit = new THREE.Ray(
      center,
      orientation.clone().normalize()
    ).intersectBox(new THREE.Box3().setFromObject(edge), new THREE.Vector3());

    if (!hit) return;

    // Giao điểm nằm hẳn phía trong mặt đang đo => hai khối đang chồng nhau
    if (
      center.distanceTo(hit) <
      center.distanceTo(faceCenter) - RAY_DISTANCE_TOLERANCE
    ) {
      return {
        distance: 0,
        endpoints: [faceCenter, faceCenter.clone()],
        module: edge,
      };
    }

    return {
      distance: faceCenter.distanceTo(hit),
      endpoints: [faceCenter, hit],
      module: edge,
    };
  }

  /**
   * Kiểm tra child có phải hậu duệ (hoặc chính) ancestor trong cây Object3D
   */
  isDescendantOf(child, ancestor) {
    let current = child;
    while (current) {
      if (current === ancestor) return true;
      current = current.parent;
    }
    return false;
  }

  /**
   * Tìm mesh gần nhất quanh mesh đang chọn theo cả 6 hướng của bounding box.
   * Chỉ xét các mesh thuộc cùng module (module.group) với mesh đang chọn –
   * không lấy mốc từ module khác trong cùng cabinet.
   * Hướng lấy thẳng từ bounding box nên không cần bộ finder riêng cho từng
   * orientation; orientation chỉ dùng để đặt tên trái/phải/trước/sau.
   * @returns {Object} { leftEdge, rightEdge, topEdge, bottomEdge, frontEdge, backEdge }
   */
  findEdgesAroundMesh({
    mesh,
    meshPoints,
    ids,
    orientation,
    sideOrientation,
    module,
  }) {
    if (!mesh || !meshPoints) return {};

    const moduleGroup = module?.group ?? module;

    const { leftVector, rightVector } = this.getSideAxisInfo(
      sideOrientation ?? orientation
    );
    const { frontVector, backVector } = this.getDepthAxisInfo(orientation);

    // Phụ kiện / trang trí không bao giờ là mốc định vị
    const accessoryTypes = [
      InsideType.HANDLE,
      InsideType.CLOTHES_HANGER,
      InsideType.LED_RECESSED,
      InsideType.LED_CORNER,
      InsideType.LED_EFFECT,
    ];
    // Cánh cửa chỉ dùng làm mốc cho mặt trước / mặt sau
    const structuralTypes = [
      ...accessoryTypes,
      InsideType.DOOR,
      InsideType.BRACE,
    ];

    const directions = [
      {
        key: 'topEdge',
        vector: new THREE.Vector3(0, 1, 0),
        exclude: structuralTypes,
      },
      {
        key: 'bottomEdge',
        vector: new THREE.Vector3(0, -1, 0),
        exclude: structuralTypes,
      },
      { key: 'leftEdge', vector: leftVector, exclude: structuralTypes },
      { key: 'rightEdge', vector: rightVector, exclude: structuralTypes },
      { key: 'frontEdge', vector: frontVector, exclude: accessoryTypes },
      { key: 'backEdge', vector: backVector, exclude: accessoryTypes },
    ];

    const box = new THREE.Box3().setFromObject(mesh);
    const center = box.getCenter(new THREE.Vector3());

    const candidates = meshPoints.filter(
      pt =>
        pt?.mesh &&
        pt.mesh.visible !== false &&
        !ids?.includes(pt.mesh.uuid) &&
        (!moduleGroup || this.isDescendantOf(pt.mesh, moduleGroup)) &&
        !pt.mesh.userData.isHelperBox
    );

    const result = {};

    directions.forEach(({ key, vector, exclude }) => {
      const faceCenter = this.getFaceCenterByVector(box, vector);
      const distanceToFace = center.distanceTo(faceCenter);
      const ray = new THREE.Ray(center, vector.clone().normalize());
      const target = new THREE.Vector3();

      let closest;

      candidates.forEach(pt => {
        if (exclude.includes(pt.mesh.userData?.type)) return;

        const hit = ray.intersectBox(this.getMeshPointBox(pt), target);
        if (!hit) return;

        // Bỏ qua khối đang chồng lên mesh (giao điểm nằm trong lòng mesh)
        if (center.distanceTo(hit) < distanceToFace - RAY_DISTANCE_TOLERANCE)
          return;

        const distance = faceCenter.distanceTo(hit);
        if (!closest || distance < closest.distance) {
          closest = { edge: pt, distance };
        }
      });

      result[key] = closest?.edge;
    });

    return result;
  }

  /**
   * Trục chiều sâu (mặt trước / mặt sau) của module theo orientation.
   * Quy ước: trục +X trong local của module luôn hướng ra mặt trước
   * (tấm hậu nằm ở phía local X nhỏ), nên hướng mặt trước trong world là:
   * NORMAL: +X, ROTATED_180: -X, ROTATED_90: -Z, ROTATED_270: +Z.
   */
  getDepthAxisInfo(orientation) {
    switch (orientation) {
      case 'ROTATED_90':
        return {
          depthAxis: 'z',
          frontSign: -1,
          frontVector: new THREE.Vector3(0, 0, -1),
          backVector: new THREE.Vector3(0, 0, 1),
        };
      case 'ROTATED_270':
        return {
          depthAxis: 'z',
          frontSign: 1,
          frontVector: new THREE.Vector3(0, 0, 1),
          backVector: new THREE.Vector3(0, 0, -1),
        };
      case 'ROTATED_180':
        return {
          depthAxis: 'x',
          frontSign: -1,
          frontVector: new THREE.Vector3(-1, 0, 0),
          backVector: new THREE.Vector3(1, 0, 0),
        };
      default:
        return {
          depthAxis: 'x',
          frontSign: 1,
          frontVector: new THREE.Vector3(1, 0, 0),
          backVector: new THREE.Vector3(-1, 0, 0),
        };
    }
  }

  /**
   * Trục ngang (trái / phải) theo quy ước trái/phải sẵn có của hệ thống
   * (findClosestZEdges / R270 / R90) – phần còn lại của code (ràng buộc
   * minPosZ/maxPosZ, minPosX/maxPosX, createBoxBorder, dependent scaling)
   * đều bám theo quy ước này nên không đổi:
   *  - NORMAL / ROTATED_180 / khác: trục Z, bên trái hướng +Z
   *  - ROTATED_270 (và ROTATED_90 khi caller quy về 270): trục X, trái hướng -X
   *  - ROTATED_90 (module góc phụ SUB): trục X, bên trái hướng +X
   */
  getSideAxisInfo(orientation) {
    switch (orientation) {
      case 'ROTATED_90':
        return {
          sideAxis: 'x',
          leftSign: 1,
          leftVector: new THREE.Vector3(1, 0, 0),
          rightVector: new THREE.Vector3(-1, 0, 0),
        };
      case 'ROTATED_270':
        return {
          sideAxis: 'x',
          leftSign: -1,
          leftVector: new THREE.Vector3(-1, 0, 0),
          rightVector: new THREE.Vector3(1, 0, 0),
        };
      default:
        return {
          sideAxis: 'z',
          leftSign: 1,
          leftVector: new THREE.Vector3(0, 0, 1),
          rightVector: new THREE.Vector3(0, 0, -1),
        };
    }
  }

  /**
   * Tìm cạnh mặt trước / mặt sau gần nhất của mesh theo trục chiều sâu
   */
  findFrontBackEdges(
    ids,
    meshPoints,
    refPoint,
    orientation,
    excludeTypes = []
  ) {
    if (!meshPoints || !refPoint) return {};

    const { depthAxis, frontSign } = this.getDepthAxisInfo(orientation);

    return EdgeDetector.findClosestFrontBackEdges(
      ids,
      meshPoints,
      refPoint,
      depthAxis,
      frontSign,
      excludeTypes
    );
  }

  /**
   * Tìm cạnh trái / cạnh phải gần nhất của mesh theo trục ngang
   */
  findLeftRightEdges(
    ids,
    meshPoints,
    refPoint,
    orientation,
    excludeTypes = null,
    moveType = null
  ) {
    if (!meshPoints || !refPoint) return {};

    const { sideAxis, leftSign } = this.getSideAxisInfo(orientation);

    return EdgeDetector.findClosestLeftRightEdges(
      ids,
      meshPoints,
      refPoint,
      sideAxis,
      leftSign,
      excludeTypes,
      moveType
    );
  }

  /**
   * Khoảng chiếm chỗ của một box trên trục chiều sâu (giá trị tăng dần về mặt trước)
   */
  getDepthRange(box, frontVector) {
    const a = box.min.dot(frontVector);
    const b = box.max.dot(frontVector);

    return { min: Math.min(a, b), max: Math.max(a, b) };
  }

  /**
   * Di chuyển mesh theo chiều sâu của module (trục local X).
   * Chỉ di chuyển khi không va chạm mặt trước / mặt sau. Nếu phía trước không có
   * tấm/cánh cửa thì lấy biên của module làm giới hạn thay vì chặn di chuyển.
   * @returns {boolean} true nếu mesh đã được di chuyển
   */
  moveMeshAlongDepth({
    mesh,
    point,
    module,
    meshPoints,
    ids,
    frontEdge,
    backEdge,
    depthOrientation,
  }) {
    const group = module?.group ?? module;
    if (!mesh || !point || !group?.worldToLocal) return false;

    const targetLocal = group.worldToLocal(point.clone());
    const currentLocal = group.worldToLocal(
      mesh.getWorldPosition(new THREE.Vector3())
    );
    const deltaLocal = targetLocal.x - currentLocal.x;
    if (nearlyEqual(deltaLocal, 0)) return false;

    const { frontVector } = this.getDepthAxisInfo(depthOrientation);

    // Quãng dịch chuyển trên trục chiều sâu trong world space
    const deltaDepth = point
      .clone()
      .sub(mesh.getWorldPosition(new THREE.Vector3()))
      .dot(frontVector);

    const edgeRange = edge =>
      edge
        ? this.getDepthRange(
            new THREE.Box3(
              new THREE.Vector3(edge.mX.min, edge.mY.min, edge.mZ.min),
              new THREE.Vector3(edge.mX.max, edge.mY.max, edge.mZ.max)
            ),
            frontVector
          )
        : undefined;

    const meshRange = this.getDepthRange(
      new THREE.Box3().setFromObject(mesh),
      frontVector
    );
    const nextMin = meshRange.min + deltaDepth;
    const nextMax = meshRange.max + deltaDepth;

    // Biên của module (bỏ qua chính mesh đang di chuyển) – dùng khi thiếu cạnh
    let moduleMin;
    let moduleMax;
    meshPoints?.forEach(pt => {
      if (ids?.includes(pt.mesh.uuid)) return;

      const range = edgeRange(pt);
      moduleMin =
        moduleMin === undefined ? range.min : Math.min(moduleMin, range.min);
      moduleMax =
        moduleMax === undefined ? range.max : Math.max(moduleMax, range.max);
    });

    const backLimit = edgeRange(backEdge)?.max ?? moduleMin;
    const frontLimit = edgeRange(frontEdge)?.min ?? moduleMax;

    const hitBack =
      backLimit !== undefined && !greaterOrEqual(nextMin, backLimit);
    const hitFront =
      frontLimit !== undefined && !lessOrEqual(nextMax, frontLimit);

    if (hitBack || hitFront) {
      openMessageError({ content: i18n.t('Invalid Position!') });
      return false;
    }

    mesh.position.x += deltaLocal;
    mesh.updateMatrixWorld(true);

    return true;
  }

  distanceMeshToEdges({
    mesh,
    currentCabinet,
    prevPos,
    bottomEdge,
    topEdge,
    leftEdge,
    rightEdge,
    frontEdge,
    backEdge,
    orientation,
    axisOrientation = orientation,
    sideOrientation = axisOrientation,
    module,
  }) {
    if (!mesh) return;
    if (
      mesh.userData.type === InsideType.DOOR_CLONE ||
      mesh.userData.type === InsideType.MIRROR_DOOR
    )
      return;

    const vectorY = new THREE.Vector3(0, 1, 0);
    const oppositeVectorY = new THREE.Vector3(0, -1, 0);

    // Trái / phải nằm trên trục ngang, trước / sau nằm trên trục chiều sâu
    const { leftVector, rightVector } = this.getSideAxisInfo(sideOrientation);
    const { frontVector, backVector } = this.getDepthAxisInfo(axisOrientation);

    const distanceToTop = this.rayDistanceToEdge({
      mesh,
      edge: topEdge?.mesh,
      orientation: vectorY,
    });
    const distanceToBottom = this.rayDistanceToEdge({
      mesh,
      edge: bottomEdge?.mesh,
      orientation: oppositeVectorY,
    });
    const distanceToLeft = this.rayDistanceToEdge({
      mesh,
      edge: leftEdge?.mesh,
      orientation: leftVector,
    });
    const distanceToRight = this.rayDistanceToEdge({
      mesh,
      edge: rightEdge?.mesh,
      orientation: rightVector,
    });

    const distanceToFront = this.rayDistanceToEdge({
      mesh,
      edge: frontEdge?.mesh,
      orientation: frontVector,
    });
    const distanceToBack = this.rayDistanceToEdge({
      mesh,
      edge: backEdge?.mesh,
      orientation: backVector,
    });

    this.clearLine();

    this.clearLabel();

    const edges = [
      { vector: vectorY, opposite: oppositeVectorY, distance: distanceToTop },
      {
        vector: oppositeVectorY,
        opposite: vectorY,
        distance: distanceToBottom,
      },
      { vector: leftVector, opposite: rightVector, distance: distanceToLeft },
      { vector: rightVector, opposite: leftVector, distance: distanceToRight },
      { vector: frontVector, opposite: backVector, distance: distanceToFront },
      { vector: backVector, opposite: frontVector, distance: distanceToBack },
    ];

    edges.forEach(({ vector, distance }) => {
      if (!nearlyEqual(distance?.distance, 0)) {
        this.createDistanceLine(vector, distance);
        this.createDistanceLabel(
          vector,
          distance,
          mesh,
          currentCabinet,
          prevPos,
          module,
          axisOrientation
        );
      }
    });
  }

  clearLine() {
    this.distanceLines.forEach(({ line, startT, endT }) => {
      this.scene.scene.remove(line);
      this.scene.scene.remove(startT);
      this.scene.scene.remove(endT);
    });
    this.distanceLines.clear();
  }

  clearLabel() {
    this.distanceLabels.forEach(label => {
      this.scene.scene.remove(label);
    });
    this.distanceLabels.clear();
  }

  /**
   * Tạo một đường line cho khoảng cách theo hướng cụ thể
   */
  createDistanceLine(direction, distanceInfo) {
    if (!distanceInfo?.endpoints || distanceInfo?.endpoints.length < 2)
      return null;

    const [start, end] = distanceInfo.endpoints;

    // Line chính
    const mainLineGeometry = new THREE.BufferGeometry().setFromPoints([
      start,
      end,
    ]);
    const material = new THREE.LineBasicMaterial({ color: 0x200000 });
    const line = new THREE.Line(mainLineGeometry, material);

    // Tạo đầu T
    const tSize = 0.05; // kích thước "T"
    const dir = new THREE.Vector3().subVectors(end, start).normalize();
    const perp = new THREE.Vector3(-dir.z, dir.x, dir.y).multiplyScalar(tSize); // vector vuông góc

    const startTGeom = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3().addVectors(start, perp),
      new THREE.Vector3().subVectors(start, perp),
    ]);

    const endTGeom = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3().addVectors(end, perp),
      new THREE.Vector3().subVectors(end, perp),
    ]);

    const startT = new THREE.Line(startTGeom, material);
    const endT = new THREE.Line(endTGeom, material);

    this.scene.scene.add(line, startT, endT);
    this.distanceLines.set(direction, { line, startT, endT });

    return { line, startT, endT };
  }

  /**
   * Tạo CSS2D label hiển thị giá trị khoảng cách
   */
  /**
   * Tạo CSS2D label hiển thị giá trị khoảng cách
   */
  createDistanceLabel(
    direction,
    distanceInfo,
    mesh,
    currentCabinet,
    prevPos,
    module,
    axisOrientation
  ) {
    if (
      !distanceInfo?.endpoints ||
      distanceInfo?.endpoints.length < 2 ||
      distanceInfo.distance === 0
    ) {
      return null;
    }

    const [startPoint, endPoint] = distanceInfo.endpoints;
    const midPoint = startPoint.clone().add(endPoint).multiplyScalar(0.5);

    if (
      mesh?.userData?.type === InsideType.HPANEL &&
      ['kitchen', 'dry'].some(item => module.moduleType.includes(item))
    ) {
      midPoint.y = midPoint.y + 0.15;
      // midPoint.x = midPoint.x + 0.5;
      midPoint.z = midPoint.z + 0.15;
    }
    if (
      mesh?.userData?.type === InsideType.VPANEL &&
      ['wardrobe'].some(item => module.moduleType.includes(item))
    ) {
      midPoint.y = midPoint.y + 0.15;
      // midPoint.x = midPoint.x + 0.5;
    }

    // Tạo div element cho label
    const labelDiv = document.createElement('div');
    labelDiv.className = 'distance-label new';
    labelDiv.textContent = `${roundTo(distanceInfo.distance * 1000, 0)} mm`;
    labelDiv.title = 'Double click to edit';

    labelDiv.dataset.distance = distanceInfo.distance;
    labelDiv.dataset.moduleId = distanceInfo.module?.uuid || '';
    labelDiv.dataset.editing = 'false';

    // Single click - highlight
    labelDiv.addEventListener('click', e => {
      e.stopPropagation();
      if (labelDiv.dataset.editing === 'false') {
        // this.setLabelActive(direction, !labelDiv.classList.contains('active'));
      }
    });

    // Double click - edit mode
    labelDiv.addEventListener('dblclick', e => {
      e.stopPropagation();
      this.enableEditMode(
        labelDiv,
        direction,
        distanceInfo,
        mesh,
        currentCabinet,
        prevPos,
        module,
        axisOrientation
      );
    });

    // Tạo CSS2DObject
    const label = new CSS2DObject(labelDiv);
    label.position.copy(midPoint);
    label.center?.set(0.5, 0.5);

    label.userData = {
      direction: direction,
      distance: distanceInfo.distance,
      targetModuleId: distanceInfo.module?.uuid || null,
      type: 'distanceLabel',
      targetPoints: distanceInfo.endpoints,
    };

    setTimeout(() => {
      labelDiv.classList.remove('new');
    }, 300);

    this.scene.scene.add(label);
    this.distanceLabels.set(direction, label);

    return label;
  }

  /**
   * Enable edit mode cho label
   */
  enableEditMode(
    labelDiv,
    direction,
    distanceInfo,
    mesh,
    currentCabinet,
    prevPos,
    module,
    axisOrientation
  ) {
    if (labelDiv.dataset.editing === 'true') return;

    labelDiv.dataset.editing = 'true';
    const currentValue = roundTo(
      parseFloat(labelDiv.dataset.distance) * 1000,
      0
    );
    const originalContent = labelDiv.textContent;

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'distance-input';
    input.value = roundTo(Math.abs(currentValue));
    input.placeholder = 'Nhập';

    labelDiv.classList.add('editing');
    labelDiv.textContent = '';
    labelDiv.appendChild(input);

    input.focus();
    input.select();

    const validateAndApply = () => {
      let newValue = parseFloat(input.value) / 1000;
      if (
        getInputType(input.value) === 'expression' &&
        isValidExpression(input.value)
      ) {
        newValue = roundTo(Math.abs(evaluateExpression(input.value))) / 1000;
      }
      if (isNaN(newValue) || newValue < 0) {
        input.classList.add('invalid');
        setTimeout(() => input.classList.remove('invalid'), 300);
        return false;
      }

      this.applyNewDistance(
        direction,
        newValue,
        labelDiv,
        distanceInfo,
        mesh,
        currentCabinet,
        prevPos,
        module,
        axisOrientation
      );
      return true;
    };

    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (validateAndApply()) {
          this.disableEditMode(labelDiv, direction);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.disableEditMode(labelDiv, direction, originalContent);
      }
    });

    input.addEventListener('blur', e => {
      setTimeout(() => {
        if (labelDiv.dataset.editing === 'true') {
          if (!validateAndApply()) {
            this.disableEditMode(labelDiv, direction, originalContent);
          } else {
            this.disableEditMode(labelDiv, direction);
          }
        }
      }, 100);
    });

    input.addEventListener('click', e => e.stopPropagation());
    input.addEventListener('dblclick', e => e.stopPropagation());
  }

  /**
   * Disable edit mode và return về label normal
   */
  disableEditMode(labelDiv, direction, content = null) {
    if (labelDiv.dataset.editing === 'false') return;

    labelDiv.dataset.editing = 'false';
    labelDiv.classList.remove('editing');

    if (content) {
      labelDiv.textContent = content;
    } else {
      const distance = parseFloat(labelDiv.dataset.distance);
      labelDiv.textContent = `${roundTo(distance * 1000, 0)}m`;
    }

    labelDiv.classList.add('new');
    setTimeout(() => labelDiv.classList.remove('new'), 300);
  }

  /**
   * Apply new distance value và update module position
   */
  applyNewDistance(
    direction,
    newDistance,
    labelDiv,
    distanceInfo,
    mesh,
    currentCabinet,
    prevPos,
    module,
    axisOrientation
  ) {
    if (!distanceInfo.endpoints || distanceInfo.endpoints.length < 2) {
      return null;
    }

    const [_, targetPoint] = distanceInfo.endpoints;
    const meshBox = new THREE.Box3().setFromObject(mesh);
    const meshSize = meshBox.getSize(new THREE.Vector3());

    // Mặt trước / mặt sau: mesh chỉ trượt trên trục chiều sâu nên tính theo
    // chênh lệch khoảng cách, không phụ thuộc gốc toạ độ của từng loại inside
    const { depthAxis, frontVector, backVector } =
      this.getDepthAxisInfo(axisOrientation);

    if (
      nearlyEqualVector(direction, frontVector) ||
      nearlyEqualVector(direction, backVector)
    ) {
      const depthKey = depthAxis === 'x' ? 'x' : 'z';
      const newMeshPosition = mesh.getWorldPosition(new THREE.Vector3());
      newMeshPosition[depthKey] +=
        (distanceInfo.distance - newDistance) * direction[depthKey];

      this.moveMesh(
        mesh,
        newMeshPosition,
        mesh.position.clone(),
        this.scene,
        currentCabinet,
        module,
        { axis: 'depth' }
      );
      this.scene?.removeWireframeBoxes?.();
      this.scene?.createWireframeBox?.(mesh);
      this.scene?.handleDrawBorder();
      return;
    }

    const newMeshPosition = targetPoint.clone();

    // Tùy theo hướng, di chuyển mesh
    if (nearlyEqualVector(direction, new THREE.Vector3(0, 1, 0))) {
      // Đo từ mặt trên xuống edge -> di chuyển mesh lên/xuống
      newMeshPosition.y -= newDistance; // Di chuyển ngược chiều để tăng khoảng cách
    } else if (nearlyEqualVector(direction, new THREE.Vector3(0, -1, 0))) {
      // Đo từ mặt dưới lên edge -> di chuyển mesh lên/xuống
      newMeshPosition.y += newDistance;
    } else if (nearlyEqualVector(direction, new THREE.Vector3(1, 0, 0))) {
      // Đo từ mặt trước ra edge -> di chuyển mesh tiến/lùi
      newMeshPosition.x -= newDistance;
    } else if (nearlyEqualVector(direction, new THREE.Vector3(-1, 0, 0))) {
      // Đo từ mặt sau ra edge -> di chuyển mesh tiến/lùi
      newMeshPosition.x += newDistance;
    } else if (nearlyEqualVector(direction, new THREE.Vector3(0, 0, 1))) {
      // Đo từ mặt trước ra edge -> di chuyển mesh tiến/lùi
      newMeshPosition.z -= newDistance;
    } else if (nearlyEqualVector(direction, new THREE.Vector3(0, 0, -1))) {
      // Đo từ mặt sau ra edge -> di chuyển mesh tiến/lùi
      newMeshPosition.z += newDistance;
    }

    const testDistance = newMeshPosition.distanceTo(targetPoint);

    // Chỉ di chuyển nếu tính toán hợp lý
    if (nearlyEqual(testDistance, newDistance)) {
      console.log('✓ Calculation looks correct, applying move...');

      if (nearlyEqualVector(direction, new THREE.Vector3(0, 1, 0))) {
        newMeshPosition.y -= meshSize.y;
      }
      if (nearlyEqualVector(direction, new THREE.Vector3(0, 0, -1))) {
        newMeshPosition.z += meshSize.z;
      }
      if (nearlyEqualVector(direction, new THREE.Vector3(1, 0, 0))) {
        newMeshPosition.x -= meshSize.x;
      }

      this.moveMesh(
        mesh,
        newMeshPosition,
        mesh.position.clone(),
        this.scene,
        currentCabinet,
        module
      );
      this.scene?.removeWireframeBoxes?.();
      this.scene?.createWireframeBox?.(mesh);
      this.scene?.handleDrawBorder();
    } else {
      console.log('✗ Calculation error, not moving mesh');
      console.log('Please check the logic!');
    }
  }

  /**
   * Cập nhật lại hàm resetEdgeDependencies để include cleanup
   */
  /**
   * Thêm vào InsideLoader.js - Clean up các dependency cũ trước khi reset
   * @param {Object} clonedModule - Module cần clean up
   */
  cleanupOldDependencies(clonedModule) {
    if (!clonedModule?.group) return;

    console.log('Cleaning up old dependencies...');

    // Traverse qua tất cả mesh trong group
    clonedModule.group.traverse(child => {
      if (child.isMesh && child.userData) {
        // Clean up edge UUIDs
        delete child.userData.leftEdgeUuid;
        delete child.userData.rightEdgeUuid;
        delete child.userData.topEdgeUuid;
        delete child.userData.bottomEdgeUuid;

        // Clean up dependent arrays
        if (child.userData.leftDependent) {
          child.userData.leftDependent = [];
        }
        if (child.userData.rightDependent) {
          child.userData.rightDependent = [];
        }
        if (child.userData.topDependent) {
          child.userData.topDependent = [];
        }
        if (child.userData.bottomDependent) {
          child.userData.bottomDependent = [];
        }
      }
    });

    console.log('Old dependencies cleaned up');
  }

  resetEdgeDependenciesAfterSave(clonedModule, currentCabinet, mesh) {
    if (!clonedModule?.listInside || clonedModule.listInside.length === 0) {
      return;
    }
    // Clean up old dependencies trước
    // this.cleanupOldDependencies(clonedModule);

    // Lấy mesh points của cabinet
    const meshPoints = currentCabinet.getActiveMeshPoints(clonedModule);

    const boundingBox = new THREE.Box3().setFromObject(mesh);
    const centerWorldPosition = boundingBox.getCenter(new THREE.Vector3());

    const orientation = this.getModuleOrientation(clonedModule.group);
    let rightEdge, leftEdge, backEdge, bottomEdge, topEdge;
    if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
      const resultZ = EdgeDetector.findClosestZEdges(
        undefined,
        meshPoints,
        centerWorldPosition,
        [InsideType.COL_PANEL] //Loại trừ bìa cột
      );
      rightEdge = resultZ.rightEdge;
      leftEdge = resultZ.leftEdge;
      backEdge = EdgeDetector.findClosestXEdges(
        meshPoints,
        centerWorldPosition,
        [InsideType.DOOR] //Loại trừ cánh cửa
      );

      const resultY = EdgeDetector.findClosestYEdges(
        undefined,
        meshPoints,
        centerWorldPosition,
        null,
        undefined,
        true
      );
      bottomEdge = resultY.bottomEdge;
      topEdge = resultY.topEdge;
    } else if (orientation === 'ROTATED_90' || orientation === 'ROTATED_270') {
      const resultZ = EdgeDetector.findClosestZEdgesR270(
        undefined,
        meshPoints,
        centerWorldPosition,
        [InsideType.COL_PANEL] //Loại trừ bìa cột
      );
      rightEdge = resultZ.rightEdge;
      leftEdge = resultZ.leftEdge;
      backEdge = EdgeDetector.findClosestXEdgesR270(
        meshPoints,
        centerWorldPosition,
        [InsideType.DOOR] //Loại trừ cánh cửa
      );

      const resultY = EdgeDetector.findClosestYEdgesR270(
        undefined,
        meshPoints,
        centerWorldPosition,
        null,
        undefined,
        true
      );
      bottomEdge = resultY.bottomEdge;
      topEdge = resultY.topEdge;
    }

    if (leftEdge) {
      mesh.userData.leftEdgeUuid = leftEdge.mesh.uuid;
      if (leftEdge.mesh.userData.type !== undefined) {
        if (!leftEdge.mesh.userData.rightDependent) {
          leftEdge.mesh.userData.rightDependent = [];
        }
        if (!leftEdge.mesh.userData.rightDependent.includes(mesh.uuid)) {
          leftEdge.mesh.userData.rightDependent.push(mesh.uuid);
        }
      }
    }

    if (bottomEdge) {
      mesh.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;
      if (bottomEdge.mesh.userData.type !== undefined) {
        if (!bottomEdge.mesh.userData.topDependent) {
          bottomEdge.mesh.userData.topDependent = [];
        }
        if (!bottomEdge.mesh.userData.topDependent.includes(mesh.uuid)) {
          bottomEdge.mesh.userData.topDependent.push(mesh.uuid);
        }
      }
    }

    if (rightEdge) {
      mesh.userData.rightEdgeUuid = rightEdge.mesh.uuid;
      if (rightEdge.mesh.userData.type !== undefined) {
        if (!rightEdge.mesh.userData.leftDependent) {
          rightEdge.mesh.userData.leftDependent = [];
        }
        if (!rightEdge.mesh.userData.leftDependent.includes(mesh.uuid)) {
          rightEdge.mesh.userData.leftDependent.push(mesh.uuid);
        }
      }
    }

    if (topEdge) {
      mesh.userData.topEdgeUuid = topEdge.mesh.uuid;
      if (topEdge.mesh.userData.type !== undefined) {
        if (!topEdge.mesh.userData.bottomDependent) {
          topEdge.mesh.userData.bottomDependent = [];
        }
        if (!topEdge.mesh.userData.bottomDependent.includes(mesh.uuid)) {
          topEdge.mesh.userData.bottomDependent.push(mesh.uuid);
        }
      }
    }
  }

  resetEdgeDependencies(clonedModule, currentCabinet) {
    if (!clonedModule?.listInside || clonedModule.listInside.length === 0) {
      return;
    }

    console.log(
      'Resetting edge dependencies for',
      clonedModule.listInside.length,
      'inside items'
    );

    // Clean up old dependencies trước
    // this.cleanupOldDependencies(clonedModule);

    // Lấy mesh points của cabinet
    const meshPoints = currentCabinet.getActiveMeshPoints();

    clonedModule.listInside.forEach(inside => {
      if (!inside?.gltf) return;

      const mesh = inside.gltf;
      const meshType = mesh.userData?.type;

      // Reset edge dependencies dựa trên type
      this.resetEdgeDependenciesForMesh(
        mesh,
        meshType,
        meshPoints,
        clonedModule
      );
    });

    console.log('Edge dependencies reset completed');
  }

  /**
   * Reset edge dependencies cho một mesh cụ thể
   * @param {Object} mesh - Mesh cần reset dependencies
   * @param {number} meshType - Type của mesh
   * @param {Array} meshPoints - Danh sách mesh points
   * @param {Object} clonedModule - Module chứa mesh
   */
  resetEdgeDependenciesForMesh(mesh, meshType, meshPoints, clonedModule) {
    // Tính toán center world position từ bounding box
    const boundingBox = new THREE.Box3().setFromObject(mesh);
    const centerWorldPosition = boundingBox.getCenter(new THREE.Vector3());

    // Convert về local position nếu cần
    const localCenterPosition = clonedModule.group.worldToLocal
      ? clonedModule.group.worldToLocal(centerWorldPosition.clone())
      : centerWorldPosition;

    switch (meshType) {
      case InsideType.HPANEL: // Xà ngang
        this.resetHorizontalBarDependencies(
          mesh,
          centerWorldPosition,
          meshPoints
        );
        break;

      case InsideType.VPANEL: // Xà dọc
      case InsideType.VPANEL2: // Xà dọc phức tạp
        this.resetVerticalBarDependencies(
          mesh,
          centerWorldPosition,
          meshPoints
        );
        break;

      case InsideType.DRAWER: // Đợt kệ
        this.resetShelfDependencies(mesh, centerWorldPosition, meshPoints);
        break;

      case InsideType.DOOR: //Cửa
        this.resetDoorDependencies(mesh, clonedModule);
        break;

      case InsideType.BRACE: // Bạ cửa
        this.resetBraceDependencies(mesh, centerWorldPosition, meshPoints);
        break;

      case InsideType.HANDLE: //Tay nắm cửa
        this.resetHandleDependencies(mesh, clonedModule);
        break;

      default:
        // Các type khác có thể cần logic riêng
        console.log(`No edge dependency reset logic for type ${meshType}`);
        break;
    }
  }

  /**
   * Reset dependencies cho xà ngang (type 0)
   */
  resetHorizontalBarDependencies(mesh, position, meshPoints) {
    // Tìm left và right edges gần nhất
    const { rightEdge, leftEdge } = EdgeDetector.findClosestZEdges(
      mesh.uuid,
      meshPoints,
      position,
      null,
      null
    );

    if (leftEdge && rightEdge) {
      // Set edge UUIDs cho mesh
      mesh.userData.leftEdgeUuid = leftEdge.mesh.uuid;
      mesh.userData.rightEdgeUuid = rightEdge.mesh.uuid;

      // Thêm mesh vào dependent arrays của edges
      if (leftEdge.mesh.userData.type !== undefined) {
        if (!leftEdge.mesh.userData.rightDependent) {
          leftEdge.mesh.userData.rightDependent = [];
        }
        if (!leftEdge.mesh.userData.rightDependent.includes(mesh.uuid)) {
          leftEdge.mesh.userData.rightDependent.push(mesh.uuid);
        }
      }

      if (rightEdge.mesh.userData.type !== undefined) {
        if (!rightEdge.mesh.userData.leftDependent) {
          rightEdge.mesh.userData.leftDependent = [];
        }
        if (!rightEdge.mesh.userData.leftDependent.includes(mesh.uuid)) {
          rightEdge.mesh.userData.leftDependent.push(mesh.uuid);
        }
      }

      console.log(`Reset horizontal bar dependencies for mesh ${mesh.uuid}`);
    }
  }

  /**
   * Reset dependencies cho xà dọc (type 1, 11)
   */
  resetVerticalBarDependencies(mesh, position, meshPoints) {
    // Tìm top và bottom edges gần nhất
    const { bottomEdge, topEdge } = EdgeDetector.findClosestYEdges(
      mesh.uuid,
      meshPoints,
      position,
      null,
      undefined,
      true
    );

    if (bottomEdge && topEdge) {
      // Set edge UUIDs cho mesh
      mesh.userData.topEdgeUuid = topEdge.mesh.uuid;
      mesh.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;

      // Thêm mesh vào dependent arrays của edges
      if (topEdge.mesh.userData.type !== undefined) {
        if (!topEdge.mesh.userData.bottomDependent) {
          topEdge.mesh.userData.bottomDependent = [];
        }
        if (!topEdge.mesh.userData.bottomDependent.includes(mesh.uuid)) {
          topEdge.mesh.userData.bottomDependent.push(mesh.uuid);
        }
      }

      if (bottomEdge.mesh.userData.type !== undefined) {
        if (!bottomEdge.mesh.userData.topDependent) {
          bottomEdge.mesh.userData.topDependent = [];
        }
        if (!bottomEdge.mesh.userData.topDependent.includes(mesh.uuid)) {
          bottomEdge.mesh.userData.topDependent.push(mesh.uuid);
        }
      }

      console.log(`Reset vertical bar dependencies for mesh ${mesh.uuid}`);
    }
  }

  /**
   * Reset dependencies cho đợt kệ (type 3)
   */
  resetShelfDependencies(mesh, position, meshPoints) {
    // Kệ cần cả left/right và bottom edges
    const { rightEdge, leftEdge } = EdgeDetector.findClosestZEdges(
      undefined,
      meshPoints,
      position
    );

    const { bottomEdge } = EdgeDetector.findClosestYEdges(
      '',
      meshPoints,
      position,
      null,
      undefined,
      true
    );

    if (leftEdge && rightEdge) {
      mesh.userData.leftEdgeUuid = leftEdge.mesh.uuid;
      mesh.userData.rightEdgeUuid = rightEdge.mesh.uuid;

      // Thêm vào dependent arrays nếu cần
      if (leftEdge.mesh.userData.type !== undefined) {
        if (!leftEdge.mesh.userData.rightDependent) {
          leftEdge.mesh.userData.rightDependent = [];
        }
        if (!leftEdge.mesh.userData.rightDependent.includes(mesh.uuid)) {
          leftEdge.mesh.userData.rightDependent.push(mesh.uuid);
        }
      }

      if (rightEdge.mesh.userData.type !== undefined) {
        if (!rightEdge.mesh.userData.leftDependent) {
          rightEdge.mesh.userData.leftDependent = [];
        }
        if (!rightEdge.mesh.userData.leftDependent.includes(mesh.uuid)) {
          rightEdge.mesh.userData.leftDependent.push(mesh.uuid);
        }
      }
    }

    if (bottomEdge) {
      mesh.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;
    }

    console.log(`Reset shelf dependencies for mesh ${mesh.uuid}`);
  }

  /**
   * Reset dependencies cho cửa
   */
  resetDoorDependencies(mesh, clonedModule) {
    const minPoint = this.getModelMinPoint(clonedModule.group);
    const tolerance = 0.017;
    const meshDistance = this.getDistanceToModelMin(mesh.position, minPoint);

    // Tìm matched item
    const matchedItem = clonedModule.listInside.find(
      item => Math.abs(item.doorDistance - meshDistance) < tolerance
    );

    if (!matchedItem?.dependent) {
      console.warn('No matched item or dependent data found');
      return;
    }

    // Reset tất cả edge UUIDs
    const edges = [
      'leftEdge',
      'rightEdge',
      'topEdge',
      'bottomEdge',
      'leftEdgeDoor',
      'rightEdgeDoor',
    ];
    edges.forEach(edge => (mesh.userData[`${edge}Uuid`] = null));

    clonedModule.group.traverse(object => {
      if (
        (object.isMesh || object.isGroup) &&
        object.userData.type !== InsideType.HANDLE
      ) {
        const objectDistance = this.getDistanceToModelMin(
          object.position,
          minPoint
        );

        for (const edge of edges) {
          if (
            !mesh.userData[`${edge}Uuid`] &&
            matchedItem.dependent[edge] !== undefined &&
            Math.abs(objectDistance - matchedItem.dependent[edge].distance) <
              tolerance &&
            matchedItem.dependent[edge].posY === object.position.y
          ) {
            mesh.userData[`${edge}Uuid`] = object.uuid;
            // return; // Chuyển sang object tiếp theo
          }
        }
      }
    });

    console.log(clonedModule);
  }

  /**
   * Reset dependencies cho bạ cửa
   */
  resetBraceDependencies(mesh, position, meshPoints) {
    // Tìm left và right edges gần nhất
    const { rightEdge, leftEdge } = EdgeDetector.findClosestZEdges(
      mesh.uuid,
      meshPoints,
      position,
      null,
      null
    );

    const { bottomEdge, topEdge } = EdgeDetector.findClosestYEdges(
      mesh.uuid,
      meshPoints,
      position,
      null,
      undefined,
      true
    );

    if (leftEdge && rightEdge && bottomEdge && topEdge) {
      // Set edge UUIDs cho mesh
      mesh.userData.leftEdgeUuid = leftEdge.mesh.uuid;
      mesh.userData.rightEdgeUuid = rightEdge.mesh.uuid;
      mesh.userData.topEdgeUuid = topEdge.mesh.uuid;
      mesh.userData.bottomEdgeUuid = bottomEdge.mesh.uuid;

      // Thêm mesh vào dependent arrays của edges
      if (leftEdge.mesh.userData.type !== undefined) {
        if (!leftEdge.mesh.userData.rightDependent) {
          leftEdge.mesh.userData.rightDependent = [];
        }
        if (!leftEdge.mesh.userData.rightDependent.includes(mesh.uuid)) {
          leftEdge.mesh.userData.rightDependent.push(mesh.uuid);
        }
      }

      if (rightEdge.mesh.userData.type !== undefined) {
        if (!rightEdge.mesh.userData.leftDependent) {
          rightEdge.mesh.userData.leftDependent = [];
        }
        if (!rightEdge.mesh.userData.leftDependent.includes(mesh.uuid)) {
          rightEdge.mesh.userData.leftDependent.push(mesh.uuid);
        }
      }

      if (topEdge.mesh.userData.type !== undefined) {
        if (!topEdge.mesh.userData.bottomDependent) {
          topEdge.mesh.userData.bottomDependent = [];
        }
        if (!topEdge.mesh.userData.bottomDependent.includes(mesh.uuid)) {
          topEdge.mesh.userData.bottomDependent.push(mesh.uuid);
        }
      }

      if (bottomEdge.mesh.userData.type !== undefined) {
        if (!bottomEdge.mesh.userData.topDependent) {
          bottomEdge.mesh.userData.topDependent = [];
        }
        if (!bottomEdge.mesh.userData.topDependent.includes(mesh.uuid)) {
          bottomEdge.mesh.userData.topDependent.push(mesh.uuid);
        }
      }

      console.log(`Reset brace dependencies for mesh ${mesh.uuid}`);
    }
  }

  /**
   * Reset dependencies cho tay nắm
   */
  resetHandleDependencies(mesh, clonedModule) {
    clonedModule.group.traverse(object => {
      if (object.userData.type === InsideType.DOOR) {
        // Kiểm tra xem mesh có nằm trong vùng của door theo Z và Y
        if (this.isObjectInsideRegionZY(mesh, object)) {
          object.userData.handleUuid = mesh.uuid;
        }
      }
    });
  }

  // Hàm helper kiểm tra overlap chỉ theo Z và Y (không tolerance)
  isObjectInsideRegionZY(targetMesh, regionObject) {
    const targetBox = new THREE.Box3().setFromObject(targetMesh);
    const regionBox = new THREE.Box3().setFromObject(regionObject);

    // Kiểm tra target có nằm hoàn toàn trong region theo Z và Y
    const zContained =
      regionBox.min.z <= targetBox.min.z && targetBox.max.z <= regionBox.max.z;

    const yContained =
      regionBox.min.y <= targetBox.min.y && targetBox.max.y <= regionBox.max.y;

    return zContained && yContained;
  }

  /**
   * Lưu và load lại cửa
   */
  // Hàm lấy điểm min của model (bounding box min)
  getModelMinPoint(model) {
    const bbox = new THREE.Box3().setFromObject(model);
    return bbox.min.clone();
  }

  getDistanceToModelMin(meshPoint, minPoint) {
    return meshPoint.distanceTo(minPoint);
  }

  setDependentDoorPosition(clonedModule, currentCabinet) {
    const minPoint = this.getModelMinPoint(currentCabinet.cloneModule.group);

    currentCabinet.cloneModule.group.traverse(object => {
      if (object.userData.type === InsideType.DOOR) {
        // Tìm item trong listInside có gltfUuid trùng với uuid của object
        const matchedItem = clonedModule.listInside.find(
          item => item.gltfUuid === object.uuid
        );

        if (matchedItem) {
          const edges = {
            leftEdge: this.findMeshByUuid(
              clonedModule.group,
              object.userData.leftEdgeUuid
            ),
            leftEdgeDoor: this.findMeshByUuid(
              clonedModule.group,
              object.userData.leftEdgeDoorUuid
            ),
            rightEdge: this.findMeshByUuid(
              clonedModule.group,
              object.userData.rightEdgeUuid
            ),
            rightEdgeDoor: this.findMeshByUuid(
              clonedModule.group,
              object.userData.rightEdgeDoorUuid
            ),
            topEdge: this.findMeshByUuid(
              clonedModule.group,
              object.userData.topEdgeUuid
            ),
            bottomEdge: this.findMeshByUuid(
              clonedModule.group,
              object.userData.bottomEdgeUuid
            ),
          };

          const dependent = {};

          Object.entries(edges).forEach(([position, edge]) => {
            if (edge) {
              dependent[position] = {
                distance: this.getDistanceToModelMin(edge.position, minPoint),
                posY: edge.position.y,
              };
            } else {
              dependent[position] = null;
            }
          });

          matchedItem.dependent = dependent;

          const doorDistance = this.getDistanceToModelMin(
            object.position,
            minPoint
          );
          matchedItem.doorDistance = doorDistance;

          // matchedItem.handlePosition = object.userData.handlePosition;
        } else {
          console.warn(`No item found with gltfUuid: ${object.uuid}`);
        }
      }
    });
  }

  duplicateEvenly(meshClick, count, insideType, clonedModule, currentCabinet) {
    return new Promise(async (resolve, reject) => {
      try {
        if (!meshClick || !clonedModule) {
          reject(new Error('Thiếu thông tin mesh hoặc module'));
          return;
        }

        if (count < 1) {
          reject(new Error('Số lượng phải lớn hơn 0'));
          return;
        }

        // Lấy thông tin của mesh gốc
        const originalMesh = meshClick;
        const originalInside = clonedModule?.listInside?.find(
          inside => inside.gltfUuid === originalMesh.uuid
        );
        const originalSize = this.getSizeRotation(
          clonedModule.group,
          originalMesh
        );
        const cabinetSize = this.getSize(clonedModule.group);

        const boundingBox = new THREE.Box3().setFromObject(originalMesh);

        // Lấy trung điểm của bounding box (chính là trung tâm của mesh)
        const intersection = new THREE.Vector3();
        boundingBox.getCenter(intersection);

        // Lấy tất cả các mesh points từ module
        const typeHau = meshClick.userData.typeHau;
        let meshPoints = currentCabinet.getActiveMeshPoints(clonedModule);
        if (typeHau === TypeConnerModule.SUB) {
          meshPoints = EdgeDetector.findMeshPointsR90(clonedModule.group);
        }
        if (typeHau) {
          const ori = this.getModuleOrientation(clonedModule.group);
          const ignoreXanBa =
            originalMesh.userData.type === InsideType.DOOR ||
            originalMesh.userData.type === InsideType.DRAWER ||
            originalMesh.userData.type === InsideType.ACCESSORY_DISH_LOWER;
          const checkXanBa = originalMesh.userData.type !== InsideType.DOOR;

          if (ori === 'NORMAL') {
            if (typeHau === TypeConnerModule.MAIN) {
              meshPoints = EdgeDetector.findMeshPoints(
                clonedModule.group,
                ignoreXanBa,
                checkXanBa
              );
            } else if (typeHau === TypeConnerModule.SUB) {
              meshPoints = EdgeDetector.findMeshPointsR90(
                clonedModule.group,
                ignoreXanBa,
                checkXanBa
              );
            }
          }
          if (ori === 'ROTATED_270') {
            if (typeHau === TypeConnerModule.MAIN) {
              meshPoints = EdgeDetector.findMeshPointsRotation(
                clonedModule.group,
                ignoreXanBa,
                checkXanBa
              );
            } else if (typeHau === TypeConnerModule.SUB) {
              meshPoints = EdgeDetector.findMeshPoints(
                clonedModule.group,
                ignoreXanBa,
                checkXanBa
              );
            }
          }
        }
        const meshIds = [originalMesh.uuid];

        let rightEdge, leftEdge, bottomEdge, topEdge;

        const orientation = this.getModuleOrientation(clonedModule.group);
        if (
          ((orientation === 'NORMAL' || orientation === 'ROTATED_180') &&
            typeHau === TypeConnerModule.MAIN) ||
          (orientation === 'ROTATED_270' && typeHau === TypeConnerModule.SUB) ||
          ((orientation === 'NORMAL' || orientation === 'ROTATED_180') &&
            !typeHau)
        ) {
          const resultZ = EdgeDetector.findClosestZEdges(
            undefined,
            meshPoints,
            intersection,
            [InsideType.COL_PANEL] //Loại trừ bìa cột
          );
          rightEdge = resultZ.rightEdge;
          leftEdge = resultZ.leftEdge;
          let resultY;
          if (originalMesh.userData.type === InsideType.DRAWER) {
            resultY = EdgeDetector.findClosestYEdgesForDrawer(
              undefined,
              meshPoints,
              intersection,
              [
                InsideType.BRACE,
                InsideType.DOOR,
                InsideType.CLOTHES_HANGER,
                InsideType.LED_RECESSED,
                InsideType.LED_CORNER,
                InsideType.DRAWER,
                InsideType.ACCESSORY_DISH_UPPER,
                InsideType.ACCESSORY_GENERAL,
                InsideType.ACCESSORY_FRIDGE,
                InsideType.ACCESSORY_HOOD,
              ],
              null,
              false
            );
          } else {
            resultY = EdgeDetector.findClosestYEdges(
              undefined,
              meshPoints,
              intersection,
              [
                InsideType.BRACE,
                InsideType.DOOR,
                InsideType.CLOTHES_HANGER,
                InsideType.LED_RECESSED,
                InsideType.LED_CORNER,
                InsideType.ACCESSORY_DISH_UPPER,
                InsideType.ACCESSORY_GENERAL,
                InsideType.ACCESSORY_FRIDGE,
                InsideType.ACCESSORY_HOOD,
              ],
              null,
              false
            );
          }

          bottomEdge = resultY.bottomEdge;
          topEdge = resultY.topEdge;
        } else if (
          orientation === 'ROTATED_270' &&
          (typeHau === TypeConnerModule.MAIN || !typeHau)
        ) {
          const resultZ = EdgeDetector.findClosestZEdgesR270(
            meshIds,
            meshPoints,
            intersection,
            [InsideType.COL_PANEL] //Loại trừ bìa cột
          );
          rightEdge = resultZ.rightEdge;
          leftEdge = resultZ.leftEdge;

          const resultY = EdgeDetector.findClosestYEdgesR270(
            undefined,
            meshPoints,
            intersection,
            [
              InsideType.BRACE,
              InsideType.DOOR,
              InsideType.CLOTHES_HANGER,
              InsideType.LED_RECESSED,
              InsideType.LED_CORNER,
              InsideType.ACCESSORY_DISH_UPPER,
              InsideType.ACCESSORY_GENERAL,
              InsideType.ACCESSORY_FRIDGE,
              InsideType.ACCESSORY_HOOD,
            ],
            undefined,
            false
          );
          bottomEdge = resultY.bottomEdge;
          topEdge = resultY.topEdge;
        } else if (
          orientation === 'ROTATED_90' ||
          typeHau === TypeConnerModule.SUB
        ) {
          const resultZ = EdgeDetector.findClosestZEdgesR90(
            undefined,
            meshPoints,
            intersection,
            [InsideType.COL_PANEL] //Loại trừ bìa cột
          );
          rightEdge = resultZ.rightEdge;
          leftEdge = resultZ.leftEdge;

          let resultY;

          if (originalMesh.userData.type === InsideType.DRAWER) {
            resultY = EdgeDetector.findClosestYEdgesR90ForDrawer(
              undefined,
              meshPoints,
              intersection,
              [
                InsideType.BRACE,
                InsideType.DOOR,
                InsideType.CLOTHES_HANGER,
                InsideType.LED_RECESSED,
                InsideType.LED_CORNER,
                InsideType.DRAWER,
                InsideType.ACCESSORY_DISH_UPPER,
                InsideType.ACCESSORY_GENERAL,
                InsideType.ACCESSORY_FRIDGE,
                InsideType.ACCESSORY_HOOD,
              ],
              null,
              false
            );
          } else {
            resultY = EdgeDetector.findClosestYEdgesR90(
              undefined,
              meshPoints,
              intersection,
              [
                InsideType.BRACE,
                InsideType.DOOR,
                InsideType.CLOTHES_HANGER,
                InsideType.LED_RECESSED,
                InsideType.LED_CORNER,
                InsideType.ACCESSORY_DISH_UPPER,
                InsideType.ACCESSORY_GENERAL,
                InsideType.ACCESSORY_FRIDGE,
                InsideType.ACCESSORY_HOOD,
              ],
              null,
              false
            );
          }

          bottomEdge = resultY.bottomEdge;
          topEdge = resultY.topEdge;
        }

        let sizeRight;
        let sizeLeft;
        let sizeBottom;
        let sizeTop;
        let rightPosLocal;
        let leftPosLocal;
        let bottomPosLocal;
        let topPosLocal;

        if (topEdge) {
          sizeTop = this.getSizeRotation(clonedModule.group, topEdge?.mesh);
          topPosLocal = clonedModule.group.worldToLocal(
            topEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
          );
        }

        if (bottomEdge) {
          sizeBottom = this.getSizeRotation(
            clonedModule.group,
            bottomEdge?.mesh
          );
          bottomPosLocal = clonedModule.group.worldToLocal(
            bottomEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
          );
        }

        if (rightEdge) {
          sizeRight = this.getSizeRotation(clonedModule.group, rightEdge?.mesh);
          rightPosLocal = clonedModule.group.worldToLocal(
            rightEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
          );
        }

        if (leftEdge) {
          sizeLeft = this.getSizeRotation(clonedModule.group, leftEdge?.mesh);
          leftPosLocal = clonedModule.group.worldToLocal(
            leftEdge.mesh.getWorldPosition(new THREE.Vector3()).clone()
          );
        }

        const addModelToCabinet = group => {
          const data = {
            _id: originalInside?._id,
            name: originalInside?.name,
            price: originalInside?.price,
            child: originalInside?.child,
            glb: originalInside?.glb,
            type: originalInside?.type,
            dependent: originalInside?.dependent,
            doorDistance: originalInside?.doorDistance,
            thumbnail_url:
              originalInside?.thumbnail_url ?? originalInside?.icon,
            limit: originalInside?.limit,
            beveled: originalInside?.beveled ?? false,
            gltfUuid: group.uuid,
            gltf: group,
            group: group,
          };

          group.userData.isInside = true;
          group.userData.model = originalInside.model;
          group.userData.parentGltf = group.uuid;

          clonedModule.listInside.push(data);
          clonedModule.group.add(group);

          return data;
        };

        switch (originalMesh.userData.type) {
          case InsideType.HPANEL:
          case InsideType.HPANEL_RIGHT:
          case InsideType.HPANEL_LEFT:
            if (bottomEdge && topEdge) {
              const remainingClearance =
                topPosLocal.y - bottomPosLocal.y - sizeBottom.y;
              const insideSpacing =
                (remainingClearance - originalSize.y * count) / (count + 1);

              if (insideSpacing < 0.01) {
                openNotification({
                  key: 'cant-add-door-existing-door',
                  icon: <Icon.ICWarning />,
                  iconColor: '#F97D16',
                  title: 'Không thể thực hiện nhân bản',
                  description: 'Không đủ khoảng cách để nhân bản đều nhau.',
                  placement: 'bottomRight',
                  closable: true,
                  duration: 4,
                });
                reject(new Error('Không đủ khoảng cách để nhân bản đều nhau'));
                break;
              }

              originalMesh.position.y =
                bottomPosLocal.y + sizeBottom.y + insideSpacing;

              for (let i = 1; i < count; i++) {
                const cloneMesh = originalMesh.clone();
                if (leftEdge?.mesh?.userData.type !== undefined) {
                  if (!leftEdge.mesh.userData.rightDependent) {
                    leftEdge.mesh.userData.rightDependent = [];
                  }
                  leftEdge.mesh.userData.rightDependent.push(cloneMesh.uuid);
                }

                if (rightEdge?.mesh?.userData.type !== undefined) {
                  if (!rightEdge.mesh.userData.leftDependent) {
                    rightEdge.mesh.userData.leftDependent = [];
                  }
                  rightEdge.mesh.userData.leftDependent.push(cloneMesh.uuid);
                }
                this.setInfoModelChild2(cloneMesh);
                addModelToCabinet(cloneMesh);
                cloneMesh.position.y =
                  bottomPosLocal.y +
                  sizeBottom.y +
                  insideSpacing * (i + 1) +
                  originalSize.y * i;
              }
            }
            break;

          case InsideType.DRAWER:
            if (bottomEdge && topEdge) {
              originalMesh.userData.drawerCount = count;
              if (clonedModule.cabinet.type === TypeCabinet.WARDROBE) {
                const remainingClearance =
                  topPosLocal.y -
                  bottomPosLocal.y -
                  hinge45Clearance -
                  hinge45Clearance * (count - 1);
                const sizeDoor = Number(
                  (remainingClearance / count).toFixed(3)
                );

                originalMesh.userData.topGap = DRAWER_DEFAULT_TOP_GAP;
                originalMesh.userData.bottomGap = DRAWER_DEFAULT_BOTTOM_GAP;

                const sizeY0 =
                  sizeDoor -
                  sizeBottom.y -
                  originalMesh.userData.topGap -
                  originalMesh.userData.bottomGap -
                  doorInsetOnPanel;

                originalMesh.position.y =
                  bottomPosLocal.y +
                  sizeBottom.y +
                  originalMesh.userData.bottomGap;

                originalMesh.userData.indexDuplicate = 0;

                //Cập nhật lại top khi nhân bản (trường hợp tấm top được thêm vào sau hộc kéo gốc)
                if (originalMesh.userData.topEdgeUuid !== topEdge?.mesh?.uuid) {
                  originalMesh.userData.topEdgeUuid = topEdge?.mesh?.uuid;
                }

                InsideCabinetHandler.scaleHeight(originalMesh, sizeY0 - 0.008);

                let sizeY1 = 0;
                for (let i = 1; i < count; i++) {
                  const cloneMesh = originalMesh.clone();
                  cloneMesh.userData.indexDuplicate = i;
                  if (leftEdge.mesh.userData.type !== undefined) {
                    if (!leftEdge.mesh.userData.rightDependent) {
                      leftEdge.mesh.userData.rightDependent = [];
                    }
                    leftEdge.mesh.userData.rightDependent.push(cloneMesh.uuid);
                  }

                  if (rightEdge.mesh.userData.type !== undefined) {
                    if (!rightEdge.mesh.userData.leftDependent) {
                      rightEdge.mesh.userData.leftDependent = [];
                    }
                    rightEdge.mesh.userData.leftDependent.push(cloneMesh.uuid);
                  }
                  this.setInfoModelChild2(cloneMesh);
                  addModelToCabinet(cloneMesh);

                  if (i === count - 1) {
                    const sizeY2 =
                      sizeDoor -
                      originalMesh.userData.topGap -
                      originalMesh.userData.bottomGap -
                      doorInsetOnPanel -
                      (sizeBarce - hinge45Clearance);

                    cloneMesh.position.y =
                      bottomPosLocal.y +
                      sizeBottom.y +
                      sizeY0 +
                      originalMesh.userData.bottomGap * (i + 1) +
                      sizeY1 * (i - 1) +
                      originalMesh.userData.topGap * i +
                      sizeBarce * i;

                    InsideCabinetHandler.scaleHeight(cloneMesh, sizeY2 - 0.008);
                  } else {
                    sizeY1 =
                      sizeDoor -
                      originalMesh.userData.topGap -
                      originalMesh.userData.bottomGap -
                      doorInsetOnPanel * 2;

                    cloneMesh.position.y =
                      bottomPosLocal.y +
                      sizeBottom.y +
                      sizeY0 +
                      sizeY1 * (i - 1) +
                      originalMesh.userData.bottomGap * (i + 1) +
                      originalMesh.userData.topGap * i +
                      sizeBarce * i;

                    InsideCabinetHandler.scaleHeight(cloneMesh, sizeY1 - 0.008);
                  }
                }
              }
              if (clonedModule.cabinet.type === TypeCabinet.TV) {
                originalMesh.userData.isTopAsRoof = false; // Đánh dấu đợt kệ trên cùng được tính là nóc
                const remainingClearance =
                  topPosLocal.y -
                  bottomPosLocal.y -
                  hinge45Clearance -
                  hinge45Clearance * (count - 1);
                const sizeDoor = Number(
                  (remainingClearance / count).toFixed(3)
                );

                originalMesh.userData.topGap = DRAWER_DEFAULT_TOP_GAP_TV;
                originalMesh.userData.bottomGap = DRAWER_DEFAULT_BOTTOM_GAP_TV;

                const sizeY0 =
                  sizeDoor -
                  sizeBottom.y -
                  originalMesh.userData.topGap -
                  originalMesh.userData.bottomGap -
                  doorInsetOnPanel;

                originalMesh.position.y =
                  bottomPosLocal.y +
                  sizeBottom.y +
                  originalMesh.userData.bottomGap;

                originalMesh.userData.indexDuplicate = 0;

                InsideCabinetHandler.scaleHeight(originalMesh, sizeY0 - 0.008);

                let sizeY1 = 0;
                for (let i = 1; i < count; i++) {
                  const cloneMesh = originalMesh.clone();
                  cloneMesh.userData.indexDuplicate = i;
                  if (leftEdge.mesh.userData.type !== undefined) {
                    if (!leftEdge.mesh.userData.rightDependent) {
                      leftEdge.mesh.userData.rightDependent = [];
                    }
                    leftEdge.mesh.userData.rightDependent.push(cloneMesh.uuid);
                  }

                  if (rightEdge.mesh.userData.type !== undefined) {
                    if (!rightEdge.mesh.userData.leftDependent) {
                      rightEdge.mesh.userData.leftDependent = [];
                    }
                    rightEdge.mesh.userData.leftDependent.push(cloneMesh.uuid);
                  }
                  this.setInfoModelChild2(cloneMesh);
                  addModelToCabinet(cloneMesh);

                  if (i === count - 1) {
                    cloneMesh.userData.bottomDrawerUuid = originalMesh.uuid;
                    const sizeY2 =
                      sizeDoor -
                      originalMesh.userData.topGap -
                      originalMesh.userData.bottomGap -
                      doorInsetOnPanel -
                      (sizeBarce - hinge45Clearance);

                    cloneMesh.position.y =
                      bottomPosLocal.y +
                      sizeBottom.y +
                      sizeY0 +
                      originalMesh.userData.bottomGap * (i + 1) +
                      sizeY1 * (i - 1) +
                      originalMesh.userData.topGap * i +
                      sizeBarce * i;

                    InsideCabinetHandler.scaleHeight(cloneMesh, sizeY2 - 0.008);
                  } else {
                    sizeY1 =
                      sizeDoor -
                      originalMesh.userData.topGap -
                      originalMesh.userData.bottomGap -
                      doorInsetOnPanel * 2;

                    cloneMesh.position.y =
                      bottomPosLocal.y +
                      sizeBottom.y +
                      sizeY0 +
                      sizeY1 * (i - 1) +
                      originalMesh.userData.bottomGap * (i + 1) +
                      originalMesh.userData.topGap * i +
                      sizeBarce * i;

                    InsideCabinetHandler.scaleHeight(cloneMesh, sizeY1 - 0.008);
                  }
                }
              }
              if (
                clonedModule.cabinet.type === TypeCabinet.SHOE &&
                topEdge?.mesh?.userData?.type === 0
              ) {
                originalMesh.userData.isTopAsRoof = false; // Đánh dấu đợt kệ trên cùng được tính là nóc
                const remainingClearance =
                  topPosLocal.y -
                  bottomPosLocal.y -
                  hinge45Clearance -
                  hinge45Clearance * (count - 1);
                const sizeDoor = Number(
                  (remainingClearance / count).toFixed(3)
                );

                originalMesh.userData.topGap = DRAWER_DEFAULT_TOP_GAP;
                originalMesh.userData.bottomGap = DRAWER_DEFAULT_BOTTOM_GAP;

                const sizeY0 =
                  sizeDoor -
                  sizeBottom.y -
                  originalMesh.userData.topGap -
                  originalMesh.userData.bottomGap -
                  doorInsetOnPanel;

                originalMesh.position.y =
                  bottomPosLocal.y +
                  sizeBottom.y +
                  originalMesh.userData.bottomGap;

                originalMesh.userData.indexDuplicate = 0;

                InsideCabinetHandler.scaleHeight(originalMesh, sizeY0 - 0.008);

                let sizeY1 = 0;
                for (let i = 1; i < count; i++) {
                  const cloneMesh = originalMesh.clone();
                  cloneMesh.userData.indexDuplicate = i;
                  if (leftEdge.mesh.userData.type !== undefined) {
                    if (!leftEdge.mesh.userData.rightDependent) {
                      leftEdge.mesh.userData.rightDependent = [];
                    }
                    leftEdge.mesh.userData.rightDependent.push(cloneMesh.uuid);
                  }

                  if (rightEdge.mesh.userData.type !== undefined) {
                    if (!rightEdge.mesh.userData.leftDependent) {
                      rightEdge.mesh.userData.leftDependent = [];
                    }
                    rightEdge.mesh.userData.leftDependent.push(cloneMesh.uuid);
                  }
                  this.setInfoModelChild2(cloneMesh);
                  addModelToCabinet(cloneMesh);

                  if (i === count - 1) {
                    const sizeY2 =
                      sizeDoor -
                      originalMesh.userData.topGap -
                      originalMesh.userData.bottomGap -
                      doorInsetOnPanel -
                      (sizeBarce - hinge45Clearance);

                    cloneMesh.position.y =
                      bottomPosLocal.y +
                      sizeBottom.y +
                      sizeY0 +
                      originalMesh.userData.bottomGap * (i + 1) +
                      sizeY1 * (i - 1) +
                      originalMesh.userData.topGap * i +
                      sizeBarce * i;

                    InsideCabinetHandler.scaleHeight(cloneMesh, sizeY2 - 0.008);
                  } else {
                    sizeY1 =
                      sizeDoor -
                      originalMesh.userData.topGap -
                      originalMesh.userData.bottomGap -
                      doorInsetOnPanel * 2;

                    cloneMesh.position.y =
                      bottomPosLocal.y +
                      sizeBottom.y +
                      sizeY0 +
                      sizeY1 * (i - 1) +
                      originalMesh.userData.bottomGap * (i + 1) +
                      originalMesh.userData.topGap * i +
                      sizeBarce * i;

                    InsideCabinetHandler.scaleHeight(cloneMesh, sizeY1 - 0.008);
                  }
                }
              }
              if (
                clonedModule.cabinet.type === TypeCabinet.SHOE &&
                !topEdge?.mesh?.userData?.type &&
                topEdge?.mesh?.userData?.type !== 0
              ) {
                originalMesh.userData.isTopAsRoof = true; // Đánh dấu đợt kệ trên cùng được tính là nóc
                const remainingClearance =
                  topPosLocal.y -
                  bottomPosLocal.y +
                  sizeTop.y -
                  hinge45Clearance * (count - 1);
                const sizeDoor = Number(
                  (remainingClearance / count).toFixed(3)
                );

                originalMesh.userData.topGap = DRAWER_DEFAULT_TOP_GAP;
                originalMesh.userData.bottomGap = DRAWER_DEFAULT_BOTTOM_GAP;

                const sizeY0 =
                  sizeDoor -
                  sizeBottom.y -
                  originalMesh.userData.topGap -
                  originalMesh.userData.bottomGap -
                  doorInsetOnPanel;

                originalMesh.position.y =
                  bottomPosLocal.y +
                  sizeBottom.y +
                  originalMesh.userData.bottomGap;

                originalMesh.userData.indexDuplicate = 0;

                InsideCabinetHandler.scaleHeight(originalMesh, sizeY0 - 0.008);

                let sizeY1 = 0;
                for (let i = 1; i < count; i++) {
                  const cloneMesh = originalMesh.clone();
                  cloneMesh.userData.indexDuplicate = i;
                  if (leftEdge.mesh.userData.type !== undefined) {
                    if (!leftEdge.mesh.userData.rightDependent) {
                      leftEdge.mesh.userData.rightDependent = [];
                    }
                    leftEdge.mesh.userData.rightDependent.push(cloneMesh.uuid);
                  }

                  if (rightEdge.mesh.userData.type !== undefined) {
                    if (!rightEdge.mesh.userData.leftDependent) {
                      rightEdge.mesh.userData.leftDependent = [];
                    }
                    rightEdge.mesh.userData.leftDependent.push(cloneMesh.uuid);
                  }
                  this.setInfoModelChild2(cloneMesh);
                  addModelToCabinet(cloneMesh);

                  if (i === count - 1) {
                    const sizeY2 =
                      sizeDoor -
                      originalMesh.userData.topGap -
                      originalMesh.userData.bottomGap -
                      doorInsetOnPanel -
                      sizeTop.y;

                    cloneMesh.position.y =
                      bottomPosLocal.y +
                      sizeBottom.y +
                      sizeY0 +
                      originalMesh.userData.bottomGap * (i + 1) +
                      sizeY1 * (i - 1) +
                      originalMesh.userData.topGap * i +
                      sizeBarce * i;

                    InsideCabinetHandler.scaleHeight(cloneMesh, sizeY2 - 0.008);
                  } else {
                    sizeY1 =
                      sizeDoor -
                      originalMesh.userData.topGap -
                      originalMesh.userData.bottomGap -
                      doorInsetOnPanel * 2;

                    cloneMesh.position.y =
                      bottomPosLocal.y +
                      sizeBottom.y +
                      sizeY0 +
                      sizeY1 * (i - 1) +
                      originalMesh.userData.bottomGap * (i + 1) +
                      originalMesh.userData.topGap * i +
                      sizeBarce * i;

                    InsideCabinetHandler.scaleHeight(cloneMesh, sizeY1 - 0.008);
                  }
                }
              }
              if (clonedModule.cabinet.type === TypeCabinet.KITCHEN) {
                const remainingClearance =
                  topPosLocal.y -
                  bottomPosLocal.y +
                  doorInsetOnPanel45 -
                  hinge45Clearance * (count - 1);
                const sizeDoor = Number(
                  (remainingClearance / count).toFixed(3)
                );

                if (count === 3) {
                  originalMesh.userData.topGap = DRAWER_3_TOP_GAP;
                  originalMesh.userData.bottomGap = DRAWER_3_BOTTOM_GAP;
                } else {
                  originalMesh.userData.topGap = DRAWER_DEFAULT_TOP_GAP;
                  originalMesh.userData.bottomGap = DRAWER_DEFAULT_BOTTOM_GAP;
                }

                const sizeY0 =
                  sizeDoor -
                  sizeBottom.y -
                  originalMesh.userData.topGap -
                  originalMesh.userData.bottomGap -
                  doorInsetOnPanel;

                originalMesh.position.y =
                  bottomPosLocal.y +
                  sizeBottom.y +
                  originalMesh.userData.bottomGap;

                originalMesh.userData.indexDuplicate = 0;

                InsideCabinetHandler.scaleHeight(originalMesh, sizeY0 - 0.008);

                let sizeY1 = 0;
                for (let i = 1; i < count; i++) {
                  const cloneMesh = originalMesh.clone();
                  cloneMesh.userData.indexDuplicate = i;
                  if (leftEdge.mesh.userData.type !== undefined) {
                    if (!leftEdge.mesh.userData.rightDependent) {
                      leftEdge.mesh.userData.rightDependent = [];
                    }
                    leftEdge.mesh.userData.rightDependent.push(cloneMesh.uuid);
                  }

                  if (rightEdge.mesh.userData.type !== undefined) {
                    if (!rightEdge.mesh.userData.leftDependent) {
                      rightEdge.mesh.userData.leftDependent = [];
                    }
                    rightEdge.mesh.userData.leftDependent.push(cloneMesh.uuid);
                  }
                  this.setInfoModelChild2(cloneMesh);
                  addModelToCabinet(cloneMesh);

                  if ((i === count - 1 && count === 3) || count === 2) {
                    const sizeY2 =
                      sizeDoor -
                      originalMesh.userData.topGap -
                      originalMesh.userData.bottomGap -
                      doorInsetOnPanel -
                      doorInsetOnPanel45;

                    cloneMesh.position.y =
                      bottomPosLocal.y +
                      sizeBottom.y +
                      originalMesh.userData.bottomGap * count +
                      sizeY0 +
                      sizeY1 +
                      originalMesh.userData.topGap * (count - 1) +
                      sizeBarce * (count - 1);

                    InsideCabinetHandler.scaleHeight(cloneMesh, sizeY2 - 0.008);
                  } else {
                    sizeY1 =
                      sizeDoor -
                      originalMesh.userData.topGap -
                      originalMesh.userData.bottomGap -
                      doorInsetOnPanel * 2;

                    cloneMesh.position.y =
                      bottomPosLocal.y +
                      sizeBottom.y +
                      originalMesh.userData.bottomGap * 2 +
                      sizeY0 +
                      originalMesh.userData.topGap +
                      sizeBarce;

                    InsideCabinetHandler.scaleHeight(cloneMesh, sizeY1 - 0.008);
                  }
                }
              }
            }
            break;

          case InsideType.VPANEL:
          case InsideType.VPANEL2:
            if (leftEdge && rightEdge) {
              const remainingClearance =
                rightPosLocal.z - leftPosLocal.z - sizeLeft.z;
              const insideSpacing =
                (remainingClearance - originalSize.z * count) / (count + 1);

              originalMesh.position.z =
                leftPosLocal.z + sizeLeft.z + insideSpacing;

              for (let i = 1; i < count; i++) {
                const cloneMesh = originalMesh.clone();
                if (topEdge?.mesh?.userData.type !== undefined) {
                  if (!topEdge.mesh.userData.bottomDependent) {
                    topEdge.mesh.userData.bottomDependent = [];
                  }
                  topEdge.mesh.userData.bottomDependent.push(cloneMesh.uuid);
                }
                if (bottomEdge?.mesh?.userData.type !== undefined) {
                  if (!bottomEdge.mesh.userData.topDependent) {
                    bottomEdge.mesh.userData.topDependent = [];
                  }
                  bottomEdge.mesh.userData.topDependent.push(cloneMesh.uuid);
                }
                this.setInfoModelChild2(cloneMesh);
                addModelToCabinet(cloneMesh);
                cloneMesh.position.z =
                  leftPosLocal.z +
                  sizeLeft.z +
                  insideSpacing * (i + 1) +
                  originalSize.z * i;
              }
              this.adjustBackPanelForHPanel(clonedModule, this.scene);
            }
            break;

          default:
            console.log('SAI ROI');
            break;
        }

        resolve();
      } catch (error) {
        console.error('Error in duplicateEvenly:', error);
        reject(error);
      }
    });
  }

  /**
   * Hàm helper: Fetch module dựa trên type
   */
  async fetchModule(childInfo, insideType) {
    if (insideType === InsideType.DRAWER) {
      return await this.fetchDrawerModule(childInfo);
    } else if (insideType === InsideType.HPANEL) {
      return await this.fetchHPanelModule(childInfo);
    }
    throw new Error('Invalid inside type');
  }

  /**
   * Xóa tất cả child drawers
   */
  removeAllChildDrawers(clonedModule) {
    const drawersToRemove = [];
    clonedModule.group.traverse(child => {
      if (
        child.userData.type === InsideType.DRAWER &&
        child.userData.isCloned
      ) {
        drawersToRemove.push(child);
      }
    });

    drawersToRemove.forEach(drawer => {
      clonedModule.group.remove(drawer);
      // Xóa khỏi listInside
      const index = clonedModule.listInside.findIndex(
        item => item.gltfUuid === drawer.uuid
      );
      if (index !== -1) {
        clonedModule.listInside.splice(index, 1);
      }
    });
  }

  /**
   * Xóa tất cả child hpanels
   */
  removeAllChildHPanels(clonedModule) {
    const panelsToRemove = [];
    clonedModule.group.traverse(child => {
      if (
        child.userData.type === InsideType.HPANEL &&
        child.userData.isCloned
      ) {
        panelsToRemove.push(child);
      }
    });

    panelsToRemove.forEach(panel => {
      clonedModule.group.remove(panel);
      // Xóa khỏi listInside
      const index = clonedModule.listInside.findIndex(
        item => item.gltfUuid === panel.uuid
      );
      if (index !== -1) {
        clonedModule.listInside.splice(index, 1);
      }
    });
  }

  handleAlignModule(selectedModels) {
    // Nhóm các model theo position.y
    const groups = Object.values(
      selectedModels.reduce((acc, model) => {
        const y = model.position.y;
        if (!acc[y]) acc[y] = [];
        acc[y].push(model);
        return acc;
      }, {})
    );

    const numberOfGroups = groups.length;

    switch (true) {
      case numberOfGroups > 1:
        return this.validateMultipleGroups(groups);

      case numberOfGroups === 1:
        return this.validateSingleGroup(groups[0]);

      default:
        console.log('Không có nhóm nào');
        return false;
    }
  }

  ////Thêm hiệu ứng led trong tủ
  addInsideLeds(sourceMesh) {
    if (!this._insideLedShaderMaterials) {
      this._insideLedShaderMaterials = this._buildInsideLedShaderMaterials();
    }

    if (!sourceMesh) return;

    const bbox = new THREE.Box3().setFromObject(sourceMesh);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    bbox.getSize(size);
    bbox.getCenter(center);

    const isAlongX = size.x > size.z;

    const worldScale = new THREE.Vector3();
    sourceMesh.getWorldScale(worldScale);

    const localCenter = sourceMesh.worldToLocal(center.clone());
    localCenter.y += size.y / worldScale.y / 2;

    const planeWidth =
      // (isAlongX ? size.x : size.z) / (isAlongX ? worldScale.x : worldScale.z);
      (isAlongX ? size.x : size.z) /
      (worldScale.x !== 1 && worldScale.z === 1 ? worldScale.x : worldScale.z);

    this._insideLedShaderMaterials.forEach(({ height, mat }) => {
      const localHeight = height / worldScale.y;
      const geo = new THREE.PlaneGeometry(planeWidth, localHeight);
      const plane = new THREE.Mesh(geo, mat);

      plane.rotation.y = Math.PI / 2;

      plane.position.set(
        localCenter.x,
        localCenter.y - localHeight / 2,
        localCenter.z
      );

      plane.userData.type = InsideType.LED_EFFECT;

      // Không ảnh hưởng bounding box
      plane.geometry.boundingBox = new THREE.Box3();
      plane.geometry.boundingSphere = new THREE.Sphere();

      // Không bị ẩn khi zoom gần
      plane.frustumCulled = false;

      // Không ảnh hưởng raycast
      plane.raycast = () => {};

      sourceMesh.add(plane);
    });
  }

  _buildInsideLedShaderMaterials() {
    const vertexShader = `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `;

    const fragmentShader = `
    uniform vec3 uColor;
    uniform float uOpacity;
    uniform float uSigma;
    varying vec2 vUv;

    void main() {
      // vUv.y: 0 = dưới (xa LED), 1 = trên (sát LED)
      float distY = 1.0 - vUv.y;
      float glowY = exp(-(distY * distY) / (uSigma * uSigma));

      float alpha = glowY * uOpacity;
      gl_FragColor = vec4(uColor, clamp(alpha, 0.0, 1.0));
    }
  `;

    const make = (height, color, opacity, sigma) => ({
      height,
      mat: new THREE.ShaderMaterial({
        uniforms: {
          uColor: { value: new THREE.Color(color) },
          uOpacity: { value: opacity },
          uSigma: { value: sigma },
        },
        vertexShader,
        fragmentShader,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: true,
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      }),
    });

    return [
      make(0.015, 0xfffef8, 0.09, 0.45),
      make(0.045, 0xfffef0, 0.08, 0.52),
      make(0.09, 0xfffce0, 0.07, 0.58),
      make(0.15, 0xfff8d0, 0.06, 0.63),
      make(0.22, 0xfff4c0, 0.05, 0.68),
      make(0.32, 0xfff0b0, 0.04, 0.73),
      make(0.44, 0xffe8a0, 0.03, 0.78),
      make(0.58, 0xffe090, 0.02, 0.83),
    ];
  }
  ////Thêm hiệu ứng led trong tủ

  validateMultipleGroups(groups) {
    const positionController = this.scene?.positionController;
    const allGroupsHaveOneElement = groups.every(group => group.length === 1);

    if (allGroupsHaveOneElement) {
      console.log('SUCCESS: Có nhiều nhóm và mỗi nhóm chỉ có 1 phần tử');
      const allModels = groups.flat();

      // Tìm moduleFirst có y bé nhất
      const moduleFirst = allModels.reduce((min, model) =>
        model.position.y < min.position.y ? model : min
      );

      // Kiểm tra và chỉnh rotation của các module khác
      allModels.forEach(model => {
        if (model !== moduleFirst) {
          // Kiểm tra rotation có khác không
          if (
            model.rotation.x !== moduleFirst.rotation.x ||
            model.rotation.y !== moduleFirst.rotation.y ||
            model.rotation.z !== moduleFirst.rotation.z
          ) {
            console.log(
              `Chỉnh rotation của model ${model.id} theo moduleFirst`
            );

            // Set rotation giống moduleFirst
            model.rotation.x = moduleFirst.rotation.x;
            model.rotation.y = moduleFirst.rotation.y;
            model.rotation.z = moduleFirst.rotation.z;
          }
        }
      });

      const orientation = this.getModuleOrientation(moduleFirst);
      const moduleFirstReal = GeometryUtils.moduleWithoutAccessory(moduleFirst);
      const sizeModuleFirst = this.getSize(moduleFirstReal);

      if (orientation === 'NORMAL') {
        // Align các module khác
        allModels.forEach(model => {
          if (model !== moduleFirst) {
            const modelReal = GeometryUtils.moduleWithoutAccessory(
              model.clone()
            );
            const sizeModel = this.getSize(modelReal);
            model.position.z = moduleFirst.position.z;
            model.position.x =
              moduleFirst.position.x - (sizeModuleFirst.x - sizeModel.x) / 2;

            const modelReal2 = GeometryUtils.moduleWithoutAccessory(
              model.clone()
            );
            const box = new THREE.Box3().setFromObject(modelReal2);
            const size = new THREE.Vector3();
            box.getSize(size);
            const center = new THREE.Vector3();
            box.getCenter(center);
            positionController.updateModule(model.uuid, {
              id: model.uuid,
              x: center.x,
              y: center.y - size.y / 2,
              z: center.z,
              rotate: model.rotation,
            });
          }
        });
      } else if (
        orientation === 'ROTATED_90' ||
        orientation === 'ROTATED_270'
      ) {
        // Align các module khác
        allModels.forEach(model => {
          if (model !== moduleFirst) {
            const modelReal = GeometryUtils.moduleWithoutAccessory(
              model.clone()
            );
            const sizeModel = this.getSize(modelReal);
            model.position.x = moduleFirst.position.x;
            model.position.z =
              moduleFirst.position.z - (sizeModuleFirst.z - sizeModel.z) / 2;

            const modelReal2 = GeometryUtils.moduleWithoutAccessory(
              model.clone()
            );
            const box = new THREE.Box3().setFromObject(modelReal2);
            const size = new THREE.Vector3();
            box.getSize(size);
            const center = new THREE.Vector3();
            box.getCenter(center);
            positionController.updateModule(model.uuid, {
              id: model.uuid,
              x: center.x,
              y: center.y - size.y / 2,
              z: center.z,
              rotate: model.rotation,
            });
          }
        });
      }

      return true;
    } else {
      openNotification({
        key: 'cant-add-door-existing-door',
        icon: <Icon.ICWarning />,
        iconColor: '#F97D16',
        title: 'Không thể thực hiện đồng trục',
        description:
          'Để đồng trục theo chiều dọc, vui lòng chỉ chọn một module cho mỗi mức độ cao.',
        placement: 'bottomRight',
        closable: true,
        duration: 4,
      });
      return false;
    }
  }

  validateSingleGroup(group) {
    const firstRotation = group[0].rotation;
    const tolerance = 0.01;

    const allSameRotation = group.every(
      model =>
        Math.abs(model.rotation.x - firstRotation.x) < tolerance &&
        Math.abs(model.rotation.y - firstRotation.y) < tolerance &&
        Math.abs(model.rotation.z - firstRotation.z) < tolerance
    );

    if (allSameRotation) {
      console.log('SUCCESS: Chỉ có 1 nhóm và tất cả phần tử có cùng rotation');
      return true;
    } else {
      console.log(
        'FAIL: Chỉ có 1 nhóm nhưng các phần tử có rotation khác nhau'
      );
      return false;
    }
  }
}
